import React, { useState, useEffect, useRef, Suspense, lazy, memo, useMemo, useCallback, startTransition } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './App.css';

// Legal components
import TermsOfService from './components/legal/TermsOfService';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import ReturnPolicy from './components/legal/ReturnPolicy';
import ContactUs from './components/legal/ContactUs';
import DataDeletion from './components/legal/DataDeletion';

// Import utilities
import { VideoErrorBoundary, fixImageOrientation, isValidVideoUrl, BACKEND_URL, API, getAPI, getBACKEND_URL } from './utils';
import { getVideoUrl } from './utils/videoHelpers';
import { isExpiredR2Url, convertExpiredR2Url, getSafeImageUrl } from './utils/imageHelpers';

// Import MediaCacheContext for Phase 3 caching
import { MediaCacheProvider, useMediaCache } from './contexts/MediaCacheContext';

// Import gift card modals
import MailInServiceEGiftPreview from './components/gift/MailInServiceEGiftPreview';
import MailInGiftOccasionModal from './components/gift/MailInGiftOccasionModal';
import CreditsEGiftPreview from './components/gift/CreditsEGiftPreview';
import GiftCardPurchaseModal from './components/credits/GiftCardPurchaseModal';
import MailInCheckoutModal from './components/mailin/MailInCheckoutModal';
import WhatsNextModal from './components/mailin/WhatsNextModal';
import AnalysisLimitModal from './components/AnalysisLimitModal';

// Component to handle invitation acceptance for logged-in users
const InviteAcceptHandler = ({ user }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const acceptInvitation = async () => {
      if (!token || !user?.id) {
        navigate('/');
        return;
      }
      
      try {
        // Accept the invitation
        const response = await axios.post(`${getAPI()}/albums/accept-invite/${token}?user_id=${user.id}`);
        
        // Store album ID for redirect
        if (response.data.album_id) {
          localStorage.setItem('redirectToAlbum', response.data.album_id);
        }
        
        toast.success(`Invitation accepted! Welcome to "${response.data.album_title}"!`);
        
        // Switch to albums tab BEFORE navigating (so function exists)
        if (window.switchToAlbumsTab) {
          window.switchToAlbumsTab();
        }
        
        // Then redirect
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 100);
      } catch (error) {
        console.error('Failed to accept invitation:', error);
        const errorMsg = error.response?.data?.detail || 'Failed to accept invitation';
        
        // Check if invitation was already accepted
        if (errorMsg.includes('already') || errorMsg.includes('processed')) {
          toast.info('This invitation was already accepted. Opening albums...');
          
          // Switch to albums tab BEFORE navigating
          if (window.switchToAlbumsTab) {
            window.switchToAlbumsTab();
          }
          
          // Then redirect
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 100);
        } else {
          toast.error(errorMsg);
          navigate('/');
        }
      }
    };
    
    acceptInvitation();
  }, [token, user, navigate]);
  
  // Show loading state while accepting invitation
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-purple-800 font-medium text-lg">Accepting invitation...</p>
      </div>
    </div>
  );
};

// Import components with code splitting
import AuthContext from './components/auth/AuthContext';
import Logo from './components/Logo';
import PurchaseSuccess from './components/PurchaseSuccess';
const MemoryLoginForm = lazy(() => import('./components/auth/MemoryLoginForm'));
const LandingPage = lazy(() => import('./components/LandingPage'));
const PublicVideoWatch = lazy(() => import('./components/PublicVideoWatch'));
const PublicAlbumShare = lazy(() => import('./components/PublicAlbumShare'));
// const ComingSoonGate = lazy(() => import('./components/ComingSoonGate')); // REMOVED - Direct access enabled
const CheckoutPage = lazy(() => import('./components/CheckoutPage'));
const OrderSuccess = lazy(() => import('./components/OrderSuccess'));
const GiftRedeemPage = lazy(() => import('./components/GiftRedeemPage'));
const GiftSuccessPage = lazy(() => import('./components/GiftSuccessPage'));
const OrderConfirmation = lazy(() => import('./components/OrderConfirmation'));
const MemoryNavbar = lazy(() => import('./components/navigation/MemoryNavbar'));
const MemoryProfileModal = lazy(() => import('./components/support/MemoryProfileModal'));
const SupportSection = lazy(() => import('./components/support/SupportSection'));
const MemoryCreditsModal = lazy(() => import('./components/credits/MemoryCreditsModal'));
const BuyCreditsModal = lazy(() => import('./components/credits/BuyCreditsModal'));
const DigitalFramesSection = lazy(() => import('./components/credits/DigitalFramesSection'));
const EarnCreditsModal = lazy(() => import('./components/credits/EarnCreditsModal'));
const RedeemCreditsModal = lazy(() => import('./components/credits/RedeemCreditsModal'));
const PricingModal = lazy(() => import('./components/credits/PricingModal'));
const VideoCard = lazy(() => import('./components/video/VideoCard'));
const FolderSidebar = lazy(() => import('./components/folders/FolderSidebar'));
const FolderMobileDropdown = lazy(() => import('./components/folders/FolderMobileDropdown'));
const DigitalFrameDemo = lazy(() => import('./components/DigitalFrameDemo'));
const ShopTab = lazy(() => import('./components/ShopTab'));
const CartModal = lazy(() => import('./components/cart/CartModal'));
const AlbumsTab = lazy(() => import('./components/albums/AlbumsTab'));
const OrdersDashboard = lazy(() => import('./components/admin/OrdersDashboard'));
const AdminPanel = lazy(() => import('./components/admin/AdminPanel'));
const AdminMetricsDashboard = lazy(() => import('./components/admin/AdminMetricsDashboard'));
const ReferralDashboard = lazy(() => import('./components/ReferralDashboard'));
const MailInGiftCheckout = lazy(() => import('./components/gift/MailInGiftCheckout'));

// Performance optimization: Add loading="lazy" to all images
const LazyImage = memo(({ src, alt, className, ...props }) => (
  <img 
    src={src} 
    alt={alt} 
    className={className}
    loading="lazy"
    decoding="async"
    {...props}
  />
));

// Enhanced photo upload with memory theme and social media import
const MemoryPhotoUpload = ({ onClose, onUpload, projectId, user }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editing, setEditing] = useState({
    brightness: 100,
    contrast: 100,
    rotation: 0,
    sepia: 0
  });
  const [photoDetails, setPhotoDetails] = useState({
    name: '',
    description: ''
  });
  const [isUploading, setIsUploading] = useState(false);
  const [socialPhotos, setSocialPhotos] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('facebook');
  const [isLoadingSocialPhotos, setIsLoadingSocialPhotos] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef();

  const handleFileSelect = (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'image/hif', 'image/webp', 'image/gif'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'hif', 'webp', 'gif'];
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error(`Unsupported file type: ${file.name}. Please upload JPEG, PNG, HEIC, HEIF, HIF, WebP, or GIF images.`, {
        duration: 5000,
        position: 'top-center'
      });
      return;
    }
    
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreview(url);
    setPhotoDetails({
      name: file.name.replace(/\.[^/.]+$/, ""),
      description: ''
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0].type.startsWith('image/')) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !photoDetails.name.trim()) {
      toast.error('Please provide a name for your memory');
      return;
    }
    
    setIsUploading(true);
    try {
      // Fix orientation issues on mobile devices before upload
      const correctedFile = await fixImageOrientation(selectedFile);
      
      // Upload image first
      const formData = new FormData();
      formData.append('file', correctedFile);
      formData.append('edit_data', JSON.stringify(editing));
      formData.append('user_id', user.id); // CRITICAL: Pass user_id for data isolation
      
      formData.append('user_id', user.id);
      const uploadResponse = await axios.post(`${getAPI()}/upload`, formData);
      
      // Add to project if projectId provided
      if (projectId) {
        const projectFormData = new FormData();
        projectFormData.append('name', photoDetails.name);
        projectFormData.append('description', photoDetails.description);
        projectFormData.append('url', uploadResponse.data.image.url);
        projectFormData.append('thumbnail_url', uploadResponse.data.image.thumbnail_url);
        
        await axios.post(`${getAPI()}/projects/${projectId}/images`, projectFormData);
      }
      
      onUpload(uploadResponse.data.image);
      toast.success('Memory captured and ready to relive!');
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || 'Upload failed. Please try again.';
      toast.error(errorMessage, {
        duration: 6000,
        position: 'top-center'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const loadSocialPhotos = async () => {
    setIsLoadingSocialPhotos(true);
    try {
      // Mock access token - in real implementation, this would come from OAuth
      const mockAccessToken = 'demo_token_123';
      const response = await axios.get(`${getAPI()}/social-media/photos?user_id=${user.id}`, {
        params: {
          platform: selectedPlatform,
          access_token: mockAccessToken
        }
      });
      setSocialPhotos(response.data.photos || []);
    } catch (error) {
      toast.error(`Failed to load ${selectedPlatform} photos`);
      setSocialPhotos([]);
    } finally {
      setIsLoadingSocialPhotos(false);
    }
  };

  const handleImportPhoto = async (photo) => {
    if (!projectId) {
      toast.error('Please select a project first');
      return;
    }

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('photo_id', photo.id);
      formData.append('photo_url', photo.url);
      formData.append('caption', photo.caption || '');
      formData.append('platform', selectedPlatform);

      formData.append('user_id', user.id);
      const response = await axios.post(`${getAPI()}/social-media/import`, formData);
      
      onUpload(response.data.image);
      toast.success(`Photo imported from ${selectedPlatform}!`);
      onClose();
    } catch (error) {
      toast.error('Failed to import photo. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  // Clear all failed videos function
  const handleClearAllFailed = async () => {
    try {
      const failedVideoIds = videoJobs
        .filter(job => ['failed', 'timeout', 'error'].includes(job.status))
        .map(job => job.id);
      
      await axios.delete(`${getAPI()}/video-generations/failed/bulk?user_id=${user.id}`, {
        data: { video_ids: failedVideoIds }
      });
      
      toast.success(`Successfully deleted ${failedVideoIds.length} failed generations`);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to clear all failed generations');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-amber-200" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-amber-200 bg-gradient-to-r from-amber-100 to-orange-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Logo className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold text-amber-800">Add Memory</h2>
                <p className="text-amber-600">Upload photos or import from social media</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex space-x-1 bg-white/60 backdrop-blur-sm p-1 rounded-2xl mt-4">
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'upload'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-amber-700 hover:text-amber-900'
              }`}
            >
              📱 Upload from Device
            </button>
            <button
              onClick={() => setActiveTab('import')}
              className={`px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'import'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-amber-700 hover:text-amber-900'
              }`}
            >
              🌐 Import from Social Media
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {/* Upload Tab Content */}
          {activeTab === 'upload' && (
            <>
              {/* Upload Area */}
              {!selectedFile ? (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className="border-2 border-dashed border-amber-300 rounded-2xl p-12 text-center hover:border-amber-400 transition-colors cursor-pointer bg-gradient-to-br from-amber-50/50 to-orange-50/50"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-400 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-amber-800 mb-2">Choose Your Memory</h3>
              <p className="text-amber-600 mb-4">Drag and drop or click to select a photo</p>
              <p className="text-sm text-amber-500">JPG, PNG up to 10MB • Best memories deserve the best quality</p>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,image/hif,image/webp,image/gif"
                onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                className="hidden"
              />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Photo Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-2">Memory Name</label>
                  <input
                    type="text"
                    value={photoDetails.name}
                    onChange={(e) => setPhotoDetails({...photoDetails, name: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                    placeholder="e.g., Summer Vacation 2024"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-amber-800 mb-2">Description (optional)</label>
                  <input
                    type="text"
                    value={photoDetails.description}
                    onChange={(e) => setPhotoDetails({...photoDetails, description: e.target.value})}
                    className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                    placeholder="What makes this special?"
                  />
                </div>
              </div>
              
              {/* Preview */}
              <div className="relative bg-amber-100 rounded-2xl border-2 border-amber-200 overflow-hidden" style={{ minHeight: '256px' }}>
                <div 
                  className="w-full h-full flex items-center justify-center p-4"
                  style={{
                    transform: `rotate(${editing.rotation}deg)`,
                    transformOrigin: 'center',
                  }}
                >
                  <img loading="lazy"
                    src={preview}
                    alt="Preview"
                    className="max-w-full max-h-full object-cover"
                    style={{
                      filter: `brightness(${editing.brightness}%) contrast(${editing.contrast}%) sepia(${editing.sepia}%)`,
                    }}
                  />
                </div>
              </div>
              
              {/* Memory Enhancement Controls */}
              <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-2xl p-4 border border-amber-200">
                <h4 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Enhance Your Memory
                </h4>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-2">Brightness</label>
                    <input
                      type="range"
                      min="50"
                      max="150"
                      value={editing.brightness}
                      onChange={(e) => setEditing({...editing, brightness: e.target.value})}
                      className="w-full h-3 bg-purple-200 rounded-lg appearance-none slider cursor-pointer accent-purple-600"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-2">Contrast</label>
                    <input
                      type="range"
                      min="50"
                      max="150"
                      value={editing.contrast}
                      onChange={(e) => setEditing({...editing, contrast: e.target.value})}
                      className="w-full h-3 bg-purple-200 rounded-lg appearance-none slider cursor-pointer accent-purple-600"
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-purple-700 mb-2">Vintage Feel</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={editing.sepia}
                    onChange={(e) => setEditing({...editing, sepia: e.target.value})}
                    className="w-full h-3 bg-purple-200 rounded-lg appearance-none slider cursor-pointer accent-purple-600"
                  />
                </div>
                
                {/* Rotation Buttons */}
                <div className="flex items-center justify-center space-x-4">
                  <button
                    onClick={() => setEditing({...editing, rotation: editing.rotation - 90})}
                    className="w-12 h-12 bg-purple-500 text-white rounded-xl flex items-center justify-center hover:bg-purple-600 transition-colors shadow-sm"
                  >
                    <svg className="w-5 h-5 transform -rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  
                  <span className="text-sm text-purple-700 font-medium">Rotate Memory</span>
                  
                  <button
                    onClick={() => setEditing({...editing, rotation: editing.rotation + 90})}
                    className="w-12 h-12 bg-purple-500 text-white rounded-xl flex items-center justify-center hover:bg-purple-600 transition-colors shadow-sm"
                  >
                    <svg className="w-5 h-5 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreview(null);
                    setPhotoDetails({name: '', description: ''});
                    setEditing({brightness: 100, contrast: 100, rotation: 0, sepia: 0});
                  }}
                  className="flex-1 px-6 py-4 text-purple-700 bg-white border-2 border-purple-300 rounded-2xl hover:bg-purple-50 transition-colors font-medium"
                >
                  Choose Different Photo
                </button>
                
                <button
                  onClick={handleUpload}
                  disabled={isUploading || !photoDetails.name.trim()}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-2xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 font-medium shadow-lg flex items-center justify-center gap-2"
                >
                  {isUploading ? (
                    'Preserving Memory...'
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      Preserve This Moment
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
            </>
          )}
          
          {/* Import Tab Content */}
          {activeTab === 'import' && (
            <div className="space-y-6">
              {/* Platform Selection */}
              <div className="bg-gradient-to-r from-white/80 to-amber-50/80 rounded-2xl p-6 border border-amber-200">
                <h4 className="font-semibold text-amber-800 mb-4">🌐 Choose Social Media Platform</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <button
                    onClick={() => setSelectedPlatform('facebook')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedPlatform === 'facebook'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-amber-200 bg-white hover:bg-amber-50'
                    }`}
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                      </div>
                      <p className="font-medium text-gray-800">Facebook</p>
                      <p className="text-xs text-gray-600">Import from albums</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setSelectedPlatform('instagram')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedPlatform === 'instagram'
                        ? 'border-pink-500 bg-pink-50'
                        : 'border-amber-200 bg-white hover:bg-amber-50'
                    }`}
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </div>
                      <p className="font-medium text-gray-800">Instagram</p>
                      <p className="text-xs text-gray-600">Import from posts</p>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setSelectedPlatform('google_photos')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedPlatform === 'google_photos'
                        ? 'border-green-500 bg-green-50'
                        : 'border-amber-200 bg-white hover:bg-amber-50'
                    }`}
                  >
                    <div className="text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-green-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm5.568 16.568C16.152 18.484 14.136 19.5 12 19.5s-4.152-1.016-5.568-2.932C5.016 15.152 4 13.136 4 11s1.016-4.152 2.432-5.568C7.848 4.016 9.864 3 12 3s4.152 1.016 5.568 2.432C19.484 6.848 20.5 8.864 20.5 11s-1.016 4.152-2.932 5.568z"/>
                        </svg>
                      </div>
                      <p className="font-medium text-gray-800">Google Photos</p>
                      <p className="text-xs text-gray-600">Import from library</p>
                    </div>
                  </button>
                </div>
                
                <button
                  onClick={loadSocialPhotos}
                  disabled={isLoadingSocialPhotos}
                  className="w-full mt-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 font-medium"
                >
                  {isLoadingSocialPhotos ? 'Loading Photos...' : `🔗 Connect to ${selectedPlatform.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}`}
                </button>
              </div>
              
              {/* Social Photos Grid */}
              {socialPhotos.length > 0 && (
                <div className="bg-gradient-to-r from-white/80 to-amber-50/80 rounded-2xl p-6 border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Select Photos to Import
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
                    {socialPhotos.map((photo) => (
                      <div key={photo.id} className="relative group">
                        <div className="aspect-square bg-amber-100 rounded-xl overflow-hidden border border-amber-200">
                          <img loading="lazy"
                            src={photo.thumbnail_url || photo.url}
                            alt={photo.caption || 'Social media photo'}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                          <button
                            onClick={() => handleImportPhoto(photo)}
                            disabled={isImporting}
                            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50 font-medium text-sm flex items-center gap-2"
                          >
                            {isImporting ? (
                              'Importing...'
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                </svg>
                                Import
                              </>
                            )}
                          </button>
                        </div>
                        
                        {/* Caption */}
                        {photo.caption && (
                          <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs p-2 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                            {photo.caption.length > 50 ? `${photo.caption.slice(0, 50)}...` : photo.caption}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Empty state for social photos */}
              {socialPhotos.length === 0 && !isLoadingSocialPhotos && (
                <div className="text-center py-12 bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl border border-amber-200">
                  <div className="w-20 h-20 bg-gradient-to-br from-amber-200 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    🌐
                  </div>
                  <h3 className="text-xl font-bold text-amber-800 mb-2">Connect Your Social Media</h3>
                  <p className="text-amber-600 mb-6 max-w-md mx-auto">
                    Import photos from your favorite platforms and relive those special moments
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Batch Analysis Warning Modal Component
const BatchAnalysisWarningModal = ({ imageCount, freeRemaining, creditsNeeded, onConfirm, onCancel, onClose, onDontShowAgain }) => {
  const [isAnimating, setIsAnimating] = React.useState(true);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);
  
  React.useEffect(() => {
    // Stop animation after 6 seconds (2 cycles)
    const timer = setTimeout(() => setIsAnimating(false), 6000);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" 
      style={{ 
        zIndex: 2147483647, // Maximum safe integer for z-index
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <div 
        className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 rounded-3xl max-w-2xl w-full p-3 sm:p-4 md:p-6 shadow-2xl max-h-[95vh] overflow-y-auto" 
        style={{ 
          position: 'relative', 
          zIndex: 2147483647
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose || onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          title="Close without generating"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Header */}
        <div className="text-center mb-3 sm:mb-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-900 mb-2">AI Analysis Optional</h2>
        </div>
        
        {/* Animated Process Visual */}
        <div className="relative bg-white/50 backdrop-blur rounded-2xl p-2 sm:p-3 md:p-4 mb-3 sm:mb-4 overflow-hidden">
          <div className="flex items-center justify-between relative z-10">
            {/* Step 1: Picture */}
            <div className={`flex flex-col items-center transition-all duration-1000 ${isAnimating ? 'scale-110' : ''}`}>
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-amber-400 to-orange-400 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2 shadow-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-purple-700">Picture</span>
            </div>
            
            {/* Arrow 1 */}
            <div className="flex-1 mx-1 sm:mx-2 md:mx-4">
              <svg className={`w-full h-2 ${isAnimating ? 'animate-pulse' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 100 10">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M0 5 L90 5 M85 1 L95 5 L85 9" stroke="url(#gradient1)" />
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            
            {/* Step 2: AI Analysis */}
            <div className={`flex flex-col items-center transition-all duration-1000 delay-500 ${isAnimating ? 'scale-110' : ''}`}>
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2 shadow-lg">
                <svg className={`w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white ${isAnimating ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-purple-700">AI Analysis</span>
            </div>
            
            {/* Arrow 2 */}
            <div className="flex-1 mx-1 sm:mx-2 md:mx-4">
              <svg className={`w-full h-2 ${isAnimating ? 'animate-pulse' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 100 10">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M0 5 L90 5 M85 1 L95 5 L85 9" stroke="url(#gradient2)" />
                <defs>
                  <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            
            {/* Step 3: Optimized Prompt */}
            <div className={`flex flex-col items-center transition-all duration-1000 delay-1000 ${isAnimating ? 'scale-110' : ''}`}>
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-green-400 to-emerald-400 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2 shadow-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-purple-700">Smart Prompt</span>
            </div>
          </div>
          
          {/* Animated particles background */}
          {isAnimating && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-purple-400 rounded-full opacity-30 animate-float"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${i * 0.3}s`,
                    animationDuration: `${3 + Math.random() * 2}s`
                  }}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Info Text */}
        <div className="bg-purple-100 border-l-4 border-purple-500 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mr-2 sm:mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-purple-900 font-medium mb-1 text-sm sm:text-base">Compute-Intensive Process</p>
              <p className="text-purple-700 text-xs sm:text-sm">
                Our advanced AI analyzes each picture to understand composition, subjects, and motion potential — generating custom prompts that produce the most realistic and magical animations.
              </p>
            </div>
          </div>
        </div>
        
        {/* Cost Display */}
        <div className="bg-white rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border-2 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs sm:text-sm">Analyzing {imageCount} picture{imageCount !== 1 ? 's' : ''}</p>
              {freeRemaining > 0 ? (
                <p className="text-green-600 font-semibold text-base sm:text-lg">
                  Free ({freeRemaining} free {freeRemaining === 1 ? 'analysis' : 'analyses'} remaining)
                </p>
              ) : (
                <p className="text-purple-600 font-semibold text-base sm:text-lg">
                  Cost: {creditsNeeded.toFixed(2)} credit{creditsNeeded !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className="text-right">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Pricing Info */}
        <div className="bg-purple-50 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4 border border-purple-200">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-purple-700 text-xs sm:text-sm">
              <span className="font-semibold">Pricing:</span> 0.05 credits per image / 1 credit = 20 image analyses
            </p>
          </div>
        </div>
        
        {/* Don't Show Again Checkbox */}
        {onDontShowAgain && (
          <div className="mb-3 sm:mb-4">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 text-purple-600 bg-white border-purple-300 rounded focus:ring-purple-500 focus:ring-2 cursor-pointer"
              />
              <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                Don't show this message again
              </span>
            </label>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={() => {
              if (dontShowAgain && onDontShowAgain) {
                onDontShowAgain();
              }
              onCancel();
            }}
            className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium text-sm sm:text-base"
          >
            Proceed without analysis (Free)
          </button>
          <button
            onClick={() => {
              if (dontShowAgain && onDontShowAgain) {
                onDontShowAgain();
              }
              onConfirm();
            }}
            className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all font-medium shadow-lg transform hover:scale-105 animate-pulse-slow text-sm sm:text-base"
          >
            Continue with AI Analysis ({imageCount} {imageCount === 1 ? 'picture' : 'pictures'})
          </button>
        </div>
      </div>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-15px) translateX(5px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-slow {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.3), 0 4px 6px -2px rgba(139, 92, 246, 0.2);
          }
          50% { 
            transform: scale(1.05);
            box-shadow: 0 20px 25px -5px rgba(139, 92, 246, 0.4), 0 10px 10px -5px rgba(139, 92, 246, 0.3);
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Single Image Analysis Warning Modal Component (for individual animations)
const SingleImageAnalysisWarningModal = ({ imageData, freeRemaining, creditsNeeded, onConfirm, onCancel, onClose, onDontShowAgain }) => {
  const [isAnimating, setIsAnimating] = React.useState(true);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);
  
  React.useEffect(() => {
    // Stop animation after 6 seconds (2 cycles)
    const timer = setTimeout(() => setIsAnimating(false), 6000);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" 
      style={{ 
        zIndex: 2147483647,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <div 
        className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 rounded-3xl max-w-2xl w-full p-3 sm:p-4 md:p-6 shadow-2xl max-h-[95vh] overflow-y-auto" 
        style={{ 
          position: 'relative', 
          zIndex: 2147483647
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose || onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          title="Close without generating"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Header */}
        <div className="text-center mb-3 sm:mb-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-purple-900 mb-2">AI Analysis Optional</h2>
        </div>
        
        {/* Animated Process Visual */}
        <div className="relative bg-white/50 backdrop-blur rounded-2xl p-2 sm:p-3 md:p-4 mb-3 sm:mb-4 overflow-hidden">
          <div className="flex items-center justify-between relative z-10">
            {/* Step 1: Picture */}
            <div className={`flex flex-col items-center transition-all duration-1000 ${isAnimating ? 'scale-110' : ''}`}>
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-amber-400 to-orange-400 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2 shadow-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-purple-700">Picture</span>
            </div>
            
            {/* Arrow 1 */}
            <div className="flex-1 mx-1 sm:mx-2 md:mx-4">
              <svg className={`w-full h-2 ${isAnimating ? 'animate-pulse' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 100 10">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M0 5 L90 5 M85 1 L95 5 L85 9" stroke="url(#gradient1-single)" />
                <defs>
                  <linearGradient id="gradient1-single" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            
            {/* Step 2: AI Analysis */}
            <div className={`flex flex-col items-center transition-all duration-1000 delay-500 ${isAnimating ? 'scale-110' : ''}`}>
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2 shadow-lg">
                <svg className={`w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white ${isAnimating ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-purple-700">AI Analysis</span>
            </div>
            
            {/* Arrow 2 */}
            <div className="flex-1 mx-1 sm:mx-2 md:mx-4">
              <svg className={`w-full h-2 ${isAnimating ? 'animate-pulse' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 100 10">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M0 5 L90 5 M85 1 L95 5 L85 9" stroke="url(#gradient2-single)" />
                <defs>
                  <linearGradient id="gradient2-single" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            
            {/* Step 3: Optimized Prompt */}
            <div className={`flex flex-col items-center transition-all duration-1000 delay-1000 ${isAnimating ? 'scale-110' : ''}`}>
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-br from-green-400 to-emerald-400 rounded-lg sm:rounded-xl flex items-center justify-center mb-1 sm:mb-2 shadow-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-[10px] sm:text-xs font-medium text-purple-700">Smart Prompt</span>
            </div>
          </div>
          
          {/* Animated particles background */}
          {isAnimating && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(10)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-purple-400 rounded-full opacity-30 animate-float"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${i * 0.3}s`,
                    animationDuration: `${3 + Math.random() * 2}s`
                  }}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Info Text */}
        <div className="bg-purple-100 border-l-4 border-purple-500 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mr-2 sm:mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-purple-900 font-medium mb-1 text-sm sm:text-base">Compute-Intensive Process</p>
              <p className="text-purple-700 text-xs sm:text-sm">
                Our AI analyzes the picture to understand composition, subjects, and motion potential — generating a custom prompt for the most realistic animation.
              </p>
            </div>
          </div>
        </div>
        
        {/* Cost Display */}
        <div className="bg-white rounded-xl p-3 sm:p-4 mb-3 sm:mb-4 border-2 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs sm:text-sm">AI Analysis Cost</p>
              {freeRemaining > 0 ? (
                <p className="text-green-600 font-semibold text-base sm:text-lg">
                  Free ({freeRemaining} free remaining)
                </p>
              ) : (
                <p className="text-purple-600 font-semibold text-base sm:text-lg">
                  {creditsNeeded.toFixed(2)} credits
                </p>
              )}
            </div>
            <div className="text-right">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Pricing Info */}
        <div className="bg-purple-50 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4 border border-purple-200">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-purple-700 text-xs sm:text-sm">
              <span className="font-semibold">Pricing:</span> 0.05 credits per image / 1 credit = 20 image analyses
            </p>
          </div>
        </div>
        
        {/* Don't Show Again Checkbox */}
        {onDontShowAgain && (
          <div className="mb-3 sm:mb-4">
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 text-purple-600 bg-white border-purple-300 rounded focus:ring-purple-500 focus:ring-2 cursor-pointer"
              />
              <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                Don't show this message again
              </span>
            </label>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button
            onClick={() => {
              if (dontShowAgain && onDontShowAgain) {
                onDontShowAgain();
              }
              onCancel();
            }}
            className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium text-sm sm:text-base"
          >
            Proceed without analysis (Free)
          </button>
          <button
            onClick={() => {
              if (dontShowAgain && onDontShowAgain) {
                onDontShowAgain();
              }
              onConfirm();
            }}
            className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all font-medium shadow-lg transform hover:scale-105 animate-pulse-slow text-sm sm:text-base"
          >
            Continue with AI Analysis
          </button>
        </div>
      </div>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-15px) translateX(5px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes pulse-slow {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.3), 0 4px 6px -2px rgba(139, 92, 246, 0.2);
          }
          50% { 
            transform: scale(1.05);
            box-shadow: 0 20px 25px -5px rgba(139, 92, 246, 0.4), 0 10px 10px -5px rgba(139, 92, 246, 0.3);
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Portal Wrapper for Batch Analysis Warning Modal
const BatchAnalysisWarningModalPortal = (props) => {
  return ReactDOM.createPortal(
    <BatchAnalysisWarningModal {...props} />,
    document.body
  );
};

// Portal Wrapper for Single Image Analysis Warning Modal
const SingleImageAnalysisWarningModalPortal = (props) => {
  return ReactDOM.createPortal(
    <SingleImageAnalysisWarningModal {...props} />,
    document.body
  );
};

// Portal Wrapper for Buy Credits Modal
const BuyCreditsModalPortal = (props) => {
  return ReactDOM.createPortal(
    <Suspense fallback={<div />}>
      <BuyCreditsModal {...props} />
    </Suspense>,
    document.body
  );
};

// Portal Wrapper for Batch Animation Modal
const BatchAnimationModalPortal = (props) => {
  return ReactDOM.createPortal(
    <BatchAnimationModal {...props} />,
    document.body
  );
};

// Portal Wrapper for Pricing Modal
const PricingModalPortal = (props) => {
  return ReactDOM.createPortal(
    <PricingModal {...props} />,
    document.body
  );
};

// Portal Wrapper for Profile Modal
const MemoryProfileModalPortal = (props) => {
  return ReactDOM.createPortal(
    <Suspense fallback={<div />}>
      <MemoryProfileModal {...props} />
    </Suspense>,
    document.body
  );
};

// Portal Wrapper for Failed Video Details Modal
const FailedVideoDetailsModalPortal = (props) => {
  return ReactDOM.createPortal(
    <FailedVideoDetailsModal {...props} />,
    document.body
  );
};

// Portal Wrapper for Video Gallery Modal
const VideoGalleryModalPortal = (props) => {
  return ReactDOM.createPortal(
    <VideoGalleryModal {...props} />,
    document.body
  );
};

// Portal Wrapper for Image Editor Modal
const ImageEditorModalPortal = (props) => {
  return ReactDOM.createPortal(
    <ImageEditorModal {...props} />,
    document.body
  );
};

// Batch Animation Modal Component
const BatchAnimationModal = ({ images, prompts, isSubmitting, isBatchAnalyzing = false, analysisCompleted = 0, estimatedProgress = 0, onCancelAnalysis, onClose, onRegeneratePrompt, onUpdatePrompt, onEditImage, onSubmitBatch, user, regenerationCounts = {}, freeRegenerationsLimit = 1, usedAIAnalysis = false }) => {
  // Helper to detect R2 presigned URLs
  const isPresignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  };
  
  // Helper function to generate image URL with user_id and authenticated_user_id
  const getImageUrl = (img) => {
    if (!img || !user?.id) return '';
    
    // Get backend URL at runtime
    const BACKEND_URL = getBACKEND_URL();
    const API = getAPI();
    
    // Get the authenticated user ID for admin impersonation support
    const effectiveUserId = user.id;
    const originalAdmin = localStorage.getItem('originalAdmin');
    const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : effectiveUserId;

    // PRIORITY 1: Direct Cloudflare CDN URLs (fastest, no proxy)
    if (img.cloudflare_delivery_url && 
        (img.cloudflare_delivery_url.includes('imagedelivery.net') || 
         img.cloudflare_delivery_url.includes('cloudflarestream.com') ||
         img.cloudflare_delivery_url.includes('customer-'))) {
      return img.cloudflare_delivery_url;
    }

    // PRIORITY 2: Check if existing URLs are Cloudflare URLs
    const candidate = img.thumbnail_url || img.url || img.r2_url || img.animated_url;
    if (candidate && 
        (candidate.includes('imagedelivery.net') || 
         candidate.includes('cloudflarestream.com') ||
         candidate.includes('customer-'))) {
      return candidate;
    }
    
    // Legacy: Prefer album-provided thumbnails or r2 URLs
    if (candidate) {
      // NEVER modify presigned URLs - they have cryptographic signatures
      if (isPresignedUrl(candidate)) {
        console.log('⚠️ Detected presigned URL, returning as-is:', candidate.substring(0, 100));
        return candidate;
      }
      
      // If full URL, check if it needs auth parameters
      if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
        // If URL points to our API and doesn't have auth params, add them
        if ((candidate.includes('/api/files/') || candidate.includes('/api/r2/')) && 
            (!candidate.includes('user_id=') || !candidate.includes('authenticated_user_id='))) {
          const separator = candidate.includes('?') ? '&' : '?';
          const hasUserId = candidate.includes('user_id=');
          const hasAuthId = candidate.includes('authenticated_user_id=');
          let urlWithParams = candidate;
          if (!hasUserId) {
            urlWithParams += `${separator}user_id=${effectiveUserId}`;
          }
          if (!hasAuthId) {
            const sep2 = urlWithParams.includes('?') ? '&' : '?';
            urlWithParams += `${sep2}authenticated_user_id=${authenticatedUserId}`;
          }
          return urlWithParams;
        }
        return candidate;
      }
      // If relative path, prepend backend URL
      const base = candidate.split('?')[0];
      if (candidate.includes('user_id=') && candidate.includes('authenticated_user_id=')) {
        // Already has auth params, just prepend backend URL if relative
        if (base.startsWith('/api/')) {
          return `${BACKEND_URL}${candidate}`;
        }
        // Fix absolute wrong-domain URLs by rewriting to API domain (debug-gated)
        if ((candidate.includes('https://magic-moments.ai/api/')) && localStorage.getItem('debugMM') === '1') {
          const path = candidate.split('magic-moments.ai')[1];
          const cleanPath = path.startsWith('/api/') ? path.substring(4) : path;
          console.log('🔧 [debugMM] Rewriting wrong-domain candidate to API domain');
          return `${API}${cleanPath}`;
        }
        return candidate;
      }
      const ts = img.updated_at || img._cacheBuster || Date.now();
      // Prepend backend URL for relative paths
      if (base.startsWith('/api/')) {
        return `${BACKEND_URL}${base}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&v=${ts}`;
      }
      return `${base}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&v=${ts}`;
    }
    
    // If image has a generic url string (non-full), sanitize it
    if (img.url) {
      // NEVER modify presigned URLs
      if (isPresignedUrl(img.url)) {
        return img.url;
      }
      
      if (img.url.startsWith('http://') || img.url.startsWith('https://')) {
        return img.url;
      }
      
      // Demo content or landing-assets with relative paths
      if (img.url.startsWith('/landing-assets/')) {
        return img.url;
      }
      
      const baseUrl = img.url.split('?')[0];
      if (img.url.includes('user_id=') && img.url.includes('authenticated_user_id=')) {
        // Already has auth params, just prepend backend URL if relative
        if (baseUrl.startsWith('/api/')) {
          return `${BACKEND_URL}${img.url}`;
        }
        return img.url;
      }
      const timestamp = img.updated_at || img._cacheBuster || Date.now();
      // Prepend backend URL for relative paths
      if (baseUrl.startsWith('/api/')) {
        return `${BACKEND_URL}${baseUrl}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&v=${timestamp}`;
      }
      return `${baseUrl}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&v=${timestamp}`;
    }
    
    // Construct URL from image ID with backend URL
    const imageId = img.id || img._id;
    if (!imageId) return '';
    const timestamp = img.updated_at || img._cacheBuster || Date.now();
    return `${BACKEND_URL}/api/files/${imageId}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&v=${timestamp}`;
  };
  
  const [selectedImage, setSelectedImage] = useState(null);
  
  console.log('🎬 BatchAnimationModal rendered with', images.length, 'images', 'isSubmitting:', isSubmitting);
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-2 sm:p-4" 
      style={{ 
        zIndex: 2147483647, // Maximum safe integer for z-index
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <div 
        className="bg-gradient-to-br from-purple-50 via-blue-50 to-white rounded-2xl sm:rounded-3xl max-w-7xl w-full max-h-[90vh] overflow-y-auto p-3 sm:p-5 md:p-8 shadow-2xl border border-purple-200/50"
        style={{ 
          position: 'relative', 
          zIndex: 2147483647
        }}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 md:mb-8 gap-3 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                <span className="hidden sm:inline">Batch Animation Generator</span>
                <span className="sm:hidden">Batch Animator</span>
              </h2>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base mt-0.5 sm:mt-1 line-clamp-1">Review AI-generated prompts and customize</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Removed visible Cancel Analysis button - functionality merged into onClose handler */}
            <button
              onClick={onClose}
              className="w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-md border border-gray-200 flex-shrink-0 self-end sm:self-auto absolute top-3 right-3 sm:relative sm:top-0 sm:right-0"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Progress Bar - Show when analyzing */}
        {isBatchAnalyzing && (
          <div className="mb-6 bg-gradient-to-r from-purple-50 via-blue-50 to-purple-50 rounded-2xl p-6 shadow-lg border-2 border-purple-200/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Analyzing Images with AI</h3>
                  <p className="text-sm text-gray-600">Generating custom animation prompts...</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  {analysisCompleted} / {images.length}
                </div>
                <div className="text-xs text-gray-500 font-medium">Completed</div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="relative">
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 via-blue-500 to-purple-600 rounded-full transition-all duration-300 ease-out relative overflow-hidden"
                  style={{ width: `${Math.min(estimatedProgress, 100)}%` }}
                >
                  {/* Animated shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500 text-center font-medium">
                {Math.round(estimatedProgress)}% Complete
              </div>
            </div>
            
            {/* Individual image status indicators */}
            <div className="mt-4 flex flex-wrap gap-2">
              {images.map((img, index) => {
                const promptData = prompts[img.id] || { prompt: '', isGenerating: true };
                const isCompleted = !promptData.isGenerating && promptData.prompt;
                const isAnalyzing = index < analysisCompleted && promptData.isGenerating;
                const isPending = index >= analysisCompleted;
                
                return (
                  <div 
                    key={img.id}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isCompleted 
                        ? 'bg-green-100 text-green-700 border border-green-300' 
                        : isAnalyzing 
                        ? 'bg-blue-100 text-blue-700 border border-blue-300 animate-pulse' 
                        : 'bg-gray-100 text-gray-500 border border-gray-200'
                    }`}
                  >
                    {isCompleted ? (
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : isAnalyzing ? (
                      <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span>
                      Image {index + 1}
                      {isCompleted ? ' ✓' : isAnalyzing ? ' ...' : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
          {images.map((img) => {
            const promptData = prompts[img.id] || { prompt: '', isGenerating: true };
            
            return (
              <div key={img.id} className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl border border-purple-100/50 hover:shadow-2xl transition-all hover:scale-[1.02]">
                <div className="aspect-video bg-gradient-to-br from-purple-100 to-blue-100 relative cursor-pointer group" onClick={() => setSelectedImage(img)}>
                  <img 
                    src={getImageUrl(img)}
                    alt="Selected for animation"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-blue-500/0 group-hover:from-purple-500/20 group-hover:to-blue-500/20 flex items-center justify-center transition-all">
                    <div className="opacity-0 group-hover:opacity-100 bg-white/90 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-gray-700 text-xs sm:text-sm font-medium shadow-lg">
                      Click to view
                    </div>
                  </div>
                </div>
                
                <div className="p-3 sm:p-4">
                  {promptData.isGenerating ? (
                    <div className="flex items-center gap-2 sm:gap-3 text-purple-600 py-6 sm:py-8 justify-center">
                      <div className="animate-spin w-4 h-4 sm:w-5 sm:h-5 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                      <span className="text-xs sm:text-sm font-medium">Generating prompt...</span>
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={promptData.prompt}
                        onChange={(e) => onUpdatePrompt(img.id, e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm border-2 border-purple-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none transition-all"
                        rows={3}
                        placeholder="Animation prompt..."
                      />
                      {usedAIAnalysis && (
                        <div className="mt-2 sm:mt-3">
                          <button
                            onClick={() => onRegeneratePrompt(img.id)}
                            className="w-full text-xs sm:text-sm bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl hover:from-purple-200 hover:to-blue-200 transition-all font-medium shadow-sm"
                          >
                            Generate new prompt (0.05 credits)
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 md:gap-4 pt-4 sm:pt-5 md:pt-6 border-t-2 border-purple-100">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-200 transition-colors font-semibold shadow-sm text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            onClick={onSubmitBatch}
            disabled={Object.values(prompts).some(p => p.isGenerating) || isSubmitting}
            className="px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg sm:rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            {isSubmitting ? (
              <>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span className="hidden sm:inline">Animate {images.length} image{images.length !== 1 ? 's' : ''} ({images.length * 5} credits)</span>
                <span className="sm:hidden">Animate ({images.length * 5} credits)</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Individual Image Edit Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" style={{zIndex: 2147483647}} onClick={() => setSelectedImage(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Edit Prompt</h3>
              <button onClick={() => setSelectedImage(null)} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <img 
                  key={`batch-image-${selectedImage.id}-${Date.now()}-${selectedImage.url?.match(/_cb=(\d+)/)?.[1] || 'no-cache'}`}
                  src={getImageUrl(selectedImage)}
                  alt="Preview"
                  className="w-full rounded-lg max-h-[400px] object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Animation Prompt</label>
                <textarea
                  value={prompts[selectedImage.id]?.prompt || ''}
                  onChange={(e) => onUpdatePrompt(selectedImage.id, e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
                  rows={6}
                  placeholder="Describe how you want this image to be animated..."
                  disabled={prompts[selectedImage.id]?.isGenerating}
                />
                <button
                  onClick={() => onRegeneratePrompt(selectedImage.id)}
                  disabled={prompts[selectedImage.id]?.isGenerating}
                  className="mt-3 w-full bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {prompts[selectedImage.id]?.isGenerating ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <span>Generate new prompt</span>
                      <span className="text-xs opacity-90">(0.05 credits)</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setSelectedImage(null)}
                className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========================================
// PREMIUM MAGICAL PROCESSING ANIMATION - Professional & Sophisticated
// ========================================
const MagicProcessingOverlay = ({ message, count }) => {
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-indigo-950 via-purple-900 to-rose-950 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 z-[200] overflow-y-auto">
      {/* Subtle animated background particles */}
      <div className="absolute inset-0 opacity-20">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-amber-300 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      <div className="text-center w-full max-w-[95vw] sm:max-w-3xl px-4 sm:px-8 relative z-10">
        {/* Main animation container */}
        <div className="relative mb-10">
          {/* Outer elegant rotating ring */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-80 h-80 rounded-full border-2 border-amber-400/20 animate-spin" style={{ animationDuration: '20s' }}></div>
          </div>
          
          {/* Middle ring - counter rotation */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-72 h-72 rounded-full border border-purple-400/20 animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}></div>
          </div>

          {/* Elegant sparkle orbit - 8 large sparkles */}
          <div className="absolute inset-0 flex items-center justify-center">
            {[...Array(8)].map((_, i) => {
              const angle = (i * 360) / 8;
              return (
                <div
                  key={i}
                  className="absolute"
                  style={{
                    animation: `orbit 8s linear infinite`,
                    animationDelay: `${i * 0.3}s`,
                  }}
                >
                  <div
                    className="w-4 h-4 bg-gradient-to-br from-amber-300 to-yellow-400 rounded-full shadow-lg shadow-amber-400/50 animate-pulse"
                    style={{
                      animationDelay: `${i * 0.2}s`,
                      animationDuration: '2s',
                      filter: 'blur(0.5px)',
                    }}
                  />
                </div>
              );
            })}
          </div>

          {/* Center - Large logo with wand effect */}
          <div className="relative w-80 h-80 mx-auto flex items-center justify-center">
            {/* Pulsing glow background */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-48 h-48 bg-gradient-to-br from-amber-500/20 to-purple-500/20 rounded-full animate-pulse blur-2xl" style={{ animationDuration: '3s' }}></div>
            </div>
            
            {/* Video frame outline being "drawn" */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-32 border-4 border-amber-400/40 rounded-lg relative overflow-hidden">
                {/* Scanning line effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-300/30 to-transparent animate-pulse" style={{ animationDuration: '2s' }}></div>
              </div>
            </div>

            {/* Main logo - prominent and centered (wand hidden since we have animated one) */}
            <div className="relative z-10 logo-without-wand">
              <Logo className="w-32 h-32 drop-shadow-2xl" animated />
            </div>
            
            {/* Animated magic wand tapping the button - using logo's wand design */}
            <div className="absolute z-20" style={{ 
              bottom: 'calc(38% - 110px)',
              right: 'calc(37% - 110px)',
              animation: 'wandTapGentle 4s ease-in-out infinite',
              transformOrigin: '70px 70px'
            }}>
              <svg width="200" height="200" viewBox="0 0 200 200" className="drop-shadow-2xl">
                <defs>
                  <radialGradient id="wandGoldGradient" cx="35%" cy="35%">
                    <stop offset="0%" style={{ stopColor: '#FFF4D6', stopOpacity: 1 }} />
                    <stop offset="20%" style={{ stopColor: '#FDE68A', stopOpacity: 1 }} />
                    <stop offset="45%" style={{ stopColor: '#FBBF24', stopOpacity: 1 }} />
                    <stop offset="70%" style={{ stopColor: '#F59E0B', stopOpacity: 1 }} />
                    <stop offset="90%" style={{ stopColor: '#D97706', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#B45309', stopOpacity: 1 }} />
                  </radialGradient>
                  <radialGradient id="wandGoldShine" cx="65%" cy="30%">
                    <stop offset="0%" style={{ stopColor: '#FFFEF5', stopOpacity: 0.9 }} />
                    <stop offset="40%" style={{ stopColor: '#FEF3C7', stopOpacity: 0.5 }} />
                    <stop offset="80%" style={{ stopColor: '#F59E0B', stopOpacity: 0.2 }} />
                    <stop offset="100%" style={{ stopColor: '#D97706', stopOpacity: 0 }} />
                  </radialGradient>
                </defs>
                
                {/* Black wand shaft - shortened by 25% total, star at (70,70), handle closer */}
                <line x1="70" y1="70" x2="154" y2="154" stroke="#1F2937" strokeWidth="8" strokeLinecap="round"/>
                
                {/* Handle cap at closer position */}
                <circle cx="154" cy="154" r="5" fill="#1F2937"/>
                
                {/* Golden 5-pointed star at tapping point (70, 70) */}
                <g transform="translate(70, 70) rotate(27)">
                  {/* Rich gold base */}
                  <path d="M0,-16 L3.5,-5 L15,-3.5 L6,3.5 L9,15 L0,9 L-9,15 L-6,3.5 L-15,-3.5 L-3.5,-5 Z" 
                        fill="url(#wandGoldGradient)"
                        stroke="#92400E" 
                        strokeWidth="1.5"
                        strokeLinejoin="round"/>
                  
                  {/* Shine layer */}
                  <path d="M0,-12 L2.5,-3.5 L10,-2.5 L5,2.5 L7,10 L0,6 L-7,10 L-5,2.5 L-10,-2.5 L-2.5,-3.5 Z" 
                        fill="url(#wandGoldShine)"/>
                  
                  {/* Highlight */}
                  <path d="M0,-9 L2,-2.5 L7,-1.5 L3,2 L5,7 L0,4 L-5,7 L-3,2 L-7,-1.5 L-2,-2.5 Z" 
                        fill="white"
                        opacity="0.4"/>
                </g>
              </svg>
            </div>
            
            {/* Sparkle burst at contact point where star taps the button */}
            <div className="absolute" style={{ bottom: '45%', right: '44%' }}>
              {/* Large central glow - pulses when wand taps */}
              <div 
                className="absolute w-6 h-6 bg-white rounded-full animate-pulse opacity-90 blur-sm" 
                style={{ 
                  animationDuration: '1s',
                  animation: 'tapBurst 4s ease-in-out infinite'
                }}
              ></div>
              <div 
                className="absolute w-4 h-4 bg-amber-300 rounded-full animate-pulse" 
                style={{ 
                  animationDuration: '0.8s',
                  animation: 'tapBurst 4s ease-in-out infinite'
                }}
              ></div>
              
              {/* Large radiating sparkles - burst when wand taps */}
              {[...Array(16)].map((_, i) => {
                const angle = (i * 360) / 16;
                const distance = 25 + (i % 4) * 10;
                const size = i % 2 === 0 ? 'w-3 h-3' : 'w-2 h-2';
                return (
                  <div
                    key={i}
                    className={`absolute ${size} bg-gradient-to-br from-amber-200 to-yellow-400 rounded-full shadow-lg shadow-amber-400/50`}
                    style={{
                      transform: `rotate(${angle}deg) translateX(${distance}px)`,
                      animationDelay: `${i * 0.08}s`,
                      animation: 'sparkleExpand 4s ease-out infinite',
                      opacity: 0.9,
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Elegant progress arc */}
          <svg className="absolute inset-0 w-full h-full" style={{ transform: 'rotate(-90deg)' }}>
            <circle
              cx="50%"
              cy="50%"
              r="38%"
              fill="none"
              stroke="rgba(251, 191, 36, 0.3)"
              strokeWidth="2"
              strokeDasharray="600"
              strokeDashoffset="150"
              className="animate-pulse"
              style={{ animationDuration: '3s' }}
            />
          </svg>
        </div>

        {/* Premium typography */}
        <div className="space-y-6">
          <h2 className="text-4xl md:text-5xl font-light text-white tracking-wide mb-3">
            Bringing Your Memories to Life
          </h2>
          
          <p className="text-xl text-amber-200/90 font-light tracking-wide">
            {message || `Animating ${count} precious moment${count !== 1 ? 's' : ''}...`}
          </p>

          {/* Refined progress indicator */}
          <div className="flex justify-center items-center space-x-2 my-8">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-amber-400/50"></div>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"
                style={{
                  animationDelay: `${i * 0.3}s`,
                  animationDuration: '1.5s',
                }}
              />
            ))}
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-amber-400/50"></div>
          </div>

          {/* Sophisticated status messages */}
          <div className="space-y-3 text-purple-200/80 text-base font-light tracking-wide max-w-lg mx-auto">
            <p className="animate-pulse" style={{ animationDelay: '0s', animationDuration: '4s' }}>
              Your wand is working its magic...
            </p>
            <p className="animate-pulse" style={{ animationDelay: '1.5s', animationDuration: '4s' }}>
              Infusing life into every frame
            </p>
            <p className="animate-pulse" style={{ animationDelay: '3s', animationDuration: '4s' }}>
              Crafting your magic moment
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Hide the wand from the logo since we have an animated one */
        .logo-without-wand svg line[stroke="#1F2937"] {
          display: none !important;
        }
        .logo-without-wand svg circle[fill="#1F2937"] {
          display: none !important;
        }
        .logo-without-wand svg g[transform*="translate(-3, 3)"] {
          display: none !important;
        }
        .logo-without-wand svg g[transform*="rotate(27 88 88)"] {
          display: none !important;
        }
        
        @keyframes orbit {
          0% {
            transform: rotate(0deg) translateX(140px) rotate(0deg);
          }
          100% {
            transform: rotate(360deg) translateX(140px) rotate(-360deg);
          }
        }
        
        @keyframes wandTapGentle {
          0%, 100% {
            transform: translateY(0px) translateX(0px) rotate(0deg);
          }
          40% {
            transform: translateY(-5px) translateX(-2px) rotate(-3deg);
          }
          50% {
            transform: translateY(8px) translateX(3px) rotate(4deg);
          }
          60% {
            transform: translateY(-3px) translateX(-1px) rotate(-1deg);
          }
        }
        
        @keyframes tapBurst {
          0%, 40%, 60%, 100% {
            transform: scale(1);
            opacity: 0.2;
          }
          50% {
            transform: scale(1.5);
            opacity: 1;
          }
        }
        
        @keyframes sparkleExpand {
          0%, 40%, 100% {
            transform: scale(0.5);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.5);
            opacity: 1;
          }
          60% {
            transform: scale(1);
            opacity: 0.7;
          }
        }
      `}</style>
    </div>
  );
};

// ========================================
// SHARED AI ANALYSIS "BRAIN" - Single source of truth for AI image analysis
// This function is used by both Animation Generator and Batch Animation
// Any improvements/training here automatically benefit both features
// ========================================
const analyzeImageWithAI = async (imageData, enhancedPrompt = null, user = null, jobId = null) => {
  // Reduced logging for batch operations - only log if DEBUG mode or error
  const DEBUG_AI_ANALYSIS = false; // Set to true for detailed debugging
  
  if (DEBUG_AI_ANALYSIS) {
    console.log('🧠 analyzeImageWithAI called');
    console.log('📸 Image data:', imageData ? 'present' : 'missing');
    console.log('👤 User:', user?.id);
    console.log('✨ Enhanced prompt parameter:', enhancedPrompt);
  }
  
  try {
    const formData = new FormData();
    
    // Handle different image input types
    if (imageData.url && imageData.url.startsWith('data:')) {
      // For edited images (data URLs), convert to blob
      if (DEBUG_AI_ANALYSIS) console.log('🎨 Analyzing edited image (data URL)');
      const response = await fetch(imageData.url);
      const blob = await response.blob();
      formData.append('image', blob, `edited_image_${imageData.editTimestamp || Date.now()}.jpg`);
    } else {
      // For original images, use URL with user_id
      if (DEBUG_AI_ANALYSIS) console.log('🖼️ Analyzing original image (URL)');
      const imageId = imageData.id || imageData._id;
      const existingUrl = imageData.thumbnail_url || imageData.url;
      let fullImageUrl;
      
      // Check if image already has a full URL (e.g., synthetic images from album videos)
      if (existingUrl && existingUrl.startsWith('http')) {
        if (DEBUG_AI_ANALYSIS) console.log('🔗 Using existing full URL from image data');
        fullImageUrl = existingUrl;
        // Ensure user_id and authenticated_user_id are present for internal /api/files/ or /api/r2/ URLs
        if ((fullImageUrl.includes('/api/files/') || fullImageUrl.includes('/api/r2/')) && user?.id) {
          const hasUserId = fullImageUrl.includes('user_id=');
          const hasAuthId = fullImageUrl.includes('authenticated_user_id=');
          
          if (!hasUserId || !hasAuthId) {
            const originalAdmin = localStorage.getItem('originalAdmin');
            const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : user.id;
            
            if (!hasUserId) {
              const separator = fullImageUrl.includes('?') ? '&' : '?';
              fullImageUrl = `${fullImageUrl}${separator}user_id=${user.id}`;
            }
            if (!hasAuthId) {
              const separator = fullImageUrl.includes('?') ? '&' : '?';
              fullImageUrl = `${fullImageUrl}${separator}authenticated_user_id=${authenticatedUserId}`;
            }
            if (DEBUG_AI_ANALYSIS) console.log('🔧 Added authentication params to internal URL');
          }
        }
      } else if (existingUrl && existingUrl.startsWith('/api/')) {
        if (DEBUG_AI_ANALYSIS) console.log('🔗 Converting relative URL to absolute');
        // For relative /api/ URLs, convert to absolute
        if (existingUrl.includes('?user_id=')) {
          // URL already has user_id parameter
          fullImageUrl = `${getBACKEND_URL()}${existingUrl}`;
        } else {
          // Add user_id parameter
          fullImageUrl = `${getBACKEND_URL()}${existingUrl}?user_id=${user?.id || ''}`;
        }
      } else if (user?.id && imageId) {
        // Construct proper URL with user_id for data isolation
        if (DEBUG_AI_ANALYSIS) console.log('🔨 Constructing URL from image ID');
        fullImageUrl = `${getBACKEND_URL()}/api/files/${imageId}?user_id=${user.id}`;
      } else {
        // Final fallback
        console.warn('⚠️ Using fallback URL construction');
        if (existingUrl.startsWith('http')) {
          fullImageUrl = existingUrl;
        } else {
          fullImageUrl = `${process.env.REACT_APP_BACKEND_URL || ''}${existingUrl}`;
        }
      }
      formData.append('image_url', fullImageUrl);
      if (DEBUG_AI_ANALYSIS) console.log('📷 Image URL:', fullImageUrl.substring(0, 120));
    }
    
    // Create comprehensive analysis prompt
    let analysisPrompt;
    
    if (enhancedPrompt) {
      if (DEBUG_AI_ANALYSIS) {
        console.log('🎯 USING ENHANCED PROMPT MODE');
        console.log('🎯 Enhanced prompt value:', enhancedPrompt);
      }
      // DOMINANT PRIORITY: Enhanced user feedback gets 80-90% weight
      analysisPrompt = `ABSOLUTE PRIORITY: The user has given SPECIFIC animation instructions: "${enhancedPrompt}"

THIS USER REQUEST MUST BE THE PRIMARY FOCUS OF ALL 3 SUGGESTIONS.

If the user mentioned FRAMING, CROPPING, or CAMERA POSITIONING (like "head doesn't come in frame", "crop out", "focus on", etc.), you MUST create suggestions that implement this framing/positioning request as the main element.

USER'S SPECIFIC INSTRUCTION: "${enhancedPrompt}"

Generate 3 animation suggestions where each one PROMINENTLY IMPLEMENTS the user's specific instruction ("${enhancedPrompt}") as the main feature. Each suggestion should be different ways to achieve what the user requested.

MINOR consideration: Basic face detection rules apply secondarily.

Format: Each suggestion should start by addressing the user's specific request, then add minimal additional animation elements. Max 60 words each.

IMPLEMENT THIS USER REQUEST: "${enhancedPrompt}"`;
      
      if (DEBUG_AI_ANALYSIS) {
        console.log('📤 Analysis prompt being sent to AI:');
        console.log(analysisPrompt);
      }
    } else {
      // Standard analysis when no enhanced prompt
      analysisPrompt = 'CRITICAL: First detect if there are ANY human faces visible in this image. If NO faces detected, generate prompts that EXPLICITLY prevent adding people (use "no people", "no humans", "no faces" in prompts). If faces ARE detected, focus animations on those specific people. Generate 3 realistic prompts: 1) Face Detection: Are there visible human faces? If YES: animate those people subtly (breathing, blinking, micro-expressions). If NO: explicitly avoid adding people. 2) Environment: Indoor=no wind/breeze, Outdoor=gentle natural movement. 3) Subject matter: Objects, animals (minimal movement), or scenery only - NEVER add people if they werent in the original. Each prompt max 40 words, must include "no people" if no faces detected.';
      
      if (DEBUG_AI_ANALYSIS) console.log('📤 Sending standard AI analysis (no enhanced prompt)');
    }
    
    formData.append('prompt', analysisPrompt);
    
    const API = process.env.REACT_APP_BACKEND_URL || '';
    if (user?.id) {
      formData.append('user_id', user.id);
    }
    
    // Add job_id as query parameter if provided (for cancellation tracking)
    const apiUrl = jobId 
      ? `${getAPI()}/ai-analyze-image?job_id=${encodeURIComponent(jobId)}`
      : `${getAPI()}/ai-analyze-image`;
    
    const response = await axios.post(apiUrl, formData);
    
    if (DEBUG_AI_ANALYSIS) {
      console.log('📨 AI Response received');
      console.log('📊 Response data:', response.data);
      console.log('💬 Suggestions:', response.data?.suggestions);
    }
    
    if (response.data && response.data.suggestions) {
      if (DEBUG_AI_ANALYSIS) console.log('✅ Returning AI suggestions:', response.data.suggestions.length, 'suggestions');
      
      // Check for border warning and faded image warning
      const hasBorderWarning = !!response.data.border_warning;
      const hasFadedWarning = !!response.data.faded_warning;
      
      if (hasBorderWarning) {
        console.warn('⚠️ Border warning detected:', response.data.border_warning);
      }
      if (hasFadedWarning) {
        console.warn('⚠️ Faded image warning detected:', response.data.faded_warning);
      }
      
      // Return suggestions with metadata if any warnings exist
      if (hasBorderWarning || hasFadedWarning) {
        return {
          suggestions: response.data.suggestions,
          borderWarning: response.data.border_warning,
          fadedWarning: response.data.faded_warning
        };
      }
      
      return response.data.suggestions;
    } else {
      // Fallback to safe, face-aware prompts if AI fails
      let fallbackPrompts = [
        "Gentle environmental animation: soft lighting changes, minimal natural movement - no people, no humans, no faces added",
        "Subtle atmospheric effects only: gentle shadows, light shifts, appropriate to setting - keep original subjects only, no additional people", 
        "Natural micro-movements: if people visible, gentle breathing/blinking only - if no people in original, maintain that with no added humans"
      ];
      
      if (enhancedPrompt) {
        fallbackPrompts = [
          `IMPLEMENT USER REQUEST: ${enhancedPrompt} - gentle natural movement while ensuring framing requirements are met`,
          `USER PRIORITY: ${enhancedPrompt} - subtle animation focused on achieving this specific framing/positioning goal`,
          `FOCUSED ON: ${enhancedPrompt} - minimal environmental effects while prioritizing the user's framing instructions`
        ];
      }
      
      return fallbackPrompts;
    }
  } catch (error) {
    console.error('❌ AI Analysis failed:', error);
    console.error('❌ Error details:', error.response?.data || error.message);
    console.log('🔍 Enhanced prompt in error handler:', enhancedPrompt);
    
    // Check if this is a rate limit error (429)
    if (error.response?.status === 429) {
      const errorData = error.response?.data?.detail || {};
      const retryAfter = errorData.retry_after || 60;
      
      console.warn(`⚠️ Rate limit hit. Backend suggests retry after ${retryAfter}s`);
      
      // Throw a specific error that the caller can handle
      throw {
        type: 'rate_limit',
        message: errorData.message || 'OpenAI rate limit reached. Please try again in a moment.',
        retryAfter: retryAfter,
        originalError: error
      };
    }
    
    // For non-rate-limit errors, fallback to context-aware realistic prompts
    let fallbackPrompts = [
      "Add only subtle breathing and gentle blinking - keep posed subjects naturally still with minimal micro-expressions",
      "Create barely perceptible motion: soft lighting shifts and minimal environmental movement appropriate to indoor/outdoor setting", 
      "Animate with natural restraint: gentle eye movements and breath rhythm while maintaining the composed nature of posed photos"
    ];
    
    if (enhancedPrompt) {
      console.log('✅ Using enhanced prompt in fallback:', enhancedPrompt);
      fallbackPrompts = [
        `IMPLEMENT USER REQUEST: ${enhancedPrompt} - gentle natural movement while ensuring framing requirements are met`,
        `USER PRIORITY: ${enhancedPrompt} - subtle animation focused on achieving this specific framing/positioning goal`,
        `FOCUSED ON: ${enhancedPrompt} - minimal environmental effects while prioritizing the user's framing instructions`
      ];
    } else {
      console.log('⚠️ No enhanced prompt in error handler, using generic fallback');
    }
    
    console.log('📋 Final fallback prompts:', fallbackPrompts);
    return fallbackPrompts;
  }
};

// Demo mode data
const DEMO_PICTURES = [
  {
    id: 'demo-pic-1',
    url: '/landing-assets/demo1-left-image.jpg',
    thumbnail_url: '/landing-assets/demo1-left-image.jpg',
    name: 'Beautiful Sunset',
    created_at: new Date().toISOString()
  },
  {
    id: 'demo-pic-2',
    url: '/landing-assets/demo2-middle-image.jpg',
    thumbnail_url: '/landing-assets/demo2-middle-image.jpg',
    name: 'Precious Moment',
    created_at: new Date().toISOString()
  },
  {
    id: 'demo-pic-3',
    url: '/landing-assets/demo3-right-image.jpg',
    thumbnail_url: '/landing-assets/demo3-right-image.jpg',
    name: 'Family Memory',
    created_at: new Date().toISOString()
  },
  {
    id: 'demo-pic-4',
    url: '/landing-assets/demo4-family-image.jpg',
    thumbnail_url: '/landing-assets/demo4-family-image.jpg',
    name: 'Joyful Day',
    created_at: new Date().toISOString()
  }
];

const DEMO_VIDEOS = [
  { 
    id: 'demo-vid-1-1', 
    image_id: 'demo-pic-1', 
    status: 'completed', 
    video_url: '/landing-assets/demo1-left-video.mp4',
    result_url: '/landing-assets/demo1-left-video.mp4',  // Add result_url for consistency
    thumbnail_url: '/landing-assets/demo1-left-image.jpg',
    imageUrl: '/landing-assets/demo1-left-image.jpg',
    created_at: new Date().toISOString(),
    prompt: 'The serene water reflects the vibrant sunset with subtle color shifts across the sky, evoking a peaceful ambiance.',
    parameters: { prompt: 'The serene water reflects the vibrant sunset with subtle color shifts across the sky, evoking a peaceful ambiance.' }
  },
  { 
    id: 'demo-vid-2-1', 
    image_id: 'demo-pic-2', 
    status: 'completed', 
    video_url: '/landing-assets/demo2-middle-video.mp4',
    result_url: '/landing-assets/demo2-middle-video.mp4',  // Add result_url for consistency
    thumbnail_url: '/landing-assets/demo2-middle-image.jpg',
    imageUrl: '/landing-assets/demo2-middle-image.jpg',
    created_at: new Date().toISOString(),
    prompt: 'The rabbit remains perfectly still in the child\'s arms, its chest rising and falling slowly with gentle breathing.',
    parameters: { prompt: 'The rabbit remains perfectly still in the child\'s arms, its chest rising and falling slowly with gentle breathing.' }
  },
  { 
    id: 'demo-vid-3-1', 
    image_id: 'demo-pic-3', 
    status: 'completed', 
    video_url: '/landing-assets/demo3-right-video.mp4',
    result_url: '/landing-assets/demo3-right-video.mp4',  // Add result_url for consistency
    thumbnail_url: '/landing-assets/demo3-right-image.jpg',
    imageUrl: '/landing-assets/demo3-right-image.jpg',
    created_at: new Date().toISOString(),
    prompt: 'Make it lively by adding subtle hand gestures from the seated family members, as if they\'re animatedly discussing a joyful memory or sharing a laugh.',
    parameters: { prompt: 'Make it lively by adding subtle hand gestures from the seated family members, as if they\'re animatedly discussing a joyful memory or sharing a laugh.' }
  },
  { 
    id: 'demo-vid-4-1', 
    image_id: 'demo-pic-4', 
    status: 'completed', 
    video_url: '/landing-assets/demo4-family-video.mp4',
    result_url: '/landing-assets/demo4-family-video.mp4',  // Add result_url for consistency
    thumbnail_url: '/landing-assets/demo4-family-image.jpg',
    imageUrl: '/landing-assets/demo4-family-image.jpg',
    created_at: new Date().toISOString(),
    prompt: 'The girl stands in a park, gently breathing and smiling while holding a basket of flowers, with minimal blinking to maintain the joyful pose.',
    parameters: { prompt: 'The girl stands in a park, gently breathing and smiling while holding a basket of flowers, with minimal blinking to maintain the joyful pose.' }
  }
];

// Mock dolphin video for tutorial (shown as pending then completed)
const TUTORIAL_DOLPHIN_VIDEO = {
  id: 'demo-vid-tutorial-dolphin',
  image_id: 'demo-pic-1',
  status: 'processing',
  video_url: '/landing-assets/demo1-left-video.mp4',
  result_url: '/landing-assets/demo1-left-video.mp4',  // Add result_url for consistency
  thumbnail_url: '/landing-assets/demo1-left-image.jpg',
  imageUrl: '/landing-assets/demo1-left-image.jpg',
  created_at: new Date().toISOString(),
  prompt: 'Make a dolphin jump in the far distance where the water meets the horizon, with a graceful arc enhancing the serene sunset colors.',
  parameters: { prompt: 'Make a dolphin jump in the far distance where the water meets the horizon, with a graceful arc enhancing the serene sunset colors.' },
  progress: 50
};

// Tutorial Overlay Component (DEMO MODE ONLY) - Forced Focus Style
const TutorialOverlay = ({ step, stepData, onNext, onSkip, onComplete, demoMode }) => {
  const [targetElement, setTargetElement] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  const [targetElements, setTargetElements] = useState([]);
  const [targetRects, setTargetRects] = useState([]);
  const [isAutoTyping, setIsAutoTyping] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  
  // CRITICAL: Don't render if no step data OR if demo mode is disabled
  // This prevents the blocking overlay from persisting
  if (!stepData || !demoMode) {
    console.log('🛡️ TutorialOverlay NOT rendering - stepData:', !!stepData, 'demoMode:', demoMode);
    return null;
  }
  console.log('📺 TutorialOverlay rendering - step:', step);
  
  // ESC key handler
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onSkip();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onSkip]);
  
  // Auto-typing effect
  useEffect(() => {
    if (stepData.action === 'auto-type' && stepData.autoTypeText && stepData.autoTypeTarget) {
      setIsAutoTyping(true);
      
      const timer = setTimeout(() => {
        const textarea = document.querySelector(stepData.autoTypeTarget);
        if (textarea) {
          // Type text character by character
          let currentIndex = 0;
          const text = stepData.autoTypeText;
          
          const typeInterval = setInterval(() => {
            if (currentIndex <= text.length) {
              textarea.value = text.substring(0, currentIndex);
              // Trigger input event so React recognizes the change
              const event = new Event('input', { bubbles: true });
              textarea.dispatchEvent(event);
              currentIndex++;
            } else {
              clearInterval(typeInterval);
              setIsAutoTyping(false);
              // Auto-progress after typing completes
              setTimeout(() => onNext(), 1000);
            }
          }, 50); // 50ms per character
          
          return () => clearInterval(typeInterval);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [stepData, onNext]);
  
  // Video playback detection
  useEffect(() => {
    if (stepData.action === 'wait-video-end' && stepData.autoProgress) {
      const timer = setTimeout(() => {
        setVideoWatched(true);
        onNext();
      }, stepData.autoProgress);
      
      return () => clearTimeout(timer);
    }
  }, [stepData, onNext]);
  
  // Keep demo pictures elevated throughout the entire tutorial
  useEffect(() => {
    if (!demoMode) return;
    
    // Always keep the 5 demo pictures above the overlay during tutorial
    for (let i = 1; i <= 5; i++) {
      const pictureDiv = document.querySelector(`[data-picture-id="demo-pic-${i}"]`);
      if (pictureDiv) {
        pictureDiv.style.position = 'relative';
        pictureDiv.style.zIndex = '10010';
        pictureDiv.style.isolation = 'isolate';
        pictureDiv.style.transform = 'translateZ(0)';
        
        const allDescendants = pictureDiv.querySelectorAll('*');
        allDescendants.forEach(child => {
          child.style.zIndex = '10010';
          child.style.position = 'relative';
        });
      }
    }
    
    return () => {
      // Only clean up when tutorial is completely done
      for (let i = 1; i <= 5; i++) {
        const pictureDiv = document.querySelector(`[data-picture-id="demo-pic-${i}"]`);
        if (pictureDiv) {
          pictureDiv.style.position = '';
          pictureDiv.style.zIndex = '';
          pictureDiv.style.isolation = '';
          pictureDiv.style.transform = '';
          
          const allDescendants = pictureDiv.querySelectorAll('*');
          allDescendants.forEach(child => {
            child.style.zIndex = '';
            child.style.position = '';
          });
        }
      }
    };
  }, [demoMode]);
  
  // Find and highlight target element(s) with cutout effect
  useEffect(() => {
    if (stepData.target) {
      const timer = setTimeout(() => {
        // Special handling for demo pictures step (multiple elements)
        if (stepData.target === 'demo-pictures-multi') {
          const elements = [];
          const rects = [];
          
          // Find all 5 demo pictures
          for (let i = 1; i <= 5; i++) {
            const pictureDiv = document.querySelector(`[data-picture-id="demo-pic-${i}"]`);
            if (pictureDiv) {
              elements.push(pictureDiv);
              pictureDiv.classList.add('tutorial-spotlight');
              
              // Hide eye buttons during demo pictures step (step 1)
              if (step === 1) {
                const eyeButtons = pictureDiv.querySelectorAll('button[title="View animated moments"]');
                eyeButtons.forEach(btn => {
                  btn.style.display = 'none';
                });
              } else {
                // Show eye buttons on other steps
                const eyeButtons = pictureDiv.querySelectorAll('button[title="View animated moments"]');
                eyeButtons.forEach(btn => {
                  btn.style.display = '';
                });
              }
              
              rects.push(pictureDiv.getBoundingClientRect());
            }
          }
          
          setTargetElements(elements);
          setTargetRects(rects);
          
          // Scroll first picture into view
          if (elements[0]) {
            elements[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          // Single element targeting
          const element = document.querySelector(stepData.target);
          if (element) {
            setTargetElement(element);
            element.classList.add('tutorial-spotlight');
            element.style.position = 'relative';
            element.style.zIndex = '10003';  // Above overlay
            element.style.isolation = 'isolate';  // Create stacking context
            
            // Scroll element into view
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Get element position after scroll
            setTimeout(() => {
              const rect = element.getBoundingClientRect();
              setTargetRect(rect);
            }, 600);
          }
        }
      }, 100);
      
      return () => {
        clearTimeout(timer);
        
        // Cleanup multiple elements (but keep demo pictures elevated)
        targetElements.forEach(el => {
          el.classList.remove('tutorial-spotlight');
          // DON'T reset z-index/position - demo pictures stay elevated throughout tutorial
        });
        
        // Cleanup single element (but keep demo pictures elevated)
        if (targetElement) {
          targetElement.classList.remove('tutorial-spotlight');
          // Only reset z-index if it's NOT a demo picture
          if (!targetElement.hasAttribute('data-picture-id') || !targetElement.getAttribute('data-picture-id').startsWith('demo-pic-')) {
            targetElement.style.position = '';
            targetElement.style.zIndex = '';
            targetElement.style.isolation = '';
          }
        }
        
        // Always clear arrays when cleaning up
        setTargetElements([]);
        setTargetRects([]);
      };
    } else {
      setTargetElement(null);
      setTargetRect(null);
      setTargetElements([]);
      setTargetRects([]);
    }
  }, [stepData.target, step]);
  
  // Interactive steps need cutout effect
  const isInteractiveStep = stepData.action === 'wait-for-gallery' || stepData.action === 'wait-for-video';
  
  // Create overlay panels that avoid the cutout areas (no SVG for better performance)
  const createCutoutOverlay = () => {
    if (targetRects.length === 0) return null;
    
    // Calculate the bounding box that contains all target elements
    const minTop = Math.min(...targetRects.map(r => r.top));
    const maxBottom = Math.max(...targetRects.map(r => r.bottom));
    const minLeft = Math.min(...targetRects.map(r => r.left));
    const maxRight = Math.max(...targetRects.map(r => r.right));
    
    return (
      <>
        {/* Top panel - from screen top to first picture */}
        <div 
          className="absolute left-0 right-0 bg-black/85 pointer-events-auto"
          style={{ top: 0, height: `${minTop - 10}px` }}
        />
        {/* Bottom panel - from last picture to screen bottom */}
        <div 
          className="absolute left-0 right-0 bg-black/85 pointer-events-auto"
          style={{ top: `${maxBottom + 10}px`, bottom: 0 }}
        />
        {/* Left panel - from screen left to first picture */}
        <div 
          className="absolute top-0 bottom-0 bg-black/85 pointer-events-auto"
          style={{ left: 0, width: `${minLeft - 10}px` }}
        />
        {/* Right panel - from last picture to screen right */}
        <div 
          className="absolute top-0 bottom-0 bg-black/85 pointer-events-auto"
          style={{ left: `${maxRight + 10}px`, right: 0 }}
        />
      </>
    );
  };
  
  return (
    <div className="fixed inset-0 z-[10000] pointer-events-none">
      {/* Overlay with cutouts for target element(s) */}
      {isInteractiveStep && (targetRect || targetRects.length > 0) ? (
        <>
          {/* Multiple elements cutout (demo pictures) */}
          {targetRects.length > 0 ? (
            <>
              {/* Full dark overlay - UNDER the pictures - ONLY blocks during tutorial */}
              <div className="absolute inset-0 bg-black/85" style={{ zIndex: 9999, pointerEvents: 'auto' }} />
              
              {/* Purple borders around each picture - pictures themselves are at z-index 10010 */}
              {targetRects.map((rect, index) => (
                <div 
                  key={index}
                  className="absolute pointer-events-none"
                  style={{
                    top: `${rect.top - 10}px`,
                    left: `${rect.left - 10}px`,
                    width: `${rect.width + 20}px`,
                    height: `${rect.height + 20}px`,
                    borderRadius: '12px',
                    border: '4px solid rgba(147, 51, 234, 0.9)',
                    boxShadow: '0 0 30px rgba(147, 51, 234, 0.6)',
                    zIndex: 10011
                  }}
                />
              ))}
            </>
          ) : (
            /* Single element cutout */
            <>
              {/* Full dark overlay - UNDER the target element - ONLY blocks during tutorial */}
              <div className="absolute inset-0 bg-black/85" style={{ zIndex: 9999, pointerEvents: 'auto' }} />
              
              {/* Purple border around the target - element itself is at z-index 10003 */}
              <div 
                className="absolute pointer-events-none"
                style={{
                  top: `${targetRect.top - 10}px`,
                  left: `${targetRect.left - 10}px`,
                  width: `${targetRect.width + 20}px`,
                  height: `${targetRect.height + 20}px`,
                  borderRadius: '12px',
                  border: '4px solid rgba(147, 51, 234, 0.9)',
                  boxShadow: '0 0 30px rgba(147, 51, 234, 0.6)',
                  zIndex: 10004
                }}
              />
            </>
          )}
        </>
      ) : (
        <div className="absolute inset-0 bg-black/70 pointer-events-auto" />
      )}
      
      {/* Animated arrow pointing at target - only for single element */}
      {isInteractiveStep && targetRect && targetRects.length === 0 && (
        <div 
          className="absolute pointer-events-none"
          style={{
            left: `${targetRect.left + targetRect.width / 2 - 30}px`,
            top: `${targetRect.top - 80}px`,
            animation: 'arrowBounce 1.5s ease-in-out infinite',
            zIndex: 10015
          }}
        >
          <div className="text-6xl">👇</div>
        </div>
      )}
      
      {/* Compact tooltip */}
      <div 
        className="absolute pointer-events-auto"
        style={
          isInteractiveStep && targetRect && targetRects.length === 0
            ? {
                left: `${targetRect.left + targetRect.width / 2}px`,
                top: `${targetRect.bottom + 30}px`,
                transform: 'translateX(-50%)',
                zIndex: 10002
              }
            : isInteractiveStep && targetRects.length > 0
            ? {
                // For multi-element (demo pictures), position BELOW the pictures grid
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10005
              }
            : {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10002
              }
        }
      >
        <div 
          className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl shadow-2xl px-4 py-3 max-w-xs border-2 border-white/30"
          style={{ animation: 'tooltipBounce 0.5s ease-out' }}
        >
          {/* Compact step indicator */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-white/60 text-xs font-medium">
              Step {step + 1} of 15
            </div>
            <button
              onClick={onSkip}
              className="text-white/70 hover:text-white text-xs font-medium ml-2"
            >
              Skip
            </button>
          </div>
          
          {/* Simplified content */}
          <p className="text-white text-sm font-medium text-center">
            {stepData.description}
          </p>
          
          {/* Actions */}
          {stepData.action === 'next' && (
            <button
              onClick={onNext}
              className="w-full mt-3 bg-white text-purple-600 px-4 py-2 rounded-lg font-semibold hover:bg-white/90 transition-all text-sm"
            >
              Next →
            </button>
          )}
          {stepData.action === 'complete' && (
            <button
              onClick={onComplete}
              className="w-full mt-3 bg-white text-purple-600 px-4 py-2 rounded-lg font-semibold hover:bg-white/90 transition-all text-sm"
            >
              Get Started! 🚀
            </button>
          )}
          {stepData.action === 'auto-type' && isAutoTyping && (
            <div className="mt-3 text-white/70 text-xs text-center animate-pulse">
              ✍️ Typing...
            </div>
          )}
          {stepData.action === 'wait-video-end' && (
            <div className="mt-3 text-white/70 text-xs text-center">
              📹 Watch the video...
            </div>
          )}
          {(stepData.action === 'wait-processing' || stepData.action === 'wait-completion') && (
            <div className="mt-3 text-white/70 text-xs text-center animate-pulse">
              ⏳ Processing...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Custom hook for HLS video management
const useHLSVideo = (videoUrl, videoElement) => {
  const hlsRef = useRef(null);
  
  useEffect(() => {
    if (!videoUrl || !videoElement) return;
    
    // Dynamically import HLS.js
    import('hls.js').then((Hls) => {
      const HlsClass = Hls.default;
      
      // Check if URL is an HLS stream (contains .m3u8 or is from Cloudflare Stream redirect)
      const isHLS = videoUrl.includes('.m3u8') || videoUrl.includes('/api/videos/');
// Lightweight lazy image with robust fallback
const LazyImage = ({ image, getImageUrl, user, className, alt, onClick }) => {
  const imgRef = React.useRef(null);
  const [src, setSrc] = React.useState('');

  // Compute candidates once
  const computeSrc = React.useCallback(() => getImageUrl(image), [image, getImageUrl]);
  const computeFullRes = React.useCallback(() => {
    // Prefer full-res Cloudflare delivery URL
    const isCf = (u) => typeof u === 'string' && (u.includes('imagedelivery.net') || u.includes('cloudflarestream.com') || u.includes('customer-'));
    if (isCf(image?.cloudflare_delivery_url)) return image.cloudflare_delivery_url;
    if (isCf(image?.url)) return image.url;
    const fileId = image?.id || image?._id;
    if (fileId && user?.id) return `${getAPI()}/files/${fileId}?user_id=${user.id}`;
    return '';
  }, [image, user]);

  // Intersection-based loading
  React.useEffect(() => {
    const node = imgRef.current;
    if (!node) return;
    if (!('IntersectionObserver' in window)) {
      setSrc(computeSrc());
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setSrc(computeSrc());
          io.disconnect();
        }
      });
    }, { root: null, rootMargin: '400px', threshold: 0.01 });
    io.observe(node);
    return () => io.disconnect();
  }, [computeSrc]);

  return (
    <img
      ref={imgRef}
      loading="lazy"
      src={src}
      alt={alt || 'Unassigned Memory'}
      className={className}
      onClick={onClick}
      onError={(e) => {
        const fallback = computeFullRes();
        if (fallback && e.target.src !== fallback) {
          e.target.src = fallback;
          return;
        }
        // Last resort: subtle background marker for diagnostics
        e.target.style.backgroundColor = 'rgba(255,0,0,0.1)';
      }}
    />
  );
};

      
      if (isHLS && HlsClass.isSupported()) {
        // Clean up existing instance
        if (hlsRef.current) {
          hlsRef.current.destroy();
        }
        
        // Initialize HLS.js
        const hls = new HlsClass({
          enableWorker: true,
          lowLatencyMode: true,
        });
        
        // Load the video source
        hls.loadSource(videoUrl);
        hls.attachMedia(videoElement);
        
        hlsRef.current = hls;
      } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        // For Safari, which supports HLS natively
        videoElement.src = videoUrl;
      } else {
        // For direct MP4 files
        videoElement.src = videoUrl;
      }
    }).catch(err => {
      console.error('Failed to load HLS.js:', err);
      // Fallback to direct src
      videoElement.src = videoUrl;
    });
    
    // Cleanup
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoUrl, videoElement]);
};

// Credit Purchase Animation Component
const CreditPurchaseAnimation = ({ credits, onComplete, isGiftOrder = false, isWelcome = false }) => {
  const [animatedCredits, setAnimatedCredits] = useState(0);

  console.log('🎬 CreditPurchaseAnimation rendered with credits:', credits, 'isGiftOrder:', isGiftOrder, 'isWelcome:', isWelcome);

  useEffect(() => {
    console.log('✅ Starting animation');
    
    // Add custom animations
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes scaleIn {
        from { transform: scale(0); }
        to { transform: scale(1); }
      }
    `;
    document.head.appendChild(styleTag);
    
    // Animate the credit count
    const duration = 2000; // 2 seconds
    const steps = 50;
    const increment = credits / steps;
    let current = 0;
    
    const interval = setInterval(() => {
      current += increment;
      if (current >= credits) {
        setAnimatedCredits(credits);
        clearInterval(interval);
        
        // Close after animation completes
        setTimeout(() => {
          onComplete(isGiftOrder); // Pass isGiftOrder to onComplete
        }, 1000);
      } else {
        setAnimatedCredits(Math.floor(current));
      }
    }, duration / steps);
    
    return () => {
      clearInterval(interval);
      if (styleTag.parentNode) {
        document.head.removeChild(styleTag);
      }
    };
  }, []); // Empty dependency array - run once on mount

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[1001] p-4">
      <div className="text-center animate-[fadeIn_0.3s_ease-in]">
        <div className="mb-6">
          <div className="w-32 h-32 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl animate-[scaleIn_0.5s_ease-out]">
            <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="text-white">
          {isWelcome ? (
            <>
              <h3 className="text-4xl font-bold mb-3">
                Welcome! 🎉
              </h3>
              <p className="text-2xl text-gray-100 mb-2">
                +{animatedCredits} FREE Credits
              </p>
              <p className="text-lg text-gray-300">
                to create your first Magic Moment!
              </p>
            </>
          ) : (
            <>
              <h3 className="text-4xl font-bold mb-3">
                +{animatedCredits} Credits Added!
              </h3>
              <p className="text-xl text-gray-200">
                Animation credits have been added to your account
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced memory-themed dashboard
const MemoryDashboard = ({ user, setUser, showSupport, setShowSupport, showOrdersDashboard, setShowOrdersDashboard, showAdminPanel, setShowAdminPanel, showReferralDashboard, setShowReferralDashboard, onLogout, onProfileClick, onCreditsClick, showCreditsDropdown, setShowCreditsDropdown, showMagicProcessing, setShowMagicProcessing, setProcessingMessage, setProcessingCount, showFrameDemo, setShowFrameDemo, preSelectedFrameColor, setPreSelectedFrameColor, preSelectedFrameProduct, setPreSelectedFrameProduct, isGiftMode, setIsGiftMode, showGiftCardModal, setShowGiftCardModal, giftCardProduct, setGiftCardProduct, loadCartFrameItem, setLoadCartFrameItem, showToast, showTerms, setShowTerms, showPrivacy, setShowPrivacy, showContact, setShowContact, fileInputRef, onEditImage, albumRefreshCallback, albumFilterSwitchCallback, showMailInCheckout, setShowMailInCheckout, selectedMailInPackage, setSelectedMailInPackage, showWhatsNextModal, setShowWhatsNextModal, mailInOrders, setMailInOrders, loadingMailInOrders, setLoadingMailInOrders, trackingOrders, loadingTrackingOrders, fetchTrackingOrders, hasFetchedTrackingOrders, showMailInGiftOccasion, setShowMailInGiftOccasion, mailInGiftOccasion, setMailInGiftOccasion, showGiftCheckout, setShowGiftCheckout }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const processedMailInSessionRef = useRef(null); // Track processed Stripe sessions to prevent duplicates
  
  // Helper function to generate image URL with user_id for proper data isolation
  const getImageUrl = (image) => {
    if (!image || !user?.id) return '';
    
    // Feature toggles (easy revert)
    const FEATURE_THUMB_FIRST_DEFAULT = true; // Toggle default behavior safely

    // Prefer thumbnail by default. Overrides:
    // - ?fullres=1 forces full-resolution delivery urls
    // - ?thumb_first=0 disables thumbnail preference
    const thumbFirst = (() => {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('fullres') === '1') return false;
        if (url.searchParams.get('thumb_first') === '0') return false;
        if (url.searchParams.get('thumb_first') === '1') return true; // explicit opt-in
        return FEATURE_THUMB_FIRST_DEFAULT;
      } catch {
        return FEATURE_THUMB_FIRST_DEFAULT;
      }
    })();

    // If image is just an ID string
    if (typeof image === 'string') {
      return `${getAPI()}/files/${image}?user_id=${user.id}`;
    }
    // If image is an object, try different properties
    const imageId = image.id || image._id;

    // Helper to check if URL is Cloudflare CDN
    const isCf = (u) => typeof u === 'string' && (u.includes('imagedelivery.net') || u.includes('cloudflarestream.com') || u.includes('customer-'));

    // If thumb-first flag is on and we have any thumbnail, use it (Cloudflare or internal)
    if (thumbFirst) {
      const cfThumb = image.cloudflare_thumbnail || image.thumbnail_url;
      if (cfThumb) {
        return cfThumb;
      }
    }

    // PRIORITY 1: Direct Cloudflare CDN URL (fastest, no proxy)
    if (isCf(image.cloudflare_delivery_url)) {
      return image.cloudflare_delivery_url;
    }

    // PRIORITY 2: Check if url/thumbnail_url are Cloudflare URLs
    if (isCf(image.url)) {
      return image.url;
    }
    if (!thumbFirst && image.thumbnail_url) {
      // If not forcing thumb-first, we still accept any thumbnail as a valid fast URL
      return image.thumbnail_url;
    }

    // NEVER modify presigned URLs - they have cryptographic signatures
    if (image.url && isPresignedUrl(image.url)) {
      return image.url;
    }

    // Legacy: If image has a full URL (demo content or landing-assets), return it directly
    if (image.url && (image.url.startsWith('http://') || image.url.startsWith('https://'))) {
      return image.url;
    }
    
    // Demo content or landing-assets with relative paths
    if (image.url && image.url.startsWith('/landing-assets/')) {
      return image.url;
    }
    
    if (!imageId) return '';
    
    // CRITICAL: Always include user_id as the FIRST parameter
    // Add cache-busting parameter if image was recently updated
    const timestamp = image.updated_at || image._cacheBuster || '';
    const cacheBuster = timestamp ? `&v=${timestamp}` : '';
    
    // If image has a URL property with ?v= only, reconstruct it properly
    if (image.url && image.url.includes('?v=') && !image.url.includes('user_id=')) {
      const baseUrl = image.url.split('?')[0];
      const vParam = image.url.split('?v=')[1];
      return `${baseUrl}?user_id=${user.id}&v=${vParam}`;
    }
    
    return `/api/files/${imageId}?user_id=${user.id}${cacheBuster}`;
  };
  
  const [activeMainTab, setActiveMainTab] = useState('moments');
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({});
  const [aiProviders, setAiProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [currentView, setCurrentView] = useState('shop'); // 'projects', 'pictures', 'living_memories', 'pending', 'failed', 'shop'
  const [shopSubtab, setShopSubtab] = useState('frames'); // 'frames', 'mail-in-service'
  const [showEarnCredits, setShowEarnCredits] = useState(false);
  
  // Handle navigation state (e.g., from order success page)
  useEffect(() => {
    if (location.state?.view) {
      setCurrentView(location.state.view);
      // Clear the state after setting view
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  
  const [showCart, setShowCart] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [videoJobs, setVideoJobs] = useState([]);
  const [previousVideoJobs, setPreviousVideoJobs] = useState([]);
  const [editingMemory, setEditingMemory] = useState(null);
  const [autoOpenVideoGenerator, setAutoOpenVideoGenerator] = useState(false);
  const [showFailedDetails, setShowFailedDetails] = useState(null);
  const [fullScreenVideo, setFullScreenVideo] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [selectedForAlbum, setSelectedForAlbum] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showFrameOrder, setShowFrameOrder] = useState(false);
  // Moved showSupport to App level
  const [selectedPictures, setSelectedPictures] = useState([]);
  const [selectedLivingMemories, setSelectedLivingMemories] = useState([]);
  const [showMultiUpload, setShowMultiUpload] = useState(false);
  const [showCreateLivingMemory, setShowCreateLivingMemory] = useState(false);
  const [albumContext, setAlbumContext] = useState(null); // For album-specific animations
  const [albumRefreshTrigger, setAlbumRefreshTrigger] = useState(0); // Trigger silent album refresh
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState(0); // Count of pending album invitations
  const [totalPendingAccessRequests, setTotalPendingAccessRequests] = useState(0); // Total access requests across all albums
  const [navbarKey, setNavbarKey] = useState(0); // Force navbar remount
  
  // Debug: Log whenever totalPendingAccessRequests changes in App.js
  React.useEffect(() => {
    console.log('🎯 App.js totalPendingAccessRequests state changed to:', totalPendingAccessRequests);
    // Force navbar remount by changing key
    setNavbarKey(prev => prev + 1);
  }, [totalPendingAccessRequests]);
  const [showCreditsManagement, setShowCreditsManagement] = useState(false);
  const [showCollaboration, setShowCollaboration] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [showFloatingAnimateButtonPictures, setShowFloatingAnimateButtonPictures] = useState(false); // Floating button for Pictures tab
  const [animatedMemoryMultiSelectMode, setAnimatedMemoryMultiSelectMode] = useState(false);
  const [showDownloadProgress, setShowDownloadProgress] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('Preparing...');
  const [selectedAnimatedMemories, setSelectedAnimatedMemories] = useState([]);
  const [unassignedImages, setUnassignedImages] = useState([]);
  const [showPendingSubtab, setShowPendingSubtab] = useState(false);
  const [showFailedSubtab, setShowFailedSubtab] = useState(false);
  const [animatedMemorySubtab, setAnimatedMemorySubtab] = useState('all');
  const [animatedMemoryPage, setAnimatedMemoryPage] = useState(1);
  const [showPendingVideos, setShowPendingVideos] = useState(false);
  const [showFailedVideos, setShowFailedVideos] = useState(false);
  
  // My Orders state
  const [myOrders, setMyOrders] = useState({ shopify_orders: [], mail_in_orders: [], credit_purchases: [], video_history: [], total_orders: 0 });
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersTab, setOrdersTab] = useState('frames'); // 'frames' or 'mailin'
  
  // Mail-in package add-on credits state
  const [mailInCredits100, setMailInCredits100] = useState(0);
  const [mailInCredits250, setMailInCredits250] = useState(0);
  const [mailInCredits500, setMailInCredits500] = useState(0);
  const [mailInCredits1000, setMailInCredits1000] = useState(0);
  
  // Fetch tracking orders for "Track Your Order" section when on mail-in-service page
  useEffect(() => {
    console.log('🔍 MemoryDashboard tracking useEffect:', { user: !!user, currentView });
    // Only fetch if we haven't fetched yet (prevent double fetch on mount)
    if (user && currentView === 'mail-in-service' && trackingOrders.length === 0 && !loadingTrackingOrders && !hasFetchedTrackingOrders.current) {
      console.log('✅ Calling fetchTrackingOrders from MemoryDashboard');
      hasFetchedTrackingOrders.current = true;
      fetchTrackingOrders();
    }
  }, [user, currentView, trackingOrders.length, loadingTrackingOrders, fetchTrackingOrders]);
  
  // Scroll detection for floating Animate buttons in Pictures tab
  useEffect(() => {
    const handleScroll = () => {
      // Show floating button when scrolled down more than 200px in Pictures tab
      const scrolled = window.scrollY > 200;
      const inPicturesTab = currentView === 'pictures';
      setShowFloatingAnimateButtonPictures(scrolled && inPicturesTab && !multiSelectMode);
    };

    window.addEventListener('scroll', handleScroll);
    // Check initial state
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentView, multiSelectMode]);

  // Handle URL tab parameter for My Orders
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const mailinSuccess = urlParams.get('mailin_success');
    const viewParam = urlParams.get('view');
    const giftParam = urlParams.get('gift');
    const albumParam = urlParams.get('album');
    
    if (tabParam === 'my-orders' || tabParam === 'my-credits') {
      // Switch to My Orders view
      setCurrentView('my-orders');
      
      // If tab=my-credits, set the credits tab
      if (tabParam === 'my-credits') {
        setOrdersTab('credits');
      }
      
      // Clean up URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Handle album redirect from public share page
    if (albumParam) {
      console.log('✅ Album ID detected from URL:', albumParam);
      console.log('✅ Switching to Collaboration tab and opening album');
      
      // Store album ID for the AlbumsTab to open
      localStorage.setItem('redirectToAlbum', albumParam);
      
      // Switch to projects view (which renders AlbumsTab)
      setCurrentView('projects');
      
      // Switch to Collaboration tab (this sets activeMainTab)
      setActiveMainTab('collaborate');
      
      // Clean up URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Handle tab=collaboration redirect (for "Create Your Own" button)
    if (tabParam === 'collaboration') {
      console.log('🔗 Opening collaboration tab from URL');
      setCurrentView('projects');
      setActiveMainTab('collaborate');
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Handle mail-in service gift redemption
    if (viewParam === 'mail-in-service' && giftParam === 'true') {
      const giftData = localStorage.getItem('mailin_gift_redemption');
      if (giftData) {
        try {
          const gift = JSON.parse(giftData);
          console.log('🎁 Opening mail-in checkout for redeemed gift:', gift);
          
          // Set view to mail-in service
          setCurrentView('mail-in-service');
          
          // Wait for view to load, then open checkout modal with gift data
          setTimeout(() => {
            handleMailInOrder(
              gift.package_id,
              gift.package_name,
              gift.photo_count,
              gift.base_price,
              gift.shipping_cost,
              gift.addon_credits,
              true // isGift flag
            );
          }, 500);
          
          // Clear the gift data
          localStorage.removeItem('mailin_gift_redemption');
          
          // Clean up URL
          window.history.replaceState({}, '', window.location.pathname);
        } catch (error) {
          console.error('Error processing gift redemption:', error);
        }
      }
    }
    
    // Handle mail-in service success redirect
    if (mailinSuccess === 'true') {
      const sessionId = urlParams.get('session_id');
      if (sessionId && user) {
        // Check if we've already processed this session
        if (processedMailInSessionRef.current === sessionId) {
          console.log('⚠️ Session already processed, skipping:', sessionId);
          return;
        }
        
        // Mark this session as processed IMMEDIATELY
        processedMailInSessionRef.current = sessionId;
        
        // Immediately clean up URL to prevent re-triggering
        window.history.replaceState({}, '', window.location.pathname);
        
        console.log('🚀 Processing new mail-in session:', sessionId);
        
        // Call webhook with minimal data - webhook will fetch Stripe session details
        (async () => {
          try {
            // Get Stripe session details from backend first
            const sessionResponse = await axios.get(
              `${getAPI()}/mailin-service/session-details/${sessionId}?user_id=${user.id}`
            );
            
            if (!sessionResponse.data || !sessionResponse.data.session) {
              throw new Error('Failed to retrieve session details');
            }
            
            const session = sessionResponse.data.session;
            const metadata = session.metadata || {};
            const shippingAddress = JSON.parse(metadata.shipping_address || '{}');
            const orderDetails = JSON.parse(metadata.order_details || '{}');
            
            // Call dedicated mail-in webhook with complete data
            const response = await axios.post(
              `${getAPI()}/mailin/webhook`,
              {
                session_id: sessionId,
                user_id: metadata.user_id || user.id,
                user_email: metadata.user_email || user.email,
                package_id: metadata.package_id || 'basic',
                addon_credits: parseInt(metadata.addon_credits || '0'),
                order_details: orderDetails,
                shipping_address: shippingAddress,
                amount_paid: session.amount_total / 100  // Convert from cents
              }
            );
            
            if (response.data.status === 'success') {
              console.log('✅ Mail-in order created successfully:', response.data.order_number);
              
              // Store order number and addon credits for modal
              setSelectedMailInPackage({ 
                order_number: response.data.order_number,
                addon_credits: parseInt(metadata.addon_credits || '0')
              });
              
              // Navigate to orders view and set tab
              setCurrentView('my-orders');
              setOrdersTab('mailin');
              
              // Fetch orders immediately and show modal after
              fetchMailInOrders().then(() => {
                console.log('✅ Orders fetched, showing success modal');
                setShowWhatsNextModal(true);
              }).catch(err => {
                console.error('❌ Error fetching orders after webhook:', err);
                setShowWhatsNextModal(true); // Still show modal even if fetch fails
              });
            }
          } catch (error) {
            console.error('Failed to complete mail-in order:', error);
            toast.error('Failed to complete order. Please contact support.');
          }
        })();
      } else {
        setCurrentView('my-orders');
        setOrdersTab('mailin');
        setShowWhatsNextModal(true);
      }
      
      // Clean up URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.search, user]);
  
  // Fetch mail-in service orders
  const fetchMailInOrders = async () => {
    if (!user) {
      console.log('❌ Cannot fetch mail-in orders: no user');
      return;
    }
    
    try {
      console.log('🔄 Fetching mail-in orders for user:', user.email);
      setLoadingMailInOrders(true);
      const url = `${getAPI()}/mailin-service/orders?user_id=${user.id}&t=${Date.now()}`;
      console.log('📡 Fetching from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        cache: 'no-store'
      });
      
      console.log('📬 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Mail-in orders received:', data.orders?.length || 0, 'orders');
        console.log('📦 Orders data:', data.orders);
        setMailInOrders(data.orders || []);
      } else {
        console.error('❌ Failed to fetch mail-in orders:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
        setMailInOrders([]); // Clear stale data on error
      }
    } catch (error) {
      console.error('❌ Error fetching mail-in orders:', error);
      setMailInOrders([]); // Clear stale data on error
    } finally {
      setLoadingMailInOrders(false);
    }
  };
  
  // Fetch mail-in orders when user is logged in and on My Orders page
  useEffect(() => {
    console.log('🔍 Mail-in orders useEffect triggered:', { user: !!user, currentView });
    if (user && currentView === 'my-orders') {
      console.log('✅ Fetching mail-in orders now...');
      // Clear old data first to force fresh fetch
      setMailInOrders([]);
      fetchMailInOrders();
    } else {
      console.log('❌ Not fetching mail-in orders:', { hasUser: !!user, currentView });
    }
  }, [user, currentView]);
  
  // Handle mail-in package order
  const handleMailInOrder = (packageId, packageName, photos, basePrice, shipping, addonCredits, isGift = false) => {
    if (!user) {
      toast.error('Please log in to order');
      return;
    }
    
    setSelectedMailInPackage({
      packageId,
      packageName,
      photos,
      basePrice,
      shipping,
      addonCredits,
      isGift  // Flag to indicate this is a redeemed gift
    });
    setShowMailInCheckout(true);
    
    // Show special message for gift orders
    if (isGift) {
      toast.success('🎁 Your gift has been applied! Complete your shipping details below.', {
        duration: 5000
      });
    }
  };
  
  // Sorting state for mobile-friendly gallery sorting
  const [pictureSort, setPictureSort] = useState({ field: 'created_at', direction: 'desc' }); // newest first by default
  const [videoSort, setVideoSort] = useState({ field: 'created_at', direction: 'desc' }); // newest first by default
  const [showPictureSortModal, setShowPictureSortModal] = useState(false);
  const [showVideoSortModal, setShowVideoSortModal] = useState(false);
  
  // Folder organization state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null); // null means load from localStorage
  const [folderSort, setFolderSort] = useState('date-desc'); // Folder sorting: date-desc, date-asc, name-asc, name-desc
  
  // Storage limit tracking
  const [storageInfo, setStorageInfo] = useState(null);
  
  // Admin user switching tracking
  const [originalAdminUser, setOriginalAdminUser] = useState(null);
  
  // Force refresh trigger - update this to force data reload
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [showCopyToFolderModal, setShowCopyToFolderModal] = useState(false);
  const [copyingImage, setCopyingImage] = useState(null);
  const [showBatchCopyToFolderModal, setShowBatchCopyToFolderModal] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState({ show: false, current: 0, total: 0, message: '' });
  const [showCreateFolderInCopyModal, setShowCreateFolderInCopyModal] = useState(false);
  const [newFolderNameInCopyModal, setNewFolderNameInCopyModal] = useState('');
  const [isDraggingFiles, setIsDraggingFiles] = useState(false); // Track when dragging files from desktop
  const [showFolderNav, setShowFolderNav] = useState(false); // Folder navigation dropdown
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openFolderMenu, setOpenFolderMenu] = useState(null); // Track which folder's three-dot menu is open
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [touchStartPos, setTouchStartPos] = useState(null);
  const pictureGalleryRef = useRef(null);
  const videoGalleryJustOpened = useRef(false); // Track if video gallery was just opened to prevent image modal
  const dropdownJustClosed = useRef(false); // Track if dropdown was just closed to prevent fullscreen opening
  const picturesContentRef = useRef(null);
  const animatedMomentsContentRef = useRef(null);

  // Grid layout state for expandable rows/columns
  const [pictureGridSize, setPictureGridSize] = useState('small'); // 'small' = 5 cols on desktop (2nd smallest), 'medium', 'large'
  const [videoGridSize, setVideoGridSize] = useState('medium');
  
  const [selectedImageForAnimation, setSelectedImageForAnimation] = useState(null);
  const [showAdjustReanimate, setShowAdjustReanimate] = useState(false);
  const [adjustReanimateVideo, setAdjustReanimateVideo] = useState(null);
  const [failedModalJob, setFailedModalJob] = useState(null);
  
  // Helper function to detect R2 presigned URLs
  // Presigned URLs contain AWS signature parameters that MUST NOT be modified
  const isPresignedUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') return false;
    // Check for AWS signature parameters that indicate a presigned URL
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  }, []);
  
  // Helper function to add cache-busting timestamps to image URLs
  const addCacheBustingToImage = useCallback((image) => {
    if (!image) return null;
    
    const cacheBust = Date.now();
    const addTimestamp = (url) => {
      if (!url) return url;
      // NEVER modify presigned URLs - they have cryptographic signatures
      if (isPresignedUrl(url)) {
        console.log('⚠️ Skipping cache-bust for presigned URL:', url.substring(0, 100));
        return url;
      }
      const separator = url.includes('?') ? '&' : '?';
      // Remove any existing _cb or _animate parameters
      const cleanUrl = url.replace(/[&?]_cb=\d+/g, '').replace(/[&?]_animate=\d+/g, '');
      const newUrl = `${cleanUrl}${separator}_cb=${cacheBust}`;
      return newUrl;
    };
    
    const result = {
      ...image,
      url: addTimestamp(image.url),
      thumbnail_url: addTimestamp(image.thumbnail_url),
      r2_url: addTimestamp(image.r2_url),
      edited_url: addTimestamp(image.edited_url)
    };
    
    console.log('🔄 Cache-busting applied:', {
      imageId: image.id,
      originalUrl: image.url?.substring(0, 80),
      cacheBustedUrl: result.url?.substring(0, 80),
      timestamp: cacheBust
    });
    
    return result;
  }, [isPresignedUrl]);
  
  // Demo mode state
  const [demoMode, setDemoMode] = useState(false);
  const [showDemoTutorial, setShowDemoTutorial] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  
  // Tutorial flow tracking
  const [tutorialPromptClicked, setTutorialPromptClicked] = useState(false);
  const [tutorialVideoId, setTutorialVideoId] = useState(null);
  const [tutorialMockPendingVideo, setTutorialMockPendingVideo] = useState(null);
  
  // Tutorial steps configuration (Comprehensive end-to-end walkthrough)
  const TUTORIAL_STEPS = [
    {
      step: 0,
      title: "Welcome to Magic Moments! 🎭",
      description: "Let's walk through creating an animated moment from a still picture!",
      target: null,
      action: "next"
    },
    {
      step: 1,
      title: "Demo Pictures",
      description: "These are demo pictures",
      target: "demo-pictures-multi",
      action: "next"
    },
    {
      step: 2,
      title: "View Animations",
      description: "Click the eye button",
      target: "[data-picture-id='demo-pic-1'] button[title='View animated moments']",
      action: "wait-for-gallery"
    },
    {
      step: 3,
      title: "Video Gallery",
      description: "Click any video",
      target: ".video-gallery-modal",
      action: "wait-for-video"
    },
    {
      step: 4,
      title: "Animated Moment",
      description: "This is an animated moment generated from a still picture",
      target: ".fullscreen-video-modal",
      action: "wait-video-end",
      autoProgress: 5000
    },
    {
      step: 5,
      title: "View Prompt",
      description: "Click to see the AI prompt used",
      target: "[data-tutorial='prompt-dropdown']",
      action: "wait-for-click"
    },
    {
      step: 6,
      title: "Adjust & Reanimate 🔄",
      description: "Click to modify the prompt",
      target: "[data-tutorial='adjust-reanimate-button']",
      action: "wait-for-click"
    },
    {
      step: 7,
      title: "Edit Instructions ✏️",
      description: "Watch as we adjust the prompt",
      target: ".adjust-reanimate-modal",
      action: "auto-type",
      autoTypeText: "Make a dolphin jump in the far distance",
      autoTypeTarget: "textarea"
    },
    {
      step: 8,
      title: "Re-animate",
      description: "Click to re-animate with new prompt",
      target: "[data-tutorial='adjust-reanimate-button']",
      action: "wait-for-click"
    },
    {
      step: 9,
      title: "AI Suggestions 🤖",
      description: "Pick one of these AI-generated prompts",
      target: "[data-tutorial='ai-prompts-section']",
      action: "wait-for-prompt-click"
    },
    {
      step: 10,
      title: "Generate!",
      description: "Click to create your animated moment",
      target: "[data-tutorial='generate-button']",
      action: "wait-for-click"
    },
    {
      step: 11,
      title: "Processing... ⏳",
      description: "Your animation is being created",
      target: null,
      action: "wait-processing"
    },
    {
      step: 12,
      title: "Pending Animations 📋",
      description: "Your animation is processing",
      target: ".pending-video-card",
      action: "wait-completion"
    },
    {
      step: 13,
      title: "View Your Creation! 🎉",
      description: "Click to see your new animated moment",
      target: ".completed-video-card",
      action: "wait-for-video"
    },
    {
      step: 14,
      title: "Complete! 🚀",
      description: "You've created your first animated moment!",
      target: null,
      action: "complete"
    }
  ];
  const [projectSubtab, setProjectSubtab] = useState('my'); // 'my', 'shared', 'collaborations'
  const [videoDurations, setVideoDurations] = useState({}); // Store actual video durations
  // Note: showBatchAnimation is passed down as a prop to components that need it (no globals)

  const [expandedPrompts, setExpandedPrompts] = useState({}); // Track which video prompts are expanded
  
  // Batch animation state
  const [showBatchAnimation, setShowBatchAnimation] = useState(false);
  const [batchAnimationImages, setBatchAnimationImages] = useState([]);
  const [batchPrompts, setBatchPrompts] = useState({}); // {imageId: {prompt, isGenerating}}
  const [batchEditingImage, setBatchEditingImage] = useState(null);
  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);
  const batchAnalysisJobIds = React.useRef([]); // Track active analysis job IDs for cancellation
  const isCancellingBatch = React.useRef(false); // Prevent duplicate cancel calls
  const lastBatchCancelTime = React.useRef(0); // Track when batch was cancelled to suppress credit sync toasts
  const [regenerationCounts, setRegenerationCounts] = useState({}); // Track regenerations per image {imageId: count}
  const [usedAIAnalysis, setUsedAIAnalysis] = useState(false); // Track if user chose AI analysis
  const [isBatchAnalyzing, setIsBatchAnalyzing] = useState(false); // Track if analysis is in progress
  const [analysisCompleted, setAnalysisCompleted] = useState(0); // Track completed analyses for cancellation charging
  const [estimatedProgress, setEstimatedProgress] = useState(0); // Simulated progress for smooth UX
  
  // Simulate smooth progress for better UX during batch analysis
  React.useEffect(() => {
    if (!isBatchAnalyzing || !batchAnimationImages.length) {
      setEstimatedProgress(0);
      return;
    }
    
    const totalImages = batchAnimationImages.length;
    const progressPerImage = 100 / totalImages; // e.g., 20% per image for 5 images
    const avgTimePerImage = 16; // Average 16 seconds per image based on logs
    const totalEstimatedTime = avgTimePerImage * totalImages; // e.g., 80 seconds for 5 images
    
    // Start progress immediately
    const startTime = Date.now();
    setEstimatedProgress(3); // Jump to 3% immediately for instant feedback
    
    const interval = setInterval(() => {
      setEstimatedProgress(prev => {
        const elapsedSeconds = (Date.now() - startTime) / 1000;
        const actualProgress = (analysisCompleted / totalImages) * 100;
        
        // Calculate time-based progress (assuming even distribution)
        const timeBasedProgress = Math.min((elapsedSeconds / totalEstimatedTime) * 100, 95);
        
        // Use the HIGHER of actual or time-based progress (never go backwards)
        const targetProgress = Math.max(actualProgress, timeBasedProgress);
        
        // If we're significantly behind target, catch up faster
        if (targetProgress > prev + 2) {
          console.log(`📊 Fast catch-up: ${Math.round(prev)}% → ${Math.round(targetProgress)}%`);
          return targetProgress;
        }
        
        // Smooth increments based on where we are
        let increment;
        const progressRemaining = 100 - prev;
        
        if (progressRemaining > 50) {
          increment = 0.8; // Fast when plenty remaining
        } else if (progressRemaining > 20) {
          increment = 0.5; // Medium speed
        } else if (progressRemaining > 5) {
          increment = 0.3; // Slower as we approach 100%
        } else {
          increment = 0.1; // Very slow near 100% (wait for actual completion)
        }
        
        // Don't exceed target or 98% (wait for actual completion at 100%)
        const newProgress = Math.min(prev + increment, targetProgress, 98);
        
        return newProgress;
      });
    }, 250); // Update every 250ms for smoother animation
    
    return () => clearInterval(interval);
  }, [isBatchAnalyzing, analysisCompleted, batchAnimationImages.length]);
  const FREE_REGENERATIONS_LIMIT = 0; // No free regenerations - always charge 0.05 credits
  // Note: albumRefreshCallback and albumFilterSwitchCallback are now passed as props from App component
  // Warning modal state
  const [showBatchWarning, setShowBatchWarning] = useState(false);
  const [batchWarningData, setBatchWarningData] = useState(null);
  const [pendingBatchImages, setPendingBatchImages] = useState([]);
  // Single image analysis warning
  const [showSingleAnalysisWarning, setShowSingleAnalysisWarning] = useState(false);
  // Analysis limit reached modal (fun upgrade modal)
  const [showAnalysisLimitModal, setShowAnalysisLimitModal] = useState(false);
  // Import from Albums modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importModalType, setImportModalType] = useState('pictures'); // 'pictures' or 'moments'
  const [singleAnalysisWarningData, setSingleAnalysisWarningData] = useState(null);
  const [pendingSingleImageAction, setPendingSingleImageAction] = useState(null); // {image, action: 'animate' | 'enhance'}
  const [skipAnalysisFlag, setSkipAnalysisFlag] = useState(false); // Track if user chose to skip analysis
  const skipAnalysisRef = useRef(false); // Ref for immediate access without re-render delays
  const [groupedView, setGroupedView] = useState(false);
  const [videoGroups, setVideoGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  // Image-to-video tracking states
  const [imagesWithVideos, setImagesWithVideos] = useState(new Set()); // Set of image IDs that have associated or pending videos
  // Open Batch Animation modal for selected images (Albums or Pictures)
  const openBatchAnimation = React.useCallback(async (selectedImages) => {
    try {
      if (!selectedImages || selectedImages.length === 0) {
        toast.error('No images selected');
        return;
      }
      
      // NEW POLICY: Analysis is FREE, just check if user can proceed
      const BACKEND_URL = getBACKEND_URL();
      try {
        const costResponse = await axios.post(`${BACKEND_URL}/api/check-batch-analysis-cost`, {
          user_id: user.id,
          image_count: selectedImages.length
        });
        
        const costData = costResponse.data;
        console.log('💰 Batch analysis check (NEW: FREE):', costData);
        
        // Check if user can proceed
        if (!costData.can_proceed) {
          // User has 0 credits and exceeded 5 free analysis limit - show fun upgrade modal
          setShowAnalysisLimitModal(true);
          return;
        }
        
        // Show friendly notification if user is close to limit
        if (!costData.has_credits && costData.free_remaining && costData.free_remaining <= 2) {
          toast.success(`Free AI analysis! ${costData.free_remaining} analyses remaining without credits.`, {
            duration: 4000,
            icon: '🎉'
          });
        }
        
        // Analysis is FREE - proceed directly without warning modal
        console.log('✅ Analysis is free, proceeding with batch animation');
        proceedWithBatchAnimation(selectedImages);
        
      } catch (error) {
        console.error('Failed to check analysis:', error);
        // If check fails, proceed anyway (fallback)
        proceedWithBatchAnimation(selectedImages);
      }
    } catch (err) {
      console.error('openBatchAnimation failed:', err);
      toast.error('Could not open batch animation. Please try again.');
    }
  }, [user]);
  
  // Proceed with batch animation WITHOUT AI analysis (free - uses default prompts)
  const proceedWithBatchAnimationNoAnalysis = React.useCallback((selectedImages) => {
    try {
      // Add cache-busting to all images
      const cacheBustedImages = selectedImages.map(img => addCacheBustingToImage(img));
      
      setBatchAnimationImages(cacheBustedImages);
      setShowBatchAnimation(true);
      setUsedAIAnalysis(false); // User skipped AI analysis
      const promptsInit = {};
      
      // Reset regeneration counts for new batch
      setRegenerationCounts({});
      
      // Use default prompt for all images (no AI analysis)
      for (const img of cacheBustedImages) {
        promptsInit[img.id] = { prompt: 'Natural lifelike motion with gentle breathing and subtle movements. For pets: SLOW, GRADUAL movements - NO rapid or quick motion. ZERO shakiness or wobbling, PERFECTLY STEADY posture, smooth controlled movements that are DELIBERATE and UNHURRIED. CRITICAL: Mouth stays COMPLETELY CLOSED - ABSOLUTELY NO tongue movement, NO tongue visible, NO licking, NO panting, NO mouth opening. The tongue NEVER appears - it looks unrealistic. NO NEW people, faces, hands, or body parts enter frame from edges. Frame boundary is LOCKED. Background and inanimate objects stay completely frozen. No new elements or phantom movements.', isGenerating: false };
      }
      setBatchPrompts(promptsInit);
      
      console.log('✅ Opened batch animation generator without AI analysis (free)');
      toast.success('Batch animation generator opened with default prompts');
    } catch (err) {
      console.error('proceedWithBatchAnimationNoAnalysis failed:', err);
      toast.error('Could not start batch animation. Please try again.');
    }
  }, []);
  
  // Cancel batch analysis
  const cancelBatchAnalysis = React.useCallback(async () => {
    try {
      // CRITICAL: Send cancellation request to backend FIRST
      const jobIdsToCancel = [...batchAnalysisJobIds.current];
      if (jobIdsToCancel.length > 0) {
        try {
          const BACKEND_URL = getBACKEND_URL();
          console.log(`🛑 Sending cancellation request for ${jobIdsToCancel.length} jobs:`, jobIdsToCancel);
          await axios.post(`${BACKEND_URL}/api/cancel-analysis`, jobIdsToCancel);
          console.log('✅ Backend cancellation request sent successfully');
        } catch (cancelError) {
          console.error('❌ Failed to send cancellation to backend:', cancelError);
          // Continue with frontend cleanup even if backend call fails
        }
      }
      
      // Count how many analyses completed
      const completedCount = Object.values(batchPrompts).filter(p => !p.isGenerating && p.prompt !== '').length;
      setAnalysisCompleted(completedCount);
      
      // Clear all analyzing flags
      setBatchPrompts(prev => {
        const updated = {...prev};
        Object.keys(updated).forEach(key => {
          if (updated[key].isGenerating) {
            updated[key] = { prompt: 'Gentle movement and subtle animation', isGenerating: false };
          }
        });
        return updated;
      });
      
      // Clear job tracking
      batchAnalysisJobIds.current = [];
      setIsBatchAnalyzing(false);
      
      // Deduct credits for completed analyses only
      if (completedCount > 0) {
        try {
          const BACKEND_URL = getBACKEND_URL();
          const deductResponse = await axios.post(`${BACKEND_URL}/api/deduct-analysis-cost`, {
            user_id: user.id,
            analysis_count: completedCount
          });
          console.log(`✅ Cost deducted for ${completedCount} completed analyses:`, deductResponse.data);
          
          // Update user credits
          if (deductResponse.data.new_credits !== undefined) {
            setUser(prev => ({ ...prev, credits: deductResponse.data.new_credits }));
          }
          
          toast.info(`Analysis cancelled. ${completedCount} analyses completed and charged (${(completedCount * 0.05).toFixed(2)} credits)`);
        } catch (error) {
          console.error('Failed to deduct cost for completed analyses:', error);
        }
      } else {
        toast.info('Analysis cancelled. No charges applied.');
      }
    } catch (err) {
      console.error('cancelBatchAnalysis failed:', err);
    }
  }, [batchPrompts, user, setUser]);

  // Proceed with batch animation after warning confirmation
  const proceedWithBatchAnimation = React.useCallback((selectedImages) => {
    try {
      // CRITICAL: If in album context, pre-emptively switch to moments view
      if (albumContext && albumContext.albumId) {
        console.log('🎯 PRE-EMPTIVE FILTER SWITCH: Batch animation starting in album - switching to moments IMMEDIATELY');
        if (albumFilterSwitchCallback.current && typeof albumFilterSwitchCallback.current === 'function') {
          try {
            albumFilterSwitchCallback.current('moments');
            console.log('✅ Filter pre-emptively switched to moments before batch modal opens');
          } catch (error) {
            console.error('❌ Error pre-switching filter:', error);
          }
        }
      }
      
      // Add cache-busting to all images
      const cacheBustedImages = selectedImages.map(img => addCacheBustingToImage(img));
      
      setBatchAnimationImages(cacheBustedImages);
      setShowBatchAnimation(true);
      setUsedAIAnalysis(true); // User chose AI analysis
      setIsBatchAnalyzing(true); // Start analyzing
      setAnalysisCompleted(0); // Reset completed count
      const promptsInit = {};
      const jobIds = []; // Track job IDs for cancellation
      
      // Reset regeneration counts for new batch
      setRegenerationCounts({});
      
      // Reset rate limit flags for new batch
      window.rateLimitToastShown = false;
      window.rateLimitLogged = false;
      
      for (const img of cacheBustedImages) {
        promptsInit[img.id] = { prompt: '', isGenerating: true };
      }
      setBatchPrompts(promptsInit);
      
      // Generate prompts in TRUE PARALLEL with Promise.all
      let completedCount = 0;
      const analysisPromises = cacheBustedImages.map((img, index) => {
        const jobId = `analysis_${img.id}_${Date.now()}_${index}`; // Unique job ID with index
        jobIds.push(jobId);
        batchAnalysisJobIds.current.push(jobId);
        
        return analyzeImageWithAI(img, null, user, jobId)
          .then(result => {
            // Update progress immediately when THIS image completes
            completedCount++;
            setAnalysisCompleted(completedCount);
            // Only log every 10 images or the last one to reduce console spam
            if (completedCount % 10 === 0 || completedCount === cacheBustedImages.length) {
              console.log(`✅ Batch progress: ${completedCount}/${cacheBustedImages.length} images analyzed`);
            }
            
            // Handle both array and object response from analyzeImageWithAI
            // It returns { suggestions: [...], borderWarning, fadedWarning } when warnings exist
            // Or just [...] array when no warnings
            let suggestions;
            if (result && typeof result === 'object' && !Array.isArray(result)) {
              // Object with suggestions property
              suggestions = result.suggestions || [];
            } else if (Array.isArray(result)) {
              // Direct array
              suggestions = result;
            } else {
              suggestions = [];
            }
            
            // Update prompt for this image
            const best = suggestions && suggestions.length > 0 ? suggestions[0] : 'Gentle movement and subtle animation';
            setBatchPrompts(prev => ({ ...prev, [img.id]: { prompt: best, isGenerating: false } }));
            
            // Remove completed job from tracking
            batchAnalysisJobIds.current = batchAnalysisJobIds.current.filter(id => id !== jobId);
            
            return {
              img,
              suggestions,
              jobId,
              success: true
            };
          })
          .catch(error => {
            // Update progress even on error
            completedCount++;
            setAnalysisCompleted(completedCount);
            
            // Check if this is a rate limit error
            if (error?.type === 'rate_limit') {
              // Only log rate limit warning once to avoid spam
              if (completedCount === 1 || !window.rateLimitLogged) {
                console.warn(`⚠️ OpenAI rate limit reached. Affected images will use fallback prompts.`);
                window.rateLimitLogged = true;
              }
              
              // Set a special prompt indicating rate limit
              setBatchPrompts(prev => ({ 
                ...prev, 
                [img.id]: { 
                  prompt: 'Gentle movement and subtle animation', 
                  isGenerating: false,
                  rateLimitHit: true 
                } 
              }));
              
              // Show toast notification on first rate limit hit
              if (!window.rateLimitToastShown) {
                toast.warning('⏳ OpenAI rate limit reached. Some analyses using fallback prompts. Please upgrade your OpenAI tier for better results.', {
                  autoClose: 8000
                });
                window.rateLimitToastShown = true;
              }
            } else {
              console.error(`❌ Image ${completedCount}/${cacheBustedImages.length} analysis failed:`, error);
              
              // Set fallback prompt for non-rate-limit errors
              setBatchPrompts(prev => ({ ...prev, [img.id]: { prompt: 'Gentle movement and subtle animation', isGenerating: false } }));
            }
            
            // Remove failed job from tracking
            batchAnalysisJobIds.current = batchAnalysisJobIds.current.filter(id => id !== jobId);
            
            return {
              img,
              error,
              jobId,
              success: false
            };
          });
      });
      
      // Execute all API calls in parallel
      console.log(`🚀 Starting ${analysisPromises.length} parallel AI analysis requests...`);
      const startTime = Date.now();
      
      Promise.all(analysisPromises).then(results => {
        const duration = ((Date.now() - startTime) / 1000).toFixed(1);
        console.log(`✅ All ${results.length} analyses completed in ${duration}s`);
        
        // All processing already done in individual promise handlers
        // Set progress to 100% and mark batch analyzing as complete
        setEstimatedProgress(100);
        setTimeout(() => {
          setIsBatchAnalyzing(false);
        }, 500); // Brief delay to show 100% before hiding
      });
      
      // Deduct cost after all analyses complete
      const analysisCount = cacheBustedImages.length;
      setTimeout(async () => {
        // Only deduct if analysis wasn't cancelled
        if (batchAnalysisJobIds.current.length === 0) {
          try {
            const BACKEND_URL = getBACKEND_URL();
            const deductResponse = await axios.post(`${BACKEND_URL}/api/deduct-analysis-cost`, {
              user_id: user.id,
              analysis_count: analysisCount
            });
            console.log('✅ Cost deducted:', deductResponse.data);
            
            // Update user credits in state
            if (deductResponse.data.new_credits !== undefined) {
              setUser(prev => ({ ...prev, credits: deductResponse.data.new_credits }));
            }
          } catch (error) {
            console.error('Failed to deduct cost:', error);
            if (error.response?.status === 402) {
              toast.error('Insufficient credits after analysis!');
            }
          }
        }
      }, 5000); // Wait 5 seconds for analyses to likely complete
    } catch (err) {
      console.error('proceedWithBatchAnimation failed:', err);
      toast.error('Could not start batch animation. Please try again.');
    }
  }, [user, setUser]);
  
  // Check and show single image analysis warning
  const checkSingleImageAnalysis = React.useCallback(async (image, actionCallback) => {
    if (!user || !user.id) {
      toast.error('Please log in to continue');
      return;
    }
    
    try {
      const BACKEND_URL = getBACKEND_URL();
      const costResponse = await axios.post(`${BACKEND_URL}/api/check-batch-analysis-cost`, {
        user_id: user.id,
        image_count: 1
      });
      
      const costData = costResponse.data;
      console.log('💰 Single analysis check (NEW: FREE):', costData);
      
      // NEW POLICY: Analysis is always free, just check if user can proceed
      if (!costData.can_proceed) {
        // User has 0 credits and hit the 5 free analysis limit - show fun upgrade modal
        setShowAnalysisLimitModal(true);
        // Note: Don't call actionCallback - analysis is blocked
        return;
      }
      
      // Show friendly message if user has limited analyses remaining
      if (!costData.has_credits && costData.free_remaining && costData.free_remaining <= 2) {
        toast.success(`Free AI analysis! ${costData.free_remaining} remaining without credits.`, {
          duration: 4000,
          icon: '🎉'
        });
      }
      
      // Proceed with analysis - the callback will trigger the actual analysis
      // After this callback, we'll track the usage
      actionCallback();
      
      // Track usage AFTER the callback (non-blocking)
      try {
        const deductResponse = await axios.post(`${BACKEND_URL}/api/deduct-analysis-cost`, {
          user_id: user.id,
          analysis_count: 1
        });
        console.log('✅ Analysis tracked:', deductResponse.data);
      } catch (error) {
        console.error('Failed to track analysis:', error);
        // Don't block - analysis already started
      }
      
    } catch (error) {
      console.error('Failed to check single analysis:', error);
      // If check fails, proceed anyway (fallback behavior)
      actionCallback();
    }
  }, [user]);
  
  // Wrapper to open animation with analysis check
  const openAnimationWithCheck = React.useCallback((image = null) => {
    const actionCallback = () => {
      if (image) {
        const cacheBustedImage = addCacheBustingToImage(image);
        setSelectedImageForAnimation(cacheBustedImage);
      }
      setShowCreateLivingMemory(true);
    };
    
    // If image is provided, show warning first
    if (image) {
      checkSingleImageAnalysis(image, actionCallback);
    } else {
      // No image yet (will select in modal), just open
      actionCallback();
    }
  }, [checkSingleImageAnalysis]);

  const [showVideoGallery, setShowVideoGallery] = useState(null); // Image ID for which to show video gallery
  const [imageVideoData, setImageVideoData] = useState({}); // Cache for image video data
  const [hideDemoContent, setHideDemoContent] = useState(false); // Toggle to hide demo content

  // Check if user has created any real (non-demo) videos
  const hasRealVideos = React.useMemo(() => {
    return videoJobs.some(video => {
      const resultUrl = video.result_url || video.video_url || '';
      // If video exists and is NOT from landing-assets, it's a real video
      // Also include completed videos without result_url (legacy or processing)
      if (video.status === 'completed' && !resultUrl) {
        return true; // Legacy completed videos without result_url are real
      }
      return resultUrl && !resultUrl.includes('/landing-assets');
    });
  }, [videoJobs]);

  // Auto-hide demo content when user has real videos
  useEffect(() => {
    if (hasRealVideos) {
      setHideDemoContent(true);
    }
  }, [hasRealVideos]);

  // Load demo mode preference from localStorage
  useEffect(() => {
    const savedDemoMode = localStorage.getItem('demoMode');
    const tutorialCompleted = localStorage.getItem('tutorialCompleted');
    
    // SAFETY: If user has uploaded images, force disable demo mode AND tutorial
    if (savedDemoMode === 'true' && user && unassignedImages.length > 0) {
      console.log('🛡️ SAFETY: Disabling demo mode AND tutorial - user has uploaded images');
      setDemoMode(false);
      setShowDemoTutorial(false);
      setDemoStep(0);
      localStorage.setItem('demoMode', 'false');
      localStorage.setItem('tutorialCompleted', 'true');
      return;
    }
    
    if (savedDemoMode !== null) {
      setDemoMode(savedDemoMode === 'true');
      
      // DISABLED: Tutorial is not ready yet - hide for now
      // Start tutorial if demo mode is on and tutorial hasn't been completed
      // if (savedDemoMode === 'true' && !tutorialCompleted) {
      //   setTimeout(() => {
      //     setShowDemoTutorial(true);
      //     setDemoStep(0);
      //   }, 1000); // Small delay to let UI render
      // }
    }
  }, [user, unassignedImages.length]);
  
  // Tutorial navigation handlers
  const handleTutorialNext = () => {
    setDemoStep(prev => prev + 1);
  };
  
  const handleTutorialSkip = () => {
    setShowDemoTutorial(false);
    setDemoStep(0);
    localStorage.setItem('tutorialCompleted', 'true');
    toast.info('Tutorial skipped. You can always explore on your own!');
  };
  
  const handleTutorialComplete = () => {
    setShowDemoTutorial(false);
    setDemoStep(0);
    localStorage.setItem('tutorialCompleted', 'true');
    toast.success('Tutorial complete! Start uploading your pictures to create magic!');
  };
  
  // Cancel batch animation analysis
  const handleCancelBatchAnimation = React.useCallback(async () => {
    // Prevent duplicate calls
    if (isCancellingBatch.current) {
      console.log('⚠️ Cancel already in progress, ignoring duplicate call');
      return;
    }
    
    isCancellingBatch.current = true;
    lastBatchCancelTime.current = Date.now(); // Record cancellation time
    console.log('🛑 Cancelling batch animation analysis...');
    
    // Close modal immediately (don't wait for async operations)
    setShowBatchAnimation(false);
    setBatchAnimationImages([]);
    setBatchPrompts({});
    setIsBatchAnalyzing(false);
    setAnalysisCompleted(0);
    
    // Get all active job IDs before clearing
    const jobIdsToCancel = [...batchAnalysisJobIds.current];
    
    // Clear job tracking immediately
    batchAnalysisJobIds.current = [];
    
    // Handle credit deduction and backend cancellation in background (don't block modal close)
    (async () => {
      try {
        // If analysis was in progress, handle credit deduction for completed work
        if (isBatchAnalyzing) {
          const completedCount = Object.values(batchPrompts).filter(p => !p.isGenerating && p.prompt !== '').length;
          
          // Deduct credits for completed analyses only
          if (completedCount > 0) {
            try {
              const BACKEND_URL = getBACKEND_URL();
              const deductResponse = await axios.post(`${BACKEND_URL}/api/deduct-analysis-cost`, {
                user_id: user.id,
                analysis_count: completedCount
              });
              console.log(`✅ Cost deducted for ${completedCount} completed analyses:`, deductResponse.data);
              
              // Update user credits silently (no toast from credit sync)
              if (deductResponse.data.new_credits !== undefined) {
                setUser(prev => ({ ...prev, credits: deductResponse.data.new_credits }));
              }
              
              // Show detailed toast about what was analyzed
              const creditsCharged = (completedCount * 0.05).toFixed(2);
              toast.info(`Analysis cancelled. ${completedCount} image${completedCount !== 1 ? 's' : ''} analyzed before cancellation. ${creditsCharged} credits charged.`, {
                duration: 6000
              });
            } catch (error) {
              console.error('Failed to deduct cost for completed analyses:', error);
              toast.error('Analysis cancelled. An error occurred while processing.');
            }
          }
          // Don't show "No charges" toast - race condition may mean charges still apply
        }
        
        // Cancel backend analysis jobs
        if (jobIdsToCancel.length > 0) {
          try {
            const BACKEND_URL = getBACKEND_URL();
            await axios.post(`${BACKEND_URL}/api/cancel-analysis`, jobIdsToCancel, {
              headers: { 'Content-Type': 'application/json' }
            });
            console.log(`✅ Cancelled ${jobIdsToCancel.length} analysis job(s)`);
          } catch (error) {
            console.error('Failed to cancel analysis jobs:', error);
          }
        }
      } catch (err) {
        console.error('Background cleanup failed during cancel:', err);
      } finally {
        // Reset cancelling flag after a short delay
        setTimeout(() => {
          isCancellingBatch.current = false;
        }, 1000);
      }
    })();
  }, [isBatchAnalyzing, batchPrompts, user, setUser]);
  
  // Handle browser close/refresh - cancel analysis
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (batchAnalysisJobIds.current.length > 0) {
        // Use synchronous request for beforeunload (browsers may block async)
        const jobIds = [...batchAnalysisJobIds.current];
        const BACKEND_URL = getBACKEND_URL();
        
        // Use sendBeacon if available (more reliable for page unload)
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(jobIds)], { type: 'application/json' });
          navigator.sendBeacon(`${BACKEND_URL}/api/cancel-analysis`, blob);
        } else {
          // Fallback to synchronous XHR
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${BACKEND_URL}/api/cancel-analysis`, false); // synchronous
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.send(JSON.stringify(jobIds));
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  
  // Auto-progress tutorial when user performs expected actions (DEMO CONTENT ONLY)
  useEffect(() => {
    if (!showDemoTutorial || !demoMode) return;
    
    // Step 2: When eye button clicked and video gallery opens, move to step 3
    if (demoStep === 2 && showVideoGallery) {
      const isDemoImage = showVideoGallery && showVideoGallery.startsWith('demo-pic-');
      if (isDemoImage) {
        setTimeout(() => setDemoStep(3), 500);
      }
    }
    
    // Step 3: When video clicked in gallery, move to step 4 (full-screen)
    if (demoStep === 3 && fullScreenVideo) {
      const isDemoVideo = fullScreenVideo.id && fullScreenVideo.id.startsWith('demo-vid-');
      if (isDemoVideo) {
        setTutorialVideoId(fullScreenVideo.id);
        setTimeout(() => setDemoStep(4), 500);
      }
    }
    
    // Step 6: When Adjust & Reanimate clicked, move to step 7 (auto-type)
    if (demoStep === 6 && showAdjustReanimate) {
      setTimeout(() => setDemoStep(7), 500);
    }
    
    // Step 8: When re-animate clicked, generator opens, move to step 9
    if (demoStep === 8 && showCreateLivingMemory) {
      setTimeout(() => setDemoStep(9), 500);
    }
    
    // Step 10: When generate clicked, show processing, move to step 11
    if (demoStep === 10) {
      // This will be triggered by the generate button click
      // We'll handle this in the generate flow
    }
    
  }, [showDemoTutorial, demoMode, demoStep, showVideoGallery, fullScreenVideo, showAdjustReanimate, showCreateLivingMemory]);
  
  // Stop tutorial if demo mode is turned off
  useEffect(() => {
    if (!demoMode && showDemoTutorial) {
      setShowDemoTutorial(false);
      setDemoStep(0);
      console.log('🎭 Tutorial stopped - demo mode disabled');
    }
  }, [demoMode, showDemoTutorial]);
  
  // Auto-delete demo content when user creates their first real moment
  useEffect(() => {
    if (hasRealVideos && demoMode) {
      console.log('🎉 User created their first real moment! Deleting demo content...');
      
      // Disable demo mode
      setDemoMode(false);
      localStorage.setItem('demoMode', 'false');
      
      // Show success message
      toast.success('🎉 Congratulations on your first animated moment! Demo content removed.');
    }
  }, [hasRealVideos, demoMode]);
  
  // EMERGENCY: Global escape hatch - press ESC 3 times quickly to force-disable demo mode
  useEffect(() => {
    let escPressCount = 0;
    let escTimer = null;
    
    const handleEmergencyEscape = (e) => {
      if (e.key === 'Escape' && demoMode) {
        escPressCount++;
        
        if (escPressCount === 1) {
          console.log('🛡️ Press ESC 2 more times quickly to force-disable demo mode');
        }
        
        if (escPressCount >= 3) {
          console.log('🚨 EMERGENCY: Force-disabling demo mode');
          setDemoMode(false);
          setShowDemoTutorial(false);
          localStorage.setItem('demoMode', 'false');
          localStorage.setItem('tutorialCompleted', 'true');
          toast.success('Demo mode force-disabled! 🛡️');
          escPressCount = 0;
        }
        
        // Reset counter after 2 seconds
        clearTimeout(escTimer);
        escTimer = setTimeout(() => {
          escPressCount = 0;
        }, 2000);
      }
    };
    
    document.addEventListener('keydown', handleEmergencyEscape);
    return () => {
      document.removeEventListener('keydown', handleEmergencyEscape);
      clearTimeout(escTimer);
    };
  }, [demoMode]);

  useEffect(() => {
    if (user) {
      loadDashboardData();
      loadAIProviders();
    }
  }, [showArchived, demoMode, user]); // Reload when demo mode changes or user logs in

  useEffect(() => {
    if (groupedView) {
      loadGroupedVideoJobs();
    }
  }, [groupedView]);

  useEffect(() => {
    // Always load video jobs - standalone videos exist independently of projects
    loadVideoJobs();
  }, [projects]);

  // Load orders when navigating to my-orders view
  useEffect(() => {
    if (currentView === 'my-orders' && user?.id) {
      loadMyOrders();
    }
  }, [currentView, user?.id]);

  // Monitor for newly completed videos and send browser notifications
  useEffect(() => {
    if (!user?.settings?.browser_notifications) return;
    if (videoJobs.length === 0 || previousVideoJobs.length === 0) {
      setPreviousVideoJobs(videoJobs);
      return;
    }

    // Find newly completed videos
    const newCompletedVideos = videoJobs.filter(job => {
      const wasNotCompleted = !previousVideoJobs.find(prev => prev.id === job.id && prev.status === 'completed');
      const isNowCompleted = job.status === 'completed';
      return wasNotCompleted && isNowCompleted;
    });

    // Send browser notification for each newly completed video
    if (newCompletedVideos.length > 0 && 'Notification' in window && Notification.permission === 'granted') {
      newCompletedVideos.forEach(video => {
        const notification = new Notification('Animated Moment Ready!', {
          body: `Your animated moment is ready to view!`,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: `video-${video.id}`,
          requireInteraction: false,
          silent: false
        });

        notification.onclick = () => {
          window.focus();
          setCurrentView('living_memories');
          setActiveMainTab('moments');
          notification.close();
        };
      });
    }

    setPreviousVideoJobs(videoJobs);
  }, [videoJobs, user]);

  useEffect(() => {
    // Only load image-video associations when NOT on Projects (Albums/Collaborate) view
    // Projects view has its own album-specific video loading
    if (currentView === 'projects') {
      console.log('🚫 Skipping image-video associations on Projects/Collaborate view');
      return;
    }
    
    // Debounce loadImageVideoAssociations to prevent excessive calls
    const debounceTimer = setTimeout(() => {
      if (videoJobs.length > 0) {
        loadImageVideoAssociations();
      }
    }, 500); // Shorter delay since we're not making API calls anymore
    
    return () => clearTimeout(debounceTimer);
  }, [videoJobs, currentView]);

  // Initialize cart count and expose functions to window for navbar
  useEffect(() => {
    const fetchCartCount = async () => {
      if (user) {
        try {
          const userStr = localStorage.getItem('user');
          const userData = userStr ? JSON.parse(userStr) : null;
          const userId = userData?.id;
          
          if (userId) {
            const response = await axios.get(`${BACKEND_URL}/api/shop/cart?user_id=${userId}`);
            setCartCount(response.data.count);
            window.cartCount = response.data.count;
          }
        } catch (err) {
          console.error('Error fetching cart count:', err);
        }
      }
    };

    fetchCartCount();

    // Update cart count on window
    window.cartCount = cartCount;

    return () => {
      delete window.cartCount;
    };
  }, [user, cartCount]);

  // Separate useEffect for event listeners and window functions
  useEffect(() => {
    // Listen for cart update events
    const handleCartUpdate = async () => {
      if (user) {
        try {
          const userStr = localStorage.getItem('user');
          const userData = userStr ? JSON.parse(userStr) : null;
          const userId = userData?.id;
          
          if (userId) {
            const response = await axios.get(`${BACKEND_URL}/api/shop/cart?user_id=${userId}`);
            setCartCount(response.data.count);
            window.cartCount = response.data.count;
          }
        } catch (err) {
          console.error('Error fetching cart count:', err);
        }
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [user]);

  // Expose window functions for navbar and other components
  useEffect(() => {
    window.openCartModal = () => setShowCart(true);
    return () => {
      delete window.openCartModal;
    };
  });

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Find all open dropdowns
      const openDropdowns = document.querySelectorAll('[data-dropdown]:not(.hidden)');
      
      // Check if click is outside any dropdown
      openDropdowns.forEach(dropdown => {
        const dropdownContainer = dropdown.closest('.relative');
        if (dropdownContainer && !dropdownContainer.contains(event.target)) {
          dropdown.classList.add('hidden');
        }
      });
    };

    // Add event listener
    document.addEventListener('click', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Auto-refresh progress for pending videos
  useEffect(() => {
    const hasPendingVideos = videoJobs.some(job => 
      job.status === 'processing' || job.status === 'queued'
    );
    
    if (hasPendingVideos) {
      const interval = setInterval(() => {
        // Only refresh video jobs, not all dashboard data
        // This prevents pictures from flashing during video processing
        loadVideoJobs();
        
        // Only reload dashboard data if we're on the Animated Moments view
        if (currentView === 'living_memories') {
          loadDashboardData();
        }
      }, 3000); // Refresh every 3 seconds
      
      return () => clearInterval(interval);
    }
  }, [videoJobs, currentView]);

  // Immediate refresh when switching to Animated Moments tab
  useEffect(() => {
    if (currentView === 'living_memories') {
      loadVideoJobs();
      loadDashboardData();
    }
  }, [currentView]);


  // ═══════════════════════════════════════════════════════════════════════════════
  // SMART POLLING FOR MOMENTS TAB - Only poll processing videos
  // ═══════════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!user || currentView !== 'living_memories') return;
    
    // Count processing videos (those without stream URL or thumbnail)
    const processingCount = videoJobs.filter(job => 
      job.status === 'processing' || 
      (!job.cloudflare_stream_url && !job.result_url?.includes('cloudflare')) ||
      !job.thumbnail_url
    ).length;
    
    if (processingCount === 0) {
      console.log('✅ Moments tab: No processing videos - no polling needed');
      return;
    }
    
    console.log(`🔄 Moments tab: Starting poll for ${processingCount} processing videos`);
    
    // Track the last count to avoid showing duplicate toasts
    let lastProcessingCount = processingCount;
    
    const pollInterval = setInterval(async () => {
      try {
        console.log(`🔄 Moments tab: Checking video status...`);
        
        // Get fresh video jobs data
        const effectiveUserId = user?.id;
        const originalAdmin = localStorage.getItem('originalAdmin');
        const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : effectiveUserId;
        const timestamp = Date.now();
        
        const response = await axios.get(
          `${getAPI()}/video-generations?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&_=${timestamp}`,
          {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          }
        );
        
        const freshJobs = response.data;
        
        // Count how many are still processing
        const stillProcessing = freshJobs.filter(job => 
          job.status === 'processing' || 
          (!job.cloudflare_stream_url && !job.result_url?.includes('cloudflare')) ||
          !job.thumbnail_url
        ).length;
        
        // Only show toast if count actually DECREASED since last check
        if (stillProcessing < lastProcessingCount) {
          const completedCount = lastProcessingCount - stillProcessing;
          console.log(`✨ Moments tab: ${completedCount} video(s) completed!`);
          loadVideoJobs();
          toast.success('🎉 Video ready!');
          lastProcessingCount = stillProcessing;
        }
      } catch (error) {
        console.error('Error polling video jobs:', error);
      }
    }, 8000); // Poll every 8 seconds
    
    return () => {
      console.log('🛑 Moments tab: Stopping video polling');
      clearInterval(pollInterval);
    };
  }, [user?.id, currentView, videoJobs.length]); // Simplified dependencies


  // Load pending invitations AND access requests once on mount
  // Access requests will update in real-time via WebSocket (see Global WebSocket connections below)
  useEffect(() => {
    if (user) {
      loadPendingInvitations();
      loadTotalAccessRequests(); // Initial load
      
      // Refresh invitations every 10 seconds (access requests handled by WebSocket)
      const interval = setInterval(() => {
        loadPendingInvitations();
        // No need to poll access requests - WebSocket handles real-time updates
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [user]);
  
  // Function to load total access requests across all albums
  const loadTotalAccessRequests = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      // Fetch all albums
      const albumsResponse = await axios.get(`${BACKEND_URL}/api/albums?user_id=${user.id}`);
      const albumsList = albumsResponse.data.albums || [];
      
      let totalCount = 0;
      for (const album of albumsList) {
        const isOwner = album.owner_id === user.id;
        const isEditor = album.collaborators?.some(c => c.user_id === user.id && c.role === 'editor');
        
        if (isOwner || isEditor) {
          try {
            const shareResponse = await axios.get(`${BACKEND_URL}/api/albums/${album.id}/share-link?user_id=${user.id}`);
            if (shareResponse.data.share_link) {
              totalCount += shareResponse.data.share_link.pending_requests || 0;
            }
          } catch (err) {
            // Ignore errors for individual albums
          }
        }
      }
      
      console.log(`📊 Total pending access requests across all albums: ${totalCount}`);
      setTotalPendingAccessRequests(totalCount);
    } catch (error) {
      console.error('Error loading total access requests:', error);
    }
  }, [user?.id, BACKEND_URL]);

  // Load last viewed folder from localStorage on mount
  useEffect(() => {
    if (folders.length > 0 && selectedFolder === null) {
      const savedFolderId = localStorage.getItem('lastViewedFolder');
      if (savedFolderId && savedFolderId !== 'view-all') {
        const folder = folders.find(f => f.id === savedFolderId);
        if (folder) {
          setSelectedFolder(folder);
        }
        // If folder was deleted, stay in View All mode (null)
      }
      // If no saved folder or savedFolderId is 'view-all', stay in View All mode (null)
    }
  }, [folders]);

  // Save selected folder to localStorage when it changes
  useEffect(() => {
    if (selectedFolder && selectedFolder.id) {
      localStorage.setItem('lastViewedFolder', selectedFolder.id);
    } else if (selectedFolder === null) {
      localStorage.setItem('lastViewedFolder', 'view-all');
    }
  }, [selectedFolder]);

  // Cancel long-press timer when scrolling on mobile (applies to all views)
  useEffect(() => {
    const handleScroll = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
        setTouchStartPos(null);
      }
    };

    // Listen for scroll on window and document (covers all scrollable areas)
    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    document.addEventListener('scroll', handleScroll, { passive: true, capture: true });
    
    // Also listen on specific gallery if it exists
    const galleryElement = pictureGalleryRef.current;
    if (galleryElement) {
      galleryElement.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
      if (galleryElement) {
        galleryElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [longPressTimer]);

  // Grid size helper functions
  const getGridClasses = (size, type) => {
    if (type === 'pictures') {
      switch (size) {
        case 'tiny':
          return 'grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10';
        case 'small':
          return 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8';
        case 'medium':
          return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6';
        case 'large':
          return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';
        default:
          return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6';
      }
    } else { // videos
      switch (size) {
        case 'tiny':
          return 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-6';
        case 'small':
          return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5';
        case 'medium':
          return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3';
        case 'large':
          return 'grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2';
        default:
          return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3';
      }
    }
  };

  // Sorting functions for mobile-friendly gallery sorting
  const sortImages = (images, sortConfig) => {
    return [...images].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];
      
      // Handle different field types
      if (sortConfig.field === 'created_at') {
        // Convert to milliseconds for proper numeric comparison
        const aTime = new Date(aValue || 0).getTime();
        const bTime = new Date(bValue || 0).getTime();
        const aValid = !isNaN(aTime) ? aTime : 0;
        const bValid = !isNaN(bTime) ? bTime : 0;
        
        if (sortConfig.direction === 'asc') {
          return aValid - bValid; // Oldest first
        } else {
          return bValid - aValid; // Newest first
        }
      } else if (sortConfig.field === 'name') {
        aValue = (a.name || a.original_name || 'Untitled').toLowerCase();
        bValue = (b.name || b.original_name || 'Untitled').toLowerCase();
        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      } else if (sortConfig.field === 'size') {
        aValue = a.size || 0;
        bValue = b.size || 0;
        if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }
      
      // Default fallback
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const sortVideos = (videos, sortConfig) => {
    return [...videos].sort((a, b) => {
      let aValue = a[sortConfig.field];
      let bValue = b[sortConfig.field];
      
      // Handle different field types
      if (sortConfig.field === 'created_at' || sortConfig.field === 'completed_at') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (sortConfig.field === 'prompt') {
        aValue = (a.prompt || 'No prompt').toLowerCase();
        bValue = (b.prompt || 'No prompt').toLowerCase();
      } else if (sortConfig.field === 'status') {
        // Custom status ordering: failed, processing, completed
        const statusOrder = { 'failed': 0, 'processing': 1, 'completed': 2 };
        aValue = statusOrder[a.status] || 1;
        bValue = statusOrder[b.status] || 1;
      } else if (sortConfig.field === 'duration') {
        aValue = a.parameters?.duration || 5;
        bValue = b.parameters?.duration || 5;
      }
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Memoized sorted pictures - recalculates when unassignedImages, pictureSort, or selectedFolder changes
  const sortedPictures = React.useMemo(() => {
    console.log('📸 Sorting pictures with:', pictureSort, 'Folder:', selectedFolder?.name || 'View All');
    
    // Filter by folder
    let filtered;
    if (!selectedFolder) {
      // View All mode - show ALL images regardless of folder_id
      filtered = unassignedImages.filter(img => !img.is_deleted);
      console.log('🌍 View All mode - showing all images:', filtered.length);
    } else {
      // Show images in the selected folder only
      filtered = unassignedImages.filter(img => !img.is_deleted && img.folder_id === selectedFolder.id);
      console.log(`📁 Folder mode - showing images in folder ${selectedFolder.name}:`, filtered.length);
    }
    
    // Filter out demo content if user wants to hide it
    if (hideDemoContent) {
      filtered = filtered.filter(img => !img.url?.includes('/landing-assets/'));
    }
    
    console.log('📊 Filtering complete:', filtered.length, 'images');
    const sorted = sortImages(filtered, pictureSort);
    console.log('✅ Pictures sorted. First 3:');
    sorted.slice(0, 3).forEach((img, idx) => {
      console.log(`  ${idx + 1}. ${img.filename || img.name} - folder_id: ${img.folder_id || 'none'}`);
    });
    return sorted;
  }, [unassignedImages, pictureSort, selectedFolder, hideDemoContent]);

  // Helper function to get actual video duration
  const getVideoDuration = (videoUrl, jobId) => {
    if (videoDurations[jobId]) {
      return videoDurations[jobId];
    }
    
    if (videoUrl && isValidVideoUrl(videoUrl)) {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.onloadedmetadata = () => {
        const duration = Math.round(video.duration);
        setVideoDurations(prev => ({ ...prev, [jobId]: duration }));
      };
    }
    
    return null; // Return null while loading
  };

  const loadDashboardData = async () => {
    console.log('🚀🚀🚀 loadDashboardData called');
    try {
      // PHASE 3 CACHE: Check cache first for pictures (using ref)
      try {
        const cache = (typeof cacheRef !== 'undefined' && cacheRef && cacheRef.current)
          ? cacheRef.current
          : (typeof window !== 'undefined' ? window.__MM_MEDIA_CACHE__ : null);
        if (cache && cache.getCachedPictures) {
          // Verbose cache logs removed for production
          const cachedPictures = cache.getCachedPictures();
          
          if (cachedPictures && Array.isArray(cachedPictures) && cachedPictures.length > 0) {
            // Cache hit - instant load
            setUnassignedImages(cachedPictures);
            // Still load in background to refresh
          } else {
            console.log('❌ Cache MISS or empty for pictures');
          }
        } else {
          console.log('⚠️ Cache not available yet');
        }
      } catch (cacheError) {
        console.error('❌ Cache error for pictures:', cacheError);
      }
      
      // Get the effective user ID (impersonated or actual)
      const effectiveUserId = user?.id;
      
      // Get the authenticated user ID (original admin if impersonating)
      const originalAdmin = localStorage.getItem('originalAdmin');
      const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : effectiveUserId;
      
      // Add cache-busting timestamp to force fresh data load
      const timestamp = Date.now();
      
      const [projectsRes, statsRes, unassignedRes, foldersRes] = await Promise.all([
        axios.get(`${getAPI()}/projects?include_archived=${showArchived}&user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&_=${timestamp}`),
        axios.get(`${getAPI()}/dashboard/stats?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&_=${timestamp}`),
        axios.get(`${getAPI()}/unassigned-images?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&_=${timestamp}`),
        axios.get(`${getAPI()}/folders?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&_=${timestamp}`)
      ]);
      
      setProjects(projectsRes.data.projects);
      setStats(statsRes.data.stats);
      
      const userImages = unassignedRes.data.unassigned_images || [];
      
      // Apply cache-busting with priority:
      // 1. Check for recent save timestamp (edit or revert)
      // 2. Fall back to revert timestamp (legacy)
      // 3. Fall back to updated_at from backend
      const processedImages = userImages.map(img => {
        // Priority 1: Check for save timestamp (most recent action)
        const saveTimestamp = localStorage.getItem(`image_save_timestamp_${img.id}`);
        if (saveTimestamp) {
          console.log(`🔄 Using save timestamp for image ${img.id}: ${saveTimestamp}`);
          const cacheBust = `&_save=${saveTimestamp}`;
          
          const cachedBustedImg = { ...img };
          
          if (cachedBustedImg.url) {
            cachedBustedImg.url = cachedBustedImg.url.includes('?')
              ? `${cachedBustedImg.url}${cacheBust}`
              : `${cachedBustedImg.url}?_save=${saveTimestamp}`;
          }
          if (cachedBustedImg.thumbnail_url) {
            cachedBustedImg.thumbnail_url = cachedBustedImg.thumbnail_url.includes('?')
              ? `${cachedBustedImg.thumbnail_url}${cacheBust}`
              : `${cachedBustedImg.thumbnail_url}?_save=${saveTimestamp}`;
          }
          if (cachedBustedImg.r2_url) {
            cachedBustedImg.r2_url = cachedBustedImg.r2_url.includes('?')
              ? `${cachedBustedImg.r2_url}${cacheBust}`
              : `${cachedBustedImg.r2_url}?_save=${saveTimestamp}`;
          }
          
          // Remove the timestamp after using it (one-time use)
          localStorage.removeItem(`image_save_timestamp_${img.id}`);
          console.log(`✅ Cache-busting applied with save timestamp and cleared for image ${img.id}`);
          
          return cachedBustedImg;
        }
        
        // Priority 2: Check for legacy revert timestamp (deprecated but keep for compatibility)
        const revertTimestamp = localStorage.getItem(`revert_timestamp_${img.id}`);
        if (revertTimestamp) {
          console.log(`🔄 Applying cache-bust to reverted image ${img.id} with timestamp ${revertTimestamp}`);
          const cacheBust = `&_t=${revertTimestamp}`;
          
          // Create a new image object with cache-busted URLs
          const cachedBustedImg = { ...img };
          
          if (cachedBustedImg.url) {
            cachedBustedImg.url = cachedBustedImg.url.includes('?')
              ? `${cachedBustedImg.url}${cacheBust}`
              : `${cachedBustedImg.url}?_t=${revertTimestamp}`;
          }
          if (cachedBustedImg.thumbnail_url) {
            cachedBustedImg.thumbnail_url = cachedBustedImg.thumbnail_url.includes('?')
              ? `${cachedBustedImg.thumbnail_url}${cacheBust}`
              : `${cachedBustedImg.thumbnail_url}?_t=${revertTimestamp}`;
          }
          if (cachedBustedImg.r2_url) {
            cachedBustedImg.r2_url = cachedBustedImg.r2_url.includes('?')
              ? `${cachedBustedImg.r2_url}${cacheBust}`
              : `${cachedBustedImg.r2_url}?_t=${revertTimestamp}`;
          }
          
          // CRITICAL: Remove timestamp after applying it once to prevent perpetual cache-busting
          localStorage.removeItem(`revert_timestamp_${img.id}`);
          console.log(`✅ Cache-busting applied to loaded image data and timestamp cleared for image ${img.id}`);
          
          return cachedBustedImg;
        }
        return img;
      });
      
      // Backend now handles URL construction with proper auth parameters
      // No need to sanitize here - just use what backend provides
      // Add timestamp to all URLs to ensure fresh images after edits
      const finalProcessedImages = processedImages.map(img => ({
        ...img,
        // Add a cache-bust timestamp to image URLs if they were recently modified
        // This ensures browser fetches fresh versions after edits
        url: img.url && img.updated_at ? (
          img.url.includes('?') 
            ? `${img.url}&_refresh=${new Date(img.updated_at).getTime()}`
            : `${img.url}?_refresh=${new Date(img.updated_at).getTime()}`
        ) : img.url,
        thumbnail_url: img.thumbnail_url && img.updated_at ? (
          img.thumbnail_url.includes('?')
            ? `${img.thumbnail_url}&_refresh=${new Date(img.updated_at).getTime()}`
            : `${img.thumbnail_url}?_refresh=${new Date(img.updated_at).getTime()}`
        ) : img.thumbnail_url,
        r2_url: img.r2_url && img.updated_at ? (
          img.r2_url.includes('?')
            ? `${img.r2_url}&_refresh=${new Date(img.updated_at).getTime()}`
            : `${img.r2_url}?_refresh=${new Date(img.updated_at).getTime()}`
        ) : img.r2_url
      }));
      
      // Auto-enable demo mode for new users (0 pictures)
      if (finalProcessedImages.length === 0 && localStorage.getItem('demoMode') === null) {
        setDemoMode(true);
        localStorage.setItem('demoMode', 'true');
        console.log('🎭 Auto-enabled demo mode for new user');
      }
      
      // Auto-disable demo mode once user has real content (from populate_demo_content)
      if (finalProcessedImages.length > 0 && localStorage.getItem('demoMode') === 'true') {
        setDemoMode(false);
        localStorage.setItem('demoMode', 'false');
        console.log('🎭 Auto-disabled demo mode - user has real content now');
      }
      
      // Merge demo data if demo mode is enabled
      const finalImages = demoMode ? [...DEMO_PICTURES, ...finalProcessedImages] : finalProcessedImages;
      setUnassignedImages(finalImages);
      
      // PHASE 3 CACHE: Store pictures in cache for instant future loads (using ref)
      try {
        const cache = (typeof cacheRef !== 'undefined' && cacheRef && cacheRef.current)
          ? cacheRef.current
          : (typeof window !== 'undefined' ? window.__MM_MEDIA_CACHE__ : null);
        if (cache && cache.setCachedPictures) {
          console.log(`💾 Caching ${finalImages.length} pictures for instant future loads`);
          cache.setCachedPictures(finalImages);
          console.log('✅ Pictures cached successfully');
        }
      } catch (cacheError) {
        console.error('❌ Error caching pictures:', cacheError);
      }
      
      setFolders(foldersRes.data.folders || []);
      
      // Load storage info for free users
      loadStorageInfo();
    } catch (error) {
      // Handle 429 rate limiting gracefully
      if (error.response?.status === 429) {
        console.warn('⚠️ Rate limited while loading dashboard data - will retry on next interval');
        return; // Return early, keep existing state
      }
      toast.error('Failed to load your memories');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStorageInfo = async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${getAPI()}/storage-info?user_id=${user.id}`);
      setStorageInfo(response.data);
    } catch (error) {
      console.error('Failed to load storage info:', error);
      // Don't show error toast, this is not critical
    }
  };


  const loadAIProviders = async () => {
    try {
      const response = await axios.get(`${getAPI()}/ai-providers?user_id=${user.id}`);
      setAiProviders(response.data.providers);
    } catch (error) {
      console.error('Failed to load AI providers');
    }
  };

  const loadVideoJobs = async () => {
    console.log('🚀🚀🚀 loadVideoJobs called');
    try {
      // PHASE 3 CACHE: Check cache first for videos (using ref)
      try {
        const cache = (typeof cacheRef !== 'undefined' && cacheRef && cacheRef.current)
          ? cacheRef.current
          : (typeof window !== 'undefined' ? window.__MM_MEDIA_CACHE__ : null);
        if (cache && cache.getCachedVideos) {
          // Verbose cache logs removed for production
          const cachedVideos = cache.getCachedVideos();
          
          if (cachedVideos && Array.isArray(cachedVideos) && cachedVideos.length > 0) {
            // Cache hit - instant load
            setVideoJobs(cachedVideos);
            // Still load in background to refresh
          } else {
            console.log('❌ Cache MISS or empty for videos');
          }
        } else {
          console.log('⚠️ Cache not available yet');
        }
      } catch (cacheError) {
        console.error('❌ Cache error for videos:', cacheError);
      }
      
      // Get the effective user ID (impersonated or actual)
      const effectiveUserId = user?.id;
      
      // Get the authenticated user ID (original admin if impersonating)
      const originalAdmin = localStorage.getItem('originalAdmin');
      const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : effectiveUserId;
      
      // Add cache-busting timestamp to force fresh data load
      const timestamp = Date.now();
      
      // Get all video generation jobs from the new API endpoint
      const response = await axios.get(`${getAPI()}/video-generations?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&_=${timestamp}`);
      const userJobs = response.data.map(job => {
        // Backend already provides imageUrl and thumbnail_url with correct parameters
        // Only construct imageUrl as fallback if not provided by backend
        const imageUrl = job.imageUrl || (job.projectId 
          ? projects.find(p => p.id === job.projectId)?.images?.find(img => img.id === job.image_id)?.thumbnail_url || `/api/files/${job.image_id}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`
          : `/api/files/${job.image_id}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`);
        
        // Check if this is a demo content video (has static file URL)
        const isDemoContent = job.video_url?.includes('/landing-assets/') || job.result_url?.includes('/landing-assets/');
        
        // Normalize video URL - use static path for demo content, or getVideoUrl for real videos
        const normalizedVideoUrl = isDemoContent 
          ? (job.result_url || job.video_url)  // Preserve static path for demo content
          : getVideoUrl(job, effectiveUserId);  // Use streaming API for real videos
        
        return {
          ...job,
          imageUrl: imageUrl,
          thumbnail_url: job.thumbnail_url || imageUrl,  // Use backend's thumbnail_url or fallback to imageUrl
          result_url: normalizedVideoUrl,  // Override with proper URL
          video_url: normalizedVideoUrl   // Also set video_url for compatibility
        };
      });
      
      // Auto-disable demo mode once user has real videos (from populate_demo_content)
      if (userJobs.length > 0 && localStorage.getItem('demoMode') === 'true') {
        setDemoMode(false);
        localStorage.setItem('demoMode', 'false');
        console.log('🎭 Auto-disabled demo mode - user has real videos now');
      }
      
      // Merge demo videos if demo mode is enabled
      const finalJobs = demoMode ? [...DEMO_VIDEOS, ...userJobs] : userJobs;
      setVideoJobs(finalJobs);
      
      // PHASE 3 CACHE: Store videos in cache for instant future loads (using ref)
      try {
        const cache = (typeof cacheRef !== 'undefined' && cacheRef && cacheRef.current)
          ? cacheRef.current
          : (typeof window !== 'undefined' ? window.__MM_MEDIA_CACHE__ : null);
        if (cache && cache.setCachedVideos) {
          console.log(`💾 Caching ${finalJobs.length} videos for instant future loads`);
          cache.setCachedVideos(finalJobs);
          console.log('✅ Videos cached successfully');
        }
      } catch (cacheError) {
        console.error('❌ Error caching videos:', cacheError);
      }
      
      // Cache videoJobs in localStorage for digital frame preview
      localStorage.setItem(`videoJobs_${effectiveUserId}`, JSON.stringify(finalJobs));
    } catch (error) {
      // Handle 429 rate limiting gracefully
      if (error.response?.status === 429) {
        console.warn('⚠️ Rate limited while loading video jobs - will retry on next interval');
        return; // Return early, keep existing videoJobs state
      }
      
      console.error('Error loading video jobs:', error);
      // Fallback to old method if new API fails
      try {
        const allJobs = projects.flatMap(project => 
          project.video_generations?.filter(job => !job.is_deleted).map(job => ({
            ...job,
            projectId: project.id,
            projectName: project.name,
            imageUrl: project.images?.find(img => img.id === job.image_id)?.thumbnail_url
          })) || []
        );
        setVideoJobs(allJobs);
      } catch (fallbackError) {
        console.error('Error in fallback video jobs loading:', fallbackError);
      }
    }
  };
  
  const loadPendingInvitations = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/albums/invitations?user_id=${user.id}`);
      const count = response.data.invitations?.length || 0;
      setPendingInvitationsCount(count);
      // Only log if there are actual pending invitations
      if (count > 0) {
        console.log(`📧 User has ${count} pending album invitations`);
      }
    } catch (error) {
      console.error('Error loading pending invitations:', error);
      setPendingInvitationsCount(0);
    }
  }, [user?.id, BACKEND_URL]);
  
  const loadPendingAccessRequests = useCallback((increment = true) => {
    console.log(`📨 loadPendingAccessRequests called with increment=${increment}`);
    setTotalPendingAccessRequests(prev => {
      const newValue = increment ? prev + 1 : Math.max(0, prev - 1);
      console.log(`📨 Updating totalPendingAccessRequests from ${prev} to ${newValue}`);
      return newValue;
    });
  }, []);
  
  // Global WebSocket connections for ALL user's albums
  // FIXED: Added exponential backoff to prevent infinite reconnection loop
  // FIXED: Added connection tracking to prevent duplicate connections from React Strict Mode
  const wsConnectionsRef = React.useRef(new Map()); // Track connections by album ID
  const wsSetupCompletedRef = React.useRef(false);
  
  React.useEffect(() => {
    if (!user?.id) return;
    
    // Prevent duplicate setup from React Strict Mode
    if (wsSetupCompletedRef.current) {
      console.log('🔌 WebSocket setup already completed, skipping duplicate');
      return;
    }
    wsSetupCompletedRef.current = true;
    
    console.log('🌐 Setting up global WebSocket connections for all user albums');
    
    const wsConnections = [];
    const reconnectAttempts = {}; // Track reconnection attempts per album
    const reconnectTimeouts = {}; // Track reconnection timeouts
    const MAX_RECONNECT_ATTEMPTS = 5;
    const BASE_RECONNECT_DELAY = 2000; // Start with 2 seconds
    
    // Function to connect to an album's WebSocket with exponential backoff
    const connectToAlbum = (albumId, attemptNumber = 0) => {
      // Prevent duplicate connections
      if (wsConnectionsRef.current.has(albumId)) {
        console.log(`🔌 WebSocket already connected for album ${albumId}, skipping`);
        return;
      }
      
      // Prevent infinite reconnection loop
      if (attemptNumber >= MAX_RECONNECT_ATTEMPTS) {
        console.warn(`⚠️ Max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached for album ${albumId}`);
        return;
      }
      
      // Convert BACKEND_URL to string explicitly
      const backendUrl = getBACKEND_URL();
      const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
      let wsHost = backendUrl.replace('http://', '').replace('https://', '');
      const wsUrl = `${wsProtocol}://${wsHost}/api/ws/albums/${albumId}`;
      
      if (attemptNumber === 0) {
        console.log('🔌 Connecting to WebSocket for album:', albumId);
      } else {
        console.log(`🔄 Reconnection attempt ${attemptNumber + 1}/${MAX_RECONNECT_ATTEMPTS} for album:`, albumId);
      }
      
      try {
        const ws = new WebSocket(wsUrl);
        let connectionEstablished = false;
        
        ws.onopen = () => {
          connectionEstablished = true;
          reconnectAttempts[albumId] = 0; // Reset counter on successful connection
          wsConnectionsRef.current.set(albumId, ws); // Track this connection
          console.log('✅ WebSocket connected for album:', albumId);
        };
        
        ws.onmessage = (event) => {
          const message = JSON.parse(event.data);
          console.log('📨 WebSocket message for album', albumId, ':', message);
          
          if (message.type === 'access_request_created') {
            console.log('🆕 New access request via WebSocket');
            loadTotalAccessRequests();
          } else if (message.type === 'access_request_approved' || message.type === 'access_request_denied') {
            console.log('✅ Access request resolved via WebSocket');
            loadTotalAccessRequests();
          } else if (message.type === 'album_update' && message.action === 'video_processing') {
            console.log('🎬 New video processing started via WebSocket - refreshing album');
            console.log('📊 WebSocket video_processing data:', { video_id: message.video_id, image_id: message.image_id });
            
            // Reload video jobs to show the new processing video
            if (typeof loadVideoJobs === 'function') {
              loadVideoJobs();
            }
            
            // CRITICAL: Call albumRefreshCallback if it exists, regardless of albumContext
            // albumContext can be stale/null even when viewing an album
            // If AlbumsTab registered a callback, it means we're viewing the album - call it!
            // PASS video_id and image_id so callback can IMMEDIATELY create placeholder without API call
            if (albumRefreshCallback.current && typeof albumRefreshCallback.current === 'function') {
              console.log('🔄 Calling albumRefreshCallback for video_processing:', message.video_id, 'image:', message.image_id);
              albumRefreshCallback.current(message.video_id, message.image_id, 'processing', null);
            } else {
              console.log('⚠️ No albumRefreshCallback registered - not viewing album or AlbumsTab not mounted');
            }
          } else if (message.type === 'video_completed') {
            console.log('🎬 Video completed via WebSocket:', message.video_id);
            console.log('📦 Full video asset data received:', message.video_asset ? 'YES' : 'NO');
            console.log('🔍 DEBUG message.video_asset structure:', message.video_asset ? {
              id: message.video_asset.id,
              asset_type: message.video_asset.asset_type,
              image_id: message.video_asset.image_id,
              metadata: message.video_asset.metadata
            } : 'NULL');
            console.log('🔍 DEBUG message.image_id from WebSocket:', message.image_id);
            
            // SILENT REFRESH: Update video jobs without full page reload
            if (typeof loadVideoJobs === 'function') {
              loadVideoJobs();
            }
            
            // CRITICAL: Call albumRefreshCallback with the FULL video asset data
            // This allows instant UI update without any API calls
            if (albumRefreshCallback.current && typeof albumRefreshCallback.current === 'function') {
              console.log('🔄 Calling albumRefreshCallback for video completion:', message.video_id);
              console.log('🔍 Parameters being passed:', {
                videoId: message.video_id,
                imageId: message.image_id,
                eventType: 'completed',
                videoAssetData: message.video_asset ? 'EXISTS' : 'NULL'
              });
              // Pass the complete video asset data from WebSocket for instant update
              albumRefreshCallback.current(
                message.video_id, 
                message.image_id, 
                'completed',  // Signal this is a completion, not new processing
                message.video_asset  // Full video asset data
              );
            } else {
              console.log('⚠️ albumRefreshCallback not available for video completion');
            }
          } else if (message.type === 'album_renamed') {
            console.log('📝 Album renamed via WebSocket:', message.new_title);
            // Trigger album refresh to show new name
            setAlbumRefreshTrigger(prev => prev + 1);
          }
        };
        
        ws.onerror = (error) => {
          console.error('❌ WebSocket error for album', albumId);
        };
        
        ws.onclose = () => {
          console.log('🔌 WebSocket disconnected from album:', albumId);
          
          // Remove from connection tracking
          wsConnectionsRef.current.delete(albumId);
          
          // Only attempt reconnection if connection was established successfully before
          // This prevents reconnection loops for connections that never succeed
          if (connectionEstablished) {
            const nextAttempt = (reconnectAttempts[albumId] || 0) + 1;
            reconnectAttempts[albumId] = nextAttempt;
            
            if (nextAttempt < MAX_RECONNECT_ATTEMPTS) {
              // Exponential backoff: 2s, 4s, 8s, 16s, 32s
              const delay = BASE_RECONNECT_DELAY * Math.pow(2, nextAttempt - 1);
              console.log(`⏳ Reconnecting in ${delay/1000}s...`);
              
              reconnectTimeouts[albumId] = setTimeout(() => {
                connectToAlbum(albumId, nextAttempt);
              }, delay);
            }
          }
        };
        
        wsConnections.push(ws);
      } catch (error) {
        console.error('❌ Failed to connect WebSocket for album', albumId, ':', error);
      }
    };
    
    // Fetch user's albums and connect to each
    const setupConnections = async () => {
      try {
        const backendUrl = getBACKEND_URL();
        const response = await axios.get(`${backendUrl}/api/albums?user_id=${user.id}`);
        const albums = response.data.albums || [];
        
        console.log(`📚 Found ${albums.length} albums, setting up WebSocket connections...`);
        
        // Connect to WebSocket for each album where user is owner or editor
        albums.forEach(album => {
          const isOwner = album.owner_id === user.id;
          const isEditor = album.collaborators?.some(c => c.user_id === user.id && c.role === 'editor');
          
          if (isOwner || isEditor) {
            connectToAlbum(album.id);
          }
        });
      } catch (error) {
        console.error('Error fetching albums for WebSocket setup:', error);
      }
    };
    
    setupConnections();
    
    // Cleanup: close all WebSocket connections and clear reconnection timeouts on unmount
    return () => {
      console.log('🧹 Closing all WebSocket connections and clearing reconnection timeouts');
      wsConnections.forEach(ws => ws.close());
      
      // Clear connection tracking
      wsConnectionsRef.current.clear();
      wsSetupCompletedRef.current = false;
      
      // Clear all pending reconnection timeouts
      Object.values(reconnectTimeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [user?.id, BACKEND_URL]);
  
  const loadMyOrders = async () => {
    if (!user?.id) return;
    
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${getAPI()}/my-orders?user_id=${user.id}`);
      setMyOrders(response.data);
      console.log(`📦 Loaded ${response.data.total_orders} orders`);
      console.log(`   Shopify orders: ${response.data.shopify_orders.length}`);
      console.log(`   Mail-in orders: ${response.data.mail_in_orders.length}`);
      console.log('   Mail-in orders data:', response.data.mail_in_orders);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  };
  
  const loadGroupedVideoJobs = async () => {
    try {
      const response = await axios.get(`${getAPI()}/video-generations/grouped?user_id=${user.id}`);
      if (response.data.success) {
        setVideoGroups(response.data.groups);
        console.log(`📊 Loaded ${response.data.total_groups} groups with ${response.data.total_videos} total videos`);
      }
    } catch (error) {
      console.error('Failed to load grouped video jobs:', error);
      // Fallback to regular view if grouping fails
      setGroupedView(false);
    }
  };

  // Load which images have associated videos - EFFICIENT VERSION
  // Instead of checking every image, we use videoJobs data to mark images with videos
  const loadImageVideoAssociations = async () => {
    try {
      const imagesWithVideosSet = new Set();
      const imageVideoCache = {};
      
      // Add demo pictures with videos if demo mode is ON
      if (demoMode) {
        DEMO_PICTURES.forEach(pic => {
          const demoVids = DEMO_VIDEOS.filter(v => v.image_id === pic.id);
          if (demoVids.length > 0) {
            imagesWithVideosSet.add(pic.id);
            imageVideoCache[pic.id] = demoVids;
            console.log(`🎭 Demo image ${pic.id} has ${demoVids.length} video(s)`);
          }
        });
      }
      
      // Use videoJobs data to efficiently mark images with videos
      // This avoids making hundreds of API calls
      videoJobs.forEach(video => {
        if (video.image_id || video.source_image_id) {
          const imageId = video.image_id || video.source_image_id;
          imagesWithVideosSet.add(imageId);
          
          if (!imageVideoCache[imageId]) {
            imageVideoCache[imageId] = [];
          }
          imageVideoCache[imageId].push(video);
        }
      });
      
      setImagesWithVideos(imagesWithVideosSet);
      setImageVideoData(imageVideoCache);
      console.log(`🎯 Efficiently marked ${imagesWithVideosSet.size} images with associated videos (from videoJobs)`);
      
    } catch (error) {
      console.error('Failed to load image-video associations:', error);
    }
  };

  // Delete picture function
  const handleDeletePicture = async (projectId, imageId) => {
    try {
      await axios.delete(`${getAPI()}/projects/${projectId}/images/${imageId}?user_id=${user.id}`);
      // Removed toast: Picture deleted successfully
      await loadDashboardData(); // Wait for dashboard to refresh
      await loadVideoJobs(); // Also refresh video jobs to update UI
    } catch (error) {
      toast.error('Failed to delete picture');
    }
  };

  // Delete video function  
  const handleDeleteVideo = async (videoId) => {
    try {
      setIsLoading(true);
      await axios.delete(`${getAPI()}/video-generations/${videoId}?user_id=${user.id}`);
      
      // Close fullscreen modal if this video is currently displayed
      if (fullScreenVideo && fullScreenVideo.id === videoId) {
        setFullScreenVideo(null);
      }
      
      // Immediately update local state to remove the video (optimistic update)
      setVideoJobs(prevJobs => prevJobs.filter(job => job.id !== videoId));
      
      // Removed toast: Memory deleted successfully
      
      // Reload to ensure consistency with backend
      await loadVideoJobs();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete memory. Please try again.');
      // Reload to revert optimistic update on error
      await loadVideoJobs();
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetryGeneration = async (job) => {
    try {
      setIsLoading(true);
      setShowFailedDetails(null);
      
      // Find the original image
      const imageId = job.image_id;
      console.log('🔍 Searching for image with ID:', imageId);
      console.log('📊 Available unassigned images:', unassignedImages.length);
      console.log('📊 Available projects:', projects.length);
      
      let foundImage = null;
      
      // Search in unassigned images (check both id and string conversion)
      foundImage = unassignedImages.find(img => 
        img.id === imageId || String(img.id) === String(imageId)
      );
      
      if (foundImage) {
        console.log('✅ Found image in unassigned images');
      }
      
      // If not found, search in projects
      if (!foundImage) {
        for (const project of projects) {
          if (project.images && project.images.length > 0) {
            foundImage = project.images.find(img => 
              img.id === imageId || String(img.id) === String(imageId)
            );
            if (foundImage) {
              console.log(`✅ Found image in project: ${project.name}`);
              foundImage = {
                ...foundImage,
                projectName: project.name,
                projectId: project.id
              };
              break;
            }
          }
        }
      } else {
        foundImage = {
          ...foundImage,
          projectName: 'Unassigned Pictures',
          projectId: 'unassigned'
        };
      }
      
      // If still not found, try to use the job's thumbnail/image data directly
      if (!foundImage && (job.imageUrl || job.thumbnail_url)) {
        console.log('⚠️ Image not found in database, creating from job data');
        foundImage = {
          id: imageId,
          url: convertExpiredR2Url(job.imageUrl || job.thumbnail_url, imageId),
          thumbnail_url: convertExpiredR2Url(job.thumbnail_url, imageId),
          name: 'Retry Image',
          projectName: 'Unassigned Pictures',
          projectId: 'unassigned'
        };
      }
      
      if (foundImage) {
        // Convert any expired R2 URLs before use
        foundImage = {
          ...foundImage,
          url: convertExpiredR2Url(foundImage.url, foundImage.id),
          thumbnail_url: convertExpiredR2Url(foundImage.thumbnail_url, foundImage.id),
          r2_url: convertExpiredR2Url(foundImage.r2_url, foundImage.id)
        };
        
        // Add the enhanced prompt from the failed job
        foundImage.enhancedPrompt = job.parameters?.prompt || '';
        
        console.log('🎬 Opening Animation Generator with image:', foundImage.id);
        
        // Open Animation Generator with pre-selected image
        setSelectedImageForAnimation(foundImage);
        setShowCreateLivingMemory(true);
        toast.info('Opening Animation Generator with original image');
      } else {
        console.error('❌ Could not find image. Job:', job);
        toast.error('Could not find original image for re-animation');
      }
    } catch (error) {
      console.error('Error retrying generation:', error);
      toast.error('Failed to open retry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete selected animated moments function
  const handleDeleteSelectedAnimatedMemories = async () => {
    if (selectedAnimatedMemories.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedAnimatedMemories.length} selected animated moments? This cannot be undone.`)) {
      return;
    }

    const results = await Promise.allSettled(
      selectedAnimatedMemories.map(async (memoryId) => {
        console.log(`Deleting animated moment: ${memoryId}`);
        return await axios.delete(`${getAPI()}/video-generations/${memoryId}?user_id=${user.id}`);
      })
    );

    let successCount = 0;
    let errorCount = 0;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successCount++;
        console.log(`✅ Successfully deleted memory ${index + 1}/${selectedAnimatedMemories.length}`);
      } else {
        errorCount++;
        console.error(`❌ Failed to delete memory ${index + 1}/${selectedAnimatedMemories.length}:`, result.reason);
      }
    });

    // Removed toast: Successfully deleted X animated moments
    if (errorCount > 0) {
      toast.error(`Failed to delete ${errorCount} animated moments`);
    }

    // Reset selection and refresh data
    setSelectedAnimatedMemories([]);
    setAnimatedMemoryMultiSelectMode(false);
    await loadDashboardData();
    await loadVideoJobs();
  };

  // Delete selected pictures function
  // Batch delete handler for multi-select mode
  const handleBatchDelete = async () => {
    console.log('handleBatchDelete called, selectedPictures:', selectedPictures);
    const selectedImageIds = selectedPictures.map(id => id.replace('unassigned-', ''));
    
    if (selectedImageIds.length === 0) {
      toast.error('No images selected');
      return;
    }
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete ${selectedImageIds.length} picture${selectedImageIds.length > 1 ? 's' : ''}?`)) {
      console.log('Delete cancelled by user');
      return;
    }
    
    // Delete all selected images
    try {
      console.log(`Deleting ${selectedImageIds.length} images...`);
      await Promise.all(selectedImageIds.map(imageId => 
        axios.delete(`${getAPI()}/unassigned-images/${imageId}?user_id=${user.id}`)
      ));
      
      toast.success(`${selectedImageIds.length} picture${selectedImageIds.length > 1 ? 's' : ''} deleted successfully`);
      
      // Exit multi-select mode and refresh data
      setMultiSelectMode(false);
      setSelectedPictures([]);
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to delete pictures:', error);
      toast.error('Failed to delete some pictures');
    }
  };

  const handleBatchCopyToFolder = async (folderId, folderName) => {
    console.log('handleBatchCopyToFolder called, selectedPictures:', selectedPictures);
    const selectedImageIds = selectedPictures.map(id => id.replace('unassigned-', ''));
    
    if (selectedImageIds.length === 0) {
      toast.error('No images selected');
      return;
    }
    
    try {
      console.log(`Copying ${selectedImageIds.length} images to folder ${folderId}...`);
      const formData = new FormData();
      selectedImageIds.forEach(id => formData.append('image_ids', id));
      formData.append('user_id', user.id);
      
      await axios.post(`${getAPI()}/folders/${folderId}/images`, formData);
      
      toast.success(`${selectedImageIds.length} picture${selectedImageIds.length > 1 ? 's' : ''} copied to ${folderName}`);
      
      // Close modal, exit multi-select mode, and refresh data
      setShowBatchCopyToFolderModal(false);
      setMultiSelectMode(false);
      setSelectedPictures([]);
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to copy pictures to folder:', error);
      toast.error('Failed to copy pictures to folder');
    }
  };

  const handleDeleteSelectedPictures = async () => {
    if (selectedPictures.length === 0) return;
    
    // Check if we're in a folder view
    const isInFolder = selectedFolder && selectedFolder.id;
    const confirmMessage = isInFolder
      ? `Remove ${selectedPictures.length} selected picture${selectedPictures.length > 1 ? 's' : ''} from "${selectedFolder.name}"? The pictures will remain in your Pictures tab.`
      : `Are you sure you want to delete ${selectedPictures.length} selected picture${selectedPictures.length > 1 ? 's' : ''}? This action cannot be undone.`;
    
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      // Show progress modal
      setBatchDeleteProgress({ 
        show: true, 
        current: 0, 
        total: selectedPictures.length, 
        message: isInFolder ? 'Removing from folder...' : 'Deleting pictures...'
      });
      
      // Optimistic update - immediately remove from UI
      const pictureIdsToDelete = selectedPictures.slice();
      
      if (isInFolder) {
        // Remove from folder only (set folder_id to null)
        setUnassignedImages(prev => prev.map(img => 
          pictureIdsToDelete.includes(`unassigned-${img.id}`) ? { ...img, folder_id: null } : img
        ));
      } else {
        // Delete entirely
        // Update unassigned images
        setUnassignedImages(prev => prev.filter(img => !pictureIdsToDelete.includes(`unassigned-${img.id}`)));
        
        // Update projects
        setProjects(prev => prev.map(project => ({
          ...project,
          images: project.images?.filter(img => !pictureIdsToDelete.includes(`${project.id}-${img.id}`)) || []
        })));
      }
      
      // Clear selection immediately
      setSelectedPictures([]);
      setMultiSelectMode(false);
      
      // Process each selected picture
      const actionPromises = pictureIdsToDelete.map(async (pictureId, index) => {
        console.log(`Processing picture ${index + 1}/${pictureIdsToDelete.length}: ${pictureId}`);
        
        // Update progress
        setBatchDeleteProgress(prev => ({ 
          ...prev, 
          current: index + 1,
          message: isInFolder 
            ? `Removing ${index + 1} of ${pictureIdsToDelete.length}...`
            : `Deleting ${index + 1} of ${pictureIdsToDelete.length}...`
        }));
        
        try {
          let response;
          if (pictureId.startsWith('unassigned-')) {
            const imageId = pictureId.replace('unassigned-', '');
            
            if (isInFolder) {
              // Remove from folder only
              response = await axios.patch(`${getAPI()}/unassigned-images/${imageId}?user_id=${user.id}`, {
                folder_id: null
              });
              console.log(`✅ Successfully removed picture ${index + 1} from folder: ${pictureId}`);
            } else {
              // Delete unassigned image entirely
              response = await axios.delete(`${getAPI()}/unassigned-images/${imageId}?user_id=${user.id}`);
              console.log(`✅ Successfully deleted picture ${index + 1}: ${pictureId}`);
            }
          } else {
            // Delete project image (these are never in folders)
            const parts = pictureId.split('-');
            const projectId = parts[0];
            const imageId = parts.slice(1).join('-');
            response = await axios.delete(`${getAPI()}/projects/${projectId}/images/${imageId}?user_id=${user.id}`);
            console.log(`✅ Successfully deleted project picture ${index + 1}: ${pictureId}`);
          }
          
          return response;
        } catch (err) {
          console.error(`❌ Failed to process picture ${index + 1}: ${pictureId}`, err.response?.data || err.message);
          throw new Error(`Failed to process picture ${index + 1}: ${err.response?.data?.detail || err.message}`);
        }
      });
      
      const results = await Promise.allSettled(actionPromises);
      
      // Check results
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      // Hide progress modal
      setBatchDeleteProgress({ show: false, current: 0, total: 0, message: '' });
      
      if (failed === 0) {
        toast.success(isInFolder 
          ? `${successful} picture${successful > 1 ? 's' : ''} removed from ${selectedFolder.name}`
          : `${successful} picture${successful > 1 ? 's' : ''} deleted successfully`
        );
      } else if (successful > 0) {
        toast.warning(`${successful} processed, ${failed} failed`);
      } else {
        toast.error(`All ${failed} operations failed`);
      }
      
      // Wait a bit for backend consistency
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh to ensure consistency
      await loadDashboardData();
      await loadVideoJobs();
    } catch (error) {
      console.error('Bulk operation error:', error);
      
      // Hide progress modal
      setBatchDeleteProgress({ show: false, current: 0, total: 0, message: '' });
      
      toast.error(isInFolder ? 'Failed to remove from folder' : 'Failed to delete selected pictures');
      // Reload to revert optimistic updates
      await loadDashboardData();
      await loadVideoJobs();
    }
  };

  // Delete selected living memories function
  const handleDeleteSelectedLivingMemories = async () => {
    if (selectedLivingMemories.length === 0) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete ${selectedLivingMemories.length} selected living memories? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      // Delete each selected living moment
      const deletePromises = selectedLivingMemories.map(videoId => 
        axios.delete(`${getAPI()}/video-generations/${videoId}?user_id=${user.id}`)
      );
      
      await Promise.all(deletePromises);
      toast.success(`${selectedLivingMemories.length} living memories deleted successfully`);
      setSelectedLivingMemories([]); // Clear selection
      await loadDashboardData();
      await loadVideoJobs();
    } catch (error) {
      toast.error('Failed to delete selected living memories');
    }
  };

  // Archive picture function removed as requested

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-6">
            <Logo className="w-24 h-24 mx-auto" animated={true} />
          </div>
          <p className="text-amber-800 font-medium text-lg">Loading your precious memories...</p>
        </div>
      </div>
    );
  }

  // Mobile-friendly sort modal component
  const SortModal = ({ isOpen, onClose, sortConfig, setSortConfig, type }) => {
    if (!isOpen) return null;

    const sortOptions = type === 'pictures' ? [
      { field: 'created_at', label: 'Date Added', icon: '📅' }
    ] : [
      { field: 'created_at', label: 'Date Created', icon: '📅' }
    ];

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end justify-center p-4">
        <div className="bg-white rounded-t-3xl w-full max-w-lg shadow-2xl animate-slide-up">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900">
              Sort {type === 'pictures' ? 'Pictures' : 'Videos'}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Sort Options */}
          <div className="p-4">
            {sortOptions.map((option) => (
              <div key={option.field} className="mb-3">
                <button
                  onClick={() => setSortConfig({ field: option.field, direction: 'desc' })}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                    sortConfig.field === option.field && sortConfig.direction === 'desc'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{option.icon}</span>
                    <span className="font-medium text-gray-900">{option.label}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{t("animation.newest_first")}</span>
                    {sortConfig.field === option.field && sortConfig.direction === 'desc' && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    )}
                  </div>
                </button>

                <button
                  onClick={() => setSortConfig({ field: option.field, direction: 'asc' })}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all mt-2 ${
                    sortConfig.field === option.field && sortConfig.direction === 'asc'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{option.icon}</span>
                    <span className="font-medium text-gray-900">{option.label}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">
                      {option.field === 'created_at' || option.field === 'completed_at' ? 'Oldest First' : 'A to Z'}
                    </span>
                    {sortConfig.field === option.field && sortConfig.direction === 'asc' && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    )}
                  </div>
                </button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={onClose}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
      <>
        {/* Hide navbar when fullscreen modals, animation generator, magic processing, credits modals, or import modal are open */}
        {!fullScreenImage && !fullScreenVideo && !showCreateLivingMemory && !showMagicProcessing && !showCredits && !showEarnCredits && !showImportModal && (
          <MemoryNavbar 
            key={navbarKey}
            user={user} 
            onLogout={onLogout}
            onProfileClick={onProfileClick}
            onCreditsClick={onCreditsClick}
            showCreditsDropdown={showCreditsDropdown}
            setShowCreditsDropdown={setShowCreditsDropdown}
            setShowSupport={setShowSupport}
            setShowOrdersDashboard={setShowOrdersDashboard}
            setShowAdminPanel={setShowAdminPanel}
            setShowReferralDashboard={setShowReferralDashboard}
            currentView={currentView}
            setCurrentView={setCurrentView}
            projects={projects}
            setProjectSubtab={setProjectSubtab}
            setShowArchived={setShowArchived}
            setShowPendingSubtab={setShowPendingSubtab}
            setShowFailedSubtab={setShowFailedSubtab}
            picturesContentRef={picturesContentRef}
            animatedMomentsContentRef={animatedMomentsContentRef}
            pendingInvitationsCount={pendingInvitationsCount}
            totalPendingAccessRequests={totalPendingAccessRequests}
          />
        )}
        
        {/* Admin Impersonation Banner */}
        {originalAdminUser && (
          <div className="bg-red-600 text-white py-2 px-4 flex items-center justify-between sticky top-0 z-50">
            <div className="flex items-center gap-2">
              <span className="text-lg">🔐</span>
              <span className="text-sm font-medium">
                Admin Mode: Viewing as {user.name || user.email}
              </span>
            </div>
            <button
              onClick={async () => {
                // Restore original admin user
                const adminUser = originalAdminUser;
                
                // Update localStorage
                localStorage.setItem('user', JSON.stringify(adminUser));
                localStorage.setItem('effectiveUserId', adminUser.id);
                localStorage.setItem('authenticatedUserId', adminUser.id);
                localStorage.removeItem('originalAdminUser');
                
                // Update React state
                setUser(adminUser);
                setOriginalAdminUser(null);
                
                // Reload dashboard data for admin
                await loadDashboardData();
                
                toast.success('Switched back to admin account');
              }}
              className="bg-white text-red-600 px-3 py-1 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
            >
              Switch Back to Admin
            </button>
          </div>
        )}
        
        <div className={currentView === 'shop' || currentView === 'projects' ? 'bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 relative' : 'min-h-screen bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 relative'}>
        
        {/* Gradient overlay for smooth transition from header */}
        <div className="bg-gradient-to-b from-purple-600/10 via-purple-300/5 to-transparent h-32 absolute top-0 left-0 right-0 pointer-events-none z-0"></div>
        
        <div className={currentView === 'projects' ? 'relative z-10' : 'relative z-10'}>
      {/* Hero Section - Show only on Shop view */}
      {currentView === 'shop' && (
        <>
      <div className="relative bg-gradient-to-r from-purple-100 via-orange-100 to-blue-50 text-gray-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1597524678053-5e6fef52d8a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjBsYXVnaGluZ3xlbnwwfHx8fDE3NTkyODQ3OTZ8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative px-4 sm:px-6 py-8 lg:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Welcome Content */}
              <div>
                <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                  <span className="text-orange-700 text-sm font-medium">{t('landing.hero_badge')}</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
                  {(() => {
                    // Check if user is new (created within last 5 minutes)
                    if (user?.created_at) {
                      const createdAt = new Date(user.created_at);
                      const now = new Date();
                      const minutesSinceCreation = (now - createdAt) / 1000 / 60;
                      
                      if (minutesSinceCreation < 5) {
                        return <>Welcome, <span className="text-indigo-600">{user?.name}!</span></>;
                      }
                    }
                    return <>{t('landing.welcome_back')} <span className="text-indigo-600">{user?.name}!</span></>;
                  })()}
                </h2>
                <p className="text-xl text-gray-700 mb-6 leading-relaxed flex items-start gap-2">
                  <svg className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {/* Clock circle */}
                    <circle cx="12" cy="12" r="8" strokeWidth="2"/>
                    {/* Clock hands */}
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 7v5l3 3"/>
                    {/* Counter-clockwise arrow wrapping around */}
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4.5 7.5C5.83 5.67 7.81 4.3 10.08 3.88M10.08 3.88L8 2M10.08 3.88L8.5 6"/>
                  </svg>
                  <span>
                    {t('landing.hero_description')}
                  </span>
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => setShowCreateLivingMemory(true)}
                    className="text-white font-bold px-6 sm:px-8 py-4 rounded-2xl transition-all text-lg flex items-center gap-2 justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      animation: 'pulseGlow 2.5s ease-in-out infinite'
                    }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    {t('landing.create_moment')}
                  </button>
                </div>
                
                <div className="mt-6 flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span>{t('landing.features.digital_frames')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <span>{t('landing.features.collaboration')}</span>
                  </div>
                </div>
              </div>
              
              {/* Stats Grid - Redesigned with 2 Tiles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Combined Stats Tile - Clean & Professional */}
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-200">
                  {/* Main Stats Display */}
                  <div className="space-y-4">
                    {/* Credits Available - Primary Focus */}
                    <div className="text-center pb-4 border-b border-gray-100">
                      <div className="text-sm text-gray-500 mb-2">{t('dashboard.available_credits')}</div>
                      <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
                        {(stats.credits_remaining || 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-400">
                        ≈ {Math.floor((stats.credits_remaining || 0) / 10)} {t('dashboard.animations_left')}
                      </div>
                    </div>
                    
                    {/* Secondary Stats - Clean Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{stats.completed_videos || 0}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {hasRealVideos ? t('dashboard.moments_created') : t('dashboard.demo_moments')}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{(stats.total_earned_credits || 0).toFixed(2)}</div>
                        <div className="text-xs text-gray-500 mt-1">{t('dashboard.credits_earned')}</div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="pt-4 space-y-2">
                      <button
                        onClick={() => setShowBuyCredits(true)}
                        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-xl font-semibold hover:opacity-90 transition-all shadow-md hover:shadow-lg"
                      >
                        {t('dashboard.get_more_credits')}
                      </button>
                      <button
                        onClick={() => startTransition(() => setShowEarnCredits(true))}
                        className="w-full bg-white text-purple-600 py-2.5 rounded-xl font-medium border-2 border-purple-200 hover:bg-purple-50 transition-all"
                      >
                        {t('dashboard.earn_free_credits')}
                      </button>
                    </div>
                    
                    {/* Subtle Footer Info - Referrals & Sharing Stats */}
                    {/* Only show if user has real videos or referrals */}
                    {(hasRealVideos || (stats.referral_signups > 0)) && (
                      <div className="text-center pt-3 border-t border-gray-100">
                        <div className="text-xs text-gray-400 space-y-1">
                          {/* Referral Count */}
                          {stats.referral_signups > 0 && (
                            <div className="flex items-center justify-center gap-1.5">
                              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              <span>{stats.referral_signups} friend{stats.referral_signups !== 1 ? 's' : ''} referred</span>
                            </div>
                          )}
                          {/* Share Stats */}
                          {hasRealVideos && stats.social_shares > 0 && (
                            <div className="flex items-center justify-center gap-1.5">
                              <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                              </svg>
                              <span>{stats.social_shares} {stats.social_shares !== 1 ? t('dashboard.moments_shared') : t('dashboard.moment_shared')}</span>
                            </div>
                          )}
                          {hasRealVideos && stats.share_views > 0 && (
                            <div className="flex items-center justify-center gap-1.5">
                              <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span>{stats.share_views.toLocaleString()} {t('dashboard.views_from_shares')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Preview in Digital Frame - Professional Redesign with Mini Preview */}
                {(() => {
                  // Get all completed videos with either result_url or video_url
                  const completedVideos = videoJobs.filter(job => 
                    (job.status === 'completed' || job.status === 'success') && 
                    (job.result_url || job.video_url) && 
                    !job.is_deleted
                  ).map(job => ({
                    ...job,
                    video_url: job.result_url || job.video_url,
                    result_url: job.result_url || job.video_url
                  }));
                  
                  // Use user videos if available, otherwise use demo videos
                  const videosToShow = completedVideos.length > 0 
                    ? completedVideos.slice(0, 4) // Show first 4 user videos
                    : FRAME_PREVIEW_VIDEOS; // Fallback to demo videos
                  
                  console.log('🖼️ Frame Preview Button - Videos:', {
                    totalCompleted: completedVideos.length,
                    showingUserVideos: completedVideos.length > 0,
                    previewCount: videosToShow.length
                  });
                  
                  return (
                    <button
                      onClick={() => {
                        console.log('🖱️ Perfect Gift button clicked!');
                        console.log('  setShowFrameDemo exists:', !!setShowFrameDemo);
                        console.log('  setPreSelectedFrameColor exists:', !!setPreSelectedFrameColor);
                        console.log('  setPreSelectedFrameProduct exists:', !!setPreSelectedFrameProduct);
                        
                        try {
                          if (setPreSelectedFrameColor) {
                            setPreSelectedFrameColor(null); // Clear any pre-selection when opening from dashboard
                          }
                          if (setPreSelectedFrameProduct) {
                            setPreSelectedFrameProduct(null); // Clear any pre-selected product
                          }
                          setShowFrameDemo(true);
                          console.log('✅ setShowFrameDemo(true) called successfully');
                        } catch (error) {
                          console.error('❌ Error in Perfect Gift button onClick:', error);
                        }
                      }}
                      className="relative z-30 group bg-gradient-to-br from-purple-600 via-purple-500 to-blue-600 rounded-2xl p-6 border-2 border-purple-300 shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 overflow-hidden touch-manipulation"
                    >
                      {/* Animated Background Pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMiIgZmlsbD0id2hpdGUiLz48L3N2Zz4=')] animate-pulse"></div>
                      </div>
                      
                      {/* Content */}
                      <div className="relative">
                        {/* Mini Frame Preview with Videos */}
                        <div className="flex items-center justify-center mb-4">
                          <MiniFramePreview 
                            videos={videosToShow} 
                            isDemo={completedVideos.length === 0}
                            totalUserVideos={completedVideos.length}
                          />
                        </div>
                        
                        {/* Title */}
                        <div className="text-2xl sm:text-3xl font-bold text-white text-center mb-3 group-hover:scale-105 transition-transform duration-300">
                          {t('dashboard.perfect_gift')}
                        </div>
                        
                        {/* Subtitle */}
                        <div className="text-center">
                          <span className="text-white/80 text-sm group-hover:text-white transition-colors font-medium">
                            {t('dashboard.perfect_gift_desc')}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      {/* Main Content - Mobile Optimized - Hide for Shop and Collaborate views */}
      {currentView !== 'shop' && currentView !== 'projects' && (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* Main Tab Navigation */}
          {/* Primary Navigation - Animated Moments and Pictures */}
          <div className="content-wrapper">
            {(currentView === 'living_memories' || currentView === 'pictures') && (
          <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="flex flex-col space-y-3 w-full">
              {/* View All Pictures button - centered */}
              {currentView === 'pictures' && !multiSelectMode && (
                <div className="flex justify-center relative">
                  <button
                    onClick={() => setShowFolderNav(!showFolderNav)}
                    className="flex items-center bg-white border border-gray-300 rounded-xl px-6 sm:px-8 py-3 sm:py-4 hover:bg-gray-50 transition-colors text-sm sm:text-base font-medium text-gray-700 shadow-lg"
                  >
                    {selectedFolder && (
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                    )}
                    {!selectedFolder && (
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                    <span>
                      {selectedFolder ? selectedFolder.name : 'Folders (All Pictures)'}
                    </span>
                    <svg className={`w-4 h-4 transition-transform ml-2 ${showFolderNav ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showFolderNav && (
                    <>
                      <div 
                        className="fixed inset-0 z-[45]" 
                        onClick={() => setShowFolderNav(false)}
                      />
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-purple-200 rounded-lg shadow-xl z-[46] min-w-[250px] max-h-[400px] overflow-y-auto">
                        <div
                          onClick={() => {
                            setShowCreateFolderModal(true);
                            setShowFolderNav(false);
                          }}
                          className="p-3 border-b border-purple-100 cursor-pointer flex items-center gap-3 hover:bg-purple-50"
                        >
                          <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="text-sm font-semibold text-purple-800">Create New Folder</span>
                        </div>
                        
                        <div
                          onClick={() => {
                            setSelectedFolder(null);
                            setShowFolderNav(false);
                          }}
                          className={`p-3 cursor-pointer flex items-center gap-3 ${!selectedFolder ? 'bg-blue-50' : 'hover:bg-purple-50'}`}
                        >
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span className="text-sm font-medium">Folders (All Pictures)</span>
                        </div>
                        
                        {folders.map((folder) => (
                          <div
                            key={folder.id}
                            className={`p-3 flex items-center justify-between group ${selectedFolder?.id === folder.id ? 'bg-purple-50' : 'hover:bg-purple-50'}`}
                          >
                            <div
                              onClick={() => {
                                setSelectedFolder(folder);
                                setShowFolderNav(false);
                              }}
                              className="flex items-center gap-3 flex-1 cursor-pointer"
                            >
                              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              </svg>
                              <span className="text-sm font-medium">{folder.name}</span>
                            </div>
                            
                            {/* Three-dot menu */}
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenFolderMenu(openFolderMenu === folder.id ? null : folder.id);
                                }}
                                className="p-2 hover:bg-purple-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                                  <circle cx="8" cy="2" r="1.5"/>
                                  <circle cx="8" cy="8" r="1.5"/>
                                  <circle cx="8" cy="14" r="1.5"/>
                                </svg>
                              </button>
                              
                              {/* Dropdown menu */}
                              {openFolderMenu === folder.id && (
                                <>
                                  {/* Backdrop */}
                                  <div 
                                    className="fixed inset-0 z-[48]" 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenFolderMenu(null);
                                    }}
                                  />
                                  
                                  {/* Menu content */}
                                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-[49] min-w-[140px]">
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setOpenFolderMenu(null);
                                        setShowFolderNav(false);
                                        setRenamingFolder(folder);
                                        setNewFolderName(folder.name);
                                        setShowRenameFolderModal(true);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-2"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                      <span>Rename</span>
                                    </button>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!window.confirm(`Delete "${folder.name}"? Images will be removed from this folder but not deleted from your account.`)) {
                                          return;
                                        }
                                        
                                        try {
                                          const BACKEND_URL = getBACKEND_URL();
                                          await axios.delete(`${BACKEND_URL}/api/folders/${folder.id}?user_id=${user.id}`);
                                          toast.success('Folder deleted');
                                          if (selectedFolder?.id === folder.id) {
                                            setSelectedFolder(null); // Return to View All
                                          }
                                          setOpenFolderMenu(null);
                                          setShowFolderNav(false);
                                          await loadDashboardData();
                                        } catch (error) {
                                          console.error('Failed to delete folder:', error);
                                          toast.error('Failed to delete folder');
                                        }
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2 border-t border-gray-100"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                      <span>Delete</span>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Hide Demo Content Toggle - Only show if user has demo content AND hasn't created real videos yet */}
              {currentView === 'pictures' && !multiSelectMode && !hasRealVideos && unassignedImages.some(img => img.url?.includes('/landing-assets/')) && (
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      setHideDemoContent(!hideDemoContent);
                      toast.success(hideDemoContent ? 'Demo content shown' : 'Demo content hidden');
                    }}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors px-4 py-2 rounded-lg hover:bg-purple-50"
                  >
                    {hideDemoContent ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>Show Demo Content</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        <span>Hide Demo Content</span>
                      </>
                    )}
                  </button>
                </div>
              )}
                
              {/* Subtabs positioned directly below main tabs */}
              <div className="pl-4">
                {/* Subtabs for Animated Moments */}
                {currentView === 'living_memories' && (
                  <div className="flex space-x-1">
                    {stats.processing_videos > 0 && (
                        <button
                          onClick={() => {
                            setShowPendingVideos(!showPendingVideos);
                          }}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                            showPendingVideos
                              ? 'bg-amber-200 text-amber-800'
                              : 'text-amber-600 hover:text-amber-800 hover:bg-amber-100'
                          }`}
                        >
                          ⏳ Pending ({stats.processing_videos || 0})
                        </button>
                      )}
                      {videoJobs.filter(job => job.status === 'failed' && !job.is_deleted).length > 0 && (
                        <button
                          onClick={() => {
                            setShowFailedVideos(!showFailedVideos);
                          }}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                            showFailedVideos
                              ? 'bg-red-200 text-red-800'
                              : 'text-red-600 hover:text-red-800 hover:bg-red-100'
                          }`}
                        >
                          ❌ Failed ({videoJobs.filter(job => job.status === 'failed' && !job.is_deleted).length})
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Subtabs for Projects */}
                  {currentView === 'projects' && (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => {
                          setProjectSubtab('my');
                          setShowArchived(false);
                        }}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                          projectSubtab === 'my' && !showArchived
                            ? 'bg-amber-200 text-amber-800'
                            : 'text-amber-600 hover:text-amber-800 hover:bg-amber-100'
                        }`}
                      >
                        📁 My Projects ({projects.filter(p => !p.is_archived).length})
                      </button>
                      <button
                        onClick={() => setProjectSubtab('shared')}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                          projectSubtab === 'shared'
                            ? 'bg-blue-200 text-blue-800'
                            : 'text-blue-600 hover:text-blue-800 hover:bg-blue-100'
                        }`}
                      >
                        🌐 Shared Projects (0)
                      </button>
                      <button
                        onClick={() => setProjectSubtab('collaborations')}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                          projectSubtab === 'collaborations'
                            ? 'bg-green-200 text-green-800'
                            : 'text-green-600 hover:text-green-800 hover:bg-green-100'
                        }`}
                      >
                        👥 Collaborations (0)
                      </button>
                      <button
                        onClick={() => {
                          setProjectSubtab('archived');
                          setShowArchived(true);
                        }}
                        className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                          showArchived
                            ? 'bg-gray-200 text-gray-800'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                        }`}
                      >
                        <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archived ({stats.archived_projects || 0})
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Context-aware Action Button */}
              <div className="flex space-x-3 mt-4 sm:mt-0">
                {currentView === 'projects' ? (
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowCollaboration(true)}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-3 sm:px-4 py-2 rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 transition-all text-xs sm:text-sm"
                    >
                      👥 Collaborate
                    </button>
                    <button
                      onClick={() => setShowNewProject(true)}
                      className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-4 sm:px-6 py-3 rounded-2xl font-semibold hover:from-purple-600 hover:to-blue-600 transition-all transform hover:scale-105 text-sm sm:text-base flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create New Project
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            )}

        {/* All Pictures View */}
        {currentView === 'pictures' && (
              <div ref={picturesContentRef} className="relative bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 p-4">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 relative z-10">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-lg sm:text-xl font-bold text-purple-800">Your Pictures</h3>
                    
                    {!multiSelectMode && (
                      <>
                        {/* Sort Dropdown - Next to Title */}
                        <div className="relative z-20">
                          <select
                            value={`${pictureSort.field}-${pictureSort.direction}`}
                            onChange={(e) => {
                              console.log('🔄 PICTURES SORT DROPDOWN CHANGED to:', e.target.value);
                              const [field, direction] = e.target.value.split('-');
                              console.log('🔄 Setting pictureSort state to:', { field, direction });
                              setPictureSort({ field, direction });
                              console.log('🔄 State update triggered');
                            }}
                            className="bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-xs text-gray-700"
                          >
                            <option value="created_at-desc">▼ Newest First</option>
                            <option value="created_at-asc">▲ Oldest First</option>
                          </select>
                        </div>
                        
                        {/* Grid Size Controls - Match Albums Style */}
                        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => setPictureGridSize('tiny')}
                            className={`p-1.5 rounded transition-colors ${
                              pictureGridSize === 'tiny' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                            }`}
                            title="Tiny"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 12 12">
                              <rect x="1" y="1" width="10" height="10" />
                              <line x1="1" y1="4.33" x2="11" y2="4.33" />
                              <line x1="1" y1="7.66" x2="11" y2="7.66" />
                              <line x1="4.33" y1="1" x2="4.33" y2="11" />
                              <line x1="7.66" y1="1" x2="7.66" y2="11" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setPictureGridSize('small')}
                            className={`p-1.5 rounded transition-colors ${
                              pictureGridSize === 'small' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                            }`}
                            title="Small"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="7" height="7" rx="1"/>
                              <rect x="13" y="3" width="7" height="7" rx="1"/>
                              <rect x="3" y="13" width="7" height="7" rx="1"/>
                              <rect x="13" y="13" width="7" height="7" rx="1"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => setPictureGridSize('medium')}
                            className={`p-1.5 rounded transition-colors ${
                              pictureGridSize === 'medium' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                            }`}
                            title="Medium"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="8" height="8" rx="1"/>
                              <rect x="14" y="3" width="8" height="8" rx="1"/>
                              <rect x="3" y="14" width="8" height="8" rx="1"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => setPictureGridSize('large')}
                            className={`p-1.5 rounded transition-colors ${
                              pictureGridSize === 'large' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                            }`}
                            title="Large"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="18" height="8" rx="1"/>
                              <rect x="3" y="13" width="18" height="8" rx="1"/>
                            </svg>
                          </button>
                        </div>
                        
                        {/* Select Multiple Button - Matches Album Style */}
                        <button
                          onClick={() => {
                            setMultiSelectMode(true);
                            setSelectedPictures([]);
                          }}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span className="text-sm font-medium">Select</span>
                        </button>
                      </>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    {!multiSelectMode ? (
                      <>
                        {/* Animate Pictures button */}
                        <button
                          onClick={() => {
                            setMultiSelectMode(true);
                            setSelectedPictures([]);
                          }}
                          className="text-white px-4 py-2 rounded-xl transition-all text-sm sm:text-base shadow-lg hover:shadow-xl relative overflow-hidden font-medium flex items-center gap-2"
                          style={{
                            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #a855f7 75%, #ec4899 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 8s linear infinite'
                          }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          Animate Pictures
                        </button>
                        
                        {/* Import from Albums button */}
                        <button
                          onClick={() => {
                            setShowImportModal(true);
                            setImportModalType('pictures');
                          }}
                          className="bg-gray-500 text-white px-4 py-2 rounded-xl transition-all text-sm sm:text-base shadow-lg hover:shadow-xl hover:bg-gray-600 font-medium flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                          </svg>
                          Import from Albums
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Select All / Deselect All - Always visible in selection mode */}
                        {selectedPictures.length === 0 ? (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Select all unassigned images
                              const allPictureIds = unassignedImages.map(img => `unassigned-${img.id}`);
                              setSelectedPictures(allPictureIds);
                              toast.info(`${allPictureIds.length} pictures selected`);
                            }}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('✅ Select All touched');
                              
                              const allPictureIds = unassignedImages.map(img => `unassigned-${img.id}`);
                              setSelectedPictures(allPictureIds);
                              toast.info(`${allPictureIds.length} pictures selected`);
                            }}
                            className="bg-blue-500 text-white px-3 py-2 rounded-xl hover:bg-blue-600 transition-colors touch-manipulation font-bold whitespace-nowrap"
                            style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto', touchAction: 'none' }}
                          >
                            ✅ Select All
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              setSelectedPictures([]); // Deselect all
                              toast.info('All pictures deselected');
                            }}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('✅ Deselect All touched');
                              
                              setSelectedPictures([]); // Deselect all
                              toast.info('All pictures deselected');
                            }}
                            className="bg-blue-500 text-white px-3 py-2 rounded-xl hover:bg-blue-600 transition-colors touch-manipulation font-bold whitespace-nowrap"
                            style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto', touchAction: 'none' }}
                          >
                            ✅ Deselect All
                          </button>
                        )}
                        {console.log('🔍 RENDER CHECK: selectedPictures.length =', selectedPictures.length, 'Pictures:', selectedPictures) || true}
                        {selectedPictures.length > 0 && (
                          <>
                            {console.log('🎯 DELETE BUTTON SHOULD BE RENDERING NOW') || null}
                            <button
                              onClick={async () => {
                                console.log('🎬 Batch Animation button clicked!');
                                // Get selected image objects
                                const selectedImages = unassignedImages.filter(img => 
                                  selectedPictures.includes(`unassigned-${img.id}`)
                                );
                                
                                console.log('Selected images:', selectedImages.length);
                                
                                if (selectedImages.length === 0) {
                                  toast.error('No images selected');
                                  return;
                                }
                                
                                // If only 1 image selected, redirect to single animation generator
                                if (selectedImages.length === 1) {
                                  console.log('🎬 Single image selected, redirecting to single animation generator');
                                  const singleImage = selectedImages[0];
                                  
                                  // Exit multi-select mode
                                  setMultiSelectMode(false);
                                  
                                  // Open the living memory modal with this image
                                  setSelectedImageForAnimation(singleImage);
                                  setShowCreateLivingMemory(true);
                                  return;
                                }
                                
                                // Exit multi-select mode
                                setMultiSelectMode(false);
                                
                                // Use the centralized openBatchAnimation function
                                openBatchAnimation(selectedImages);
                              }}
                              className="text-white px-4 py-2 rounded-xl transition-all shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
                              style={{
                                background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #a855f7 75%, #ec4899 100%)',
                                backgroundSize: '200% 100%',
                                animation: 'shimmer 8s linear infinite'
                              }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                              </svg>
                              Animate Selected ({selectedPictures.length})
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('📁 COPY TO FOLDER BUTTON CLICKED!');
                                setShowBatchCopyToFolderModal(true);
                              }}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('📁 COPY TO FOLDER BUTTON TOUCHED');
                                setShowBatchCopyToFolderModal(true);
                              }}
                              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2.5 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl touch-manipulation font-semibold whitespace-nowrap flex items-center gap-2"
                              style={{ 
                                WebkitTapHighlightColor: 'transparent', 
                                pointerEvents: 'auto', 
                                minHeight: '44px', 
                                minWidth: '44px',
                                touchAction: 'none'
                              }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                              </svg>
                              Copy ({selectedPictures.length})
                            </button>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('⬇️ DOWNLOAD SELECTED BUTTON CLICKED');
                                
                                // Get selected image objects
                                const selectedImages = unassignedImages.filter(img => 
                                  selectedPictures.includes(`unassigned-${img.id}`)
                                );
                                
                                if (selectedImages.length === 0) {
                                  toast.error('No images selected');
                                  return;
                                }
                                
                                // Detect if mobile
                                const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                                
                                // If 2+ items and desktop, use zip download
                                if (selectedImages.length >= 2 && !isMobile) {
                                  try {
                                    const BACKEND_URL = getBACKEND_URL();
                                    
                                    // Show progress modal
                                    setShowDownloadProgress(true);
                                    setDownloadProgress(10);
                                    setDownloadStatus('Preparing download...');
                                    
                                    // Prepare items for zip download
                                    const items = selectedImages.map(img => {
                                      let downloadUrl = img.r2_url || img.url;
                                      
                                      // Add authentication parameters
                                      if (downloadUrl && user?.id) {
                                        const separator = downloadUrl.includes('?') ? '&' : '?';
                                        downloadUrl = `${downloadUrl}${separator}user_id=${user.id}&authenticated_user_id=${user.id}`;
                                        
                                        // Auto-prefix with backend URL if it's a relative path
                                        if (!downloadUrl.startsWith('http')) {
                                          downloadUrl = `${BACKEND_URL}${downloadUrl}`;
                                        }
                                      }
                                      
                                      return {
                                        url: downloadUrl,
                                        filename: img.filename || `image-${img.id}.jpg`,
                                        type: 'image'
                                      };
                                    });
                                    
                                    setDownloadProgress(20);
                                    setDownloadStatus('Creating zip archive...');
                                    
                                    // Simulate realistic progress during zip creation with file names
                                    let simulatedProgress = 20;
                                    const progressInterval = setInterval(() => {
                                      if (simulatedProgress < 55) {
                                        simulatedProgress += Math.random() * 3 + 1; // Increment 1-4%
                                        const fileIndex = Math.floor((simulatedProgress - 20) / 35 * items.length);
                                        const currentFile = items[fileIndex]?.filename || `file-${fileIndex + 1}`;
                                        setDownloadProgress(Math.min(simulatedProgress, 55));
                                        setDownloadStatus(`Adding to archive: ${currentFile}`);
                                      }
                                    }, 300); // Update every 300ms
                                    
                                    // Call zip endpoint
                                    const response = await fetch(`${BACKEND_URL}/api/media/download-zip?user_id=${user.id}`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ items })
                                    });
                                    
                                    // Stop simulated progress
                                    clearInterval(progressInterval);
                                    
                                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                                    
                                    setDownloadProgress(60);
                                    setDownloadStatus('Processing files...');
                                    
                                    const blob = await response.blob();
                                    
                                    setDownloadProgress(95);
                                    setDownloadStatus('Starting download...');
                                    
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = `magic-moments-${new Date().toISOString().slice(0,19).replace(/:/g, '-')}.zip`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                                    
                                    setDownloadProgress(100);
                                    setDownloadStatus('Download complete!');
                                    
                                    // Close modal after 2 seconds
                                    setTimeout(() => {
                                      setShowDownloadProgress(false);
                                      setDownloadProgress(0);
                                    }, 2000);
                                    
                                  } catch (error) {
                                    console.error('Failed to create zip:', error);
                                    setShowDownloadProgress(false);
                                    setDownloadProgress(0);
                                    toast.error('Failed to create zip file. Trying individual downloads...');
                                    
                                    // Fallback to individual downloads
                                    for (let i = 0; i < selectedImages.length; i++) {
                                      const img = selectedImages[i];
                                      try {
                                        const BACKEND_URL = getBACKEND_URL();
                                        let downloadUrl = img.r2_url || img.url;
                                        
                                        if (downloadUrl && user?.id) {
                                          const separator = downloadUrl.includes('?') ? '&' : '?';
                                          downloadUrl = `${downloadUrl}${separator}user_id=${user.id}&authenticated_user_id=${user.id}`;
                                          if (!downloadUrl.startsWith('http')) {
                                            downloadUrl = `${BACKEND_URL}${downloadUrl}`;
                                          }
                                        }
                                        
                                        const response = await fetch(downloadUrl, { mode: 'cors', credentials: 'omit' });
                                        const blob = await response.blob();
                                        const blobUrl = window.URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = blobUrl;
                                        link.download = img.filename || `image-${img.id}.jpg`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                                        await new Promise(resolve => setTimeout(resolve, 300));
                                      } catch (error) {
                                        console.error(`Failed to download ${img.filename}:`, error);
                                      }
                                    }
                                  }
                                } else {
                                  // Single item or mobile - use original individual download logic
                                  toast.info(`Downloading ${selectedImages.length} image${selectedImages.length !== 1 ? 's' : ''}...`);
                                  
                                  for (let i = 0; i < selectedImages.length; i++) {
                                    const img = selectedImages[i];
                                    try {
                                      const BACKEND_URL = getBACKEND_URL();
                                      let downloadUrl = img.r2_url || img.url;
                                      
                                      if (downloadUrl && user?.id) {
                                        const separator = downloadUrl.includes('?') ? '&' : '?';
                                        downloadUrl = `${downloadUrl}${separator}user_id=${user.id}&authenticated_user_id=${user.id}`;
                                        if (!downloadUrl.startsWith('http')) {
                                          downloadUrl = `${BACKEND_URL}${downloadUrl}`;
                                        }
                                      }
                                      
                                      const response = await fetch(downloadUrl, { mode: 'cors', credentials: 'omit' });
                                      const blob = await response.blob();
                                      const blobUrl = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      link.download = img.filename || `image-${img.id}.jpg`;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                                      await new Promise(resolve => setTimeout(resolve, 300));
                                    } catch (error) {
                                      console.error(`Failed to download ${img.filename}:`, error);
                                      toast.error(`Failed to download ${img.filename || 'image'}`);
                                    }
                                  }
                                  
                                  toast.success(`${selectedImages.length} image${selectedImages.length !== 1 ? 's' : ''} downloaded!`);
                                }
                              }}
                              className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2.5 rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg hover:shadow-xl font-semibold whitespace-nowrap flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download ({selectedPictures.length})
                            </button>
                            <button
                              onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('🗑️ DELETE BUTTON TOUCH START - BLOCKING ALL EVENTS');
                              }}
                              onTouchEnd={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('🗑️ DELETE BUTTON TOUCH END - CALLING handleBatchDelete');
                                await handleBatchDelete();
                              }}
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('🗑️ DELETE BUTTON CLICKED!');
                                await handleBatchDelete();
                              }}
                              className="bg-gradient-to-r from-red-500 to-rose-500 text-white px-4 py-2.5 rounded-xl hover:from-red-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-xl touch-manipulation font-semibold whitespace-nowrap flex items-center gap-2"
                              style={{ 
                                WebkitTapHighlightColor: 'transparent', 
                                pointerEvents: 'auto', 
                                minHeight: '44px', 
                                minWidth: '44px',
                                touchAction: 'none'
                              }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              {selectedFolder ? `Remove (${selectedPictures.length})` : `Delete (${selectedPictures.length})`}
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Removed stopImmediatePropagation
                            setMultiSelectMode(false);
                            setSelectedPictures([]);
                            toast.info('Multi-select cancelled');
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Removed stopImmediatePropagation
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Removed stopImmediatePropagation
                            console.log('❌ Cancel button touched');
                            
                            // MOBILE FIX: Handle directly
                            setMultiSelectMode(false);
                            setSelectedPictures([]);
                            toast.info('Multi-select cancelled');
                          }}
                          className="bg-gray-500 text-white px-3 py-2 rounded-xl hover:bg-gray-600 transition-colors touch-manipulation font-bold whitespace-nowrap"
                          style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto', touchAction: 'none' }}
                        >
                          ❌ Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Helpful hint in multi-select mode */}
                {multiSelectMode && (
                  <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 hidden md:flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    <span>Tip: Drag selected images to a folder in the sidebar to organize them</span>
                  </div>
                )}

                {/* Storage limit indicator */}
                {storageInfo && storageInfo.percentage_used > 50 && (
                  <div className={`mt-3 p-3 rounded-lg text-sm ${
                    storageInfo.percentage_used >= 90 
                      ? 'bg-red-50 border border-red-200 text-red-700' 
                      : storageInfo.percentage_used >= 75
                      ? 'bg-orange-50 border border-orange-200 text-orange-700'
                      : 'bg-blue-50 border border-blue-200 text-blue-700'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <div>
                          <span className="font-semibold">
                            {storageInfo.total_pictures} / {storageInfo.limit} pictures
                          </span>
                          <span className="text-xs ml-2 opacity-80">({storageInfo.tier_name})</span>
                        </div>
                      </div>
                      <span className="text-xs">
                        {storageInfo.remaining} remaining
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${
                          storageInfo.percentage_used >= 90 
                            ? 'bg-red-500' 
                            : storageInfo.percentage_used >= 75
                            ? 'bg-orange-500'
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${storageInfo.percentage_used}%` }}
                      />
                    </div>
                    {storageInfo.next_tier ? (
                      <p className="text-xs flex items-center gap-1">
                        {storageInfo.percentage_used >= 90 ? (
                          <>
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Almost full! {storageInfo.next_tier.requirement} to unlock {storageInfo.next_tier.limit} pictures.
                          </>
                        ) : storageInfo.percentage_used >= 75 ? (
                          <>
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            {storageInfo.next_tier.requirement} to unlock {storageInfo.next_tier.name} tier ({storageInfo.next_tier.limit} pictures)
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            {storageInfo.next_tier.requirement} to unlock {storageInfo.next_tier.name} tier!
                          </>
                        )}
                      </p>
                    ) : (
                      <p className="text-xs flex items-center gap-1">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        You're at the maximum tier! 🎉
                      </p>
                    )}
                  </div>
                )}
                
                {/* Drag and drop overlay */}
                {isDraggingFiles && (
                  <div className="fixed inset-0 bg-blue-500/20 border-4 border-dashed border-blue-500 z-50 flex items-center justify-center pointer-events-none">
                    <div className="bg-white rounded-xl p-8 shadow-2xl">
                      <div className="text-6xl mb-4 text-center">📁</div>
                      <div className="text-2xl font-bold text-gray-800">Drop files to upload</div>
                      <div className="text-gray-600 mt-2">
                        {selectedFolder ? `Upload to ${selectedFolder.name}` : 'Upload to Pictures'}
                      </div>
                    </div>
                  </div>
                )}

                <div 
                  ref={pictureGalleryRef}
                  className={`grid ${getGridClasses(pictureGridSize, 'pictures')} gap-4 ${isDraggingFiles ? 'ring-4 ring-blue-500' : ''}`}
                  data-tutorial="demo-pictures-grid"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Check if dragging files (not images from gallery)
                    if (e.dataTransfer.types.includes('Files')) {
                      setIsDraggingFiles(true);
                    }
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Only set to false if leaving the gallery area
                    if (e.currentTarget === e.target) {
                      setIsDraggingFiles(false);
                    }
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDraggingFiles(false);
                    
                    // Handle file drop
                    const files = Array.from(e.dataTransfer.files);
                    if (files.length === 0) return;
                    
                    // Filter for image files only
                    const imageFiles = files.filter(file => file.type.startsWith('image/'));
                    if (imageFiles.length === 0) {
                      toast.error('Please drop only image files');
                      return;
                    }
                    
                    toast.info(`Uploading ${imageFiles.length} image(s)...`);
                    
                    try {
                      const uploadPromises = imageFiles.map(async (file) => {
                        const correctedFile = await fixImageOrientation(file);
                        const formData = new FormData();
                        formData.append('file', correctedFile);
                        formData.append('user_id', user.id); // CRITICAL: Pass user_id for data isolation
                        formData.append('user_id', user.id);
                        const response = await axios.post(`${getAPI()}/upload`, formData);
                        return response.data;
                      });
                      
                      const uploadedImages = await Promise.all(uploadPromises);
                      
                      // If a folder is selected, assign all uploaded images to that folder
                      if (selectedFolder && selectedFolder.id) {
                        const imageIds = uploadedImages.map(result => result.image?.id).filter(Boolean);
                        if (imageIds.length > 0) {
                          const formData = new FormData();
                          imageIds.forEach(id => formData.append('image_ids', id));
                          formData.append('user_id', user.id);
                          formData.append('user_id', user.id);
                          await axios.post(`${getAPI()}/folders/${selectedFolder.id}/images`, formData);
                          toast.success(`${imageFiles.length} image(s) uploaded to ${selectedFolder.name}!`);
                        }
                      } else {
                        toast.success(`${imageFiles.length} image(s) uploaded successfully!`);
                      }
                      
                      loadDashboardData();
                      // Force re-render by updating refresh trigger
                      setRefreshTrigger(prev => prev + 1);
                    } catch (error) {
                      console.error('Upload failed:', error);
                      
                      // Check if it's a storage limit error
                      if (error.response?.status === 403 && error.response?.data?.detail?.includes('Free tier limit')) {
                        const detail = error.response.data.detail;
                        toast.error(
                          <div className="space-y-2">
                            <div className="font-semibold flex items-center gap-2">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Storage Limit Reached!
                            </div>
                            <div className="text-sm">{detail}</div>
                            <div className="text-xs text-gray-300 mt-2">Create your first magic moment to unlock unlimited uploads!</div>
                          </div>,
                          { duration: 8000 }
                        );
                      } else {
                        toast.error('Failed to upload images');
                      }
                    }
                  }}
                >
                  {/* Upload Pictures Box - First Item (hidden when no pictures) */}
                  {sortedPictures.length > 0 && (
                    <label
                      htmlFor="pictures-direct-upload"
                      className="relative aspect-square bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-100 transition-all cursor-pointer flex flex-col items-center justify-center group"
                    >
                      <div className="text-center flex flex-col items-center">
                        <div className={`flex items-center justify-center group-hover:scale-110 transition-transform ${
                          pictureGridSize === 'tiny' ? 'mb-0.5' : pictureGridSize === 'small' ? 'mb-1' : 'mb-2'
                        }`}>
                          <svg 
                            className={`text-gray-400 group-hover:text-gray-500 ${
                              pictureGridSize === 'tiny' ? 'w-4 h-4' : 
                              pictureGridSize === 'small' ? 'w-6 h-6' : 
                              'w-12 h-12'
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                          </svg>
                        </div>
                        {pictureGridSize !== 'tiny' && (
                          <>
                            <div className={`font-medium text-gray-600 ${
                              pictureGridSize === 'small' ? 'text-[10px]' : 'text-sm'
                            }`}>
                              Upload Pictures
                            </div>
                            {pictureGridSize === 'large' && (
                              <div className="text-xs text-gray-400 mt-1">Click to add photos</div>
                            )}
                          </>
                        )}
                      </div>
                    </label>
                  )}
                  
                  {/* Hidden file input for direct upload */}
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length === 0) return;
                      
                      // Validate file types
                      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'image/webp', 'image/gif'];
                      const invalidFiles = files.filter(file => !validTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|heic|heif|webp|gif)$/i));
                      
                      if (invalidFiles.length > 0) {
                        toast.error(`Only image files are allowed (JPEG, PNG, HEIC, WebP, GIF). Invalid files: ${invalidFiles.map(f => f.name).join(', ')}`);
                        e.target.value = ''; // Reset input
                        return;
                      }
                      
                      // Show uploading toast
                      const toastId = toast.loading(`Uploading ${files.length} picture${files.length > 1 ? 's' : ''}...`);
                      
                      // Upload files
                      (async () => {
                        try {
                          // Upload all files
                          const uploadPromises = files.map(async (file) => {
                            // Fix orientation issues on mobile devices
                            const correctedFile = await fixImageOrientation(file);
                            
                            const formData = new FormData();
                            formData.append('file', correctedFile);
                            formData.append('user_id', user.id);
                            
                            const response = await axios.post(`${getAPI()}/upload`, formData);
                            return response.data;
                          });

                          const uploadedImages = await Promise.all(uploadPromises);
                          
                          // If a folder is selected, assign all uploaded images to that folder
                          if (selectedFolder && selectedFolder.id) {
                            const imageIds = uploadedImages.map(result => result.image?.id).filter(Boolean);
                            if (imageIds.length > 0) {
                              const formData = new FormData();
                              imageIds.forEach(id => formData.append('image_ids', id));
                              formData.append('user_id', user.id);
                              
                              try {
                                await axios.post(`${getAPI()}/folders/${selectedFolder.id}/images`, formData);
                                toast.success(`${files.length} picture${files.length > 1 ? 's' : ''} uploaded to ${selectedFolder.name}!`, { id: toastId });
                              } catch (error) {
                                console.error('Failed to assign images to folder:', error);
                                toast.success(`${files.length} picture${files.length > 1 ? 's' : ''} uploaded!`, { id: toastId });
                              }
                            }
                          } else {
                            toast.success(`${files.length} picture${files.length > 1 ? 's' : ''} uploaded successfully!`, { id: toastId });
                          }
                          
                          // Small delay to ensure backend has finished processing
                          await new Promise(resolve => setTimeout(resolve, 500));
                          
                          // Force refresh
                          await loadDashboardData();
                          await loadVideoJobs();
                        } catch (error) {
                          console.error('❌ Upload error:', error);
                          console.error('Error details:', {
                            message: error.message,
                            status: error.response?.status,
                            data: error.response?.data,
                            stack: error.stack,
                            uploadURL: `${getAPI()}/upload`
                          });
                          
                          // Mobile debugging - show alert with error details
                          const debugInfo = `Error: ${error.message}\nStatus: ${error.response?.status || 'N/A'}\nURL: ${getAPI()}/upload\nDetail: ${error.response?.data?.detail || 'N/A'}`;
                          alert(`Upload Debug Info:\n\n${debugInfo}`);
                          
                          // Check if it's a storage limit error
                          if (error.response?.status === 403 && error.response?.data?.detail?.includes('Free tier limit')) {
                            const detail = error.response.data.detail;
                            toast.error(
                              <div className="space-y-2">
                                <div className="font-semibold">📸 Storage Limit Reached!</div>
                                <div className="text-sm">{detail}</div>
                                <div className="text-xs text-gray-300 mt-2">Create your first magic moment to unlock unlimited uploads! 🎥✨</div>
                              </div>,
                              { duration: 8000, id: toastId }
                            );
                          } else {
                            // Show more detailed error message
                            const errorMsg = error.response?.data?.detail || error.message || 'Failed to upload pictures';
                            toast.error(`Upload failed: ${errorMsg}`, { id: toastId, duration: 6000 });
                          }
                        }
                      })();
                      
                      // Reset file input
                      e.target.value = '';
                    }}
                    className="hidden"
                    id="pictures-direct-upload"
                  />
                  
                  {/* Empty state message when no pictures */}
                  {sortedPictures.length === 0 && (
                    <div className="col-span-full">
                      <div 
                        onClick={() => {
                          // Directly trigger file input
                          document.getElementById('pictures-direct-upload')?.click();
                        }}
                        className="text-center py-16 bg-gradient-to-br from-white/80 to-purple-50/80 backdrop-blur-sm rounded-3xl border-2 border-dashed border-purple-300 hover:border-purple-400 hover:bg-purple-50/50 transition-all cursor-pointer group"
                      >
                        <div className="w-24 h-24 bg-gradient-to-br from-purple-200 to-indigo-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                          <svg className="w-12 h-12 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-bold text-purple-800 mb-2">Upload your first picture</h3>
                        <p className="text-purple-600 mb-6 max-w-md mx-auto">
                          Start by uploading photos to create animated moments and memories
                        </p>
                        <button className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2 mx-auto">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                          </svg>
                          Upload Pictures
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Display unassigned images with sorting applied */}
                  {sortedPictures.map(image => {
                    // Detect if mobile device - disable drag on mobile
                    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                    
                    return (
                    <div 
                      key={`unassigned-${image.id}`} 
                      className="relative group"
                      draggable={!isMobile}
                      data-picture-id={image.id}
                      onDragStart={(e) => {
                        if (isMobile) {
                          e.preventDefault();
                          return;
                        }
                        const imageId = image.id;
                        let imagesToDrag = [];
                        
                        if (multiSelectMode && selectedPictures.length > 0) {
                          // In multi-select mode, drag all selected images
                          imagesToDrag = selectedPictures.map(id => id.replace('unassigned-', ''));
                        } else if (selectedPictures.includes(`unassigned-${imageId}`)) {
                          // If this image is part of selection, drag all selected
                          imagesToDrag = selectedPictures.map(id => id.replace('unassigned-', ''));
                        } else {
                          // Drag only this image
                          imagesToDrag = [imageId];
                        }
                        
                        e.dataTransfer.setData('imageIds', JSON.stringify(imagesToDrag));
                        e.dataTransfer.effectAllowed = 'move';
                        
                        // Visual feedback: show how many images are being dragged
                        if (imagesToDrag.length > 1) {
                          const dragImage = document.createElement('div');
                          dragImage.className = 'bg-blue-500 text-white px-3 py-2 rounded-lg font-medium';
                          dragImage.textContent = `Moving ${imagesToDrag.length} images`;
                          dragImage.style.position = 'absolute';
                          dragImage.style.top = '-1000px';
                          document.body.appendChild(dragImage);
                          e.dataTransfer.setDragImage(dragImage, 0, 0);
                          setTimeout(() => document.body.removeChild(dragImage), 0);
                        }
                      }}
                      onDragEnd={(e) => {
                        // Optional: visual feedback cleanup
                      }}
                      onMouseDown={(e) => {
                        // Disable mouse events on mobile to prevent conflicts with touch events
                        if (isMobile) return;
                        
                        // Track mouse position to detect click vs. drag
                        setTouchStartPos({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseUp={(e) => {
                        // Disable mouse events on mobile to prevent conflicts with touch events
                        if (isMobile) return;
                        
                        // Only open full-screen if this was a click (not a drag)
                        if (touchStartPos && !multiSelectMode && !videoGalleryJustOpened.current && !dropdownJustClosed.current) {
                          const deltaX = Math.abs(e.clientX - touchStartPos.x);
                          const deltaY = Math.abs(e.clientY - touchStartPos.y);
                          
                          // Only open if movement was minimal (actual click, not drag)
                          if (deltaX < 10 && deltaY < 10) {
                            setFullScreenImage({...image, projectName: 'Unassigned'});
                          }
                        }
                        
                        // Reset tracking
                        setTouchStartPos(null);
                      }}
                      onMouseLeave={(e) => {
                        // Disable mouse events on mobile to prevent conflicts with touch events
                        if (isMobile) return;
                        
                        // Reset tracking when mouse leaves
                        setTouchStartPos(null);
                      }}
                      onTouchStart={(e) => {
                        // In multi-select mode, let the overlay handle touches
                        if (multiSelectMode) {
                          return;
                        }
                        
                        // Track touch start position to detect scrolling vs. tapping
                        const touch = e.touches[0];
                        setTouchStartPos({ x: touch.clientX, y: touch.clientY });
                      }}
                      onTouchMove={(e) => {
                        // In multi-select mode, let the overlay handle touches
                        if (multiSelectMode) {
                          return;
                        }
                        
                        // Detect scrolling by tracking movement distance
                        if (touchStartPos) {
                          const touch = e.touches[0];
                          const deltaX = Math.abs(touch.clientX - touchStartPos.x);
                          const deltaY = Math.abs(touch.clientY - touchStartPos.y);
                          
                          // Mark as scrolling if moved more than 10px
                          if (deltaX > 10 || deltaY > 10) {
                            setTouchStartPos(null); // Clear to indicate scrolling happened
                          }
                        }
                      }}
                      onTouchEnd={(e) => {
                        // In multi-select mode, let the overlay handle touches
                        if (multiSelectMode) {
                          return;
                        }
                        
                        // Only open full-screen if this was a tap (not a scroll)
                        // touchStartPos will be null if user scrolled
                        if (touchStartPos && !multiSelectMode && !videoGalleryJustOpened.current && !dropdownJustClosed.current) {
                          setFullScreenImage({...image, projectName: 'Unassigned'});
                        }
                        
                        // Reset touch tracking
                        setTouchStartPos(null);
                      }}
                      onTouchCancel={(e) => {
                        // In multi-select mode, let the overlay handle touches
                        if (multiSelectMode) {
                          return;
                        }
                        
                        // Reset touch tracking on cancel
                        setTouchStartPos(null);
                      }}
                    >
                      {/* Multi-select overlay - prevents background interactions when in select mode */}
                      {multiSelectMode && (
                        <div 
                          className="absolute inset-0 z-10 cursor-pointer bg-black/10 rounded-xl"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Toggle checkbox selection
                            const pictureId = `unassigned-${image.id}`;
                            if (selectedPictures.includes(pictureId)) {
                              setSelectedPictures(selectedPictures.filter(id => id !== pictureId));
                            } else {
                              setSelectedPictures([...selectedPictures, pictureId]);
                            }
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Toggle checkbox selection for mobile
                            const pictureId = `unassigned-${image.id}`;
                            if (selectedPictures.includes(pictureId)) {
                              setSelectedPictures(selectedPictures.filter(id => id !== pictureId));
                            } else {
                              setSelectedPictures([...selectedPictures, pictureId]);
                            }
                          }}
                        />
                      )}
                      
                      <div className={`relative aspect-square bg-amber-100 rounded-xl overflow-hidden transition-all duration-300 ${
                        imagesWithVideos.has(image.id) 
                          ? 'border-4 border-gradient-to-r from-purple-500 to-blue-500 shadow-lg border-purple-500' 
                          : 'border border-amber-200'
                      }`}>
                        <img
                          loading="lazy"
                          key={`picture-${image.id}-${image.cloudflare_image_id || image.updated_at || image.created_at || ''}`}
                          src={getImageUrl(image)}
                          alt={image.name || 'Unassigned Memory'}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 cursor-pointer"
                          onClick={(e) => {
                            if (!multiSelectMode && !videoGalleryJustOpened.current && !dropdownJustClosed.current) {
                              setFullScreenImage({...image, projectName: 'Unassigned'});
                            }
                          }}
                          onError={(e) => {
                            // Fallback to a full-res URL if thumbnail fails
                            const isCf = (u) => typeof u === 'string' && (u.includes('imagedelivery.net') || u.includes('cloudflarestream.com') || u.includes('customer-'));
                            let fallback = '';
                            if (isCf(image?.cloudflare_delivery_url)) fallback = image.cloudflare_delivery_url;
                            else if (isCf(image?.url)) fallback = image.url;
                            else if ((image?.id || image?._id) && user?.id) fallback = `${getAPI()}/files/${image.id || image._id}?user_id=${user.id}`;
                            if (fallback && e.target.src !== fallback) {
                              e.target.src = fallback;
                            } else {
                              e.target.style.backgroundColor = 'rgba(255,0,0,0.1)';
                            }
                          }}
                        />
                        
                        {/* Demo content watermark badge */}
                        {(() => {
                          const imageUrl = image.url || image.thumbnail_url || '';
                          const isDemo = imageUrl.includes('/landing-assets');
                          return isDemo ? (
                            <div className="absolute top-2 right-2 bg-amber-500 text-white px-2 py-1 rounded-full text-[10px] font-bold shadow-lg z-10">
                              DEMO
                            </div>
                          ) : null;
                        })()}
                        
                        {/* Video button - Bottom Right - MOVED INSIDE */}
                        {(imagesWithVideos.has(image.id) || imagesWithVideos.has(String(image.id))) && !showCreateLivingMemory && !showMultiUpload && (() => {
                        const videoCount = videoJobs.filter(job => 
                          String(job.image_id) === String(image.id) && 
                          job.status === 'completed'
                        ).length;
                        // Adjust badge size based on grid size
                        const badgeClasses = pictureGridSize === 'tiny' 
                          ? 'px-1.5 py-0.5 gap-0.5' 
                          : pictureGridSize === 'small'
                          ? 'px-2 py-1 gap-1'
                          : 'px-2.5 py-1.5 gap-1.5';
                        const iconSize = pictureGridSize === 'tiny' 
                          ? 'w-3 h-3' 
                          : pictureGridSize === 'small'
                          ? 'w-3.5 h-3.5'
                          : 'w-4 h-4';
                        const textSize = pictureGridSize === 'tiny' || pictureGridSize === 'small'
                          ? 'text-[10px]'
                          : 'text-xs';
                        return (
                          <div className="absolute bottom-1 right-1 z-20">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowVideoGallery(image.id);
                              }}
                              onTouchStart={(e) => {
                                e.stopPropagation();
                              }}
                              onTouchEnd={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowVideoGallery(image.id);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              onMouseUp={(e) => {
                                e.stopPropagation();
                              }}
                              className={`bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full hover:from-purple-600 hover:to-blue-600 transition-all duration-300 shadow-lg flex items-center ${badgeClasses}`}
                              title="View animated moments"
                            >
                              <svg className={iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              <span className={`font-semibold ${textSize}`}>{videoCount}</span>
                            </button>
                          </div>
                        );
                      })()}
                      </div>
                      
                      {/* Photo details removed - only show in full-screen mode */}
                      
                      {/* Multi-select checkbox - Bottom Left */}
                      {multiSelectMode && (
                        <div className="absolute bottom-2 left-2 z-20">
                          <div 
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                              selectedPictures.includes(`unassigned-${image.id}`)
                                ? 'bg-amber-500 border-amber-500'
                                : 'bg-white/80 border-white'
                            }`}
                            style={{ 
                              WebkitAppearance: 'none',
                              appearance: 'none'
                            }}
                          >
                            {selectedPictures.includes(`unassigned-${image.id}`) && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Action buttons - Top Right */}
                      <div className="absolute top-2 right-2 flex items-center gap-2">
                        
                        {/* Three-dot button - always visible on mobile (except tiny/small), only show on hover on desktop */}
                        {!multiSelectMode && pictureGridSize !== 'tiny' && pictureGridSize !== 'small' && (
                        <div className={`relative opacity-0 md:group-hover:opacity-100 opacity-100 md:opacity-0 transition-opacity duration-200`}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const dropdownId = `unassigned-${image.id}-dropdown`;
                              const currentDropdown = document.querySelector(`[data-dropdown="${dropdownId}"]`);
                              const currentBackdrop = document.querySelector(`[data-backdrop="${dropdownId}"]`);
                              const allDropdowns = document.querySelectorAll('[data-dropdown]');
                              const allBackdrops = document.querySelectorAll('[data-backdrop]');
                              allDropdowns.forEach(dropdown => {
                                if (dropdown !== currentDropdown) {
                                  dropdown.classList.add('hidden');
                                }
                              });
                              allBackdrops.forEach(backdrop => {
                                if (backdrop !== currentBackdrop) {
                                  backdrop.classList.add('hidden');
                                }
                              });
                              if (currentDropdown) {
                                const isHidden = currentDropdown.classList.contains('hidden');
                                currentDropdown.classList.toggle('hidden');
                                if (currentBackdrop) currentBackdrop.classList.toggle('hidden');
                                
                                // Prevent/enable scrolling based on menu state
                                if (isHidden) {
                                  // Menu is opening - prevent scrolling
                                  document.body.style.overflow = 'hidden';
                                } else {
                                  // Menu is closing - re-enable scrolling
                                  document.body.style.overflow = '';
                                }
                                
                                // Position dropdown below button when opening
                                if (isHidden) {
                                  const buttonRect = e.currentTarget.getBoundingClientRect();
                                  currentDropdown.style.top = `${buttonRect.bottom + 8}px`;
                                  currentDropdown.style.left = `${buttonRect.right - 200}px`; // Align to right edge
                                }
                              }
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              // Mobile: trigger menu opening on touch end (since onClick won't fire due to preventDefault)
                              const dropdownId = `unassigned-${image.id}-dropdown`;
                              const currentDropdown = document.querySelector(`[data-dropdown="${dropdownId}"]`);
                              const currentBackdrop = document.querySelector(`[data-backdrop="${dropdownId}"]`);
                              const allDropdowns = document.querySelectorAll('[data-dropdown]');
                              const allBackdrops = document.querySelectorAll('[data-backdrop]');
                              allDropdowns.forEach(dropdown => {
                                if (dropdown !== currentDropdown) {
                                  dropdown.classList.add('hidden');
                                }
                              });
                              allBackdrops.forEach(backdrop => {
                                if (backdrop !== currentBackdrop) {
                                  backdrop.classList.add('hidden');
                                }
                              });
                              if (currentDropdown) {
                                const isHidden = currentDropdown.classList.contains('hidden');
                                currentDropdown.classList.toggle('hidden');
                                if (currentBackdrop) currentBackdrop.classList.toggle('hidden');
                                
                                // Prevent/enable scrolling based on menu state
                                if (isHidden) {
                                  // Menu is opening - prevent scrolling
                                  document.body.style.overflow = 'hidden';
                                } else {
                                  // Menu is closing - re-enable scrolling
                                  document.body.style.overflow = '';
                                }
                                
                                // Position dropdown below button when opening
                                if (isHidden) {
                                  const buttonRect = e.currentTarget.getBoundingClientRect();
                                  currentDropdown.style.top = `${buttonRect.bottom + 8}px`;
                                  currentDropdown.style.left = `${buttonRect.right - 200}px`; // Align to right edge
                                }
                              }
                              // Prevent fullscreen from opening
                              dropdownJustClosed.current = true;
                              setTimeout(() => { dropdownJustClosed.current = false; }, 100);
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseUp={(e) => {
                              e.stopPropagation();
                            }}
                            className="bg-white/90 text-gray-700 p-2 rounded-full hover:bg-white transition-all duration-200 shadow-lg z-30 animate-fadeIn"
                            title="More options"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                            </svg>
                          </button>
                          
                          {/* Backdrop to prevent scrolling and catch outside clicks */}
                          <div
                            data-backdrop={`unassigned-${image.id}-dropdown`}
                            className="hidden fixed inset-0 z-[99998] bg-transparent"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Set flag immediately to prevent fullscreen
                              dropdownJustClosed.current = true;
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Set flag immediately to prevent fullscreen
                              dropdownJustClosed.current = true;
                              
                              const currentDropdown = document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`);
                              const currentBackdrop = document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`);
                              
                              // Hide dropdown first
                              if (currentDropdown) currentDropdown.classList.add('hidden');
                              
                              // Delay hiding the backdrop to ensure click doesn't pass through
                              setTimeout(() => {
                                if (currentBackdrop) currentBackdrop.classList.add('hidden');
                                // Re-enable scrolling
                                document.body.style.overflow = '';
                                // Reset the flag after backdrop is fully hidden
                                setTimeout(() => { dropdownJustClosed.current = false; }, 50);
                              }, 150);
                            }}
                            onTouchStart={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Set flag immediately to prevent fullscreen
                              dropdownJustClosed.current = true;
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              // Set flag immediately to prevent fullscreen
                              dropdownJustClosed.current = true;
                              
                              const currentDropdown = document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`);
                              const currentBackdrop = document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`);
                              
                              // Hide dropdown first
                              if (currentDropdown) currentDropdown.classList.add('hidden');
                              
                              // Delay hiding the backdrop to ensure touch doesn't pass through
                              setTimeout(() => {
                                if (currentBackdrop) currentBackdrop.classList.add('hidden');
                                // Re-enable scrolling
                                document.body.style.overflow = '';
                                // Reset the flag after backdrop is fully hidden
                                setTimeout(() => { dropdownJustClosed.current = false; }, 50);
                              }, 150);
                            }}
                          />
                          
                          {/* Dropdown menu with highest z-index */}
                          <div
                            data-dropdown={`unassigned-${image.id}-dropdown`}
                            className="fixed bg-white rounded-lg shadow-2xl border border-gray-300 py-2 min-w-[200px] hidden"
                            style={{ zIndex: 99999 }}
                          >
                            <button
                              onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                setFullScreenImage({...image, projectName: 'Unassigned'});
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                setFullScreenImage({...image, projectName: 'Unassigned'});
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View Full Screen
                            </button>
                            
                            <button
                              onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                setEditingMemory(image);
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                setEditingMemory(image);
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors text-blue-700 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              Edit Picture
                            </button>

                            <button
                              onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                
                                // CRITICAL: Always fetch FRESH data for this image
                                console.log('🎯 Animate clicked (touch) - fetching FRESH image data from backend');
                                
                                // Fetch fresh image data directly from backend
                                axios.get(`${getAPI()}/unassigned-images?user_id=${user.id}&authenticated_user_id=${user.id}`)
                                  .then(response => {
                                    const freshImages = response.data.unassigned_images || [];
                                    const freshImage = freshImages.find(img => img.id === image.id);
                                    
                                    if (freshImage) {
                                      console.log('✅ Got fresh image data from backend');
                                      
                                      const cacheBust = `&_animate=${Date.now()}`;
                                      const imageToAnimate = {
                                        ...freshImage,
                                        url: freshImage.url && freshImage.url.includes('?') 
                                          ? `${freshImage.url}${cacheBust}`
                                          : `${freshImage.url}?_animate=${Date.now()}`,
                                        thumbnail_url: freshImage.thumbnail_url && freshImage.thumbnail_url.includes('?')
                                          ? `${freshImage.thumbnail_url}${cacheBust}`
                                          : `${freshImage.thumbnail_url}?_animate=${Date.now()}`,
                                        r2_url: freshImage.r2_url && freshImage.r2_url.includes('?')
                                          ? `${freshImage.r2_url}${cacheBust}`
                                          : `${freshImage.r2_url}?_animate=${Date.now()}`,
                                        projectName: 'Unassigned Pictures',
                                        projectId: 'unassigned'
                                      };
                                      
                                      console.log('🚀 Opening animation modal with FRESH image data');
                                      setSelectedImageForAnimation(imageToAnimate);
                                      setShowCreateLivingMemory(true);
                                    } else {
                                      console.error('❌ Could not find fresh image data');
                                      const cacheBustedImage = addCacheBustingToImage({
                                        ...image,
                                        projectName: 'Unassigned Pictures',
                                        projectId: 'unassigned'
                                      });
                                      setSelectedImageForAnimation(cacheBustedImage);
                                      setShowCreateLivingMemory(true);
                                    }
                                  })
                                  .catch(error => {
                                    console.error('❌ Error fetching fresh image data:', error);
                                    const cacheBustedImage = addCacheBustingToImage({
                                      ...image,
                                      projectName: 'Unassigned Pictures',
                                      projectId: 'unassigned'
                                    });
                                    setSelectedImageForAnimation(cacheBustedImage);
                                    setShowCreateLivingMemory(true);
                                  });
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                
                                // CRITICAL: Always fetch FRESH data for this image
                                // The image object here might have stale URLs from before edit/revert
                                console.log('🎯 Animate clicked - fetching FRESH image data from backend');
                                
                                // Fetch fresh image data directly from backend
                                axios.get(`${getAPI()}/unassigned-images?user_id=${user.id}&authenticated_user_id=${user.id}`)
                                  .then(response => {
                                    const freshImages = response.data.unassigned_images || [];
                                    const freshImage = freshImages.find(img => img.id === image.id);
                                    
                                    if (freshImage) {
                                      console.log('✅ Got fresh image data from backend');
                                      
                                      // Generate a fresh cache-bust timestamp to force browser refresh
                                      const cacheBust = `&_animate=${Date.now()}`;
                                      const imageToAnimate = {
                                        ...freshImage,
                                        url: freshImage.url && freshImage.url.includes('?') 
                                          ? `${freshImage.url}${cacheBust}`
                                          : `${freshImage.url}?_animate=${Date.now()}`,
                                        thumbnail_url: freshImage.thumbnail_url && freshImage.thumbnail_url.includes('?')
                                          ? `${freshImage.thumbnail_url}${cacheBust}`
                                          : `${freshImage.thumbnail_url}?_animate=${Date.now()}`,
                                        r2_url: freshImage.r2_url && freshImage.r2_url.includes('?')
                                          ? `${freshImage.r2_url}${cacheBust}`
                                          : `${freshImage.r2_url}?_animate=${Date.now()}`,
                                        projectName: 'Unassigned Pictures',
                                        projectId: 'unassigned'
                                      };
                                      
                                      console.log('🚀 Opening animation modal with FRESH image data');
                                      setSelectedImageForAnimation(imageToAnimate); // Already has cache-busting from above
                                      setShowCreateLivingMemory(true);
                                    } else {
                                      console.error('❌ Could not find fresh image data');
                                      // Fallback to old behavior with cache-busting
                                      const cacheBustedImage = addCacheBustingToImage({
                                        ...image,
                                        projectName: 'Unassigned Pictures',
                                        projectId: 'unassigned'
                                      });
                                      setSelectedImageForAnimation(cacheBustedImage);
                                      setShowCreateLivingMemory(true);
                                    }
                                  })
                                  .catch(error => {
                                    console.error('❌ Error fetching fresh image data:', error);
                                    // Fallback to old behavior with cache-busting
                                    const cacheBustedImage = addCacheBustingToImage({
                                      ...image,
                                      projectName: 'Unassigned Pictures',
                                      projectId: 'unassigned'
                                    });
                                    setSelectedImageForAnimation(cacheBustedImage);
                                    setShowCreateLivingMemory(true);
                                  });
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-purple-50 transition-colors text-purple-700 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                              </svg>
                              Animate Picture
                            </button>
                            
                            <button
                              onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                setCopyingImage(image);
                                setShowCreateFolderInCopyModal(false);
                                setNewFolderNameInCopyModal('');
                                setShowCopyToFolderModal(true);
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                setCopyingImage(image);
                                setShowCreateFolderInCopyModal(false);
                                setNewFolderNameInCopyModal('');
                                setShowCopyToFolderModal(true);
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors text-gray-700 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                              </svg>
                              Copy to Folder
                            </button>
                            
                            <hr className="my-1" />
                            
                            <button
                              onTouchStart={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onTouchEnd={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                
                                // Check if we're in a folder view
                                const isInFolder = selectedFolder && selectedFolder.id;
                                const confirmMessage = isInFolder 
                                  ? `Remove this picture from "${selectedFolder.name}"? The picture will remain in your Pictures tab.`
                                  : 'Are you sure you want to delete this picture? This action cannot be undone.';
                                
                                if (window.confirm(confirmMessage)) {
                                  try {
                                    if (isInFolder) {
                                      // Remove from folder only (set folder_id to null)
                                      await axios.patch(`${getAPI()}/unassigned-images/${image.id}?user_id=${user.id}`, {
                                        folder_id: null
                                      });
                                      
                                      // Remove from UI for instant feedback
                                      setUnassignedImages(prev => prev.map(img => 
                                        img.id === image.id ? { ...img, folder_id: null } : img
                                      ));
                                      
                                      toast.success(`Picture removed from ${selectedFolder.name}`);
                                    } else {
                                      // Delete picture entirely when in "All Pictures" view
                                      setUnassignedImages(prev => prev.filter(img => img.id !== image.id));
                                      await axios.delete(`${getAPI()}/unassigned-images/${image.id}?user_id=${user.id}`);
                                      // Removed toast: Picture deleted successfully
                                    }
                                    
                                    await new Promise(resolve => setTimeout(resolve, 500));
                                    await loadDashboardData();
                                    setPictureGridSize(prev => prev);
                                  } catch (error) {
                                    toast.error(isInFolder ? 'Failed to remove from folder' : 'Failed to delete picture');
                                    console.error('Delete/Remove error:', error);
                                    await loadDashboardData();
                                  }
                                }
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                document.querySelector(`[data-dropdown="unassigned-${image.id}-dropdown"]`).classList.add('hidden');
                                document.querySelector(`[data-backdrop="unassigned-${image.id}-dropdown"]`)?.classList.add('hidden');
                                document.body.style.overflow = ''; // Re-enable scrolling
                                // Prevent fullscreen from opening
                                dropdownJustClosed.current = true;
                                setTimeout(() => { dropdownJustClosed.current = false; }, 300);
                                
                                // Check if we're in a folder view
                                const isInFolder = selectedFolder && selectedFolder.id;
                                const confirmMessage = isInFolder 
                                  ? `Remove this picture from "${selectedFolder.name}"? The picture will remain in your Pictures tab.`
                                  : 'Are you sure you want to delete this picture? This action cannot be undone.';
                                
                                if (window.confirm(confirmMessage)) {
                                  try {
                                    if (isInFolder) {
                                      // Remove from folder only (set folder_id to null)
                                      await axios.patch(`${getAPI()}/unassigned-images/${image.id}?user_id=${user.id}`, {
                                        folder_id: null
                                      });
                                      
                                      // Remove from UI for instant feedback
                                      setUnassignedImages(prev => prev.map(img => 
                                        img.id === image.id ? { ...img, folder_id: null } : img
                                      ));
                                      
                                      toast.success(`Picture removed from ${selectedFolder.name}`);
                                    } else {
                                      // Delete picture entirely when in "All Pictures" view
                                      // Immediately remove from UI state for instant feedback
                                      setUnassignedImages(prev => prev.filter(img => img.id !== image.id));
                                      
                                      // Make API call to delete
                                      await axios.delete(`${getAPI()}/unassigned-images/${image.id}?user_id=${user.id}`);
                                      // Removed toast: Picture deleted successfully
                                    }
                                    
                                    // Wait a bit for database consistency
                                    await new Promise(resolve => setTimeout(resolve, 500));
                                    
                                    // Force a complete refresh to ensure grid reflows properly
                                    await loadDashboardData();
                                    
                                    // Force React to re-render the grid by updating a state
                                    setPictureGridSize(prev => prev); // Triggers re-render
                                  } catch (error) {
                                    toast.error(isInFolder ? 'Failed to remove from folder' : 'Failed to delete picture');
                                    console.error('Delete/Remove error:', error);
                                    // Restore by reloading
                                    await loadDashboardData();
                                  }
                                }
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-red-50 transition-colors text-red-600 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              {selectedFolder ? 'Remove from Folder' : 'Delete Picture'}
                            </button>
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Floating Animate Pictures Button - appears when scrolled down (LEFT SIDE) */}
                {showFloatingAnimateButtonPictures && !multiSelectMode && (
                  <button
                    onClick={() => {
                      setMultiSelectMode(true);
                      setSelectedPictures([]);
                      // Don't scroll - let user stay where they are
                    }}
                    className="fixed bottom-6 left-6 z-50 flex items-center gap-2 px-6 py-3 text-white rounded-full transition-all text-sm shadow-2xl hover:shadow-3xl font-medium hover:scale-105 transform"
                    style={{
                      background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #a855f7 75%, #ec4899 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 8s linear infinite, slideInUp 0.3s ease-out'
                    }}
                    title="Animate Pictures"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    <span className="hidden sm:inline">Animate Pictures</span>
                    <span className="sm:hidden">Animate</span>
                  </button>
                )}

                {/* Floating Animate Selected Button - appears when pictures are selected (LEFT SIDE) */}
                {multiSelectMode && selectedPictures.length > 0 && (
                  <button
                    onClick={() => {
                      // Filter selected images using unassigned- prefix
                      const selectedImages = unassignedImages.filter(img => 
                        selectedPictures.includes(`unassigned-${img.id}`)
                      );
                      
                      if (selectedImages.length === 0) {
                        toast.error('No images selected');
                        return;
                      }
                      
                      if (selectedImages.length === 1) {
                        // Single image - open single animation modal
                        const singleImage = {
                          ...selectedImages[0],
                          projectName: 'Your Pictures'
                        };
                        
                        // Exit multi-select mode
                        setMultiSelectMode(false);
                        setSelectedPictures([]);
                        
                        // Open the living memory modal with this image
                        setSelectedImageForAnimation(singleImage);
                        setShowCreateLivingMemory(true);
                        return;
                      }
                      
                      // Exit multi-select mode
                      setMultiSelectMode(false);
                      setSelectedPictures([]);
                      
                      // Use the centralized openBatchAnimation function
                      openBatchAnimation(selectedImages);
                    }}
                    className="fixed bottom-6 left-6 z-50 flex items-center gap-2 px-6 py-3 text-white rounded-full transition-all text-sm shadow-2xl hover:shadow-3xl font-medium hover:scale-105 transform"
                    style={{
                      background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #a855f7 75%, #ec4899 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 8s linear infinite, slideInUp 0.3s ease-out'
                    }}
                    title={`Animate ${selectedPictures.length} selected picture${selectedPictures.length > 1 ? 's' : ''}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    <span className="hidden sm:inline">Animate {selectedPictures.length} Selected</span>
                    <span className="sm:hidden">Animate ({selectedPictures.length})</span>
                  </button>
                )}
              </div>
            )}
            
            {/* Create Folder Modal */}
            {showCreateFolderModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" onClick={() => setShowCreateFolderModal(false)}>
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-amber-800 mb-4">Create New Folder</h3>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newFolderName.trim()) {
                      toast.error('Please enter a folder name');
                      return;
                    }
                    try {
                      const formData = new FormData();
                      formData.append('name', newFolderName.trim());
                      formData.append('user_id', user.id);
                      formData.append('user_id', user.id);
                      await axios.post(`${getAPI()}/folders`, formData);
                      toast.success('Folder created!');
                      setNewFolderName('');
                      setShowCreateFolderModal(false);
                      loadDashboardData();
                    } catch (error) {
                      console.error('Failed to create folder:', error);
                      toast.error('Failed to create folder');
                    }
                  }}>
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder="Folder name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateFolderModal(false);
                          setNewFolderName('');
                        }}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                      >
                        Create
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Rename Folder Modal */}
            {showRenameFolderModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" onClick={() => setShowRenameFolderModal(false)}>
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-amber-800 mb-4">Rename Folder</h3>
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newFolderName.trim()) {
                      toast.error('Please enter a folder name');
                      return;
                    }
                    try {
                      const BACKEND_URL = getBACKEND_URL();
                      const formData = new FormData();
                      formData.append('name', newFolderName.trim());
                      formData.append('user_id', user.id);
                      await axios.put(`${BACKEND_URL}/api/folders/${renamingFolder.id}`, formData);
                      toast.success('Folder renamed!');
                      setNewFolderName('');
                      setShowRenameFolderModal(false);
                      setRenamingFolder(null);
                      loadDashboardData();
                    } catch (error) {
                      console.error('Failed to rename folder:', error);
                      toast.error('Failed to rename folder');
                    }
                  }}>
                    <input
                      type="text"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      placeholder="Folder name"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
                      autoFocus
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setShowRenameFolderModal(false);
                          setNewFolderName('');
                          setRenamingFolder(null);
                        }}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                      >
                        Rename
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Move to Folder Modal */}
            {showCopyToFolderModal && copyingImage && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]" onClick={() => setShowCopyToFolderModal(false)}>
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-amber-800 mb-4">Copy to Folder</h3>
                  <p className="text-sm text-gray-600 mb-4">Select a folder to move this picture to:</p>
                      
                      {folders.length === 0 && !showCreateFolderInCopyModal ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500 mb-4">No folders yet</p>
                          <button
                            onClick={() => {
                              setShowCreateFolderInCopyModal(true);
                            }}
                            className="text-amber-600 hover:text-amber-700 font-medium underline"
                          >
                            Create a folder first
                          </button>
                        </div>
                      ) : folders.length === 0 && showCreateFolderInCopyModal ? (
                        <div className="mb-6">
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                            <input
                              type="text"
                              placeholder="Enter folder name..."
                              value={newFolderNameInCopyModal}
                              onChange={(e) => setNewFolderNameInCopyModal(e.target.value)}
                              onKeyPress={async (e) => {
                                if (e.key === 'Enter' && newFolderNameInCopyModal.trim()) {
                                  try {
                                    const formData = new FormData();
                                    formData.append('name', newFolderNameInCopyModal);
                                    formData.append('user_id', user.id);
                                    formData.append('icon', '📁');
                                    
                                    const folderRes = await axios.post(`${getAPI()}/folders`, formData);
                                    const newFolder = folderRes.data.folder;
                                    
                                    // Copy image to the new folder
                                    const copyFormData = new FormData();
                                    copyFormData.append('image_ids', copyingImage.id);
                                    copyFormData.append('user_id', user.id);
                                    await axios.post(`${getAPI()}/folders/${newFolder.id}/images`, copyFormData);
                                    
                                    toast.success(`Created folder "${newFolderNameInCopyModal}" and copied picture`);
                                    setShowCreateFolderInCopyModal(false);
                                    setNewFolderNameInCopyModal('');
                                    setShowCopyToFolderModal(false);
                                    setCopyingImage(null);
                                    await loadDashboardData();
                                  } catch (error) {
                                    console.error('Failed to create folder:', error);
                                    toast.error('Failed to create folder');
                                  }
                                }
                              }}
                              className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  if (!newFolderNameInCopyModal.trim()) {
                                    toast.error('Please enter a folder name');
                                    return;
                                  }
                                  
                                  try {
                                    const formData = new FormData();
                                    formData.append('name', newFolderNameInCopyModal);
                                    formData.append('user_id', user.id);
                                    formData.append('icon', '📁');
                                    
                                    const folderRes = await axios.post(`${getAPI()}/folders`, formData);
                                    const newFolder = folderRes.data.folder;
                                    
                                    // Copy image to the new folder
                                    const copyFormData = new FormData();
                                    copyFormData.append('image_ids', copyingImage.id);
                                    copyFormData.append('user_id', user.id);
                                    await axios.post(`${getAPI()}/folders/${newFolder.id}/images`, copyFormData);
                                    
                                    toast.success(`Created folder "${newFolderNameInCopyModal}" and copied picture`);
                                    setShowCreateFolderInCopyModal(false);
                                    setNewFolderNameInCopyModal('');
                                    setShowCopyToFolderModal(false);
                                    setCopyingImage(null);
                                    await loadDashboardData();
                                  } catch (error) {
                                    console.error('Failed to create folder:', error);
                                    toast.error('Failed to create folder');
                                  }
                                }}
                                className="flex-1 px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                              >
                                Create & Copy
                              </button>
                              <button
                                onClick={() => {
                                  setShowCreateFolderInCopyModal(false);
                                  setNewFolderNameInCopyModal('');
                                }}
                                className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2 mb-6">
                          {folders.map((folder) => (
                            <button
                              key={folder.id}
                              onClick={async () => {
                                try {
                                  const formData = new FormData();
                                  formData.append('image_ids', copyingImage.id);
                                  
                                  formData.append('user_id', user.id);
                                  await axios.post(`${getAPI()}/folders/${folder.id}/images`, formData);
                                  toast.success(`Copied to ${folder.name}`);
                                  setShowCopyToFolderModal(false);
                                  setCopyingImage(null);
                                  // Wait for dashboard data to reload before closing modal
                                  await loadDashboardData();
                                } catch (error) {
                                  console.error('Failed to move to folder:', error);
                                  toast.error('Failed to move to folder');
                                }
                              }}
                              className="w-full p-3 border border-gray-200 rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors text-left flex items-center gap-3"
                            >
                              <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              </svg>
                              <span className="font-medium text-gray-700">{folder.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setShowCopyToFolderModal(false);
                            setCopyingImage(null);
                            setShowCreateFolderInCopyModal(false);
                            setNewFolderNameInCopyModal('');
                          }}
                          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

            {/* Batch Copy to Folder Modal */}
            {showBatchCopyToFolderModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]" onClick={() => setShowBatchCopyToFolderModal(false)}>
                <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <h3 className="text-lg font-semibold text-amber-800 mb-4">Copy to Folder</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Copy {selectedPictures.length} picture{selectedPictures.length > 1 ? 's' : ''} to a folder:
                  </p>
                  
                  {/* Create New Folder Option */}
                  {!showCreateFolderInCopyModal ? (
                    <button
                      onClick={() => setShowCreateFolderInCopyModal(true)}
                      className="w-full mb-4 p-3 border-2 border-dashed border-amber-300 rounded-lg hover:bg-amber-50 hover:border-amber-400 transition-colors text-left flex items-center gap-3"
                    >
                      <span className="text-xl">➕</span>
                      <span className="font-medium text-amber-700">Create New Folder</span>
                    </button>
                  ) : (
                    <div className="mb-4 p-3 border-2 border-amber-300 rounded-lg bg-amber-50">
                      <input
                        type="text"
                        placeholder="Enter folder name..."
                        value={newFolderNameInCopyModal}
                        onChange={(e) => setNewFolderNameInCopyModal(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            if (!newFolderNameInCopyModal.trim()) {
                              toast.error('Please enter a folder name');
                              return;
                            }
                            
                            try {
                              const formData = new FormData();
                              formData.append('name', newFolderNameInCopyModal.trim());
                              formData.append('color', '#FFA500');
                              formData.append('icon', '📁');
                              formData.append('user_id', user.id);
                              
                              const response = await axios.post(`${getAPI()}/folders`, formData);
                              const newFolder = response.data.folder;
                              
                              toast.success(`Folder "${newFolder.name}" created!`);
                              
                              // Reload folders
                              const effectiveUserId = currentlyViewingUserId || user.id;
                              const authenticatedUserId = user.id;
                              const foldersResponse = await axios.get(`${getAPI()}/folders?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`);
                              setFolders(foldersResponse.data.folders || []);
                              
                              // Copy pictures to the new folder
                              await handleBatchCopyToFolder(newFolder.id, newFolder.name);
                              
                              // Reset state
                              setNewFolderNameInCopyModal('');
                              setShowCreateFolderInCopyModal(false);
                            } catch (error) {
                              console.error('Failed to create folder:', error);
                              toast.error('Failed to create folder');
                            }
                          }}
                          className="flex-1 px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                        >
                          Create & Copy
                        </button>
                        <button
                          onClick={() => {
                            setShowCreateFolderInCopyModal(false);
                            setNewFolderNameInCopyModal('');
                          }}
                          className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Existing Folders List */}
                  {folders.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-2">No folders yet</p>
                      <p className="text-sm text-gray-400">Create one above to get started</p>
                    </div>
                  ) : (
                    <div className="space-y-2 mb-6">
                      <p className="text-xs text-gray-500 mb-2">Or select an existing folder:</p>
                      {folders.map((folder) => (
                        <button
                          key={folder.id}
                          onClick={() => handleBatchCopyToFolder(folder.id, folder.name)}
                          className="w-full p-3 border border-gray-200 rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors text-left flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          <span className="font-medium text-gray-700">{folder.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => {
                        setShowBatchCopyToFolderModal(false);
                        setShowCreateFolderInCopyModal(false);
                        setNewFolderNameInCopyModal('');
                      }}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Living Memories View (Completed subtab) */}
            {currentView === 'living_memories' && !showPendingSubtab && !showFailedSubtab && (
              <div ref={animatedMomentsContentRef} className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 pt-4">
                {/* Hide Demo Content Toggle for Videos - Only show if user has demo videos AND hasn't created real videos yet */}
                {!hasRealVideos && videoJobs.some(job => {
                  const resultUrl = job.result_url || job.video_url || '';
                  return resultUrl.includes('/landing-assets');
                }) && (
                  <div className="flex justify-center mb-4">
                    <button
                      onClick={() => {
                        setHideDemoContent(!hideDemoContent);
                        toast.success(hideDemoContent ? 'Demo content shown' : 'Demo content hidden');
                      }}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors px-4 py-2 rounded-lg hover:bg-purple-50"
                    >
                      {hideDemoContent ? (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <span>Show Demo Content</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                          <span>Hide Demo Content</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-4 px-4 relative z-10">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-lg sm:text-xl font-bold text-amber-800">Your Animated Moments</h3>
                    
                    {!animatedMemoryMultiSelectMode && (
                      <>
                        {/* Sort Dropdown - Next to Title */}
                        <div className="relative z-20">
                          <select
                            value={`${videoSort.field}-${videoSort.direction}`}
                            onChange={(e) => {
                              const [field, direction] = e.target.value.split('-');
                              setVideoSort({ field, direction });
                            }}
                            className="bg-white border border-gray-300 rounded-lg px-2 py-1.5 text-xs text-gray-700"
                          >
                            <option value="created_at-desc">▼ Newest First</option>
                            <option value="created_at-asc">▲ Oldest First</option>
                          </select>
                        </div>
                        
                        {/* Grid Size Controls - Match Albums Style */}
                        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                          <button
                            onClick={() => setVideoGridSize('tiny')}
                            className={`p-1.5 rounded transition-colors ${
                              videoGridSize === 'tiny' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                            }`}
                            title="Tiny"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1" viewBox="0 0 12 12">
                              <rect x="1" y="1" width="10" height="10" />
                              <line x1="1" y1="4.33" x2="11" y2="4.33" />
                              <line x1="1" y1="7.66" x2="11" y2="7.66" />
                              <line x1="4.33" y1="1" x2="4.33" y2="11" />
                              <line x1="7.66" y1="1" x2="7.66" y2="11" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setVideoGridSize('small')}
                            className={`p-1.5 rounded transition-colors ${
                              videoGridSize === 'small' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                            }`}
                            title="Small"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="7" height="7" rx="1"/>
                              <rect x="13" y="3" width="7" height="7" rx="1"/>
                              <rect x="3" y="13" width="7" height="7" rx="1"/>
                              <rect x="13" y="13" width="7" height="7" rx="1"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => setVideoGridSize('medium')}
                            className={`p-1.5 rounded transition-colors ${
                              videoGridSize === 'medium' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                            }`}
                            title="Medium"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="8" height="8" rx="1"/>
                              <rect x="14" y="3" width="8" height="8" rx="1"/>
                              <rect x="3" y="14" width="8" height="8" rx="1"/>
                            </svg>
                          </button>
                          <button
                            onClick={() => setVideoGridSize('large')}
                            className={`p-1.5 rounded transition-colors ${
                              videoGridSize === 'large' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                            }`}
                            title="Large"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="3" y="3" width="18" height="8" rx="1"/>
                              <rect x="3" y="13" width="18" height="8" rx="1"/>
                            </svg>
                          </button>
                        </div>
                        
                        {/* Select Button - Matches Album Style */}
                        <button
                          onClick={() => {
                            setAnimatedMemoryMultiSelectMode(true);
                            setSelectedAnimatedMemories([]);
                          }}
                          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span className="text-sm font-medium">Select</span>
                        </button>
                      </>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2 sm:space-x-2 sm:gap-0">
                    {!animatedMemoryMultiSelectMode ? (
                      <>
                        <button
                          onClick={() => {
                            setShowCreateLivingMemory(true);
                          }}
                          className="inline-flex items-center gap-2 text-white px-4 py-1.5 rounded-xl transition-all text-sm sm:text-base shadow-lg hover:shadow-xl relative overflow-hidden font-medium"
                          style={{
                            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #a855f7 75%, #ec4899 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 8s linear infinite'
                          }}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          Animate More
                        </button>
                        
                        {/* Import from Albums button */}
                        <button
                          onClick={() => {
                            setShowImportModal(true);
                            setImportModalType('moments');
                          }}
                          className="inline-flex items-center gap-2 bg-gray-500 text-white px-4 py-1.5 rounded-xl transition-all text-sm sm:text-base shadow-lg hover:shadow-xl hover:bg-gray-600 font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                          </svg>
                          Import from Albums
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            // Only select videos on the current page, not all videos
                            const ITEMS_PER_PAGE = 12;
                            const currentPage = animatedMemoryPage || 1;
                            const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
                            const endIndex = startIndex + ITEMS_PER_PAGE;
                            
                            let allVideos = videoJobs.filter(job => !job.is_deleted);
                            if (hideDemoContent) {
                              allVideos = allVideos.filter(job => {
                                const resultUrl = job.result_url || job.video_url || '';
                                return !resultUrl.includes('/landing-assets');
                              });
                            }
                            allVideos = allVideos.filter(job => {
                              const isCompleted = job.status === 'completed';
                              const isFailed = ['failed', 'timeout', 'error'].includes(job.status);
                              const isPending = ['queued', 'processing', 'submitted'].includes(job.status);
                              if (isCompleted) return true;
                              if (isFailed) return showFailedVideos;
                              if (isPending) return showPendingVideos;
                              return false;
                            });
                            const sortedVideos = sortVideos(allVideos, videoSort);
                            const currentPageVideos = sortedVideos.slice(startIndex, endIndex);
                            const currentPageVideoIds = currentPageVideos.map(job => job.id);
                            
                            // Check if all videos on current page are selected
                            const allCurrentPageSelected = currentPageVideoIds.every(id => selectedAnimatedMemories.includes(id));
                            
                            if (allCurrentPageSelected) {
                              // Deselect all videos on current page
                              setSelectedAnimatedMemories(prev => prev.filter(id => !currentPageVideoIds.includes(id)));
                            } else {
                              // Select all videos on current page (merge with existing selections)
                              setSelectedAnimatedMemories(prev => {
                                const newSelections = [...prev];
                                currentPageVideoIds.forEach(id => {
                                  if (!newSelections.includes(id)) {
                                    newSelections.push(id);
                                  }
                                });
                                return newSelections;
                              });
                            }
                          }}
                          className={`px-6 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 shadow-md hover:shadow-lg ${
                            selectedAnimatedMemories.length > 0
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
                              : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300'
                          }`}
                        >
                          {(() => {
                            // Calculate if all current page videos are selected
                            const ITEMS_PER_PAGE = 12;
                            const currentPage = animatedMemoryPage || 1;
                            const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
                            const endIndex = startIndex + ITEMS_PER_PAGE;
                            
                            let allVideos = videoJobs.filter(job => !job.is_deleted);
                            if (hideDemoContent) {
                              allVideos = allVideos.filter(job => {
                                const resultUrl = job.result_url || job.video_url || '';
                                return !resultUrl.includes('/landing-assets');
                              });
                            }
                            allVideos = allVideos.filter(job => {
                              const isCompleted = job.status === 'completed';
                              const isFailed = ['failed', 'timeout', 'error'].includes(job.status);
                              const isPending = ['queued', 'processing', 'submitted'].includes(job.status);
                              if (isCompleted) return true;
                              if (isFailed) return showFailedVideos;
                              if (isPending) return showPendingVideos;
                              return false;
                            });
                            const sortedVideos = sortVideos(allVideos, videoSort);
                            const currentPageVideos = sortedVideos.slice(startIndex, endIndex);
                            const currentPageVideoIds = currentPageVideos.map(job => job.id);
                            const allCurrentPageSelected = currentPageVideoIds.length > 0 && currentPageVideoIds.every(id => selectedAnimatedMemories.includes(id));
                            
                            return allCurrentPageSelected ? '✅ Deselect All' : '☑️ Select All';
                          })()}
                        </button>
                        <button
                          onClick={() => {
                            setAnimatedMemoryMultiSelectMode(false);
                            setSelectedAnimatedMemories([]);
                          }}
                          className="bg-gradient-to-r from-gray-400 to-gray-500 text-white px-6 py-2.5 rounded-xl font-medium hover:from-gray-500 hover:to-gray-600 transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {selectedAnimatedMemories.length > 0 && (
                      <>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            const selectedVideos = videoJobs.filter(job => selectedAnimatedMemories.includes(job.id));
                            
                            if (selectedVideos.length === 0) {
                              toast.error('No videos selected');
                              return;
                            }
                            
                            // Detect if mobile
                            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                            
                            // If 2+ videos and desktop, use zip download
                            if (selectedVideos.length >= 2 && !isMobile) {
                              try {
                                const BACKEND_URL = getBACKEND_URL();
                                
                                // Show progress modal
                                setShowDownloadProgress(true);
                                setDownloadProgress(10);
                                setDownloadStatus(`Preparing ${selectedVideos.length} videos for download...`);
                                
                                // Prepare items for zip download
                                const items = selectedVideos.map(video => {
                                  const downloadUrl = `${BACKEND_URL}/api/videos/${video.id}?user_id=${encodeURIComponent(user.id)}&authenticated_user_id=${encodeURIComponent(user.id)}&download=true`;
                                  
                                  return {
                                    url: downloadUrl,
                                    filename: `moment-${video.id}.mp4`,
                                    type: 'video'
                                  };
                                });
                                
                                setDownloadProgress(20);
                                setDownloadStatus('Creating zip archive...');
                                
                                // Simulate realistic progress during zip creation with file names
                                let simulatedProgress = 20;
                                const progressInterval = setInterval(() => {
                                  if (simulatedProgress < 55) {
                                    simulatedProgress += Math.random() * 3 + 1; // Increment 1-4%
                                    const fileIndex = Math.floor((simulatedProgress - 20) / 35 * items.length);
                                    const currentFile = items[fileIndex]?.filename || `moment-${fileIndex + 1}.mp4`;
                                    setDownloadProgress(Math.min(simulatedProgress, 55));
                                    setDownloadStatus(`Adding to archive: ${currentFile}`);
                                  }
                                }, 300); // Update every 300ms
                                
                                // Call zip endpoint
                                const response = await fetch(`${BACKEND_URL}/api/media/download-zip?user_id=${user.id}`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ items })
                                });
                                
                                // Stop simulated progress
                                clearInterval(progressInterval);
                                
                                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                                
                                setDownloadProgress(60);
                                setDownloadStatus('Processing files...');
                                
                                const blob = await response.blob();
                                
                                setDownloadProgress(95);
                                setDownloadStatus('Starting download...');
                                
                                const blobUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.download = `magic-moments-${new Date().toISOString().slice(0,19).replace(/:/g, '-')}.zip`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                                
                                setDownloadProgress(100);
                                setDownloadStatus('Download complete!');
                                
                                // Close modal after 2 seconds
                                setTimeout(() => {
                                  setShowDownloadProgress(false);
                                  setDownloadProgress(0);
                                }, 2000);
                                
                                // Removed toast: videos downloaded as zip
                              } catch (error) {
                                console.error('Failed to create zip:', error);
                                setShowDownloadProgress(false);
                                setDownloadProgress(0);
                                toast.error('Failed to create zip. Trying individual downloads...');
                                
                                // Detect Safari for fallback downloads
                                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                                
                                // Fallback to individual downloads
                                for (const video of selectedVideos) {
                                  try {
                                    const BACKEND_URL = getBACKEND_URL();
                                    const downloadUrl = `${BACKEND_URL}/api/videos/${video.id}?user_id=${encodeURIComponent(user.id)}&authenticated_user_id=${encodeURIComponent(user.id)}&download=true`;
                                    
                                    // For Safari, use direct link approach
                                    if (isSafari) {
                                      const link = document.createElement('a');
                                      link.href = downloadUrl;
                                      link.download = `moment-${video.id}.mp4`;
                                      link.target = '_blank'; // Fallback for Safari
                                      link.style.display = 'none';
                                      document.body.appendChild(link);
                                      link.click();
                                      setTimeout(() => {
                                        document.body.removeChild(link);
                                      }, 300);
                                    } else {
                                      // Non-Safari: use blob approach
                                      const response = await fetch(downloadUrl);
                                      if (!response.ok) throw new Error(`HTTP ${response.status}`);
                                      const blob = await response.blob();
                                      const blobUrl = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = blobUrl;
                                      link.download = `moment-${video.id}.mp4`;
                                      link.style.display = 'none';
                                      document.body.appendChild(link);
                                      link.click();
                                      setTimeout(() => {
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(blobUrl);
                                      }, 300);
                                    }
                                    await new Promise(resolve => setTimeout(resolve, 500));
                                  } catch (error) {
                                    console.error(`Failed to download video ${video.id}:`, error);
                                  }
                                }
                                toast.success(`${selectedVideos.length} video${selectedVideos.length !== 1 ? 's' : ''} downloaded!`);
                              }
                            } else {
                              // Detect Safari for single/mobile downloads
                              const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                              
                              // Single video or mobile - use original individual download
                              toast.info(`Downloading ${selectedVideos.length} video${selectedVideos.length !== 1 ? 's' : ''}...`);
                              
                              for (const video of selectedVideos) {
                                try {
                                  const BACKEND_URL = getBACKEND_URL();
                                  const downloadUrl = `${BACKEND_URL}/api/videos/${video.id}?user_id=${encodeURIComponent(user.id)}&authenticated_user_id=${encodeURIComponent(user.id)}&download=true`;
                                  
                                  // For Safari, use direct link approach
                                  if (isSafari) {
                                    const link = document.createElement('a');
                                    link.href = downloadUrl;
                                    link.download = `moment-${video.id}.mp4`;
                                    link.target = '_blank'; // Fallback for Safari
                                    link.style.display = 'none';
                                    document.body.appendChild(link);
                                    link.click();
                                    setTimeout(() => {
                                      document.body.removeChild(link);
                                    }, 300);
                                  } else {
                                    // Non-Safari: use blob approach
                                    const response = await fetch(downloadUrl);
                                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                                    const blob = await response.blob();
                                    const blobUrl = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.download = `moment-${video.id}.mp4`;
                                    link.style.display = 'none';
                                    document.body.appendChild(link);
                                    link.click();
                                    setTimeout(() => {
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(blobUrl);
                                    }, 300);
                                  }
                                  await new Promise(resolve => setTimeout(resolve, 500));
                                } catch (error) {
                                  console.error(`Failed to download video ${video.id}:`, error);
                                  toast.error(`Failed to download video ${video.id}`);
                                }
                              }
                              
                              toast.success(`${selectedVideos.length} video${selectedVideos.length !== 1 ? 's' : ''} downloaded!`);
                            }
                          }}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download ({selectedAnimatedMemories.length})
                        </button>
                        <button
                          onClick={handleDeleteSelectedAnimatedMemories}
                          className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-red-600 hover:to-rose-700 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete ({selectedAnimatedMemories.length})
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* Pagination controls */}
                {(() => {
                  // Get all videos
                  if (!videoJobs || !Array.isArray(videoJobs)) {
                    return null;
                  }
                  
                  let allVideos = videoJobs.filter(job => !job.is_deleted);
                  
                  // Filter out demo content if user wants to hide it
                  if (hideDemoContent) {
                    allVideos = allVideos.filter(job => {
                      const resultUrl = job.result_url || job.video_url || '';
                      return !resultUrl.includes('/landing-assets');
                    });
                  }
                  
                  allVideos = allVideos.filter(job => {
                    const isCompleted = job.status === 'completed';
                    const isFailed = ['failed', 'timeout', 'error'].includes(job.status);
                    const isPending = ['queued', 'processing', 'submitted'].includes(job.status);
                    if (isCompleted) return true;
                    if (isFailed) return showFailedVideos;
                    if (isPending) return showPendingVideos;
                    return false;
                  });
                  
                  const sortedVideos = sortVideos(allVideos, videoSort);
                  const ITEMS_PER_PAGE = 50;
                  const totalPages = Math.ceil(sortedVideos.length / ITEMS_PER_PAGE);
                  const currentPage = animatedMemoryPage || 1;
                  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
                  const endIndex = startIndex + ITEMS_PER_PAGE;
                  const displayedVideos = sortedVideos.slice(startIndex, endIndex);
                  
                  return (
                    <>
                      {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mb-4">
                          <button
                            onClick={() => setAnimatedMemoryPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-2 sm:px-3 py-1 sm:py-2 bg-gray-100 text-gray-700 rounded border border-gray-300 text-xs sm:text-sm hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            ← Prev
                          </button>
                          <span className="text-amber-700 text-xs sm:text-sm">
                            {currentPage}/{totalPages}
                          </span>
                          <button
                            onClick={() => setAnimatedMemoryPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-2 sm:px-3 py-1 sm:py-2 bg-gray-100 text-gray-700 rounded border border-gray-300 text-xs sm:text-sm hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next →
                          </button>
                        </div>
                      )}
                      
                      <div className={`grid ${getGridClasses(videoGridSize, 'videos')} gap-6`}>
                        {displayedVideos.map(job => (
                          <Suspense key={job.id} fallback={<div className="animate-pulse bg-gray-200 rounded-2xl h-64" />}>
                            <VideoCard
                              job={job}
                              user={user}
                              isMultiSelectMode={animatedMemoryMultiSelectMode}
                              isSelected={selectedAnimatedMemories.includes(job.id)}
                              onToggleSelect={(id) => {
                                setSelectedAnimatedMemories(prev =>
                                  prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
                                );
                              }}
                              onVideoClick={setFullScreenVideo}
                              onFailedClick={setShowFailedDetails}
                              onPendingClick={() => toast.info('Video is still processing')}
                              onEditClick={() => {}}
                              onDeleteClick={(id) => {
                                if (confirm('Delete this animated moment?')) {
                                  handleDeleteVideo(id);
                                }
                              }}
                              onAddToAlbumClick={(job) => {
                                setSelectedForAlbum(job);
                                setShowAlbumModal(true);
                              }}
                              expandedPrompts={expandedPrompts}
                              setExpandedPrompts={setExpandedPrompts}
                            />
                          </Suspense>
                        ))}
                      </div>
                      
                      {/* Bottom pagination for mobile convenience */}
                      {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-6 pt-6 border-t border-amber-200">
                          <button
                            onClick={() => {
                              setAnimatedMemoryPage(Math.max(1, currentPage - 1));
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            disabled={currentPage === 1}
                            className="px-2 sm:px-3 py-1 sm:py-2 bg-gray-100 text-gray-700 rounded border border-gray-300 text-xs sm:text-sm hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            ← Prev
                          </button>
                          <span className="text-amber-700 text-xs sm:text-sm font-medium">
                            {currentPage}/{totalPages}
                          </span>
                          <button
                            onClick={() => {
                              setAnimatedMemoryPage(Math.min(totalPages, currentPage + 1));
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            disabled={currentPage === totalPages}
                            className="px-2 sm:px-3 py-1 sm:py-2 bg-gray-100 text-gray-700 rounded border border-gray-300 text-xs sm:text-sm hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next →
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
                
                {/* Pending Videos Section (togglable) - Now integrated into main grid */}
                {false && showPendingVideos && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-amber-700 mb-4 flex items-center">
                      ⏳ Processing Videos ({videoJobs.filter(job => ['queued', 'processing', 'submitted'].includes(job.status)).length})
                    </h4>
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {videoJobs.filter(job => ['queued', 'processing', 'submitted'].includes(job.status)).map(job => (
                        <div 
                          key={job.id} 
                          onClick={() => {
                            setSelectedForAlbum({
                              id: job.id,
                              url: job.thumbnail_url || job.imageUrl || (job.image_id ? `${getAPI()}/files/${job.image_id}?user_id=${user?.id}` : ''),
                              name: job.parameters?.prompt || 'Processing Video',
                              type: 'image',
                              status: job.status,
                              created_at: job.created_at
                            });
                            setShowAlbumModal(true);
                          }}
                          className="bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl border border-amber-200 overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-all duration-200 hover:scale-105"
                        >
                          <div className="aspect-video bg-amber-100 relative">
                            {(() => {
                              const imageUrl = job.imageUrl || job.thumbnail_url || (job.image_id ? `${getAPI()}/files/${encodeURIComponent(job.image_id)}?user_id=${user?.id}` : '');
                              console.log('🖼️ Pending video image debug:', { 
                                imageUrl: job.imageUrl, 
                                thumbnail_url: job.thumbnail_url, 
                                image_id: job.image_id,
                                finalUrl: imageUrl 
                              });
                              
                              if (!imageUrl) {
                                // Show placeholder if no URL available
                                return (
                                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100">
                                    <div className="text-center">
                                      <svg className="w-16 h-16 mx-auto text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <p className="text-xs text-amber-600 opacity-70">Processing...</p>
                                    </div>
                                  </div>
                                );
                              }
                              
                              return (
                                <img loading="lazy"
                                  src={imageUrl}
                                  alt="Source image"
                                  className="w-full h-full object-cover opacity-50"
                                  onError={(e) => {
                                    console.error('❌ Image load failed for:', imageUrl);
                                    // If image fails to load, try sanitized version
                                    if (!e.target.dataset.retried && job.image_id) {
                                      e.target.dataset.retried = 'true';
                                      const sanitizedUrl = `${getAPI()}/files/${encodeURIComponent(job.image_id.replace(/[^a-zA-Z0-9-]/g, '_'))}?user_id=${user?.id}`;
                                      console.log('🔄 Retrying with sanitized URL:', sanitizedUrl);
                                      e.target.src = sanitizedUrl;
                                    } else {
                                      // Show placeholder on final failure
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `
                                        <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100">
                                          <div class="text-center">
                                            <svg class="w-16 h-16 mx-auto text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <p class="text-xs text-amber-600 opacity-70">Processing...</p>
                                          </div>
                                        </div>
                                      `;
                                    }
                                  }}
                                />
                              );
                            })()}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                                <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            <h4 className="font-semibold text-amber-800 mb-2">{job.parameters?.prompt || 'Living Memory'}</h4>
                            <p className="text-sm text-amber-600 mb-2">Project: {job.projectName}</p>
                            <p className="text-xs text-amber-500 italic">Click to view original image</p>
                            
                            {/* Progress bar with detailed steps */}
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-xs text-amber-600 mb-1">
                                <span className="font-medium">{job.step || 'Processing'}</span>
                                <span className="font-bold">{job.progress || 0}%</span>
                              </div>
                              <div className="w-full bg-amber-200 rounded-full h-3">
                                <div 
                                  className="bg-gradient-to-r from-amber-500 to-orange-500 h-3 rounded-full transition-all duration-1000 flex items-center justify-end pr-2"
                                  style={{width: `${Math.max(job.progress || 0, 5)}%`}}
                                >
                                  {(job.progress || 0) > 15 && (
                                    <div className="w-1 h-1 bg-white rounded-full animate-pulse"></div>
                                  )}
                                </div>
                              </div>
                              <div className="text-xs text-amber-500 mt-1 italic">
                                {job.step ? `Current step: ${job.step}` : 'Initializing...'}
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between mt-3">
                              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                                ✨ {job.status}
                              </span>
                              <span className="text-xs text-amber-600">
                                ETA: ~{Math.max(1, 5 - Math.floor((job.progress || 0) / 20))} min
                              </span>
                            </div>
                            
                            <p className="text-xs text-amber-500 mt-2">
                              Started: {new Date(job.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Failed Videos Section (togglable) - Now integrated into main grid */}
                {false && showFailedVideos && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-red-700 mb-4 flex items-center">
                      ❌ Failed Generations ({videoJobs.filter(job => job.status === 'failed' && !job.is_deleted).length})
                    </h4>
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {videoJobs.filter(job => job.status === 'failed' && !job.is_deleted).map(job => (
                        <div key={job.id} className="bg-gradient-to-br from-white/80 to-red-50/80 rounded-2xl border border-red-200 overflow-hidden shadow-lg">
                          <div className="aspect-video bg-red-100 relative">
                            <img loading="lazy"
                              src={job.imageUrl || job.thumbnail_url || `/api/files/${job.image_id}`}
                              alt="Source image" 
                              className="w-full h-full object-cover opacity-50"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                                <span className="text-2xl">❌</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            <h4 className="font-semibold text-red-800 mb-2">{job.parameters?.prompt || 'Failed Generation'}</h4>
                            <p className="text-sm text-red-600">Project: {job.projectName}</p>
                            <p className="text-xs text-red-500 mt-2">
                              Failed: {new Date(job.completed_at || job.created_at).toLocaleString()}
                            </p>
                            
                            <div className="flex space-x-2 mt-3">
                              <button
                                onClick={() => setShowFailedDetails(job)}
                                className="flex-1 bg-red-100 text-red-700 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                              >
                                📋 View Details
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this failed generation?')) {
                                    handleDeleteVideo(job.id);
                                  }
                                }}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Empty state for living memories */}
                {(() => {
                  // Don't show empty state if we know there are images with videos (prevents false empty state during rate limiting)
                  if (imagesWithVideos.size > 0) {
                    return false;
                  }
                  
                  // Calculate if there should be any videos displayed based on current toggle states
                  const completedVideos = videoJobs.filter(job => job.status === 'completed');
                  const failedVideos = videoJobs.filter(job => ['failed', 'timeout', 'error'].includes(job.status));
                  const pendingVideos = videoJobs.filter(job => ['pending', 'queued', 'processing', 'submitted'].includes(job.status));
                  
                  let shouldShowVideos = completedVideos.length > 0; // Always show completed
                  if (showFailedVideos) shouldShowVideos = shouldShowVideos || failedVideos.length > 0;
                  if (showPendingVideos) shouldShowVideos = shouldShowVideos || pendingVideos.length > 0;
                  
                  return !shouldShowVideos;
                })() && (
                  <div className="text-center py-16 bg-gradient-to-br from-white/80 to-amber-50/80 backdrop-blur-sm rounded-3xl border border-amber-200">
                    <div className="w-24 h-24 bg-gradient-to-br from-amber-200 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-12 h-12 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-amber-800 mb-2">Create your first animated moment</h3>
                    <p className="text-amber-600 mb-6 max-w-md mx-auto">
                      Generate a video from your photos to bring your memories to life
                    </p>
                    <button
                      onClick={() => setShowCreateLivingMemory(true)}
                      className="text-white px-8 py-4 rounded-2xl transition-all font-semibold text-lg flex items-center gap-2 mx-auto"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        animation: 'pulseGlow 2.5s ease-in-out infinite'
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      Create Animated Moment
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Failed Videos View (now as subtab) */}
            {currentView === 'living_memories' && showFailedSubtab && (
              <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-red-800">Failed Videos</h3>
                  <div className="flex space-x-2">
                    {!animatedMemoryMultiSelectMode ? (
                      <button
                        onClick={() => {
                          setAnimatedMemoryMultiSelectMode(true);
                          setSelectedAnimatedMemories([]);
                        }}
                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-200 transition-colors border border-gray-300"
                      >
                        ☑️
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            const failedMemoryIds = videoJobs.filter(job => ['failed', 'timeout', 'error'].includes(job.status) && !job.is_deleted).map(job => job.id);
                            if (selectedAnimatedMemories.length === failedMemoryIds.length) {
                              setSelectedAnimatedMemories([]); // Deselect all
                            } else {
                              setSelectedAnimatedMemories(failedMemoryIds); // Select all
                            }
                          }}
                          className={`px-6 py-2.5 rounded-xl font-medium transition-all transform hover:scale-105 shadow-md hover:shadow-lg ${
                            selectedAnimatedMemories.length > 0
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
                              : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300'
                          }`}
                        >
                          {selectedAnimatedMemories.length === videoJobs.filter(job => ['failed', 'timeout', 'error'].includes(job.status) && !job.is_deleted).length && selectedAnimatedMemories.length > 0
                            ? '✅ Deselect All' 
                            : '☑️ Select All'}
                        </button>
                        <button
                          onClick={() => {
                            setAnimatedMemoryMultiSelectMode(false);
                            setSelectedAnimatedMemories([]);
                          }}
                          className="bg-gradient-to-r from-gray-400 to-gray-500 text-white px-6 py-2.5 rounded-xl font-medium hover:from-gray-500 hover:to-gray-600 transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    {selectedAnimatedMemories.length > 0 && (
                      <button
                        onClick={handleDeleteSelectedAnimatedMemories}
                        className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-6 py-2.5 rounded-xl font-medium hover:from-red-600 hover:to-rose-700 transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
                      >
                        🗑️ Delete Selected ({selectedAnimatedMemories.length})
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videoJobs.filter(job => ['failed', 'timeout', 'error'].includes(job.status) && !job.is_deleted).map(job => (
                    <div key={job.id} className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl border border-red-200 overflow-hidden shadow-lg relative">
                      {/* Multi-select overlay - prevents background interactions when in select mode */}
                      {animatedMemoryMultiSelectMode && (
                        <div 
                          className="absolute inset-0 z-10 cursor-pointer bg-black/10 rounded-2xl"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Toggle checkbox selection
                            if (selectedAnimatedMemories.includes(job.id)) {
                              setSelectedAnimatedMemories(selectedAnimatedMemories.filter(id => id !== job.id));
                            } else {
                              setSelectedAnimatedMemories([...selectedAnimatedMemories, job.id]);
                            }
                          }}
                        />
                      )}
                      
                      {/* Multi-select checkbox - only show in multi-select mode */}
                      {animatedMemoryMultiSelectMode && (
                        <div className="absolute top-2 left-2 z-20">
                          <div 
                            className={`w-4 h-4 rounded flex items-center justify-center cursor-pointer transition-all ${
                              selectedAnimatedMemories.includes(job.id)
                                ? 'bg-red-600 border-2 border-red-600'
                                : 'bg-white border-2 border-red-300'
                            }`}
                            style={{ 
                              WebkitAppearance: 'none',
                              appearance: 'none'
                            }}
                          >
                            {selectedAnimatedMemories.includes(job.id) && (
                              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="aspect-video bg-red-100 relative">
                        <img loading="lazy"
                          src={job.imageUrl || job.thumbnail_url}
                          alt="Source image"
                          className="w-full h-full object-cover opacity-50"
                        />
                        
                        {/* Failed overlay */}
                        <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </div>
                            <p className="text-white font-semibold">Failed</p>
                            <p className="text-red-100 text-sm mt-1">{job.status}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-4">
                        <p className="font-medium text-red-800 truncate mb-1">{job.prompt || 'Memory Creation'}</p>
                        <p className="text-sm text-red-600 mb-3">
                          Failed: {new Date(job.updated_at).toLocaleString()}
                        </p>
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setFailedModalJob(job)}
                            className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                          >
                            View Details
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await axios.delete(`${getAPI()}/video-generations/${job.id}?user_id=${user.id}`);
                                toast.success('Failed video deleted');
                                await loadDashboardData();
                                await loadVideoJobs();
                              } catch (error) {
                                console.error('Delete error:', error);
                                toast.error('Failed to delete video');
                              }
                            }}
                            className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Empty state for failed */}
                {videoJobs.filter(job => ['failed', 'timeout', 'error'].includes(job.status) && !job.is_deleted).length === 0 && (
                  <div className="text-center py-16 bg-gradient-to-br from-white/80 to-red-50/80 backdrop-blur-sm rounded-3xl border border-red-200">
                    <div className="w-24 h-24 bg-gradient-to-br from-red-200 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      ❌
                    </div>
                    <h3 className="text-xl font-bold text-red-800 mb-2">No failed videos</h3>
                    <p className="text-red-600 mb-6 max-w-md mx-auto">
                      Great! All your video generations have been successful
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Projects Grid Container - OLD VIEW DISABLED - Using new AlbumsTab component */}
            {/* {currentView === 'projects' && (
              <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
                
                {/* Empty State */}
                {/* {projects.filter(p => showArchived ? p.is_archived : !p.is_archived).length === 0 && (
                  <div className="col-span-full">
                    <div className="text-center py-16 bg-gradient-to-br from-white/80 to-amber-50/80 backdrop-blur-sm rounded-3xl border border-amber-200">
                      <div className="w-24 h-24 bg-gradient-to-br from-amber-200 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-6">
                        {showArchived ? '📦' : '✨'}
                      </div>
                      <h3 className="text-xl font-bold text-amber-800 mb-2">
                        {showArchived ? 'No archived projects yet' : 'Your memory journey starts here'}
                      </h3>
                      <p className="text-amber-600 mb-6 max-w-md mx-auto">
                        {showArchived 
                          ? 'Archived projects will appear here for safekeeping'
                          : 'Upload your precious photos and watch them come alive through time'
                        }
                      </p>
                      
                      {!showArchived && (
                        <button
                          onClick={() => setShowNewProject(true)}
                          className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl hover:from-amber-600 hover:via-orange-600 hover:to-red-600 transition-all transform hover:scale-105 font-semibold text-lg shadow-lg"
                        >
                          ✨ Start Time Traveling
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )} */}
          </div>

      </div>
      )}

        {/* Modals */}
        {showNewProject && <MemoryNewProjectModal onClose={() => setShowNewProject(false)} onRefresh={loadDashboardData} />}
      {showUpload && <MemoryPhotoUpload onClose={() => setShowUpload(false)} onUpload={(image) => console.log('Uploaded:', image)} user={user} />}
      {showProfile && <MemoryProfileModalPortal 
        user={user} 
        onClose={() => setShowProfile(false)}
        onNavigateToProjects={() => {
          setCurrentView('projects');
          setProjectSubtab('my');
          setShowArchived(false);
        }}
        onProfileUpdate={(updatedUser) => {
          // Update user context with new data
          setUser(updatedUser);
          
          // If we're impersonating, also update localStorage
          const impersonatedUser = localStorage.getItem('impersonatedUser');
          if (impersonatedUser) {
            const parsedImpersonated = JSON.parse(impersonatedUser);
            if (parsedImpersonated.id === updatedUser.id) {
              localStorage.setItem('impersonatedUser', JSON.stringify(updatedUser));
            }
          }
        }}
        onUserSwitch={(newUser) => {
          // Store impersonation in localStorage
          localStorage.setItem('impersonatedUser', JSON.stringify(newUser));
          localStorage.setItem('originalAdmin', JSON.stringify(user));
          
          setUser(newUser);
          toast.success(`Switched to ${newUser.email}`);
          // Reload page to refresh all data for new user
          setTimeout(() => window.location.reload(), 500);
        }}
      />}
      {showCredits && <MemoryCreditsModal onClose={() => setShowCredits(false)} onRefresh={loadDashboardData} />}
      {showBuyCredits && <BuyCreditsModalPortal isOpen={showBuyCredits} onClose={() => setShowBuyCredits(false)} user={user} onPurchaseComplete={loadDashboardData} />}
      {showAnalysisLimitModal && <AnalysisLimitModal isOpen={showAnalysisLimitModal} onClose={() => setShowAnalysisLimitModal(false)} user={user} />}
      {showEarnCredits && <EarnCreditsModal isOpen={showEarnCredits} onClose={() => setShowEarnCredits(false)} user={user} onCreditsUpdated={async () => {
        // Refresh user data to get updated credits
        try {
          const response = await axios.get(`${getAPI()}/auth/me?user_id=${user.id}`);
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          console.error('Failed to refresh user data:', error);
        }
      }} />}
      
      {/* Batch Analysis Warning Modal */}
      {showBatchWarning && batchWarningData && (
        <BatchAnalysisWarningModalPortal
          imageCount={batchWarningData.imageCount}
          freeRemaining={batchWarningData.freeRemaining}
          creditsNeeded={batchWarningData.creditsNeeded}
          onConfirm={() => {
            setShowBatchWarning(false);
            proceedWithBatchAnimation(pendingBatchImages);
          }}
          onCancel={() => {
            setShowBatchWarning(false);
            setBatchWarningData(null);
            // Open batch animation generator without AI analysis (free)
            proceedWithBatchAnimationNoAnalysis(pendingBatchImages);
            setPendingBatchImages([]);
          }}
          onClose={() => {
            setShowBatchWarning(false);
            setBatchWarningData(null);
            setPendingBatchImages([]);
            // Close the batch animation generator modal completely
            setShowBatchAnimation(false);
          }}
          onDontShowAgain={async () => {
            try {
              const BACKEND_URL = getBACKEND_URL();
              await axios.post(`${BACKEND_URL}/api/update-batch-warning-preference`, {
                user_id: user.id,
                hide_warning: true
              });
              // Update user state
              setUser(prev => ({ ...prev, hide_batch_warning: true }));
              console.log('✅ User preference updated: hide batch warning');
            } catch (error) {
              console.error('Failed to update warning preference:', error);
            }
          }}
        />
      )}
      
      {/* Single Image Analysis Warning Modal */}
      {showSingleAnalysisWarning && singleAnalysisWarningData && (
        <SingleImageAnalysisWarningModalPortal
          imageData={singleAnalysisWarningData.imageData}
          freeRemaining={singleAnalysisWarningData.freeRemaining}
          creditsNeeded={singleAnalysisWarningData.creditsNeeded}
          onConfirm={async () => {
            setShowSingleAnalysisWarning(false);
            
            // Deduct cost
            try {
              const BACKEND_URL = getBACKEND_URL();
              const deductResponse = await axios.post(`${BACKEND_URL}/api/deduct-analysis-cost`, {
                user_id: user.id,
                analysis_count: 1
              });
              if (deductResponse.data.new_credits !== undefined) {
                setUser(prev => ({ ...prev, credits: deductResponse.data.new_credits }));
              }
            } catch (error) {
              console.error('Failed to deduct cost:', error);
            }
            
            // Execute the pending action
            if (pendingSingleImageAction) {
              pendingSingleImageAction();
              setPendingSingleImageAction(null);
            }
            setSingleAnalysisWarningData(null);
          }}
          onCancel={() => {
            setShowSingleAnalysisWarning(false);
            setSingleAnalysisWarningData(null);
            // Proceed without AI analysis (free)
            console.log('🆓 User clicked "Proceed without analysis" - setting skip flags to TRUE');
            setSkipAnalysisFlag(true); // Set state flag
            skipAnalysisRef.current = true; // Set ref immediately for synchronous access
            if (pendingSingleImageAction) {
              console.log('🚀 Executing pending action with skipAnalysisRef.current =', skipAnalysisRef.current);
              pendingSingleImageAction();
              setPendingSingleImageAction(null);
            }
          }}
          onClose={() => {
            setShowSingleAnalysisWarning(false);
            setSingleAnalysisWarningData(null);
            setPendingSingleImageAction(null);
            // Close the animation generator modal completely
            setSelectedImageForAnimation(null);
          }}
          onDontShowAgain={async () => {
            try {
              const BACKEND_URL = getBACKEND_URL();
              await axios.post(`${BACKEND_URL}/api/update-batch-warning-preference`, {
                user_id: user.id,
                hide_warning: true
              });
              setUser(prev => ({ ...prev, hide_batch_warning: true }));
              console.log('✅ User preference updated: hide single analysis warning');
            } catch (error) {
              console.error('Failed to update warning preference:', error);
            }
          }}
        />
      )}
      
      {/* Batch Animation Modal */}
      {/* Verbose log removed for production */}
      {showBatchAnimation && (
        <BatchAnimationModalPortal
          images={batchAnimationImages}
          prompts={batchPrompts}
          isSubmitting={isBatchSubmitting}
          user={user}
          regenerationCounts={regenerationCounts}
          freeRegenerationsLimit={FREE_REGENERATIONS_LIMIT}
          usedAIAnalysis={usedAIAnalysis}
          isBatchAnalyzing={isBatchAnalyzing}
          analysisCompleted={analysisCompleted}
          estimatedProgress={estimatedProgress}
          onCancelAnalysis={cancelBatchAnalysis}
          onClose={handleCancelBatchAnimation}
          onRegeneratePrompt={async (imageId) => {
            // Check regeneration count for this image
            const currentCount = regenerationCounts[imageId] || 0;
            
            // Always charge for regenerations (no free regenerations)
            if (currentCount >= FREE_REGENERATIONS_LIMIT) {
              const CREDITS_NEEDED = 0.05; // 0.05 credits per regeneration
              if (user.credits < CREDITS_NEEDED) {
                toast.error(`Insufficient credits! Need ${CREDITS_NEEDED.toFixed(2)} credits for regeneration.`, {
                  duration: 4000
                });
                return;
              }
              
              // Confirm with user
              if (!window.confirm(`This will cost ${CREDITS_NEEDED.toFixed(2)} credits to generate a new prompt. Continue?`)) {
                return;
              }
            }
            
            setBatchPrompts(prev => ({
              ...prev,
              [imageId]: { ...prev[imageId], isGenerating: true }
            }));
            
            try {
              const img = batchAnimationImages.find(i => i.id === imageId);
              console.log('🧠 Using shared AI analysis brain to regenerate prompt for:', imageId);
              
              // Generate unique job ID for tracking
              const jobId = `analysis_${imageId}_${Date.now()}`;
              batchAnalysisJobIds.current.push(jobId);
              
              const result = await analyzeImageWithAI(img, null, user, jobId);
              
              // Remove completed job from tracking
              batchAnalysisJobIds.current = batchAnalysisJobIds.current.filter(id => id !== jobId);
              
              // Handle both array and object response from analyzeImageWithAI
              let suggestions;
              if (result && typeof result === 'object' && !Array.isArray(result)) {
                suggestions = result.suggestions || [];
              } else if (Array.isArray(result)) {
                suggestions = result;
              } else {
                suggestions = [];
              }
              
              // Get suggestions array and pick a random one for variety
              const randomPrompt = suggestions.length > 0 
                ? suggestions[Math.floor(Math.random() * suggestions.length)]
                : 'Gentle movement and subtle animation';
              
              setBatchPrompts(prev => ({
                ...prev,
                [imageId]: { prompt: randomPrompt, isGenerating: false }
              }));
              
              // Increment regeneration count
              setRegenerationCounts(prev => ({
                ...prev,
                [imageId]: currentCount + 1
              }));
              
              // Deduct credits if over free limit
              if (currentCount >= FREE_REGENERATIONS_LIMIT) {
                const BACKEND_URL = getBACKEND_URL();
                try {
                  const deductResponse = await axios.post(`${BACKEND_URL}/api/deduct-analysis-cost`, {
                    user_id: user.id,
                    analysis_count: 1
                  });
                  console.log('✅ Regeneration cost deducted:', deductResponse.data);
                  
                  // Update user credits
                  if (deductResponse.data.new_credits !== undefined) {
                    setUser(prev => ({ ...prev, credits: deductResponse.data.new_credits }));
                  }
                  
                  // Removed toast: 0.05 credits used
                } catch (error) {
                  console.error('Failed to deduct regeneration cost:', error);
                  toast.error('Regeneration succeeded but failed to deduct credits');
                }
              } else {
                // Show how many free regenerations remain
                const remaining = FREE_REGENERATIONS_LIMIT - (currentCount + 1);
                if (remaining > 0) {
                  toast.success(`Prompt regenerated! (${remaining} free ${remaining === 1 ? 'regeneration' : 'regenerations'} left)`);
                } else {
                  toast.success('Prompt regenerated! (Future regenerations will cost credits)');
                }
              }
            } catch (error) {
              toast.error('Failed to regenerate prompt');
              setBatchPrompts(prev => ({
                ...prev,
                [imageId]: { ...prev[imageId], isGenerating: false }
              }));
            }
          }}
          onUpdatePrompt={(imageId, newPrompt) => {
            setBatchPrompts(prev => ({
              ...prev,
              [imageId]: { prompt: newPrompt, isGenerating: false }
            }));
          }}
          onEditImage={(image) => {
            setBatchEditingImage(image);
          }}
          onSubmitBatch={async () => {
            if (isBatchSubmitting) {
              console.log('⚠️ Batch submission already in progress, ignoring click');
              return;
            }
            
            setIsBatchSubmitting(true);
            console.log('🚀 Starting PARALLEL batch submission...');
            console.log('📊 DEBUG: batchAnimationImages count:', batchAnimationImages.length);
            console.log('📊 DEBUG: batchAnimationImages:', batchAnimationImages);
            console.log('📊 DEBUG: batchPrompts:', batchPrompts);
            const startTime = Date.now();
            
            // Get backend URL at runtime (CRITICAL FIX)
            const BACKEND_URL = getBACKEND_URL();
            console.log('🔍 DEBUG: BACKEND_URL resolved to:', BACKEND_URL);
            
            try {
              // Create array of submission promises to run in parallel
              const submissionPromises = batchAnimationImages.map(async (img, index) => {
                console.log(`🔍 DEBUG [${index}]: Processing image`, img.id);
                const prompt = batchPrompts[img.id]?.prompt;
                console.log(`🔍 DEBUG [${index}]: Prompt for ${img.id}:`, prompt);
                if (!prompt) {
                  console.warn(`⚠️ DEBUG [${index}]: No prompt found for image ${img.id}, skipping`);
                  return { success: false, skipped: true };
                }
                
                try {
                  const formData = new FormData();
                  formData.append('image_id', img.id);
                  formData.append('prompt', prompt);
                  formData.append('provider', 'luma');
                  formData.append('duration', '5');
                  
                  // Add album context if present
                  // Ensure album context is present; derive from image if needed
                  let effectiveAlbumId = albumContext?.albumId;
                  if (!effectiveAlbumId && img.album_id) effectiveAlbumId = img.album_id;
                  // SAFETY: Guard against undeclared selectedAlbum in non-album (Pictures) flows
                  if (!effectiveAlbumId && typeof selectedAlbum !== 'undefined' && selectedAlbum && selectedAlbum.id) {
                    effectiveAlbumId = selectedAlbum.id; // safety fallback
                  }

                  if (effectiveAlbumId) {
                    formData.append('album_id', effectiveAlbumId);
                    formData.append('album_only', (albumContext?.albumOnly ?? true) ? 'true' : 'false');
                  } else {
                    console.warn('⚠️ No album context available for batch submit; this may fail backend auth.');
                  }
                  
                  console.log(`🚀 Submitting video for image: ${img.id}${albumContext ? ` (album: ${albumContext.albumId})` : ''}`);
                  console.log(`🔍 DEBUG [${index}]: About to call axios.post for ${img.id}`);
                  console.log(`🔍 DEBUG [${index}]: BACKEND_URL:`, BACKEND_URL);
                  console.log(`🔍 DEBUG [${index}]: Full URL:`, `${BACKEND_URL}/api/generate`);
                  
                  formData.append('user_id', user.id);
                  console.log(`🔍 DEBUG [${index}]: FormData prepared, making axios call...`);
                  const response = await axios.post(`${BACKEND_URL}/api/generate`, formData);
                  console.log(`🔍 DEBUG [${index}]: axios.post completed for ${img.id}`);
                  console.log(`✅ Successfully submitted video for ${img.id}`);
                  
                  // IMMEDIATE UI UPDATE: Add this job to state right away (don't wait for all submissions)
                  if (response.data?.job) {
                    console.log(`📌 IMMEDIATE: Adding job for ${img.id} to UI`);
                    setVideoJobs(prevJobs => [response.data.job, ...prevJobs]);
                    
                    // DON'T call albumRefreshCallback here! Backend will send WebSocket event.
                    // The WebSocket handler will call loadAlbumDetails and show the card.
                    // Calling albumRefreshCallback here causes conflicts with WebSocket timing.
                    console.log(`⏭️ Skipping albumRefreshCallback - WebSocket will handle card display`);
                  }
                  
                  return { success: true, imageId: img.id, data: response.data };
                } catch (error) {
                  console.error(`❌ DEBUG [${index}]: Failed to submit ${img.id}:`, error);
                  console.error(`❌ DEBUG [${index}]: Error details:`, error.response?.data || error.message);
                  return { success: false, imageId: img.id, error };
                }
              });
              
              // Wait for all submissions to complete in parallel
              const results = await Promise.all(submissionPromises);
              const endTime = Date.now();
              const duration = ((endTime - startTime) / 1000).toFixed(2);
              
              // Count successes and failures
              const successCount = results.filter(r => r.success).length;
              const failCount = results.filter(r => r.success === false && !r.skipped).length;
              
              console.log(`⚡ Parallel submission completed in ${duration}s (${successCount} success, ${failCount} failed)`);
              console.log(`📌 Note: Jobs were added to UI immediately as each submission completed`)
              
              // Clear job tracking since submission is complete
              batchAnalysisJobIds.current = [];
              
              // Close batch modal immediately
              setShowBatchAnimation(false);
              setBatchAnimationImages([]);
              setBatchPrompts({});
              setMultiSelectMode(false);
              setSelectedPictures([]);
              
              if (successCount > 0) {
                console.log('🔍 POST-SUBMIT BATCH ANIMATION - Checking for album context');
                console.log('📊 albumContext:', JSON.stringify(albumContext));
                console.log('📊 currentView:', currentView);
                
                // PICTURES TAB: Redirect immediately to Moments tab (don't wait)
                // Check BOTH albumContext AND currentView to avoid stale albumContext from previous album visit
                const isActuallyInAlbum = albumContext?.albumId && currentView === 'projects';
                console.log('🔍 isActuallyInAlbum:', isActuallyInAlbum, '(albumContext:', !!albumContext?.albumId, ', currentView:', currentView, ')');
                
                if (!isActuallyInAlbum) {
                  console.log('📍 PICTURES TAB DETECTED - Immediate redirect to Moments tab');
                  setCurrentView('living_memories');
                  setActiveMainTab('moments');
                  setShowPendingVideos(true);
                  
                  // Show success message immediately
                  toast.success(`✨ ${successCount} video${successCount > 1 ? 's' : ''} submitted in ${duration}s!`);
                  return; // Exit early, no overlay needed for Pictures tab
                }
                
                // ALBUM CONTEXT: Show overlay and handle album-specific logic
                console.log('✅✅✅ ALBUM CONTEXT DETECTED ✅✅✅');
                setShowMagicProcessing(true);
                setProcessingMessage(`Animating ${successCount} precious moment${successCount > 1 ? 's' : ''}...`);
                setProcessingCount(successCount);
                
                console.log(`✨ Showing overlay for 2 seconds...`);
                
                // Note: Album refresh happens immediately after each submission (see above)
                // This is just for the overlay and final navigation
                setTimeout(() => {
                  console.log('⏰ 2 seconds elapsed, hiding overlay');
                  
                  // Hide overlay
                  setShowMagicProcessing(false);
                  
                  // Show success message
                  toast.success(`✨ ${successCount} video${successCount > 1 ? 's' : ''} submitted in ${duration}s!`);
                  
                  console.log('🔄🔄🔄 BATCH ANIMATION SUBMITTED - FORCING MOMENTS VIEW SWITCH 🔄🔄🔄');
                  
                  // Force switch to moments view
                  if (albumFilterSwitchCallback.current && typeof albumFilterSwitchCallback.current === 'function') {
                    console.log('✅ Calling albumFilterSwitchCallback to switch to moments (batch)');
                    try {
                      albumFilterSwitchCallback.current('moments');
                      console.log('✅ albumFilterSwitchCallback executed successfully');
                    } catch (error) {
                      console.error('❌ Error calling albumFilterSwitchCallback:', error);
                    }
                  } else {
                    console.error('❌❌❌ albumFilterSwitchCallback NOT AVAILABLE OR NOT A FUNCTION ❌❌❌');
                    console.error('Callback current value:', albumFilterSwitchCallback.current);
                  }
                  
                  // Auto-scroll to first processing video after a short delay to ensure DOM is updated
                  setTimeout(() => {
                    // Find first video element that's processing/pending
                    const processingVideo = document.querySelector('[data-video-status="processing"], [data-video-status="queued"], [data-video-status="submitted"]');
                    if (processingVideo) {
                      console.log('📍 Auto-scrolling to first processing video');
                      processingVideo.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center',
                        inline: 'nearest'
                      });
                    }
                  }, 500);
                }, 2000); // 2 second delay for album only
              }
              
              if (failCount > 0) {
                toast.error(`❌ ${failCount} video${failCount > 1 ? 's' : ''} failed to submit`);
              }
            } catch (error) {
              toast.error('Failed to submit batch');
              console.error('Batch submission error:', error);
              // Clear job tracking on error
              batchAnalysisJobIds.current = [];
            } finally {
              setIsBatchSubmitting(false);
              console.log('✅ Batch submission complete');
            }
          }}
        />
      )}
      
      {editingMemory && <ImageEditorModalPortal 
        image={editingMemory} 
        context="pictures"
        user={user}
        onClose={() => setEditingMemory(null)} 
        onSave={async (updatedImage) => {
          console.log('💾 Image saved - will wait for backend to process');
          setEditingMemory(null);
          
          const imageId = editingMemory.id;
          const isRevert = updatedImage && updatedImage.reverted;
          
          console.log(`🔄 ${isRevert ? 'Revert' : 'Edit'} action - waiting for backend to update`);
          
          // Wait for backend to process (give it time)
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Generate timestamp AFTER backend has processed
          const saveTimestamp = Date.now();
          console.log('🔄 Generated save timestamp AFTER backend processing:', saveTimestamp);
          localStorage.setItem(`image_save_timestamp_${imageId}`, saveTimestamp.toString());
          
          // Now reload dashboard - backend should have fresh data
          await loadDashboardData();
          await loadVideoJobs();
          
          console.log('✅ Dashboard reloaded with backend-processed changes');
        }} 
      />}
      {showAlbumModal && selectedForAlbum && <AlbumModal item={selectedForAlbum} projects={projects} user={user} onClose={() => {setShowAlbumModal(false); setSelectedForAlbum(null);}} onRefresh={loadDashboardData} />}
      {showFailedDetails && <FailedVideoDetailsModalPortal job={showFailedDetails} onClose={() => setShowFailedDetails(null)} onRetry={handleRetryGeneration} onDelete={handleDeleteVideo} />}
      {fullScreenImage && (() => {
        const imageList = sortedPictures;
        const currentIndex = imageList.findIndex(img => img.id === fullScreenImage.id);
        
        return (
          <FullScreenImageModal 
            image={fullScreenImage} 
            imageList={imageList}
            currentIndex={currentIndex}
            user={user}
            onClose={() => setFullScreenImage(null)} 
            onNavigate={(newIndex) => {
              if (newIndex >= 0 && newIndex < imageList.length) {
                setFullScreenImage(imageList[newIndex]);
              }
            }}
            onAnimate={(image) => {
              const cacheBustedImage = addCacheBustingToImage({
                ...image,
                projectName: fullScreenImage.projectName || 'Unassigned Pictures',
                projectId: fullScreenImage.projectId || 'unassigned'
              });
              setSelectedImageForAnimation(cacheBustedImage);
              setShowCreateLivingMemory(true);
            }}
            onEdit={(image) => {
              setEditingMemory(image);
            }}
            onDelete={async (image) => {
              try {
                await axios.delete(`${getAPI()}/unassigned-images/${image.id}?user_id=${user.id}`);
                // Removed toast: Picture deleted successfully
                loadDashboardData();
              } catch (error) {
                console.error('Failed to delete image:', error);
                toast.error('Failed to delete picture. Please try again.');
              }
            }}
            onCopyToFolder={(image) => {
              setCopyingImage(image);
              setShowCreateFolderInCopyModal(false);
              setNewFolderNameInCopyModal('');
              setShowCopyToFolderModal(true);
            }}
            imagesWithVideos={imagesWithVideos}
            selectedFolder={selectedFolder}
          />
        );
      })()}
      {fullScreenVideo && <FullScreenVideoModal 
        video={fullScreenVideo} 
        videoList={fullScreenVideo._returnToGallery ? fullScreenVideo._returnToGallery.videos : videoJobs.filter(job => ['completed', 'success'].includes(job.status) && !job.is_deleted)}
        currentIndex={fullScreenVideo._returnToGallery ? fullScreenVideo._returnToGallery.videos.findIndex(v => v.id === fullScreenVideo.id) : videoJobs.filter(job => ['completed', 'success'].includes(job.status) && !job.is_deleted).findIndex(job => job.id === fullScreenVideo.id)}
        user={user}
        onNavigate={(index) => {
          const videoList = fullScreenVideo._returnToGallery ? fullScreenVideo._returnToGallery.videos : videoJobs.filter(job => ['completed', 'success'].includes(job.status) && !job.is_deleted);
          const selectedVideo = videoList[index];
          if (selectedVideo) {
            // Preserve the _returnToGallery state when navigating
            const videoWithReturn = fullScreenVideo._returnToGallery ? {
              ...selectedVideo,
              _returnToGallery: fullScreenVideo._returnToGallery
            } : selectedVideo;
            setFullScreenVideo(videoWithReturn);
          }
        }}
        onClose={() => {
          // Check if we should return to video gallery
          if (fullScreenVideo._returnToGallery) {
            setShowVideoGallery(fullScreenVideo._returnToGallery.imageId);
          }
          setFullScreenVideo(null);
        }} 
        onEdit={setEditingMemory}
        onDelete={async (videoId) => {
          try {
            // Immediately update UI (optimistic update)
            setFullScreenVideo(null);
            setVideoJobs(prevJobs => prevJobs.filter(job => job.id !== videoId));
            
            // Make API call
            await axios.delete(`${getAPI()}/video-generations/${videoId}?user_id=${user.id}`);
            // Removed toast: Video deleted successfully
            
            // Reload to ensure consistency
            await loadVideoJobs();
          } catch (error) {
            console.error('Failed to delete video:', error);
            toast.error('Failed to delete video');
            // Reload to revert optimistic update
            await loadVideoJobs();
          }
        }}
        onAdjustReanimate={(video, enhancedPrompt = null) => {
          console.log('🎬 onAdjustReanimate called with video:', video.id, 'image_id:', video.image_id);
          console.log('📋 Video thumbnail_url:', video.thumbnail_url);
          console.log('📋 Video imageUrl:', video.imageUrl);
          console.log('📋 Video parameters:', video.parameters);
          setAdjustReanimateVideo(video);
          if (enhancedPrompt) {
            // If enhanced prompt is provided, go directly to animation generator
            console.log('🔍 Searching for image with id:', video.image_id);
            console.log('📦 Available unassigned images:', unassignedImages.length);
            console.log('📂 Available projects:', projects.length);
            
            // Try to find the image - check both exact match and string comparison
            const allImages = [...unassignedImages, ...projects.flatMap(p => p.images || [])];
            let imageForAnimation = allImages.find(img => 
              img.id === video.image_id || 
              String(img.id) === String(video.image_id)
            );
            
            // If not found in main arrays, create from video data (album videos)
            if (!imageForAnimation && video.image_id && (video.thumbnail_url || video.imageUrl)) {
              console.log('⚠️ Image not in main arrays, creating from video data (likely album video)');
              const thumbnailUrl = video.thumbnail_url || video.imageUrl;
              imageForAnimation = {
                id: video.image_id,
                url: convertExpiredR2Url(thumbnailUrl, video.image_id),
                thumbnail_url: convertExpiredR2Url(thumbnailUrl, video.image_id),
                name: 'Album Image',
                projectName: 'Album',
                projectId: video.album_id || 'album'
              };
            }
            
            if (imageForAnimation) {
              console.log('✅ Found image for animation:', imageForAnimation.id, imageForAnimation.name);
              console.log('📷 Original image URL:', imageForAnimation.url?.substring(0, 50));
              
              // Convert any expired R2 URLs before use
              const safeImage = {
                ...imageForAnimation,
                url: convertExpiredR2Url(imageForAnimation.url, imageForAnimation.id),
                thumbnail_url: convertExpiredR2Url(imageForAnimation.thumbnail_url, imageForAnimation.id),
                r2_url: convertExpiredR2Url(imageForAnimation.r2_url, imageForAnimation.id)
              };
              
              // Use video thumbnail_url or imageUrl as the edited version if it exists and is different from original
              const videoThumbnail = video.thumbnail_url || video.imageUrl;
              const safeVideoThumbnail = convertExpiredR2Url(videoThumbnail, video.image_id);
              const isEditedVersion = safeVideoThumbnail && safeVideoThumbnail !== safeImage.url && safeVideoThumbnail !== `/api/files/${video.image_id}`;
              
              console.log('🖼️ Video has thumbnail?', !!videoThumbnail);
              console.log('🖼️ Is edited version?', isEditedVersion);
              
              const imageToUse = isEditedVersion ? {
                ...safeImage,
                url: safeVideoThumbnail,
                thumbnail_url: safeVideoThumbnail,
                isEdited: true,
                original_url: safeImage.url,
                original_id: safeImage.id
              } : safeImage;
              
              console.log('🎯 Final image URL to use:', imageToUse.url?.substring(0, 50));
              
              const cacheBustedImage = addCacheBustingToImage({
                ...imageToUse,
                projectName: imageToUse.projectName || 'Unassigned Pictures',
                enhancedPrompt: enhancedPrompt // Add enhanced prompt to the selected image
              });
              setSelectedImageForAnimation(cacheBustedImage);
              
              // Close video gallery modal before opening animation generator
              setShowVideoGallery(null);
              
              // Open animation generator - it will show warning when auto-loading prompts
              // (because enhancedPrompt exists, triggering loadAIPrompts in useEffect)
              setShowCreateLivingMemory(true);
            } else {
              console.error('❌ Image not found! video.image_id:', video.image_id, 'type:', typeof video.image_id);
              console.log('Available image IDs:', allImages.slice(0, 5).map(img => ({ id: img.id, type: typeof img.id, name: img.name })));
              toast.error('Could not find the original image. Please try again.');
            }
          } else {
            // Show the old modal interface as fallback
            setShowAdjustReanimate(true);
          }
        }}
      />}
      {showVideoGallery && <VideoGalleryModalPortal 
        imageId={showVideoGallery} 
        videos={imageVideoData[showVideoGallery] || []} 
        user={user}
        showDownloadProgress={showDownloadProgress}
        setShowDownloadProgress={setShowDownloadProgress}
        downloadProgress={downloadProgress}
        setDownloadProgress={setDownloadProgress}
        downloadStatus={downloadStatus}
        setDownloadStatus={setDownloadStatus}
        onClose={() => {
          // Clear any pending long-press timers to prevent full-screen from opening
          if (longPressTimer) {
            clearTimeout(longPressTimer);
            setLongPressTimer(null);
          }
          // Set flag to prevent full-screen from opening
          videoGalleryJustOpened.current = true;
          setShowVideoGallery(null);
          // Clear the flag after modal has fully closed
          setTimeout(() => {
            videoGalleryJustOpened.current = false;
          }, 500);
        }}
        onVideoSelect={(video) => {
          // Store the current gallery state to return to
          const currentGalleryState = {
            imageId: showVideoGallery,
            videos: imageVideoData[showVideoGallery] || []
          };
          // Add return navigation to the video object
          const videoWithReturn = {
            ...video,
            _returnToGallery: currentGalleryState
          };
          setShowVideoGallery(null); // Close the gallery modal
          setFullScreenVideo(videoWithReturn); // Open the video in fullscreen mode with return info
        }}
        onCreateAnother={() => {
          // Find the image that corresponds to this imageId
          const imageForAnimation = [...unassignedImages, ...projects.flatMap(p => p.images || [])].find(img => img.id === showVideoGallery);
          
          if (imageForAnimation) {
            // Set up the image for animation and open Create Living Memory modal with cache-busting
            const cacheBustedImage = addCacheBustingToImage({
              ...imageForAnimation,
              projectName: 'Unassigned Pictures',
              projectId: 'unassigned'
            });
            setSelectedImageForAnimation(cacheBustedImage);
            setShowVideoGallery(null); // Close video gallery
            setShowCreateLivingMemory(true); // Open animation creator
          }
        }}
      />}
      {selectedProject && (
        <MemoryProjectModal
          project={selectedProject}
          aiProviders={aiProviders}
          autoOpenVideoGenerator={autoOpenVideoGenerator}
          checkSingleImageAnalysis={checkSingleImageAnalysis}
          onClose={() => {
            setSelectedProject(null);
            setAutoOpenVideoGenerator(false);
          }}
          onRefresh={() => {
            loadDashboardData();
            loadVideoJobs();
          }}
          setCurrentView={setCurrentView}
          setActiveMainTab={setActiveMainTab}
          setShowPendingVideos={setShowPendingVideos}
          setShowMagicProcessing={setShowMagicProcessing}
          setProcessingCount={setProcessingCount}
        />
      )}
      
      {/* New Modals */}
      {showMultiUpload && <MultiUploadModal onClose={() => setShowMultiUpload(false)} onRefresh={async () => {await loadDashboardData(); await loadVideoJobs(); setRefreshTrigger(prev => prev + 1);}} selectedFolder={selectedFolder} user={user} />}
      {showCreateLivingMemory && <CreateLivingMemoryModal 
        projects={projects} 
        unassignedImages={unassignedImages}
        folders={folders}
        preSelectedImage={selectedImageForAnimation}
        imagesWithVideos={imagesWithVideos}
        setShowVideoGallery={setShowVideoGallery}
        fileInputRef={fileInputRef}
        user={user}
        albumContext={albumContext}
        checkSingleImageAnalysis={checkSingleImageAnalysis}
        skipAnalysisRef={skipAnalysisRef}
        albumFilterSwitchCallback={albumFilterSwitchCallback}
        albumRefreshCallback={albumRefreshCallback}
        setVideoJobs={setVideoJobs}
        onClose={() => {
          console.log('🚪 Animation generator onClose called - setting showCreateLivingMemory to FALSE');
          setShowCreateLivingMemory(false);
          setSelectedImageForAnimation(null);
          // PERFORMANCE FIX: Don't clear albumContext on modal close!
          // Clearing it causes AlbumDetailView to unmount/remount, triggering full reload
          // albumContext will be cleared when user navigates away from album
          // setAlbumContext(null); // ← REMOVED - was causing unnecessary unmount/remount
          console.log('⏭️ Keeping albumContext active to prevent unmount/remount');
          setSkipAnalysisFlag(false); // Reset skip analysis flag when modal closes
          skipAnalysisRef.current = false; // Reset ref
        }} 
        onRefresh={() => {
          // If we have an album context, ONLY refresh album data (not entire dashboard)
          // Album polling will handle updates - no need for heavy API calls
          if (albumContext && albumContext.albumId) {
            console.log('🔄🔄🔄 ANIMATION SUBMITTED IN ALBUM - SKIPPING ALL HEAVY RELOADS 🔄🔄🔄');
            // Don't do anything here - let the setTimeout handle the view switch
            console.log('⏭️ Letting setTimeout handle filter switch and refresh');
          } else {
            // NOT in album context - load full dashboard data
            console.log('🔄 Animation submitted outside album - loading dashboard data');
            loadDashboardData(); 
            loadVideoJobs();
          }
        }}
        currentView={currentView}
        setCurrentView={setCurrentView}
        setActiveMainTab={setActiveMainTab}
        setShowPendingVideos={setShowPendingVideos}
        setShowMagicProcessing={setShowMagicProcessing}
        setProcessingCount={setProcessingCount}
      />}
      {showCollaboration && <CollaborationModal onClose={() => setShowCollaboration(false)} onRefresh={() => {loadDashboardData(); loadVideoJobs();}} />}
      {failedModalJob && <FailedVideoDetailsModalPortal job={failedModalJob} onClose={() => setFailedModalJob(null)} onDelete={handleDeleteVideo} />}
      
      {/* Import from Albums Modal */}
      {showImportModal && (
        <ImportFromAlbumsModal
          type={importModalType}
          user={user}
          userVideos={videoJobs} // Pass existing videos to filter out
          userImages={unassignedImages} // Pass existing images to filter out
          onClose={() => setShowImportModal(false)}
          onImport={async () => {
            console.log('🔄 Refreshing data after import...');
            await loadDashboardData();
            await loadVideoJobs();
            console.log('✅ Data refresh complete');
          }}
        />
      )}
      
      {/* Adjust & Re-animate Modal */}
      {showAdjustReanimate && adjustReanimateVideo && (
        <AdjustReanimateModal
          video={adjustReanimateVideo}
          user={user}
          onClose={() => {
            setShowAdjustReanimate(false);
            setAdjustReanimateVideo(null);
          }}
          onSubmit={(enhancedPrompt) => {
            // Open animation generator with enhanced prompt and original image
            let imageForAnimation = [...unassignedImages, ...projects.flatMap(p => p.images || [])].find(img => img.id === adjustReanimateVideo.image_id);
            
            if (imageForAnimation) {
              // Convert any expired R2 URLs to /api/files/ format before cache-busting
              const safeImage = {
                ...imageForAnimation,
                url: convertExpiredR2Url(imageForAnimation.url, imageForAnimation.id),
                thumbnail_url: convertExpiredR2Url(imageForAnimation.thumbnail_url, imageForAnimation.id),
                r2_url: convertExpiredR2Url(imageForAnimation.r2_url, imageForAnimation.id)
              };
              
              const cacheBustedImage = addCacheBustingToImage({
                ...safeImage,
                projectName: 'Unassigned Pictures',
                projectId: 'unassigned',
                enhancedPrompt: enhancedPrompt // Pass the enhanced prompt
              });
              setSelectedImageForAnimation(cacheBustedImage);
              setShowAdjustReanimate(false);
              setAdjustReanimateVideo(null);
              setShowCreateLivingMemory(true);
            } else {
              toast.error('Could not find original image for re-animation');
            }
          }}
        />
      )}
      
      {/* Sort Modals */}
      <SortModal 
        isOpen={showPictureSortModal}
        onClose={() => setShowPictureSortModal(false)}
        sortConfig={pictureSort}
        setSortConfig={(newSort) => {
          setPictureSort(newSort);
          setShowPictureSortModal(false);
        }}
        type="pictures"
      />
      <SortModal 
        isOpen={showVideoSortModal}
        onClose={() => setShowVideoSortModal(false)}
        sortConfig={videoSort}
        setSortConfig={(newSort) => {
          setVideoSort(newSort);
          setShowVideoSortModal(false);
        }}
        type="videos"
      />
        </div>
      </div>
      
      {/* Floating Support Button - Bottom Right - OUTSIDE all containers - Hide when fullscreen modals, animation generator, magic processing, credits, or batch animation modal are open - Desktop only */}
      {!fullScreenImage && !fullScreenVideo && !showCreateLivingMemory && !showMagicProcessing && !showCredits && !showEarnCredits && !showBatchAnimation && (
        <button
          onClick={() => setShowSupport(true)}
          className="hidden md:flex fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-600 hover:via-orange-600 hover:to-red-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 items-center justify-center z-[60] text-sm"
          title="Contact Support"
        >
          💬
        </button>
      )}
      
      {/* Tutorial Overlay - Only active in Demo Mode */}
      {showDemoTutorial && demoMode && (
        <TutorialOverlay
          step={demoStep}
          stepData={TUTORIAL_STEPS[demoStep]}
          onNext={handleTutorialNext}
          onSkip={handleTutorialSkip}
          onComplete={handleTutorialComplete}
          demoMode={demoMode}
        />
      )}

      {/* Shop View */}
      {currentView === 'shop' && !showCreateLivingMemory && (
        <>
          {/* Shop Subtabs */}
          <div className="bg-white border-b border-gray-200 sticky top-[73px] z-50 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-center space-x-8">
                <button
                  onClick={() => setShopSubtab('frames')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    shopSubtab === 'frames'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    {/* Outer frame */}
                    <rect x="5" y="3" width="14" height="18" strokeLinecap="round" strokeLinejoin="round" />
                    {/* Inner frame */}
                    <rect x="7" y="5" width="10" height="14" strokeLinecap="round" strokeLinejoin="round" />
                    {/* Kickstand/easel leg */}
                    <path d="M5 21 L5 11" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Frames
                </button>
                <button
                  onClick={() => setShopSubtab('mail-in-service')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                    shopSubtab === 'mail-in-service'
                      ? 'border-purple-600 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Mail-in Service
                </button>
              </div>
            </div>
          </div>

          {/* Frames Subtab Content (existing shop products) */}
          {shopSubtab === 'frames' && (
            <ShopTab 
          onCartUpdate={async () => {
            try {
              const userStr = localStorage.getItem('user');
              const userData = userStr ? JSON.parse(userStr) : null;
              const userId = userData?.id;
              
              if (userId) {
                const response = await axios.get(`${BACKEND_URL}/api/shop/cart?user_id=${userId}`);
                setCartCount(response.data.count);
                window.cartCount = response.data.count;
              }
            } catch (err) {
              console.error('Error updating cart count:', err);
            }
          }}
          onOpenDigitalFrame={(product, frameColor, isGiftMode = false, giftProduct = null) => {
            // Clear album preload when opening from shop tab (should default to "All Moments")
            localStorage.removeItem('preloadFromAlbum');
            localStorage.removeItem('preloadFrameConfigId');
            // Force refresh by setting a timestamp
            localStorage.setItem('shopTabOpen', Date.now().toString());
            setPreSelectedFrameColor(frameColor);
            setPreSelectedFrameProduct(product);
            setIsGiftMode(isGiftMode); // Set gift mode flag
            if (giftProduct) {
              setGiftCardProduct(giftProduct); // Store gift product data
            }
            setShowFrameDemo(true);
          }}
            />
          )}

          {/* Mail-in Service Subtab Content - Redirect to render from main view */}
          {shopSubtab === 'mail-in-service' && (() => {
            // Temporarily set currentView to trigger mail-in service content render
            const tempView = currentView;
            if (tempView === 'shop') {
              // Render mail-in service inline by checking shopSubtab in the main mail-in view
              return null; // Content is rendered below in the main mail-in-service view block
            }
            return null;
          })()}
        </>
      )}

      {/* Projects (Albums) View */}
      {currentView === 'projects' && (
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>}>
          <AlbumsTab 
            user={user} 
            setShowCreateLivingMemory={setShowCreateLivingMemory}
            setSelectedImageForAnimation={setSelectedImageForAnimation}
            albumContext={albumContext}
            setAlbumContext={setAlbumContext}
            showCreateLivingMemory={showCreateLivingMemory}
            setShowFrameDemo={setShowFrameDemo}
            setPreSelectedFrameProduct={setPreSelectedFrameProduct}
            setPreSelectedFrameColor={setPreSelectedFrameColor}
            onInvitationUpdate={loadPendingInvitations}
            onAccessRequestUpdate={loadPendingAccessRequests}
            onBatchAnimateSelected={openBatchAnimation}
            showBatchAnimation={showBatchAnimation}
            albumRefreshCallback={albumRefreshCallback}
            albumFilterSwitchCallback={albumFilterSwitchCallback}
            setTotalPendingAccessRequests={setTotalPendingAccessRequests}
            onAlbumFilterChange={(newFilter) => {
              // Direct callback to force filter change in album
              console.log('🎯 DIRECT ALBUM FILTER CHANGE CALLBACK:', newFilter);
            }}
            onAdjustReanimate={(video, enhancedPrompt = null, album = null, sourceAlbumImageId = null) => {
              console.log('🎬 onAdjustReanimate called from AlbumsTab with video:', video.id);
              console.log('📋 Enhanced prompt received:', enhancedPrompt);
              console.log('📁 Album context:', album?.id, album?.title);
              setAdjustReanimateVideo(video);
              
              // Set album context if album is provided
              if (album && setAlbumContext) {
                console.log('✅ Setting albumContext state:', album.id);
                setAlbumContext({
                  albumId: album.id,
                  albumTitle: album.title,
                  albumOnly: true
                });
              }
              
              if (enhancedPrompt) {
                // If enhanced prompt is provided, go directly to animation generator (same logic as Animated Moments)
                console.log('🔍 Searching for image with id:', video.image_id);

                const addAuthParams = (url) => {
                  if (!url) return url;
                  
                  // CRITICAL: Don't add params to presigned URLs (they have signatures)
                  if (url.includes('X-Amz-Algorithm') || url.includes('X-Amz-Signature')) {
                    console.log('⚠️ Detected presigned URL, skipping auth params');
                    return url;
                  }
                  
                  const hasUserId = url.includes('user_id=');
                  const hasAuthId = url.includes('authenticated_user_id=');
                  if (hasUserId && hasAuthId) return url;
                  
                  const originalAdmin = localStorage.getItem('originalAdmin');
                  const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : user?.id;
                  
                  let updatedUrl = url;
                  if (!hasUserId) {
                    const sep = updatedUrl.includes('?') ? '&' : '?';
                    updatedUrl = `${updatedUrl}${sep}user_id=${user?.id || ''}`;
                  }
                  if (!hasAuthId) {
                    const sep = updatedUrl.includes('?') ? '&' : '?';
                    updatedUrl = `${updatedUrl}${sep}authenticated_user_id=${authenticatedUserId}`;
                  }
                  return updatedUrl;
                };
                
                // Prefer album asset when re-animating within an album
                if (album && sourceAlbumImageId && Array.isArray(album.assets)) {
                  const albumImage = album.assets.find(a => a.id === sourceAlbumImageId);
                  if (albumImage) {
                    console.log('📷 Using album image asset for re-animate:', albumImage.id);
                    let imgUrl = albumImage.thumbnail_url || albumImage.r2_url || `/api/files/${albumImage.id}`;
                    if (imgUrl && imgUrl.startsWith('/api/')) {
                      imgUrl = `${process.env.REACT_APP_BACKEND_URL || ''}${imgUrl}`;
                    }
                    imgUrl = addAuthParams(imgUrl);
                    const albumImageForAnimation = {
                      id: albumImage.id,
                      url: imgUrl,
                      thumbnail_url: imgUrl,
                      name: albumImage.filename || 'Album Image',
                    };

                    const cacheBustedImage = addCacheBustingToImage({
                      ...albumImageForAnimation,
                      projectName: album.title || 'Album',
                      enhancedPrompt: enhancedPrompt,
                      albumId: album.id,
                      albumTitle: album.title,
                      albumOnly: true,
                      sourceVideoId: video.id
                    });
                    setSelectedImageForAnimation(cacheBustedImage);
                    setShowCreateLivingMemory(true);
                    return;
                  }
                }
                
                // Try to find the image in user library
                const allImages = [...unassignedImages, ...projects.flatMap(p => p.images || [])];
                let imageForAnimation = allImages.find(img => 
                  img.id === video.image_id || 
                  String(img.id) === String(video.image_id)
                );
                
                console.log('📦 Available images:', allImages.length);
                console.log('🎯 Image found:', !!imageForAnimation);
                
                if (imageForAnimation) {
                  console.log('✅ Found image for animation:', imageForAnimation.id);
                  
                  // Use video thumbnail as the edited version if it exists
                  const videoThumbnail = video.thumbnail_url || video.imageUrl;
                  const isEditedVersion = videoThumbnail && videoThumbnail !== imageForAnimation.url && videoThumbnail !== `/api/files/${video.image_id}`;
                  
                  const imageToUse = isEditedVersion ? {
                    ...imageForAnimation,
                    url: videoThumbnail,
                    thumbnail_url: videoThumbnail,
                    isEdited: true,
                    original_url: imageForAnimation.url,
                    original_id: imageForAnimation.id
                  } : imageForAnimation;
                  
                  console.log('🎯 Opening animation generator with enhanced prompt:', enhancedPrompt);
                  
                  const cacheBustedImage = addCacheBustingToImage({
                    ...imageToUse,
                    projectName: 'Unassigned Pictures',
                    enhancedPrompt: enhancedPrompt,
                    albumId: album?.id,  // Pass album context
                    albumTitle: album?.title,
                    sourceVideoId: video.id  // Track the source video for association
                  });
                  setSelectedImageForAnimation(cacheBustedImage);
                  setShowCreateLivingMemory(true);
                  
                } else {
                  // Image not found - create synthetic image with proper URL
                  console.log('⚠️ Image not found in library, creating synthetic image');
                  console.log('📹 Video data for synthetic:', {
                    id: video.id,
                    image_id: video.image_id,
                    thumbnail_url: video.thumbnail_url,
                    imageUrl: video.imageUrl,
                    image_url: video.image_url
                  });
                  
                  // Construct proper image URL - prefer full URLs
                  let imageUrl = video.thumbnail_url || video.imageUrl || video.image_url;
                  
                  // If URL is relative, make it absolute
                  if (imageUrl && !imageUrl.startsWith('http')) {
                    if (imageUrl.startsWith('/api/')) {
                      imageUrl = `${process.env.REACT_APP_BACKEND_URL || ''}${imageUrl}`;
                    }
                  }
                  
                  // Add auth params if needed
                  imageUrl = addAuthParams(imageUrl);
                  
                  console.log('🖼️ Constructed image URL for AI:', imageUrl);
                  
                  const syntheticImage = {
                    id: video.image_id || `synthetic_${video.id}`,
                    url: imageUrl,
                    thumbnail_url: imageUrl,
                    name: 'Video Source Image',
                    created_at: video.created_at,
                    isSynthetic: true  // Mark as synthetic so handleGenerate sends image_url
                  };
                  
                  console.log('📦 Synthetic image object:', syntheticImage);
                  console.log('🎯 Enhanced prompt:', enhancedPrompt);
                  console.log('🔗 Source video ID being set:', video.id);
                  
                  const cacheBustedImage = addCacheBustingToImage({
                    ...syntheticImage,
                    projectName: 'Album Video',
                    enhancedPrompt: enhancedPrompt,
                    albumId: album?.id,  // Pass album context
                    albumTitle: album?.title,
                    sourceVideoId: video.id  // Track the source video for association
                  });
                  setSelectedImageForAnimation(cacheBustedImage);
                  setShowCreateLivingMemory(true);
                }
              } else {
                // No enhanced prompt - show adjustment modal
                setShowAdjustReanimate(true);
              }
            }}
            onEditImage={onEditImage}
          />
        </Suspense>
      )}

      {/* Orders Dashboard Modal (Admin Only) */}
      {showOrdersDashboard && user?.email === 'brianjamessurvey@gmail.com' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[90vw] max-h-[90vh] overflow-hidden flex flex-col">
            <Suspense fallback={<div className="flex items-center justify-center flex-1"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>}>
              <OrdersDashboard userEmail={user.email} onClose={() => setShowOrdersDashboard(false)} />
            </Suspense>
          </div>
        </div>
      )}

      {/* My Orders View */}
      {currentView === 'my-orders' && (
        <div style={{display: 'block', visibility: 'visible', position: 'absolute', top: '120px', left: 0, right: 0, zIndex: 1, minHeight: 'calc(100vh - 120px)', background: 'linear-gradient(to bottom right, #dbeafe, #e9d5ff, #e0e7ff)'}} className="overflow-auto">
          {console.log('🔍 Rendering My Orders view, ordersTab:', ordersTab, 'myOrders:', myOrders)}
          <div style={{maxWidth: '72rem', margin: '0 auto', padding: '1.5rem'}}>
            
            {/* Header */}
            <div className="mb-8 mt-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                My Orders
              </h1>
            </div>

            {/* Loading State */}
            {loadingOrders && (
              <div className="text-center py-16">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your orders...</p>
              </div>
            )}

            {!loadingOrders && (
              <>
                {/* Tab Selector */}
                <div className="flex gap-2 sm:gap-4 mb-6 border-b border-gray-200 overflow-x-auto">
                  <button 
                    onClick={() => setOrdersTab('frames')}
                    className={`px-3 sm:px-6 py-3 font-semibold transition-all relative whitespace-nowrap text-sm sm:text-base ${
                      ordersTab === 'frames'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-600 hover:text-purple-600'
                    }`}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="hidden sm:inline">Frame Orders</span>
                      <span className="sm:hidden">Frames</span>
                      <span>({myOrders.mail_in_orders.length})</span>
                    </span>
                  </button>
                  <button 
                    onClick={() => {
                      console.log('Switching to Mail-in Service tab');
                      console.log('Current myOrders state:', myOrders);
                      setOrdersTab('mailin');
                    }}
                    className={`px-3 sm:px-6 py-3 font-semibold transition-all relative whitespace-nowrap text-sm sm:text-base ${
                      ordersTab === 'mailin'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-600 hover:text-purple-600'
                    }`}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                      </svg>
                      <span className="hidden sm:inline">Mail-in Service</span>
                      <span className="sm:hidden">Mail-in</span>
                      <span>({mailInOrders.length})</span>
                    </span>
                  </button>
                  <button 
                    onClick={() => setOrdersTab('credits')}
                    className={`px-3 sm:px-6 py-3 font-semibold transition-all relative whitespace-nowrap text-sm sm:text-base ${
                      ordersTab === 'credits'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-600 hover:text-purple-600'
                    }`}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="hidden sm:inline">Credit Purchases</span>
                      <span className="sm:hidden">Credits</span>
                      <span>({myOrders.credit_purchases?.length || 0})</span>
                    </span>
                  </button>
                  {/* Moments tab hidden per user request */}
                  {/*
                  <button 
                    onClick={() => setOrdersTab('videos')}
                    className={`px-3 sm:px-6 py-3 font-semibold transition-all relative whitespace-nowrap text-sm sm:text-base ${
                      ordersTab === 'videos'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-gray-600 hover:text-purple-600'
                    }`}
                  >
                    <span className="flex items-center gap-1 sm:gap-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="hidden sm:inline">Moments Created</span>
                      <span className="sm:hidden">Moments</span>
                      <span>({myOrders.video_history?.length || 0})</span>
                    </span>
                  </button>
                  */}
                </div>

                {/* Frame Orders Tab */}
                {ordersTab === 'frames' && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-8">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Digital Frame Orders
                      </h2>
                      <button 
                        onClick={() => {
                          setCurrentView('shop');
                          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
                      >
                        Shop Frames
                      </button>
                    </div>
                    
                    {/* Orders List or Empty State */}
                    {myOrders.mail_in_orders.length === 0 ? (
                      <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">No Frame Orders Yet</h3>
                        <p className="text-gray-600 max-w-md mx-auto mb-6">
                          When you purchase a digital frame, you'll be able to track its delivery status here.
                        </p>
                        <button 
                          onClick={() => {
                            setCurrentView('shop');
                            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                          }}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          Browse Digital Frames
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {myOrders.mail_in_orders.map((order) => {
                          // Determine fulfillment stage
                          const getFulfillmentStage = () => {
                            if (order.fulfillment_status === 'fulfilled' || order.tracking_number) return 3;  // "fulfilled" or has tracking = stage 3 (Shipped - Final Stage)
                            if (order.payment_status === 'paid' || order.status === 'paid') return 2;
                            return 1;
                          };
                          const currentStage = getFulfillmentStage();
                          
                          const stages = [
                            { 
                              id: 1, 
                              name: 'Order Placed', 
                              icon: '<svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20"><path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/></svg>',
                              date: order.created_at 
                            },
                            { 
                              id: 2, 
                              name: 'Processing', 
                              icon: '<svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd"/></svg>',
                              date: order.created_at 
                            },
                            { 
                              id: 3, 
                              name: 'Shipped', 
                              icon: '<svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20"><path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/><path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/></svg>',
                              date: order.shipped_at 
                            }
                          ];

                          return (
                            <div key={order.id} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                              {/* Order Header */}
                              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 sm:p-6 border-b border-gray-200">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-start gap-3 sm:gap-0 mb-4">
                                  <div>
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                                      Order {order.order_number || order.shopify_order_number || `#${order.order_token?.substring(3, 10).toUpperCase()}`}
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                      Placed on {new Date(order.created_at).toLocaleString('en-US', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </p>
                                  </div>
                                  {/* Status Badge - Top Right */}
                                  {(order.status === 'cancelled' || order.fulfillment_status === 'cancelled') ? (
                                    <span className="inline-flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-full text-sm font-semibold shadow-md">
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                      Cancelled
                                    </span>
                                  ) : (
                                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                                      currentStage === 3 ? 'bg-green-500 text-white' :
                                      currentStage === 2 ? 'bg-yellow-500 text-white' :
                                      'bg-gray-500 text-white'
                                    }`}>
                                      {stages[currentStage - 1].name}
                                    </span>
                                  )}
                                </div>

                                {/* Fulfillment Progress Bar */}
                                <div className="relative overflow-x-auto">
                                  <div className="flex justify-between items-center min-w-full">
                                    {stages.map((stage, idx) => (
                                      <div key={stage.id} className="flex flex-col items-center relative" style={{ width: '33.33%' }}>
                                        {/* Circle */}
                                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center z-10 ${
                                          stage.id <= currentStage 
                                            ? 'bg-gradient-to-br from-purple-500 to-blue-500 text-white shadow-lg' 
                                            : 'bg-gray-200 text-gray-400'
                                        }`}>
                                          <div className="w-5 h-5 sm:w-6 sm:h-6" dangerouslySetInnerHTML={{ __html: stage.icon }} />
                                        </div>
                                        {/* Label */}
                                        <p className={`text-[10px] sm:text-xs mt-2 font-medium text-center ${
                                          stage.id <= currentStage ? 'text-gray-900' : 'text-gray-400'
                                        }`}>
                                          {stage.name}
                                        </p>
                                        {/* Date - only show if stage is completed and has a date */}
                                        {stage.id <= currentStage && stage.date && (
                                          <p className="text-[10px] sm:text-xs text-gray-500 mt-1 text-center hidden sm:block">
                                            {new Date(stage.date).toLocaleDateString('en-US', { 
                                              month: 'short', 
                                              day: 'numeric'
                                            })}
                                          </p>
                                        )}
                                        {/* Connecting Line */}
                                        {idx < stages.length - 1 && (
                                          <div 
                                            className={`absolute top-5 sm:top-6 left-1/2 h-0.5 sm:h-1 ${
                                              stage.id < currentStage ? 'bg-gradient-to-r from-purple-500 to-blue-500' : 'bg-gray-200'
                                            }`}
                                            style={{ width: 'calc(100% - 20px)', marginLeft: '10px' }}
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Order Content */}
                              <div className="p-4 sm:p-6">
                                {/* Frame Product with Image */}
                                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                                  {/* Frame Image */}
                                  <div className="flex-shrink-0 w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                    {order.frame_image_url ? (
                                      <img 
                                        src={order.frame_image_url} 
                                        alt={order.frame_product}
                                        className="w-full h-full object-contain"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                      </div>
                                    )}
                                  </div>

                                  {/* Frame Details */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <h4 className="font-bold text-gray-900 text-base sm:text-lg break-words">{order.frame_product}</h4>
                                      
                                      {/* Quantity Badge */}
                                      {order.quantity && order.quantity > 1 && (
                                        <span className="inline-flex items-center px-2.5 py-1 bg-gray-200 text-gray-800 rounded-md text-sm font-semibold">
                                          Qty: {order.quantity}
                                        </span>
                                      )}
                                      
                                      {/* Gift Badge - Only show for non-cancelled gift orders */}
                                      {order.is_gift && order.status !== 'cancelled' && order.fulfillment_status !== 'cancelled' && (
                                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm font-semibold shadow-md">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                          </svg>
                                          Gift Order
                                        </span>
                                      )}
                                    </div>
                                    <div className="space-y-1 text-sm text-gray-600">
                                      <p>
                                        <span className="font-semibold">Configuration:</span> {order.config_name || 'Custom Setup'}
                                      </p>
                                      <p>
                                        <span className="font-semibold">Moments Included:</span> {order.media_count || order.selected_media?.length || 0} video{(order.media_count || order.selected_media?.length || 0) !== 1 ? 's' : ''}/photo{(order.media_count || order.selected_media?.length || 0) !== 1 ? 's' : ''}
                                      </p>
                                      {/* Show recipient info for gift orders */}
                                      {order.is_gift && (order.recipient_name || order.gift_recipient_name) && (
                                        <p>
                                          <span className="font-semibold">Gift Recipient:</span> {order.recipient_name || order.gift_recipient_name}
                                          {(order.recipient_email || order.gift_recipient_email) && ` (${order.recipient_email || order.gift_recipient_email})`}
                                        </p>
                                      )}
                                      {(order.shipping_address?.address1 || order.shipping_address_1) && (
                                        <p>
                                          <span className="font-semibold">Shipping To:</span> {order.shipping_address?.address1 || order.shipping_address_1}{(order.shipping_address?.address2 || order.shipping_address_2) && `, ${order.shipping_address?.address2 || order.shipping_address_2}`}, {order.shipping_address?.city || order.shipping_city}, {order.shipping_address?.province || order.shipping_state} {order.shipping_address?.zip || order.shipping_zip}
                                        </p>
                                      )}
                                      {order.frame_location && (
                                        <p>
                                          <span className="font-semibold">Location:</span> {order.frame_location}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Order Total */}
                                  {order.total && (
                                    <div className="flex-shrink-0 text-right sm:text-right w-full sm:w-auto mt-4 sm:mt-0">
                                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                                        ${typeof order.total === 'number' ? order.total.toFixed(2) : order.total}
                                      </p>
                                      {order.item_price && order.shipping_cost ? (
                                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                          <p>Item: ${typeof order.item_price === 'number' ? order.item_price.toFixed(2) : order.item_price}</p>
                                          <p>Shipping: ${typeof order.shipping_cost === 'number' ? order.shipping_cost.toFixed(2) : order.shipping_cost}</p>
                                        </div>
                                      ) : null}
                                    </div>
                                  )}
                                </div>

                                {/* Tracking Information */}
                                {order.tracking_number && (
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <h5 className="font-semibold text-blue-900">Tracking Information</h5>
                                    </div>
                                    <p className="text-sm text-blue-800">
                                      <span className="font-medium">Tracking Number:</span> {order.tracking_number}
                                    </p>
                                    {order.tracking_url && (
                                      <a 
                                        href={order.tracking_url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                                      >
                                        Track Your Package →
                                      </a>
                                    )}
                                  </div>
                                )}

                                {/* Cancelled Status */}
                                {(order.status === 'cancelled' || order.fulfillment_status === 'cancelled') && (
                                  <div className="bg-red-50 border border-red-300 rounded-lg p-4 mt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                      <h5 className="font-semibold text-red-900">Order Cancelled</h5>
                                    </div>
                                    {order.cancelled_at && (
                                      <p className="text-sm text-red-800 mb-1">
                                        <span className="font-medium">Date:</span> {new Date(order.cancelled_at).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                          hour12: true
                                        })}
                                      </p>
                                    )}
                                    {order.cancellation_reason && (
                                      <p className="text-sm text-red-800">
                                        <span className="font-medium">Reason:</span> {order.cancellation_reason}
                                      </p>
                                    )}
                                    
                                    {/* Refund Information */}
                                    {order.refund_amount ? (
                                      <div className="mt-3 pt-3 border-t border-red-200">
                                        <p className="text-sm text-red-900 font-semibold mb-2">💰 Refund Information</p>
                                        <div className="space-y-1">
                                          <p className="text-sm text-red-800">
                                            <span className="font-medium">Amount:</span> ${order.refund_amount.toFixed(2)}
                                          </p>
                                          <p className="text-sm text-red-800">
                                            <span className="font-medium">Status:</span>{' '}
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                                              order.refund_status === 'succeeded' ? 'bg-green-100 text-green-800' :
                                              order.refund_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                              order.refund_status === 'failed' ? 'bg-red-100 text-red-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              {order.refund_status === 'succeeded' ? '✓ Refunded' :
                                               order.refund_status === 'pending' ? '⏳ Processing' :
                                               order.refund_status === 'failed' ? '✗ Failed' :
                                               'Pending'}
                                            </span>
                                          </p>
                                          {order.refund_status === 'succeeded' && (
                                            <p className="text-xs text-red-700 mt-2 bg-red-50 rounded p-2">
                                              💳 Refund typically appears in your account within <strong>5-10 business days</strong> depending on your bank.
                                            </p>
                                          )}
                                          {order.refund_status === 'pending' && (
                                            <p className="text-xs text-yellow-700 mt-2 bg-yellow-50 rounded p-2">
                                              ⏳ Your refund is being processed. This usually takes 1-2 business days.
                                            </p>
                                          )}
                                          {order.requires_manual_refund && (
                                            <p className="text-xs text-orange-700 mt-2 bg-orange-50 rounded p-2">
                                              ⚠️ Your refund requires manual processing. Our team will process this within 2-3 business days.
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    ) : order.no_refund_policy && order.no_refund_reason ? (
                                      <div className="mt-3 pt-3 border-t border-red-200">
                                        <p className="text-sm text-red-900 font-semibold mb-2">ℹ️ Refund Policy</p>
                                        <p className="text-xs text-red-700 bg-red-50 rounded p-2">
                                          {order.no_refund_reason}
                                        </p>
                                      </div>
                                    ) : null}
                                  </div>
                                )}

                                {/* Estimated Delivery (if not delivered and not cancelled) */}
                                {currentStage < 4 && order.status !== 'cancelled' && order.fulfillment_status !== 'cancelled' && (
                                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                                    <p className="text-sm text-yellow-800">
                                      <span className="font-semibold">📅 Estimated Delivery:</span> 5-7 business days
                                    </p>
                                  </div>
                                )}

                                {/* Moment Thumbnails */}
                                {order.selected_media && order.selected_media.length > 0 && (
                                  <div className="mt-4 border-t border-gray-200 pt-4">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      Moments Included ({order.selected_media.length})
                                    </h5>
                                    <div className="flex flex-wrap gap-2">
                                      {order.selected_media.slice(0, 10).map((media, idx) => {
                                        // Helper to add user_id to URL if needed (for /api/ URLs)
                                        const getAuthenticatedUrl = (url) => {
                                          if (!url) return '';
                                          // If it's an internal API URL, add user_id
                                          if (url.startsWith('/api/') && user?.id) {
                                            const separator = url.includes('?') ? '&' : '?';
                                            return `${url}${separator}user_id=${user.id}`;
                                          }
                                          return url;
                                        };
                                        
                                        const thumbnailUrl = media.type === 'video' 
                                          ? getAuthenticatedUrl(media.thumbnail_url || media.cloudflare_thumbnail)
                                          : getAuthenticatedUrl(media.cloudflare_delivery_url || media.thumbnail_url || media.url);
                                        
                                        return (
                                          <div key={idx} className="relative w-12 h-12 bg-gray-100 rounded overflow-hidden border border-gray-200 hover:border-purple-400 transition-all flex-shrink-0">
                                            {media.type === 'video' ? (
                                              <div className="relative w-full h-full">
                                                <img 
                                                  src={thumbnailUrl} 
                                                  alt={`Moment ${idx + 1}`}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    // Fallback to a placeholder on error
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gray-200 flex items-center justify-center"><svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg></div>';
                                                  }}
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                                  </svg>
                                                </div>
                                              </div>
                                            ) : (
                                              <img 
                                                src={thumbnailUrl} 
                                                alt={`Moment ${idx + 1}`}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                  // Fallback to a placeholder on error
                                                  e.target.style.display = 'none';
                                                  e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gray-200 flex items-center justify-center"><svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                                                }}
                                              />
                                            )}
                                          </div>
                                        );
                                      })}
                                      {order.selected_media.length > 10 && (
                                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 font-semibold text-xs border border-gray-300">
                                          +{order.selected_media.length - 10}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Mail-in Service Orders Tab */}
                {ordersTab === 'mailin' && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-8">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                        </svg>
                        Mail-in Service Orders
                      </h2>
                      <button 
                        onClick={() => {
                          setCurrentView('mail-in-service');
                          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
                      >
                        View Packages
                      </button>
                    </div>
                    
                    {/* Orders List or Empty State */}
                    {loadingMailInOrders ? (
                      <div className="text-center py-16">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                        <p className="mt-4 text-gray-600">Loading your orders...</p>
                      </div>
                    ) : mailInOrders.length === 0 ? (
                      <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                        </svg>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">No Mail-in Orders Yet</h3>
                        <p className="text-gray-600 max-w-md mx-auto mb-6">
                          Send us your physical photos to digitize and animate them. Track your submission here once you place an order.
                        </p>
                        <button 
                          onClick={() => {
                            setCurrentView('mail-in-service');
                            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                          }}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                          </svg>
                          Explore Mail-in Service
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {mailInOrders.map((order) => {
                          // Check if order is cancelled
                          const isCancelled = order.status === 'cancelled' || order.fulfillment_status === 'cancelled';
                          
                          // Check if this is a gift purchase (purchaser view, not recipient)
                          const isGiftPurchase = order.is_gift && !order.is_gift_recipient;
                          
                          // Determine fulfillment stage (5 stages for mail-in service)
                          const getFulfillmentStage = () => {
                            // If cancelled, return 0 to indicate no active stage
                            if (isCancelled) return 0;
                            // Stage 5: Photos returned (completed/delivered)
                            if (order.status === 'completed' || order.status === 'photos_returned') return 5;
                            // Stage 4: Digitizing (photos being processed + prep_return is admin-only, shows as digitizing to customer)
                            if (order.status === 'digitizing' || order.status === 'processing_photos' || order.status === 'prep_return') return 4;
                            // Stage 3: Photos shipped (customer sent photos back)
                            if (order.status === 'photos_received' || order.status === 'photos_in_transit') return 3;
                            // Stage 2: Shipping kit sent (kit was actually shipped - has tracking or shipped status)
                            if (order.status === 'kit_shipped' || 
                                order.status === 'shipped' ||
                                order.tracking_number || 
                                order.fulfillment_status === 'fulfilled' ||
                                order.shipstation_info?.order_status === 'shipped' ||
                                order.status === 'kit_delivered') return 2;
                            // Stage 1: Order placed (initial order, kit not shipped yet)
                            if (order.payment_status === 'paid') return 1;
                            return 1;
                          };
                          const currentStage = getFulfillmentStage();
                          
                          const stages = [
                            { 
                              id: 1, 
                              name: 'Order Placed', 
                              icon: '<svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20"><path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/></svg>',
                              date: order.created_at 
                            },
                            { 
                              id: 2, 
                              name: 'Shipping Kit Sent', 
                              icon: '<svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20"><path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/><path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/></svg>',
                              date: order.kit_shipped_at || order.created_at
                            },
                            { 
                              id: 3, 
                              name: 'Photos Received', 
                              icon: '<svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>',
                              date: order.photos_received_at 
                            },
                            { 
                              id: 4, 
                              name: 'Digitizing', 
                              icon: '<svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/></svg>',
                              date: order.digitizing_started_at 
                            },
                            { 
                              id: 5, 
                              name: 'Photos Returned', 
                              icon: '<svg class="w-full h-full" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>',
                              date: order.photos_returned_at 
                            }
                          ];

                          return (
                            <div key={order.order_id} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                              {/* Order Header */}
                              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 sm:p-6 border-b border-gray-200">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-start gap-3 sm:gap-0 mb-4">
                                  <div>
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                                      Order {order.order_number}
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                      Placed on {new Date(order.created_at).toLocaleString('en-US', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </p>
                                  </div>
                                  {/* Status Badge - Top Right */}
                                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                                    isCancelled ? 'bg-red-500 text-white' :
                                    isGiftPurchase ? (order.redeemed ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white') :
                                    currentStage === 5 ? 'bg-green-500 text-white' :
                                    currentStage === 4 ? 'bg-indigo-500 text-white' :
                                    currentStage === 3 ? 'bg-blue-500 text-white' :
                                    currentStage === 2 ? 'bg-yellow-500 text-white' :
                                    'bg-gray-500 text-white'
                                  }`}>
                                    {isCancelled ? 'Cancelled' : 
                                     isGiftPurchase ? (order.redeemed ? '✓ Redeemed' : 'Pending Redemption') :
                                     stages[currentStage - 1].name}
                                  </span>
                                </div>

                                {/* Fulfillment Progress Bar OR Simple Gift Status */}
                                {isGiftPurchase ? (
                                  /* Simple redemption status for gift purchasers */
                                  <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 border border-pink-200">
                                    <div className="flex items-center justify-center gap-3">
                                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                        order.redeemed ? 'bg-gradient-to-br from-green-500 to-emerald-500' : 'bg-gradient-to-br from-yellow-500 to-orange-500'
                                      } text-white shadow-lg`}>
                                        {order.redeemed ? (
                                          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        ) : (
                                          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                        )}
                                      </div>
                                      <div className="text-left">
                                        <p className="font-bold text-gray-900 text-lg">
                                          {order.redeemed ? 'Gift Redeemed!' : 'Awaiting Redemption'}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                          {order.redeemed 
                                            ? `Redeemed by ${order.gift_to_name || order.gift_recipient_name || order.recipient_name || order.to_name || 'recipient'} (${order.gift_to_email || order.gift_recipient_email || order.recipient_email || order.to_email}) on ${new Date(order.redeemed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                                            : `Sent to ${order.gift_to_name || order.gift_recipient_name || order.recipient_name || order.to_name || 'recipient'} (${order.gift_to_email || order.gift_recipient_email || order.recipient_email || order.to_email})`
                                          }
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  /* Full fulfillment progress for recipients and regular orders */
                                  <div className="relative overflow-x-auto">
                                    <div className="flex justify-between items-center min-w-full">
                                      {stages.map((stage, idx) => (
                                        <div key={stage.id} className="flex flex-col items-center relative" style={{ width: '20%' }}>
                                          {/* Circle */}
                                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center z-10 ${
                                            stage.id <= currentStage 
                                              ? 'bg-gradient-to-br from-purple-500 to-blue-500 text-white shadow-lg' 
                                              : 'bg-gray-200 text-gray-400'
                                          }`}>
                                            <div className="w-5 h-5 sm:w-6 sm:h-6" dangerouslySetInnerHTML={{ __html: stage.icon }} />
                                          </div>
                                          {/* Label */}
                                          <p className={`text-[10px] sm:text-xs mt-2 font-medium text-center ${
                                            stage.id <= currentStage ? 'text-gray-900' : 'text-gray-400'
                                          }`}>
                                            {stage.name}
                                          </p>
                                          {/* Date - only show if stage is completed and has a date */}
                                          {stage.id <= currentStage && stage.date && (
                                            <p className="text-[10px] sm:text-xs text-gray-500 mt-1 text-center hidden sm:block">
                                              {new Date(stage.date).toLocaleDateString('en-US', { 
                                                month: 'short', 
                                                day: 'numeric'
                                              })}
                                            </p>
                                          )}
                                          {/* Connecting Line */}
                                          {idx < stages.length - 1 && (
                                            <div 
                                              className={`absolute top-5 sm:top-6 left-1/2 h-0.5 sm:h-1 ${
                                                stage.id < currentStage ? 'bg-gradient-to-r from-purple-500 to-blue-500' : 'bg-gray-200'
                                              }`}
                                              style={{ width: 'calc(100% - 20px)', marginLeft: '10px' }}
                                            />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Order Content */}
                              <div className="p-4 sm:p-6">
                                {/* Package Details */}
                                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                                  {/* Package Icon */}
                                  <div className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg overflow-hidden border border-purple-200 flex items-center justify-center">
                                    <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                  </div>

                                  {/* Package Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <h4 className="font-bold text-gray-900 text-base sm:text-lg break-words">
                                        {order.package_details?.package_name || 'Mail-In Digitization Service'}
                                      </h4>
                                      
                                      {/* Gift Badge if applicable */}
                                      {order.is_gift && (
                                        <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm font-semibold shadow-md">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                          </svg>
                                          Gift Order
                                        </span>
                                      )}
                                    </div>
                                    <div className="space-y-1 text-sm text-gray-600">
                                      <p>
                                        <span className="font-semibold">Package Type:</span> {order.package_id === '100' ? 'Digitization-Only (100 photos)' : order.package_id === '200' ? 'Digitization-Only (200 photos)' : order.package_id === '300' ? 'Digitization-Only (300 photos)' : 'Custom Package'}
                                      </p>
                                      {order.shipping_cost && (
                                        <p>
                                          <span className="font-semibold">Shipping (Roundtrip):</span> ${parseFloat(order.shipping_cost).toFixed(2)}
                                        </p>
                                      )}
                                      {order.addon_credits > 0 && (
                                        <p className="text-purple-600 font-semibold">
                                          <span className="inline-flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                            </svg>
                                            {order.addon_credits} Animation Credits Added
                                          </span>
                                        </p>
                                      )}
                                      {order.shipping_address && (
                                        <p>
                                          <span className="font-semibold">Return Address:</span> {order.shipping_address.street1}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Order Total - Only show for purchaser, not gift recipient */}
                                  {!order.is_gift_recipient && order.amount_paid && (
                                    <div className="flex-shrink-0 text-right sm:text-right w-full sm:w-auto mt-4 sm:mt-0">
                                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                                        ${order.amount_paid.toFixed(2)}
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">Total Paid</p>
                                    </div>
                                  )}
                                  
                                  {/* Gift Badge for recipient */}
                                  {order.is_gift_recipient && (
                                    <div className="flex-shrink-0 text-right sm:text-right w-full sm:w-auto mt-4 sm:mt-0">
                                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg shadow-md">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                        </svg>
                                        <span className="font-semibold">Gift from {order.gift_sender_name}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Shipping/Tracking Info */}
                                {(order.tracking_number || order.return_tracking_number) && (
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                                    <h5 className="font-semibold text-blue-900 mb-3">Tracking Information</h5>
                                    
                                    <div className="space-y-2">
                                      {/* Outbound Kit Tracking */}
                                      {order.tracking_number && !order.tracking_number.includes('will be provided') && (
                                        <a 
                                          href={order.tracking_url || `#`}
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="flex items-center text-sm text-blue-700 hover:text-blue-900 hover:underline"
                                        >
                                          <span>📦 Shipping Kit (to you) - Tracking: {order.tracking_number}</span>
                                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                        </a>
                                      )}
                                      
                                      {/* Return Shipment Tracking */}
                                      {order.return_tracking_number && !order.return_tracking_number.includes('will be provided') && (
                                        <a 
                                          href={order.return_tracking_url || `#`}
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="flex items-center text-sm text-blue-700 hover:text-blue-900 hover:underline"
                                        >
                                          <span>📦 Your Photos (to us) - Tracking: {order.return_tracking_number}</span>
                                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                        </a>
                                      )}
                                      
                                      {/* Final Return Shipment Tracking (digitized photos back to customer) */}
                                      {order.final_return_tracking_number && !order.final_return_tracking_number.includes('will be provided') && (
                                        <a 
                                          href={order.final_return_tracking_url || `#`}
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="flex items-center text-sm text-purple-700 hover:text-purple-900 hover:underline"
                                        >
                                          <span>🎉 Your Photos (back to you) - Tracking: {order.final_return_tracking_number}</span>
                                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Cancelled Order Information */}
                                {isCancelled && (
                                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                                    <div className="flex items-center gap-2 mb-3">
                                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <h5 className="font-semibold text-red-900">Order Cancelled</h5>
                                    </div>
                                    
                                    {order.cancelled_at && (
                                      <p className="text-sm text-red-800 mb-2">
                                        <span className="font-medium">Date:</span> {new Date(order.cancelled_at).toLocaleString('en-US', {
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric',
                                          hour: 'numeric',
                                          minute: '2-digit',
                                          hour12: true
                                        })}
                                      </p>
                                    )}
                                    
                                    {order.cancellation_reason && (
                                      <p className="text-sm text-red-800 mb-2">
                                        <span className="font-medium">Reason:</span> {order.cancellation_reason}
                                      </p>
                                    )}
                                    
                                    {order.refund_status && (
                                      <div className="mt-3">
                                        <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                          order.refund_status === 'succeeded' ? 'bg-green-100 text-green-800' :
                                          order.refund_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                          order.refund_status === 'failed' ? 'bg-red-100 text-red-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {order.refund_status === 'succeeded' ? '✓ Refunded' :
                                           order.refund_status === 'pending' ? '⏳ Refund Processing' :
                                           order.refund_status === 'failed' ? '✗ Refund Failed' :
                                           order.refund_status}
                                        </span>
                                        
                                        {order.refund_status === 'succeeded' && (
                                          <p className="text-xs text-green-700 mt-2">
                                            Your refund has been processed and should appear in your account within 5-10 business days.
                                          </p>
                                        )}
                                        
                                        {order.refund_status === 'pending' && (
                                          <p className="text-xs text-yellow-700 mt-2">
                                            Your refund is being processed. Please allow 5-10 business days for it to appear in your account.
                                          </p>
                                        )}
                                        
                                        {order.refund_status === 'failed' && (
                                          <p className="text-xs text-red-700 mt-2">
                                            There was an issue processing your refund. Please contact support for assistance.
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Info Box for Label - Only show for recipients/regular orders, not gift purchasers */}
                                {!isCancelled && !order.tracking_number && !isGiftPurchase && (
                                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <h5 className="font-semibold text-yellow-900">Shipping Kit Label Pending</h5>
                                    </div>
                                    <p className="text-sm text-yellow-800">
                                      Your return kit shipping label will be generated during order fulfillment. Check back soon for tracking details!
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Credit Purchases Tab */}
                {ordersTab === 'credits' && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Credit Purchase History
                      </h2>
                    </div>
                    
                    {/* Purchases List or Empty State */}
                    {(!myOrders.credit_purchases || myOrders.credit_purchases.length === 0) ? (
                      <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">No Credit Purchases Yet</h3>
                        <p className="text-gray-600 max-w-md mx-auto mb-6">
                          Purchase video credits to create AI-powered memories. Your purchase history will appear here.
                        </p>
                        <button 
                          onClick={() => setShowBuyCreditsModal(true)}
                          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Buy Credits
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {myOrders.credit_purchases.map((purchase) => (
                          <div key={purchase.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                            {/* Purchase Header */}
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                  {purchase.is_gift ? (
                                    <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                    </svg>
                                  ) : (
                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                    </svg>
                                  )}
                                  {purchase.is_gift ? (
                                    <>
                                      {purchase.credits} Credits Gift 🎁
                                    </>
                                  ) : (
                                    <>
                                      {purchase.credits} Video Credits
                                    </>
                                  )}
                                </h3>
                                {purchase.is_gift && purchase.gift_to_name && (
                                  <p className="text-sm text-pink-600 font-medium mt-1">
                                    Gift for: {purchase.gift_to_name} ({purchase.gift_to_email})
                                  </p>
                                )}
                                <p className="text-sm text-gray-500">
                                  {new Date(purchase.created_at).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                purchase.status === 'paid' ? 'bg-green-100 text-green-800' :
                                purchase.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {purchase.status?.toUpperCase()}
                              </span>
                            </div>

                            {/* Purchase Details */}
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">Amount Paid</p>
                                  <p className="text-lg font-bold text-gray-900">
                                    ${purchase.amount.toFixed(2)} {purchase.currency}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">{purchase.is_gift ? 'Status' : 'Credits Received'}</p>
                                  {purchase.is_gift ? (
                                    <div className="flex items-center gap-2">
                                      {purchase.redemption_status === 'redeemed' ? (
                                        <>
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            Redeemed
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                            </svg>
                                            Not Redeemed
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  ) : (
                                    <p className="text-lg font-bold text-purple-600">
                                      {purchase.credits} credits
                                    </p>
                                  )}
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">{purchase.is_gift ? 'Order Number' : 'Transaction ID'}</p>
                                  <p className="text-xs font-mono text-gray-600 truncate">
                                    {purchase.is_gift && purchase.order_number ? purchase.order_number : purchase.id}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 mb-1">{purchase.is_gift ? 'Type' : 'Package'}</p>
                                  <p className="text-sm text-gray-700 capitalize">
                                    {purchase.is_gift ? (
                                      <span className="text-pink-600 font-semibold">🎁 Gift</span>
                                    ) : (
                                      purchase.package_id?.replace(/_/g, ' ')
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Moments Created Tab */}
                {ordersTab === 'videos' && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        {t('dashboard.your_moments_created')}
                      </h2>
                      <div className="text-sm text-gray-600 bg-purple-50 px-4 py-2 rounded-lg border border-purple-200">
                        <span className="font-semibold">Total Credits Used:</span> {myOrders.video_history?.filter(v => v.status === 'completed').length * 5 || 0}
                      </div>
                    </div>
                    
                    {/* Video List or Empty State */}
                    {(!myOrders.video_history || myOrders.video_history.length === 0) ? (
                      <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <svg className="w-20 h-20 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('dashboard.no_moments_created')}</h3>
                        <p className="text-gray-600 max-w-md mx-auto mb-6">
                          Start creating AI-powered video memories! Your magical moments will appear here.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {myOrders.video_history.map((video) => (
                          <div key={video.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow bg-white">
                            <div className="flex gap-3">
                              {/* Video Thumbnail - Even Smaller */}
                              <div className="flex-shrink-0">
                                <div className="w-16 h-16 rounded overflow-hidden bg-gray-200 relative">
                                  {video.video_thumbnail_url || video.source_image_url ? (
                                    <img 
                                      src={(() => {
                                        const url = video.video_thumbnail_url || video.source_image_url;
                                        // If URL is relative, prepend backend URL
                                        if (url && url.startsWith('/api/')) {
                                          return `${getBackendUrl()}${url}`;
                                        }
                                        return url;
                                      })()} 
                                      alt="Moment"
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                      decoding="async"
                                      onError={(e) => {
                                        // Fallback to placeholder on error
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = `
                                          <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-blue-100">
                                            <svg class="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                          </div>
                                        `;
                                      }}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-blue-100">
                                      <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                    </div>
                                  )}
                                  {/* Status Badge */}
                                  {video.status !== 'completed' && (
                                    <div className="absolute top-0.5 right-0.5">
                                      <span className={`px-1 py-0.5 rounded text-xs font-semibold ${
                                        video.status === 'processing' ? 'bg-blue-500 text-white' :
                                        video.status === 'failed' ? 'bg-red-500 text-white' :
                                        'bg-gray-500 text-white'
                                      }`}>
                                        {video.status?.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Video Details - Ultra Compact */}
                              <div className="flex-grow min-w-0 flex items-center justify-between">
                                <div className="flex-grow min-w-0">
                                  <p className="text-xs text-gray-700 italic line-clamp-1 mb-1">
                                    "{video.ai_analysis}"
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {new Date(video.created_at).toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                                <div className="text-right ml-3 flex-shrink-0">
                                  <p className="text-xs font-semibold text-purple-600">{video.credits_used} credits</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      )}


      {/* Mail-in Service View - Accessible via Shop > Mail-in Service subtab OR direct view */}
      {((currentView === 'shop' && shopSubtab === 'mail-in-service') || currentView === 'mail-in-service') && (
        <div style={currentView === 'shop' ? {} : {display: 'block', visibility: 'visible', position: 'absolute', top: '120px', left: 0, right: 0, zIndex: 1, minHeight: 'calc(100vh - 120px)', backgroundColor: '#ffffff'}} className="overflow-auto">
          {console.log('🔍 Rendering Mail-in Service view')}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-12 bg-white rounded-2xl shadow-sm my-6">
            
            {/* Header */}
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                </svg>
                <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Mail-In Photo Service
                </h1>
              </div>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
                Send us your physical photos, and we'll professionally digitize all your precious memories. Then you can animate, and display them in a beautiful digital frame.
              </p>
            </div>

            {/* Process Steps */}
            <div className="mb-12">
              
              {/* Enhanced animation styles */}
              <style>{`
                @keyframes mailDrop {
                  0% { transform: translateY(-30px) rotate(-15deg) scale(0.8); opacity: 0; }
                  20% { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
                  40% { transform: translateY(5px) rotate(3deg); }
                  60% { transform: translateY(-3px) rotate(-2deg); }
                  80% { transform: translateY(2px) rotate(1deg); }
                  100% { transform: translateY(0) rotate(0deg); }
                }
                
                @keyframes mailFloat {
                  0%, 100% { transform: translateY(0); }
                  50% { transform: translateY(-8px); }
                }
                
                @keyframes scanBeam {
                  0% { transform: translateY(-120%); opacity: 0; }
                  10% { opacity: 1; }
                  90% { opacity: 1; }
                  100% { transform: translateY(120%); opacity: 0; }
                }
                
                @keyframes scanPulse {
                  0%, 100% { transform: scale(1); }
                  50% { transform: scale(1.15); }
                }
                
                @keyframes imageReveal {
                  0%, 100% { opacity: 0.3; transform: scale(0.9); }
                  50% { opacity: 1; transform: scale(1); }
                }
                
                @keyframes scan {
                  0% { top: 0%; opacity: 0; }
                  10% { opacity: 1; }
                  90% { opacity: 1; }
                  100% { top: 100%; opacity: 0; }
                }
                
                .animate-scan {
                  animation: scan 2s ease-in-out infinite;
                }
                
                @keyframes sparkleOrbit {
                  0% { transform: rotate(0deg) translateX(35px) rotate(0deg); opacity: 0; }
                  10% { opacity: 1; }
                  90% { opacity: 1; }
                  100% { transform: rotate(360deg) translateX(35px) rotate(-360deg); opacity: 0; }
                }
                
                @keyframes sparkleOrbit2 {
                  0% { transform: rotate(120deg) translateX(35px) rotate(-120deg); opacity: 0; }
                  10% { opacity: 1; }
                  90% { opacity: 1; }
                  100% { transform: rotate(480deg) translateX(35px) rotate(-480deg); opacity: 0; }
                }
                
                @keyframes sparkleOrbit3 {
                  0% { transform: rotate(240deg) translateX(35px) rotate(-240deg); opacity: 0; }
                  10% { opacity: 1; }
                  90% { opacity: 1; }
                  100% { transform: rotate(600deg) translateX(35px) rotate(-600deg); opacity: 0; }
                }
                
                @keyframes magicTransform {
                  0%, 100% { transform: scale(1) rotate(0deg); }
                  25% { transform: scale(0.95) rotate(-5deg); }
                  50% { transform: scale(1.1) rotate(5deg); }
                  75% { transform: scale(0.98) rotate(-3deg); }
                }
                
                @keyframes playBounce {
                  0%, 100% { transform: scale(1); }
                  50% { transform: scale(1.3); }
                }
                
                @keyframes homeGlow {
                  0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.3); transform: translateY(0); }
                  50% { box-shadow: 0 0 50px rgba(34, 197, 94, 0.8), 0 0 80px rgba(34, 197, 94, 0.4); transform: translateY(-12px); }
                }
                
                @keyframes heartBeat {
                  0%, 100% { transform: scale(1); }
                  25% { transform: scale(1.2); }
                  40% { transform: scale(1.1); }
                  60% { transform: scale(1.15); }
                }
                
                @keyframes ringPulse {
                  0% { transform: scale(0.8); opacity: 0.8; }
                  50% { transform: scale(1.3); opacity: 0.3; }
                  100% { transform: scale(1.5); opacity: 0; }
                }
                
                .mail-icon { animation: mailDrop 2s ease-in-out infinite; }
                .mail-container:hover .mail-icon { animation: mailFloat 1.5s ease-in-out infinite; }
                .scan-icon { animation: scanPulse 2s ease-in-out infinite; }
                .scan-beam { animation: scanBeam 3s linear infinite; }
                .scan-image { animation: imageReveal 3s ease-in-out infinite; }
                .ai-icon { animation: magicTransform 3s ease-in-out infinite; }
                .sparkle-1 { animation: sparkleOrbit 4s linear infinite; }
                .sparkle-2 { animation: sparkleOrbit2 4s linear infinite; }
                .sparkle-3 { animation: sparkleOrbit3 4s linear infinite; }
                .play-pulse { animation: playBounce 2s ease-in-out infinite; }
                .home-icon { animation: homeGlow 3s ease-in-out infinite; }
                .heart-beat { animation: heartBeat 1.5s ease-in-out infinite; }
                .ring-pulse { animation: ringPulse 2s ease-out infinite; }
              `}</style>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-start">
                {/* Step 1 - Mail (Dramatic drop animation) */}
                <div className="flex flex-col items-center text-center mail-container">
                  <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center shadow-2xl mb-4 relative overflow-visible">
                    {/* Envelope dropping in */}
                    <svg className="w-12 h-12 text-white relative z-10 mail-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
                    </svg>
                    {/* Motion lines */}
                    <div className="absolute top-2 left-2 w-1 h-6 bg-white/30 rounded" style={{animation: 'mailDrop 2s ease-in-out infinite', animationDelay: '0.1s'}}></div>
                    <div className="absolute top-3 right-3 w-1 h-4 bg-white/30 rounded" style={{animation: 'mailDrop 2s ease-in-out infinite', animationDelay: '0.2s'}}></div>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Mail Your Photos</h4>
                  <p className="text-sm text-gray-600">
                    Pack your precious photos safely and mail them to us. We'll handle them with care!
                  </p>
                </div>

                {/* Step 2 - Digitize (Scanning beam animation) */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full flex items-center justify-center shadow-2xl mb-4 relative overflow-hidden">
                    {/* Multiple scanning beams */}
                    <div className="scan-beam absolute inset-0" style={{
                      background: 'linear-gradient(to bottom, transparent 0%, rgba(59, 130, 246, 0.4) 45%, rgba(147, 197, 253, 0.8) 50%, rgba(59, 130, 246, 0.4) 55%, transparent 100%)',
                      height: '20%',
                      boxShadow: '0 0 20px rgba(59, 130, 246, 0.6)'
                    }}></div>
                    <div className="scan-beam absolute inset-0" style={{
                      background: 'linear-gradient(to bottom, transparent 0%, rgba(59, 130, 246, 0.3) 45%, rgba(147, 197, 253, 0.6) 50%, rgba(59, 130, 246, 0.3) 55%, transparent 100%)',
                      height: '15%',
                      animationDelay: '1s'
                    }}></div>
                    {/* Photo being revealed */}
                    <div className="scan-image absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 bg-white/20 rounded"></div>
                    </div>
                    <svg className="w-12 h-12 text-white relative z-10 scan-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">We Digitize</h4>
                  <p className="text-sm text-gray-600">
                    Professional high-resolution scanning of every photo with quality checks.
                  </p>
                </div>

                {/* Step 3 - AI Animation (Orbiting sparkles + transform) */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center shadow-2xl mb-4 relative overflow-visible">
                    {/* Orbiting sparkles */}
                    <div className="absolute inset-0">
                      <div className="sparkle-1 absolute top-1/2 left-1/2 w-3 h-3 bg-yellow-300 rounded-full" style={{marginLeft: '-6px', marginTop: '-6px', filter: 'blur(1px)', boxShadow: '0 0 10px #fde047'}}></div>
                      <div className="sparkle-2 absolute top-1/2 left-1/2 w-2.5 h-2.5 bg-cyan-300 rounded-full" style={{marginLeft: '-5px', marginTop: '-5px', filter: 'blur(1px)', boxShadow: '0 0 8px #67e8f9'}}></div>
                      <div className="sparkle-3 absolute top-1/2 left-1/2 w-2 h-2 bg-pink-300 rounded-full" style={{marginLeft: '-4px', marginTop: '-4px', filter: 'blur(1px)', boxShadow: '0 0 6px #f9a8d4'}}></div>
                    </div>
                    {/* Magic wand stars */}
                    <div className="absolute top-2 right-2 text-yellow-300" style={{animation: 'sparkleOrbit 2s ease-in-out infinite'}}>✦</div>
                    <div className="absolute bottom-3 left-2 text-cyan-300" style={{animation: 'sparkleOrbit2 2.5s ease-in-out infinite'}}>✦</div>
                    <div className="absolute top-3 left-3 text-pink-300" style={{animation: 'sparkleOrbit3 2.2s ease-in-out infinite'}}>✦</div>
                    
                    {/* Play icon transforming */}
                    <svg className="w-12 h-12 text-white relative z-10 ai-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">AI Animation</h4>
                  <p className="text-sm text-gray-600">
                    Watch your memories come alive with our AI-powered animation technology!
                  </p>
                </div>

                {/* Step 4 - Enjoy (Glowing home with heartbeat) */}
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center shadow-2xl mb-4 home-icon relative overflow-visible">
                    {/* Pulsing rings */}
                    <div className="ring-pulse absolute inset-0 border-4 border-green-400 rounded-full" style={{animationDelay: '0s'}}></div>
                    <div className="ring-pulse absolute inset-0 border-4 border-green-400 rounded-full" style={{animationDelay: '0.7s'}}></div>
                    <div className="ring-pulse absolute inset-0 border-4 border-green-400 rounded-full" style={{animationDelay: '1.4s'}}></div>
                    
                    {/* Home icon with heartbeat */}
                    <svg className="w-12 h-12 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    
                    {/* Heart overlay */}
                    <svg className="heart-beat w-5 h-5 text-red-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Keep Your Memories</h4>
                  <p className="text-sm text-gray-600">
                    We return your originals plus give you 3 years of free digital access. Optional: pre-loaded digital frame!
                  </p>
                </div>
              </div>
            </div>

            {/* Trust Badges - Moved after process steps */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-lg p-4 mb-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center text-white">
                <div>
                  <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  <h4 className="font-semibold text-base mb-0.5">3 Years Free Storage</h4>
                  <p className="text-xs opacity-90">Access your precious moments anytime</p>
                </div>
                <div>
                  <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <h4 className="font-semibold text-base mb-0.5">Fast Turnaround</h4>
                  <p className="text-xs opacity-90">Most orders completed within 7-10 days</p>
                </div>
                <div>
                  <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="font-semibold text-base mb-0.5">Satisfaction Guaranteed</h4>
                  <p className="text-xs opacity-90">Love it or your money back</p>
                </div>
              </div>
            </div>

            {/* Service Packages */}
            <div className="bg-gradient-to-br from-purple-50/50 via-blue-50/30 to-white rounded-3xl shadow-lg border border-purple-100/50 p-8 mb-16 backdrop-blur-sm">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-3 text-center">Choose Your Package</h2>
              <p className="text-center text-sm text-gray-500 mb-10">
                Shipping costs are calculated separately at checkout. We'll return your original photos with care.
              </p>
              
              {/* Digitization-Only Packages */}
              <div className="mb-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  
                  {/* 100 Photos */}
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-200 hover:border-amber-400 transition-all hover:scale-105 flex flex-col">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-4 text-white text-center">
                      <h4 className="text-xl font-bold mb-1">Starter</h4>
                      <p className="text-sm opacity-90">100 Photos</p>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <span className="text-4xl font-bold text-gray-900">${99 + (mailInCredits100 === 25 ? 5 : mailInCredits100 === 50 ? 8 : mailInCredits100 === 100 ? 15 : mailInCredits100 === 250 ? 34 : mailInCredits100 === 500 ? 59 : 0)}</span>
                        <p className="text-xs text-gray-500 mt-1">$0.99 per photo{mailInCredits100 > 0 ? ` + ${mailInCredits100} credits` : ''}</p>
                      </div>

                      {/* Add Animation Credits (Optional) */}
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Add animation credits (Optional)
                        </label>
                        <select 
                          value={mailInCredits100}
                          onChange={(e) => setMailInCredits100(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                        >
                          <option value="0">No animation credits</option>
                          <option value="25">+25 credits (+$5) - 5 videos</option>
                          <option value="50">+50 credits (+$8) - 10 videos</option>
                          <option value="100">+100 credits (+$15) - 20 videos</option>
                          <option value="250">+250 credits (+$34) - 50 videos</option>
                          <option value="500">+500 credits (+$59) - 100 videos</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">15% discount · Never expires</p>
                      </div>

                      <ul className="space-y-3 mb-6 text-sm flex-1">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>100 photos digitized</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>High-resolution scans</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>3 years free cloud storage</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Originals returned</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>7-10 day processing</span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <button 
                          onClick={() => handleMailInOrder('100', 'Starter - 100 Photos', 100, 99, 17.99, mailInCredits100)}
                          className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors font-bold mb-2"
                        >
                          Order Now
                        </button>
                        <button 
                          onClick={() => {
                            // Correct credit addon pricing
                            const getAddonPrice = (credits) => {
                              const pricing = { 0: 0, 25: 5, 50: 8, 100: 15, 250: 34, 500: 59 };
                              return pricing[credits] || 0;
                            };
                            const addonPrice = getAddonPrice(mailInCredits100);
                            setSelectedMailInPackage({
                              id: '100',
                              name: 'Starter - 100 Photos',
                              photo_count: 100,
                              base_price: 99,
                              addon_credits: mailInCredits100,
                              addon_price: addonPrice,
                              shipping_cost: 17.99,
                              total: 99 + addonPrice + 17.99,
                              isGift: true
                            });
                            setShowMailInGiftOccasion(true);
                          }}
                          className="w-full bg-white text-purple-600 py-2 rounded-lg border-2 border-purple-600 hover:bg-purple-50 transition-colors font-medium text-sm"
                        >
                          🎁 Gift This
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 250 Photos */}
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-300 hover:border-amber-400 transition-all hover:scale-105 flex flex-col">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-4 text-white text-center">
                      <h4 className="text-xl font-bold mb-1">Standard</h4>
                      <p className="text-sm opacity-90">250 Photos</p>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <span className="text-4xl font-bold text-gray-900">${199 + (mailInCredits250 === 25 ? 5 : mailInCredits250 === 50 ? 8 : mailInCredits250 === 100 ? 15 : mailInCredits250 === 250 ? 34 : mailInCredits250 === 500 ? 59 : 0)}</span>
                        <p className="text-xs text-gray-500 mt-1">$0.80 per photo{mailInCredits250 > 0 ? ` + ${mailInCredits250} credits` : ''}</p>
                      </div>

                      {/* Add Animation Credits (Optional) */}
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Add animation credits (Optional)
                        </label>
                        <select 
                          value={mailInCredits250}
                          onChange={(e) => setMailInCredits250(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                        >
                          <option value="0">No animation credits</option>
                          <option value="25">+25 credits (+$5) - 5 videos</option>
                          <option value="50">+50 credits (+$8) - 10 videos</option>
                          <option value="100">+100 credits (+$15) - 20 videos</option>
                          <option value="250">+250 credits (+$34) - 50 videos</option>
                          <option value="500">+500 credits (+$59) - 100 videos</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">15% discount · Never expires</p>
                      </div>

                      <ul className="space-y-3 mb-6 text-sm flex-1">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>250 photos digitized</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>High-resolution scans</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>3 years free cloud storage</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Originals returned</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>7-10 day processing</span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <button 
                          onClick={() => handleMailInOrder('250', 'Standard - 250 Photos', 250, 199, 17.99, mailInCredits250)}
                          className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors font-bold mb-2"
                        >
                          Order Now
                        </button>
                        <button 
                          onClick={() => {
                            // Correct credit addon pricing
                            const getAddonPrice = (credits) => {
                              const pricing = { 0: 0, 25: 5, 50: 8, 100: 15, 250: 34, 500: 59 };
                              return pricing[credits] || 0;
                            };
                            const addonPrice = getAddonPrice(mailInCredits250);
                            setSelectedMailInPackage({
                              id: '250',
                              name: 'Standard - 250 Photos',
                              photo_count: 250,
                              base_price: 199,
                              addon_credits: mailInCredits250,
                              addon_price: addonPrice,
                              shipping_cost: 17.99,
                              total: 199 + addonPrice + 17.99
                            });
                            setShowMailInGiftOccasion(true);
                          }}
                          className="w-full bg-white text-purple-600 py-2 rounded-lg border-2 border-purple-600 hover:bg-purple-50 transition-colors font-medium text-sm"
                        >
                          🎁 Gift This
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 500 Photos */}
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-400 hover:border-amber-500 transition-all hover:scale-105 flex flex-col">
                    <div className="bg-gradient-to-br from-amber-600 to-orange-600 p-4 text-white text-center">
                      <h4 className="text-xl font-bold mb-1">Premium</h4>
                      <p className="text-sm opacity-90">500 Photos</p>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <span className="text-4xl font-bold text-gray-900">${299 + (mailInCredits500 === 25 ? 5 : mailInCredits500 === 50 ? 8 : mailInCredits500 === 100 ? 15 : mailInCredits500 === 250 ? 34 : mailInCredits500 === 500 ? 59 : 0)}</span>
                        <p className="text-xs text-gray-500 mt-1">$0.60 per photo{mailInCredits500 > 0 ? ` + ${mailInCredits500} credits` : ''}</p>
                      </div>

                      {/* Add Animation Credits (Optional) */}
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Add animation credits (Optional)
                        </label>
                        <select 
                          value={mailInCredits500}
                          onChange={(e) => setMailInCredits500(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                        >
                          <option value="0">No animation credits</option>
                          <option value="25">+25 credits (+$5) - 5 videos</option>
                          <option value="50">+50 credits (+$8) - 10 videos</option>
                          <option value="100">+100 credits (+$15) - 20 videos</option>
                          <option value="250">+250 credits (+$34) - 50 videos</option>
                          <option value="500">+500 credits (+$59) - 100 videos</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">15% discount · Never expires</p>
                      </div>

                      <ul className="space-y-3 mb-6 text-sm flex-1">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>500 photos digitized</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>High-resolution scans</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>3 years free cloud storage</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Originals returned</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>7-10 day processing</span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <button 
                          onClick={() => handleMailInOrder('500', 'Premium - 500 Photos', 500, 299, 29.99, mailInCredits500)}
                          className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors font-bold mb-2"
                        >
                          Order Now
                        </button>
                        <button 
                          onClick={() => {
                            // Correct credit addon pricing
                            const getAddonPrice = (credits) => {
                              const pricing = { 0: 0, 25: 5, 50: 8, 100: 15, 250: 34, 500: 59 };
                              return pricing[credits] || 0;
                            };
                            const addonPrice = getAddonPrice(mailInCredits500);
                            setSelectedMailInPackage({
                              id: '500',
                              name: 'Premium - 500 Photos',
                              photo_count: 500,
                              base_price: 299,
                              addon_credits: mailInCredits500,
                              addon_price: addonPrice,
                              shipping_cost: 29.99,
                              total: 299 + addonPrice + 29.99
                            });
                            setShowMailInGiftOccasion(true);
                          }}
                          className="w-full bg-white text-purple-600 py-2 rounded-lg border-2 border-purple-600 hover:bg-purple-50 transition-colors font-medium text-sm"
                        >
                          🎁 Gift This
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 1,000 Photos */}
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-500 hover:border-amber-600 transition-all hover:scale-105 flex flex-col">
                    <div className="bg-gradient-to-br from-amber-600 to-red-600 p-4 text-white text-center">
                      <h4 className="text-xl font-bold mb-1">Ultimate</h4>
                      <p className="text-sm opacity-90 mb-2">1,000 Photos</p>
                      <span className="inline-block bg-amber-300 text-amber-900 px-3 py-1 rounded-full text-xs font-bold">
                        BEST VALUE
                      </span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <span className="text-4xl font-bold text-gray-900">${499 + (mailInCredits1000 === 25 ? 5 : mailInCredits1000 === 50 ? 8 : mailInCredits1000 === 100 ? 15 : mailInCredits1000 === 250 ? 34 : mailInCredits1000 === 500 ? 59 : 0)}</span>
                        <p className="text-xs text-gray-500 mt-1">$0.50 per photo{mailInCredits1000 > 0 ? ` + ${mailInCredits1000} credits` : ''}</p>
                      </div>

                      {/* Add Animation Credits (Optional) */}
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Add animation credits (Optional)
                        </label>
                        <select 
                          value={mailInCredits1000}
                          onChange={(e) => setMailInCredits1000(parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                        >
                          <option value="0">No animation credits</option>
                          <option value="25">+25 credits (+$5) - 5 videos</option>
                          <option value="50">+50 credits (+$8) - 10 videos</option>
                          <option value="100">+100 credits (+$15) - 20 videos</option>
                          <option value="250">+250 credits (+$34) - 50 videos</option>
                          <option value="500">+500 credits (+$59) - 100 videos</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">15% discount · Never expires</p>
                      </div>

                      <ul className="space-y-3 mb-6 text-sm flex-1">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>1,000 photos digitized</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>High-resolution scans</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>3 years free cloud storage</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Originals returned</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>7-10 day processing</span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <button 
                          onClick={() => handleMailInOrder('1000', 'Ultimate - 1000 Photos', 1000, 499, 29.99, mailInCredits1000)}
                          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg hover:opacity-90 transition-opacity font-bold mb-2"
                        >
                          Order Now
                        </button>
                        <button 
                          onClick={() => {
                            // Correct credit addon pricing
                            const getAddonPrice = (credits) => {
                              const pricing = { 0: 0, 25: 5, 50: 8, 100: 15, 250: 34, 500: 59 };
                              return pricing[credits] || 0;
                            };
                            const addonPrice = getAddonPrice(mailInCredits1000);
                            setSelectedMailInPackage({
                              id: '1000',
                              name: 'Ultimate - 1000 Photos',
                              photo_count: 1000,
                              base_price: 499,
                              addon_credits: mailInCredits1000,
                              addon_price: addonPrice,
                              shipping_cost: 29.99,
                              total: 499 + addonPrice + 29.99
                            });
                            setShowMailInGiftOccasion(true);
                          }}
                          className="w-full bg-white text-purple-600 py-2 rounded-lg border-2 border-purple-600 hover:bg-purple-50 transition-colors font-medium text-sm"
                        >
                          🎁 Gift This
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Animation Combo Packages */}
              <div className="mb-12 hidden">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-gray-800 mb-3">Animation Credits</h3>
                  <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
                    Upload your own digital photos and animate the ones you choose
                  </p>
                  <div className="mt-4 inline-block bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-3 rounded-full border-2 border-amber-300">
                    <p className="text-sm font-semibold text-gray-800">
                      💡 No physical photos required · Upload digitally · Choose which photos to animate
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                
                {/* 100 Credits */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-300 hover:border-amber-400 transition-all hover:scale-105 flex flex-col">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-4 text-white text-center">
                      <h4 className="text-xl font-bold mb-1">Starter Credits</h4>
                      <p className="text-sm opacity-90">100 Animation Credits</p>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <span className="text-4xl font-bold text-gray-900">$155.00</span>
                        <p className="text-xs text-gray-500 mt-1">$1.55 per credit · ~20 videos</p>
                      </div>

                      <ul className="space-y-3 mb-6 text-sm flex-1">
                        <li className="flex items-start">
                          <span className="text-amber-500 mr-2">★</span>
                          <span className="font-bold">100 animation credits</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-amber-500 mr-2">★</span>
                          <span className="font-bold">Upload your own digital photos</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Choose which photos to animate</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>3 years free cloud storage</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Credits never expire</span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <button 
                          onClick={() => toast.info('Mail-in service launching soon! We\'ll notify you.')}
                          className="w-full bg-amber-500 text-white py-3 rounded-lg hover:bg-amber-600 transition-colors font-bold mb-2"
                        >
                          Order Now
                        </button>
                        <button 
                          onClick={() => toast.info('Gift option coming soon!')}
                          className="w-full bg-white text-amber-500 py-2 rounded-lg border-2 border-amber-500 hover:bg-amber-50 transition-colors font-medium text-sm"
                        >
                          Gift This
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 250 Credits */}
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-400 hover:border-amber-500 transition-all hover:scale-105 flex flex-col transform lg:scale-105">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-4 text-white text-center">
                      <h4 className="text-xl font-bold mb-1">Standard Credits</h4>
                      <p className="text-sm opacity-90 mb-2">250 Animation Credits</p>
                      <span className="inline-block bg-amber-300 text-amber-900 px-3 py-1 rounded-full text-xs font-bold">
                        POPULAR
                      </span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <span className="text-4xl font-bold text-gray-900">$362.50</span>
                        <p className="text-xs text-gray-500 mt-1">$1.45 per credit · ~50 videos</p>
                      </div>

                      <ul className="space-y-3 mb-6 text-sm flex-1">
                        <li className="flex items-start">
                          <span className="text-amber-500 mr-2">★</span>
                          <span className="font-bold">250 animation credits</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-amber-500 mr-2">★</span>
                          <span className="font-bold">Upload your own digital photos</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Choose which photos to animate</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>3 years free cloud storage</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Credits never expire</span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <button 
                          onClick={() => toast.info('Mail-in service launching soon! We\'ll notify you.')}
                          className="w-full bg-amber-500 text-white py-3 rounded-lg hover:bg-amber-600 transition-colors font-bold mb-2"
                        >
                          Order Now
                        </button>
                        <button 
                          onClick={() => toast.info('Gift option coming soon!')}
                          className="w-full bg-white text-amber-500 py-2 rounded-lg border-2 border-amber-500 hover:bg-amber-50 transition-colors font-medium text-sm"
                        >
                          Gift This
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 500 Credits */}
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-500 hover:border-amber-600 transition-all hover:scale-105 flex flex-col">
                    <div className="bg-gradient-to-br from-amber-600 to-orange-600 p-4 text-white text-center">
                      <h4 className="text-xl font-bold mb-1">Premium Credits</h4>
                      <p className="text-sm opacity-90">500 Animation Credits</p>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <span className="text-4xl font-bold text-gray-900">$625.00</span>
                        <p className="text-xs text-gray-500 mt-1">$1.25 per credit · ~100 videos</p>
                      </div>

                      <ul className="space-y-3 mb-6 text-sm flex-1">
                        <li className="flex items-start">
                          <span className="text-amber-500 mr-2">★</span>
                          <span className="font-bold">500 animation credits</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-amber-500 mr-2">★</span>
                          <span className="font-bold">Upload your own digital photos</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Choose which photos to animate</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>3 years free cloud storage</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Credits never expire</span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <button 
                          onClick={() => toast.info('Mail-in service launching soon! We\'ll notify you.')}
                          className="w-full bg-amber-600 text-white py-3 rounded-lg hover:bg-amber-700 transition-colors font-bold mb-2"
                        >
                          Order Now
                        </button>
                        <button 
                          onClick={() => toast.info('Gift option coming soon!')}
                          className="w-full bg-white text-amber-600 py-2 rounded-lg border-2 border-amber-600 hover:bg-amber-50 transition-colors font-medium text-sm"
                        >
                          Gift This
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* 1,000 Credits */}
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-600 hover:border-amber-700 transition-all hover:scale-105 flex flex-col">
                    <div className="bg-gradient-to-br from-amber-600 to-red-600 p-4 text-white text-center">
                      <h4 className="text-xl font-bold mb-1">Ultimate Credits</h4>
                      <p className="text-sm opacity-90 mb-2">1,000 Animation Credits</p>
                      <span className="inline-block bg-amber-300 text-amber-900 px-3 py-1 rounded-full text-xs font-bold">
                        BEST VALUE
                      </span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-center mb-4">
                        <span className="text-4xl font-bold text-gray-900">$1,150.00</span>
                        <p className="text-xs text-gray-500 mt-1">$1.15 per credit · ~200 videos</p>
                      </div>

                      <ul className="space-y-3 mb-6 text-sm flex-1">
                        <li className="flex items-start">
                          <span className="text-amber-600 mr-2">★</span>
                          <span className="font-bold">1,000 animation credits</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-amber-600 mr-2">★</span>
                          <span className="font-bold">Upload your own digital photos</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Choose which photos to animate</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>3 years free cloud storage</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>Credits never expire</span>
                        </li>
                      </ul>
                      <div className="mt-auto">
                        <button 
                          onClick={() => toast.info('Mail-in service launching soon! We\'ll notify you.')}
                          className="w-full bg-gradient-to-r from-amber-600 to-red-600 text-white py-3 rounded-lg hover:opacity-90 transition-opacity font-bold mb-2"
                        >
                          Order Now
                        </button>
                        <button 
                          onClick={() => toast.info('Gift option coming soon!')}
                          className="w-full bg-white text-amber-600 py-2 rounded-lg border-2 border-amber-600 hover:bg-amber-50 transition-colors font-medium text-sm"
                        >
                          Gift This
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Digital Upload Service section removed as per user request */}

            {/* Track Your Order Section */}
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 mb-12 border border-gray-100">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0 mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-gradient-to-br from-purple-100 to-blue-100 p-2 sm:p-3 rounded-xl flex-shrink-0">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4-4a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                    Track Your Order
                  </h2>
                </div>
                <button
                  onClick={() => {
                    setCurrentView('my-orders');
                    setOrdersTab('mailin');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Go to my orders
                </button>
              </div>
              
              {!user ? (
                <div className="text-center py-12 sm:py-16 px-4 sm:px-6 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 rounded-2xl border border-purple-100">
                  <div className="bg-gradient-to-br from-purple-100 to-blue-100 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 px-4">Please Log In</h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6 text-base sm:text-lg px-4 leading-relaxed">
                    Log in to view your active mail-in service orders.
                  </p>
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all text-sm sm:text-base"
                  >
                    Log In
                  </button>
                </div>
              ) : loadingTrackingOrders ? (
                <div className="text-center py-16 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 rounded-2xl border border-purple-100">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                  <p className="text-gray-600">Loading your orders...</p>
                </div>
              ) : trackingOrders.length === 0 ? (
                <div className="text-center py-12 sm:py-16 px-4 sm:px-6 bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 rounded-2xl border border-purple-100">
                  <div className="bg-gradient-to-br from-purple-100 to-blue-100 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 px-4">No Active Orders</h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6 sm:mb-8 text-base sm:text-lg px-4 leading-relaxed">
                    Once you submit an order, you'll be able to track its status here in real-time.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {trackingOrders.map((order) => {
                    // Check if this is a gift order
                    const isGift = order.is_gift || order.type === 'gift_sent' || order.redeemed_from_gift;
                    const isGiftRecipient = order.is_gift_recipient || order.redeemed_from_gift;
                    
                    // Get the proper status display
                    const getOrderStatus = () => {
                      // For gift sent (not redeemed yet)
                      if (order.type === 'gift_sent' && !order.redeemed) {
                        return { label: 'Gift Sent', color: 'bg-pink-500' };
                      }
                      
                      // Mail-in service specific statuses
                      if (order.status === 'digitizing' || order.status === 'processing_photos') {
                        return { label: 'Digitizing Photos', color: 'bg-indigo-500' };
                      }
                      if (order.status === 'prep_return' || order.status === 'return_shipped') {
                        return { label: 'Preparing Return', color: 'bg-purple-500' };
                      }
                      if (order.status === 'photos_received' || order.status === 'photos_in_transit') {
                        return { label: 'Photos Received', color: 'bg-blue-500' };
                      }
                      if (order.status === 'kit_delivered') {
                        return { label: 'Kit Delivered', color: 'bg-green-500' };
                      }
                      if (order.status === 'kit_shipped' || order.status === 'shipped' || order.tracking_number || order.fulfillment_status === 'fulfilled') {
                        return { label: 'Kit Shipped', color: 'bg-yellow-500' };
                      }
                      if (order.status === 'paid' || order.status === 'processing') {
                        return { label: 'Processing', color: 'bg-amber-500' };
                      }
                      
                      // Default
                      return { label: 'Order Placed', color: 'bg-gray-500' };
                    };
                    const orderStatus = getOrderStatus();
                    
                    return (
                      <div key={order.order_id} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                        {/* Order Header */}
                        <div className={`p-4 sm:p-6 border-b border-gray-200 ${
                          isGift ? 'bg-gradient-to-r from-pink-50 to-purple-50' : 'bg-gradient-to-r from-purple-50 to-blue-50'
                        }`}>
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                                  Order {order.order_number}
                                </h3>
                                {isGift && (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-semibold">
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                                    </svg>
                                    {isGiftRecipient ? 'Gift Received' : 'Gift Order'}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {order.package_details?.package_name || 'Mail-In Digitization Service'}
                              </p>
                              {isGift && order.recipient_email && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {isGiftRecipient ? `Gift from: ${order.sender_email || 'a friend'}` : `Gift to: ${order.recipient_email}`}
                                </p>
                              )}
                            </div>
                            <span className={`px-4 py-2 rounded-full text-sm font-semibold text-white ${orderStatus.color}`}>
                              {orderStatus.label}
                            </span>
                          </div>
                        </div>

                        {/* Order Content */}
                        <div className="p-4 sm:p-6">
                          <div className="flex items-start gap-4">
                            {/* Package Icon */}
                            <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-lg flex items-center justify-center">
                              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>

                            {/* Package Info */}
                            <div className="flex-1 min-w-0">
                              <div className="space-y-1 text-sm text-gray-600">
                                <p>
                                  <span className="font-semibold">Placed:</span> {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                                {order.shipping_address && (
                                  <p>
                                    <span className="font-semibold">Return Address:</span> {order.shipping_address.street1}, {order.shipping_address.city}, {order.shipping_address.state}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Tracking Info */}
                          {order.tracking_number && !order.tracking_number.includes('will be provided') && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                              <h5 className="font-semibold text-blue-900 mb-2">Tracking Information</h5>
                              <a 
                                href={order.tracking_url || `#`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center text-sm text-blue-700 hover:text-blue-900 hover:underline"
                              >
                                <span>📦 Shipping Kit - Tracking: {order.tracking_number}</span>
                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-8 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="max-w-3xl mx-auto space-y-3">
                <details className="group bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl p-5 hover:shadow-md transition-all border border-gray-100">
                  <summary className="cursor-pointer font-semibold text-gray-900 hover:text-purple-600 transition-colors list-none flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-purple-100 to-blue-100 p-2 rounded-lg flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span>How long does the process take?</span>
                    </div>
                    <svg className="w-5 h-5 group-open:rotate-180 transition-transform text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-gray-600 pl-12">
                    Most orders are completed within 7-10 days from when we receive your photos until we ship everything back.
                  </p>
                </details>
                
                <details className="group bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-5 hover:shadow-md transition-all border border-gray-100">
                  <summary className="cursor-pointer font-semibold text-gray-900 hover:text-purple-600 transition-colors list-none flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-2 rounded-lg flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <span>Are my original photos safe?</span>
                    </div>
                    <svg className="w-5 h-5 group-open:rotate-180 transition-transform text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-gray-600 pl-12">
                    Absolutely! We handle your precious memories with extreme care, and we return your originals in protective packaging with tracking.
                  </p>
                </details>
                
                <details className="group bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl p-5 hover:shadow-md transition-all border border-gray-100">
                  <summary className="cursor-pointer font-semibold text-gray-900 hover:text-purple-600 transition-colors list-none flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-purple-100 to-pink-100 p-2 rounded-lg flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <span>What formats do you accept?</span>
                    </div>
                    <svg className="w-5 h-5 group-open:rotate-180 transition-transform text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-gray-600 pl-12">
                    We accept standard photo prints (4x6, 5x7, 8x10), polaroids, vintage photos, and photos from albums. We can even work with damaged or faded photos!
                  </p>
                </details>
                
                <details className="group bg-gradient-to-br from-gray-50 to-indigo-50 rounded-xl p-5 hover:shadow-md transition-all border border-gray-100">
                  <summary className="cursor-pointer font-semibold text-gray-900 hover:text-purple-600 transition-colors list-none flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-2 rounded-lg flex-shrink-0">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                        </svg>
                      </div>
                      <span>What's included in the digitization?</span>
                    </div>
                    <svg className="w-5 h-5 group-open:rotate-180 transition-transform text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-gray-600 pl-12">
                    Professional high-resolution scanning, color correction, dust removal, 3 years of free cloud storage (additional cost after), and secure access from any device. Animation packages also include AI-powered video generation!
                  </p>
                </details>
                
                <details className="group bg-gradient-to-br from-gray-50 to-pink-50 rounded-xl p-5 hover:shadow-md transition-all border border-gray-100">
                  <summary className="cursor-pointer font-semibold text-gray-900 hover:text-purple-600 transition-colors list-none flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-pink-100 to-purple-100 p-2 rounded-lg flex-shrink-0">
                        <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        </svg>
                      </div>
                      <span>Can I gift this service?</span>
                    </div>
                    <svg className="w-5 h-5 group-open:rotate-180 transition-transform text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-gray-600 pl-12">
                    Yes! Our mail-in service makes a perfect gift. You can send themed egift cards straight to their email. Just pick the occasion  - Birthday, Holidays, Anniversary, you name it, and they'll receive a themed email with their mail in service gift and redemption instructions!
                  </p>
                </details>

                <details className="group bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-5 hover:shadow-md transition-all border border-gray-100">
                  <summary className="cursor-pointer font-semibold text-gray-900 hover:text-purple-600 transition-colors list-none flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-gradient-to-br from-blue-100 to-cyan-100 p-2 rounded-lg flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span>Where do I ship my photos?</span>
                    </div>
                    <svg className="w-5 h-5 group-open:rotate-180 transition-transform text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <p className="mt-4 text-gray-600 pl-12">
                    After ordering, we'll send you a shipping kit with a prepaid label included. Simply pack your photos safely in the kit and drop off at any shipping location. We'll email you when they arrive!
                  </p>
                </details>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Admin Panel Modal (Admin Only) */}
      {showAdminPanel && user?.email === 'brianjamessurvey@gmail.com' && (
        <Suspense fallback={<div className="flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>}>
          <AdminPanel
            currentUser={user}
            onSwitchUser={async (targetUser) => {
              // Switch user: update current user state and localStorage
              console.log('Switching to user:', targetUser);
              
              // Store original admin user before switching (only if not already stored)
              if (!originalAdminUser) {
                setOriginalAdminUser(user);
                localStorage.setItem('originalAdminUser', JSON.stringify(user));
              }
              
              // Update localStorage with new user
              localStorage.setItem('user', JSON.stringify(targetUser));
              localStorage.setItem('effectiveUserId', targetUser.id);
              localStorage.setItem('authenticatedUserId', targetUser.id);
              
              // Update React state
              setUser(targetUser);
              
              // Close admin panel
              setShowAdminPanel(false);
              
              // Reload dashboard data for new user
              await loadDashboardData();
              
              // Show success message
              toast.success(`Switched to ${targetUser.name || targetUser.email}`);
            }}
            onClose={() => setShowAdminPanel(false)}
          />
        </Suspense>
      )}

      {/* Cart Modal */}
      {showCart && (
        <Suspense fallback={<div>Loading...</div>}>
          <CartModal
            isOpen={showCart}
            onClose={() => setShowCart(false)}
            onOpenGiftCheckout={() => setShowGiftCheckout(true)}
            onCartUpdate={async () => {
              try {
                const userStr = localStorage.getItem('user');
                const userData = userStr ? JSON.parse(userStr) : null;
                const userId = userData?.id;
                
                if (userId) {
                  const response = await axios.get(`${BACKEND_URL}/api/shop/cart?user_id=${userId}`);
                  setCartCount(response.data.count);
                  window.cartCount = response.data.count;
                }
              } catch (err) {
                console.error('Error updating cart count:', err);
              }
            }}
            onViewFrameConfig={(cartItem) => {
              console.log('View frame config:', cartItem);
              
              // Set the order token and config name to load in DigitalFrameDemo
              setLoadCartFrameItem(cartItem);
              setShowCart(false);
              setShowFrameDemo(true);
            }}
          />
        </Suspense>
      )}

      {/* Footer */}
      <footer className="bg-gradient-to-r from-purple-800 to-blue-700 text-white py-8 sm:py-10 lg:py-12 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Logo and tagline */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
              <Logo className="w-10 h-10 sm:w-12 sm:h-12" animated={true} />
              <span className="text-xl sm:text-2xl lg:text-3xl font-bold">{t('landing.app_name')}</span>
            </div>
            <p className="text-sm sm:text-base lg:text-lg text-purple-100 max-w-3xl mx-auto leading-relaxed px-4">
              {t('landing.footer.made_with_love')} ❤️
            </p>
          </div>

          {/* Footer links */}
          <div className="flex flex-col justify-center items-center gap-3 text-xs sm:text-sm border-t border-purple-600 pt-6 sm:pt-8 pb-8 sm:pb-0 relative">
            {/* Copyright and links together */}
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 lg:gap-6">
              <div className="text-purple-200 text-center">{t('landing.footer.copyright')}</div>
              <div className="flex items-center justify-center gap-3 sm:gap-4 lg:gap-6">
                <button onClick={() => setShowContact(true)} className="hover:text-purple-200 transition-colors">{t('landing.footer.contact')}</button>
                <span className="text-purple-600">|</span>
                <button onClick={() => setShowPrivacy(true)} className="hover:text-purple-200 transition-colors">{t('landing.footer.privacy')}</button>
                <span className="text-purple-600">|</span>
                <button onClick={() => setShowTerms(true)} className="hover:text-purple-200 transition-colors">{t('landing.footer.terms')}</button>
              </div>
            </div>
            
            {/* Version info - Bottom right, below links on mobile */}
            <div className="absolute bottom-2 right-2 sm:static text-purple-300 text-[10px] sm:text-xs opacity-60 hover:opacity-100 transition-opacity">
              v1.0.0.{process.env.REACT_APP_GIT_SHA?.substring(0, 7) || process.env.VERCEL_GIT_COMMIT_SHA?.substring(0, 7) || 'dev'}
            </div>
          </div>
        </div>
      </footer>

      {/* Digital Frame Demo */}
      {showFrameDemo && (() => {
        console.log('🎯 APP.JS - Starting DigitalFrameDemo render');
        console.log('  📦 Total videoJobs:', videoJobs.length);
        
        // Debug: Check statuses
        const statusCounts = {};
        videoJobs.forEach(job => {
          statusCounts[job.status] = (statusCounts[job.status] || 0) + 1;
        });
        console.log('  📊 Status breakdown:', statusCounts);
        
        // Debug: Check thumbnail data in first 3 jobs
        console.log('  🖼️ Sample thumbnail data from videoJobs:', videoJobs.slice(0, 3).map(v => ({
          id: v.id,
          thumbnail_url: v.thumbnail_url || 'none',
          imageUrl: v.imageUrl || 'none',
          image_url: v.image_url || 'none',
          image_id: v.image_id || 'none'
        })));
        
        // Debug: Check result_url presence
        const withResultUrl = videoJobs.filter(job => job.result_url).length;
        const withoutResultUrl = videoJobs.filter(job => !job.result_url).length;
        console.log('  🔗 With result_url:', withResultUrl, '| Without:', withoutResultUrl);
        
        // Debug: Check for alternative URL fields
        const sampleJobWithoutUrl = videoJobs.find(job => !job.result_url);
        if (sampleJobWithoutUrl) {
          console.log('  🔍 Sample job WITHOUT result_url - available fields:', Object.keys(sampleJobWithoutUrl));
          console.log('  🔍 Full sample job:', sampleJobWithoutUrl);
        }
        const sampleJobWithUrl = videoJobs.find(job => job.result_url);
        if (sampleJobWithUrl) {
          console.log('  ✅ Sample job WITH result_url:', {
            result_url: sampleJobWithUrl.result_url,
            video_url: sampleJobWithUrl.video_url,
            url: sampleJobWithUrl.url
          });
        }
        
        // Debug: Check is_deleted
        const deleted = videoJobs.filter(job => job.is_deleted).length;
        const notDeleted = videoJobs.filter(job => !job.is_deleted).length;
        console.log('  🗑️ Deleted:', deleted, '| Not deleted:', notDeleted);
        
        const filteredVideos = videoJobs.filter(job => 
          (job.status === 'completed' || job.status === 'success') && 
          (job.result_url || job.video_url) && 
          !job.is_deleted
        ).map(job => {
          // Construct thumbnail URL - prefer /api/files/ format over R2 presigned URLs
          let thumbnailUrl = null;
          
          // Priority 1: If we have image_id, use /api/files/ endpoint (doesn't expire)
          if (job.image_id && user?.id) {
            thumbnailUrl = `/api/files/${job.image_id}?user_id=${user.id}&authenticated_user_id=${user.id}`;
            console.log('  ✅ Using /api/files/ for video', job.id);
          } 
          // Priority 2: Check if existing URL is /api/files/ format (good)
          else if (job.thumbnail_url && job.thumbnail_url.includes('/api/files/')) {
            thumbnailUrl = job.thumbnail_url;
          }
          else if (job.imageUrl && job.imageUrl.includes('/api/files/')) {
            thumbnailUrl = job.imageUrl;
          }
          // Priority 3: DON'T use expired R2 URLs - use null for placeholder
          else {
            const possibleUrl = job.thumbnail_url || job.imageUrl || job.image_url;
            if (possibleUrl && possibleUrl.includes('r2.cloudflarestorage')) {
              console.warn('  ⚠️ Skipping expired R2 URL for video', job.id, '- will show placeholder');
              thumbnailUrl = null; // Force placeholder
            } else if (possibleUrl && !possibleUrl.includes('X-Amz-')) {
              // Use it if it's not a presigned URL
              thumbnailUrl = possibleUrl;
            } else {
              console.error('  ❌ NO VALID THUMBNAIL for video', job.id, '- image_id:', job.image_id || 'MISSING');
              thumbnailUrl = null; // Force placeholder
            }
          }
          
          return {
            ...job,
            result_url: job.result_url || job.video_url, // Normalize to result_url
            thumbnail_url: thumbnailUrl,
            imageUrl: thumbnailUrl
          };
        });
        console.log('  ✅ Final filtered userVideos:', filteredVideos.length);
        console.log('  🎬 Sample URLs:', filteredVideos.slice(0, 3).map(v => v.result_url));
        console.log('  🖼️ Sample thumbnails:', filteredVideos.slice(0, 3).map(v => v.thumbnail_url || 'missing'));
        
        // Count videos with missing thumbnails
        const missingThumbnails = filteredVideos.filter(v => !v.thumbnail_url).length;
        if (missingThumbnails > 0) {
          console.error(`  ❌ ${missingThumbnails} videos have NO thumbnail URL!`);
        }
        
        return (
          <DigitalFrameDemo
            user={user}
            images={unassignedImages}
            imagesWithVideos={imagesWithVideos}
            userVideos={filteredVideos}
            folders={folders}
            getImageUrl={getImageUrl}
            preSelectedFrameColor={preSelectedFrameColor}
            preSelectedFrameProduct={preSelectedFrameProduct}
            isGiftMode={isGiftMode}
            onOpenGiftModal={(giftData) => {
              setGiftCardProduct(giftData);
              setShowGiftCardModal(true);
            }}
            onNavigateToCheckout={() => navigate('/checkout')}
            loadCartFrameItem={loadCartFrameItem}
            albumRefreshCallback={albumRefreshCallback}
          loadImageVideos={async (imageId) => {
            try {
              console.log('Loading videos for image:', imageId);
              
              // Check if it's a demo image first
              if (demoMode && imageId.startsWith('demo-pic-')) {
                const demoVids = DEMO_VIDEOS.filter(v => v.image_id === imageId).map(v => ({
                  ...v,
                  result_url: v.video_url || v.result_url // Normalize field name
                }));
                console.log('Demo videos for', imageId, ':', demoVids);
                return demoVids;
              }
              
              const response = await axios.get(`${getAPI()}/images/${imageId}/videos?user_id=${user.id}`);
              console.log('Response:', response.data);
              if (response.data.success && response.data.videos) {
                // Normalize field names for consistency
                return response.data.videos.map(v => ({
                  ...v,
                  result_url: v.result_url || v.video_url
                }));
              }
              return [];
            } catch (error) {
              console.error('Error loading image videos:', error);
              return [];
            }
          }}
          onCartUpdate={async () => {
            try {
              const response = await axios.get(`${BACKEND_URL}/api/shop/cart?user_id=${user.id}`);
              setCartCount(response.data.count);
              window.cartCount = response.data.count;
            } catch (err) {
              console.error('Error updating cart count:', err);
            }
          }}
          setCartCount={setCartCount}
          onClose={(options = {}) => {
            setShowFrameDemo(false);
            setPreSelectedFrameColor(null); // Clear pre-selection when closing
            setPreSelectedFrameProduct(null); // Clear pre-selected product when closing
            setLoadCartFrameItem(null); // Clear loaded cart frame item when closing
            
            // If item was added to cart, show notification and redirect to shop
            if (options.addedToCart) {
              // Switch to shop view
              setCurrentView('shop');
              
              // Show toast notification
              showToast('Added to cart!');
            }
          }}
          onBuyFrame={() => {
            // Scroll to mail-in service section on landing page
            setShowFrameDemo(false);
            onLogout();
            setTimeout(() => {
              const mailInSection = document.getElementById('mail-in-service');
              if (mailInSection) {
                mailInSection.scrollIntoView({ behavior: 'smooth' });
              }
            }, 500);
          }}
        />
        );
      })()}

      {/* Support Modal - Rendered at top level to appear above all tabs */}
      {showSupport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12" style={{ zIndex: 10000 }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full h-full md:w-[90vw] md:h-[85vh] lg:w-[80vw] lg:h-[80vh] xl:w-[70vw] xl:h-[75vh] max-w-5xl overflow-y-auto">
            <SupportSection user={user} onClose={() => setShowSupport(false)} />
          </div>
        </div>
      )}

      {showReferralDashboard && user && (
        <ReferralDashboard 
          user={user} 
          onClose={() => setShowReferralDashboard(false)} 
        />
      )}

      {/* Batch Delete Progress Modal */}
      {batchDeleteProgress.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mb-4">
                <svg className="animate-spin h-16 w-16 mx-auto text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {batchDeleteProgress.message}
              </h3>
              <p className="text-gray-600 mb-4">
                {batchDeleteProgress.current} of {batchDeleteProgress.total} completed
              </p>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(batchDeleteProgress.current / batchDeleteProgress.total) * 100}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Please don't close this window
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Gift Card Modal - For gifting frames */}
      {showGiftCardModal && giftCardProduct && (
        <GiftCardPurchaseModal
          isOpen={showGiftCardModal}
          onClose={() => {
            setShowGiftCardModal(false);
            setGiftCardProduct(null);
            setIsGiftMode(false); // Reset gift mode when closing
          }}
          user={user}
          selectedPackage={giftCardProduct}
          productType="frame"
        />
      )}

      {/* Download Progress Modal - For Pictures & Moments Downloads */}
      {showDownloadProgress && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
                {downloadProgress === 100 ? (
                  <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {downloadProgress === 100 ? 'Download Complete!' : 'Downloading Files'}
              </h3>
              <p className="text-sm text-gray-600 px-4 truncate max-w-full" title={downloadStatus}>
                {downloadStatus}
              </p>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-purple-600 to-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${downloadProgress}%` }}
                />
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-gray-500">{Math.round(downloadProgress)}%</span>
                {downloadProgress === 100 && (
                  <span className="text-sm font-semibold text-green-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Complete!
                  </span>
                )}
              </div>
            </div>
            
            {downloadProgress < 100 && (
              <p className="text-xs text-center text-gray-500">
                Please wait while we prepare your files...
              </p>
            )}
            
            {downloadProgress === 100 && (
              <button
                onClick={() => {
                  setShowDownloadProgress(false);
                  setDownloadProgress(0);
                }}
                className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      </>
  );
};

// Multi Upload Modal
const MultiUploadModal = ({ onClose, onRefresh, selectedFolder, user }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types - only images allowed
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'image/webp', 'image/gif'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|heic|heif|webp|gif)$/i));
    
    if (invalidFiles.length > 0) {
      alert(`Only image files are allowed (JPEG, PNG, HEIC, WebP, GIF).\n\nInvalid files:\n${invalidFiles.map(f => f.name).join('\n')}`);
      e.target.value = ''; // Reset input
      return;
    }
    
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    let uploadSucceeded = false;
    
    try {
      // Upload all files
      const uploadPromises = selectedFiles.map(async (file, index) => {
        // Fix orientation issues on mobile devices
        const correctedFile = await fixImageOrientation(file);
        
        const formData = new FormData();
        formData.append('file', correctedFile);
        formData.append('user_id', user.id); // CRITICAL: Pass user_id for data isolation
        // Don't auto-create projects - let users create projects manually
        
        formData.append('user_id', user.id);
        const response = await axios.post(`${getAPI()}/upload`, formData, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(((index + progressEvent.loaded / progressEvent.total) / selectedFiles.length) * 100);
            setUploadProgress(progress);
          }
        });
        return response.data;
      });

      const uploadedImages = await Promise.all(uploadPromises);
      uploadSucceeded = true; // Mark upload as successful
      
      // If a folder is selected, assign all uploaded images to that folder
      if (selectedFolder && selectedFolder.id) {
        const imageIds = uploadedImages.map(result => result.image?.id).filter(Boolean);
        if (imageIds.length > 0) {
          const formData = new FormData();
          imageIds.forEach(id => formData.append('image_ids', id));
          
          try {
            formData.append('user_id', user.id);
            await axios.post(`${getAPI()}/folders/${selectedFolder.id}/images`, formData);
            toast.success(`${selectedFiles.length} pictures uploaded to ${selectedFolder.name}!`);
          } catch (error) {
            console.error('Failed to assign images to folder:', error);
            toast.success(`${selectedFiles.length} pictures uploaded (but not added to folder)`);
          }
        }
      } else {
        toast.success(`${selectedFiles.length} pictures uploaded successfully!`);
      }
      
      // Small delay to ensure backend has finished processing
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force refresh with state update using the onRefresh callback
      console.log('🔄 Force refreshing to show uploaded images...');
      await onRefresh();
      console.log('✅ Images should now be visible');
    } catch (error) {
      console.error('Upload error:', error);
      
      // Check if it's a storage limit error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('Free tier limit')) {
        const detail = error.response.data.detail;
        toast.error(
          <div className="space-y-2">
            <div className="font-semibold">📸 Storage Limit Reached!</div>
            <div className="text-sm">{detail}</div>
            <div className="text-xs text-gray-300 mt-2">Create your first magic moment to unlock unlimited uploads! 🎥✨</div>
          </div>,
          { duration: 8000 }
        );
      } else if (!uploadSucceeded) {
        // Only show generic error toast if the actual upload failed
        toast.error('Failed to upload pictures');
      }
    } finally {
      setUploading(false);
      // Always close the modal, even if there were errors
      onClose();
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-60" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl max-w-2xl w-full p-6 shadow-2xl border border-amber-200" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-amber-800 mb-4">
          ✨ Upload Pictures
          {selectedFolder && (
            <span className="text-sm font-normal text-amber-600 ml-2">
              → to {selectedFolder.icon} {selectedFolder.name}
            </span>
          )}
        </h3>
        
        <div className="border-2 border-dashed border-amber-300 rounded-2xl p-8 text-center mb-4">
          <input
            type="file"
            multiple
            accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,image/webp,image/gif"
            onChange={handleFileSelect}
            className="hidden"
            id="multi-file-upload"
          />
          <label htmlFor="multi-file-upload" className="cursor-pointer">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <p className="text-amber-800 font-medium">Click to select pictures</p>
            <p className="text-amber-600 text-sm mt-1">JPG, PNG up to 10MB each</p>
          </label>
        </div>

        {selectedFiles.length > 0 && (
          <div className="mb-4">
            <p className="text-amber-800 font-medium mb-2">Selected Pictures ({selectedFiles.length}):</p>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {selectedFiles.map((file, index) => (
                <div key={index} className="text-sm text-amber-700 bg-amber-100 p-2 rounded">
                  {file.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {uploading && (
          <div className="mb-4">
            <div className="bg-amber-200 rounded-full h-2">
              <div className="bg-amber-500 h-2 rounded-full transition-all" style={{width: `${uploadProgress}%`}}></div>
            </div>
            <p className="text-amber-700 text-sm mt-1">Uploading... {uploadProgress}%</p>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || uploading}
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-colors disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Pictures`}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

// Enhanced Fullscreen Image Editor Modal Component  
// DEBUG: Check if environment variables are being read correctly
if (localStorage.getItem('debugMM') === '1') console.log('🔍 ENV CHECK AT MODULE LOAD:', {
  REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
  typeof: typeof process.env.REACT_APP_BACKEND_URL
});

const ImageEditorModal = ({ image, onClose, onSave, context = 'pictures', user, albumRefreshCallback = null }) => {
  // Helper to detect R2 presigned URLs
  const isPresignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  };
  
  // Helper function to generate image URL with user_id and authenticated_user_id
  const getImageUrl = (img) => {
    if (!img) {
      console.warn('ImageEditor getImageUrl: No image provided');
      return '';
    }
    
    // Get backend URL at runtime
    const BACKEND_URL = getBACKEND_URL();
    const API = getAPI();
    
    console.log('ImageEditor getImageUrl called with:', {
      hasUrl: !!img.url,
      hasThumbnailUrl: !!img.thumbnail_url,
      hasId: !!(img.id || img._id),
      url: img.url,
      thumbnail_url: img.thumbnail_url
    });
    
    // Prefer using existing URL fields (already have correct parameters from backend)
    if (img.url && img.url.startsWith('data:')) {
      console.log('Using data URL');
      return img.url;
    }
    
    // NEVER modify presigned URLs - they have cryptographic signatures
    if (img.url && isPresignedUrl(img.url)) {
      console.log('⚠️ Detected presigned url, returning as-is');
      return img.url;
    }
    if (img.thumbnail_url && isPresignedUrl(img.thumbnail_url)) {
      console.log('⚠️ Detected presigned thumbnail_url, returning as-is');
      return img.thumbnail_url;
    }

    // PRIORITY 1: Direct Cloudflare CDN URL
    if (img.cloudflare_delivery_url && 
        (img.cloudflare_delivery_url.includes('imagedelivery.net') || 
         img.cloudflare_delivery_url.includes('cloudflarestream.com') ||
         img.cloudflare_delivery_url.includes('customer-'))) {
      console.log('Using cloudflare_delivery_url:', img.cloudflare_delivery_url);
      return img.cloudflare_delivery_url;
    }
    
    // PRIORITY 2: Check if url is already a Cloudflare URL
    if (img.url && 
        (img.url.includes('imagedelivery.net') || 
         img.url.includes('cloudflarestream.com') ||
         img.url.includes('customer-'))) {
      console.log('Using Cloudflare URL from url field:', img.url);
      return img.url;
    }

    // Prefer URL first (constructed from r2_key if available via handleEditImage)
    if (img.url) {
      // Demo content or landing-assets with relative paths
      if (img.url.startsWith('/landing-assets/')) {
        console.log('Using landing-assets URL:', img.url);
        return img.url;
      }
      console.log('Using url:', img.url);
      return img.url;
    }
    if (img.thumbnail_url) {
      // Demo content or landing-assets with relative paths
      if (img.thumbnail_url.startsWith('/landing-assets/')) {
        console.log('Using landing-assets thumbnail_url:', img.thumbnail_url);
        return img.thumbnail_url;
      }
      console.log('Using thumbnail_url:', img.thumbnail_url);
      return img.thumbnail_url;
    }
    
    // Fallback: construct URL from image ID
    const imageId = img.id || img._id;
    if (!imageId || !user?.id) {
      console.warn('ImageEditor getImageUrl: Missing image ID or user ID');
      return '';
    }
    
    const effectiveUserId = user.id;
    const originalAdmin = localStorage.getItem('originalAdmin');
    const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : effectiveUserId;
    
    const constructedUrl = `${getAPI()}/files/${imageId}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`;
    console.log('Constructed URL from image ID:', constructedUrl);
    return constructedUrl;
  };
  
  // context can be 'pictures' (permanent changes) or 'animation' (temporary changes for Luma)
  const canvasRef = useRef(null);
  const [editing, setEditing] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    rotation: 0,
    sepia: 0,
    blur: 0,
    cropX: 0,
    cropY: 0,
    cropWidth: 100,
    cropHeight: 100
  });
  const [isSaving, setIsSaving] = useState(false);
  const [cropMode, setCropMode] = useState(false);
  const [fullScreenCrop, setFullScreenCrop] = useState(false);
  const [originalImage, setOriginalImage] = useState(null);
  const [originalImageUrl, setOriginalImageUrl] = useState(null);  // Track the true original URL
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showBasicAdjustments, setShowBasicAdjustments] = useState(false);
  const [showRotationEffects, setShowRotationEffects] = useState(false);
  const [showEffects, setShowEffects] = useState(false);

  useEffect(() => {
    // Always use the current image state for editing (not original)
    let currentUrl = getImageUrl(image);
    
    // Remove cache buster parameter but keep other query params
    if (currentUrl.includes('&v=')) {
      currentUrl = currentUrl.replace(/&v=\d+/, '');
    } else if (currentUrl.includes('?v=')) {
      // If v= is the first/only parameter, remove it but keep others
      currentUrl = currentUrl.replace(/\?v=\d+&/, '?').replace(/\?v=\d+$/, '');
    }
    
    // Store the original URL for revert functionality only
    if (image.original_url) {
      setOriginalImageUrl(image.original_url);
    } else {
      setOriginalImageUrl(currentUrl);
    }
    
    // Load the CURRENT image state (edited version if it exists)
    // Use fetch to properly handle query parameters with CORS
    const loadImage = async () => {
      // Convert relative URL to absolute URL and add authentication parameters
      let absoluteUrl = currentUrl.startsWith('http')
        ? currentUrl
        : (currentUrl.startsWith('/api/') ? `${BACKEND_URL}${currentUrl}` : `${window.location.origin}${currentUrl}`);
      
      // Add proxy=true for /api/r2/ URLs to avoid CORS issues
      if (absoluteUrl.includes('/api/r2/') && !absoluteUrl.includes('proxy=')) {
        const separator = absoluteUrl.includes('?') ? '&' : '?';
        absoluteUrl = `${absoluteUrl}${separator}proxy=true`;
        console.log('✅ Added proxy=true to R2 URL for CORS-safe loading');
      }
      
      // Add authentication parameters for /api/files/ endpoints if not already present
      if (absoluteUrl.includes('/api/files/') && !absoluteUrl.includes('user_id=')) {
        const userId = user?.id || '';
        const originalAdmin = localStorage.getItem('originalAdmin');
        const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : userId;
        
        const separator = absoluteUrl.includes('?') ? '&' : '?';
        absoluteUrl = `${absoluteUrl}${separator}user_id=${userId}&authenticated_user_id=${authenticatedUserId}`;
        console.log('✅ Added auth parameters to file URL for editor');
      }
      
      try {
        console.log('Editor loading current image state:', absoluteUrl);
        
        // Check if it's a Cloudflare URL (CORS-restricted)
        const isCloudflareUrl = absoluteUrl.includes('imagedelivery.net') || 
                                absoluteUrl.includes('cloudflarestream.com') ||
                                absoluteUrl.includes('customer-');
        
        // For Cloudflare URLs, don't send cache-control headers (causes CORS error)
        const fetchOptions = isCloudflareUrl 
          ? {} 
          : {
              cache: 'no-cache',
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              }
            };
        
        const response = await fetch(absoluteUrl, fetchOptions);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        // Set originalImageUrl FIRST so the img tag doesn't try to load getImageUrl(image)
        setOriginalImageUrl(blobUrl);
        console.log('Original URL (blob) stored:', blobUrl);
        
        const img = new Image();
        img.onload = () => {
          console.log('✅ Image loaded successfully in editor from blob');
          setOriginalImage(img);
          // Don't revoke blob URL yet - it's being used by the img tag in the preview
        };
        img.onerror = (error) => {
          console.error('❌ Failed to load image blob in editor:', error);
          URL.revokeObjectURL(blobUrl);
          toast.error('Failed to load image for editing. Please try again.');
        };
        img.src = blobUrl;
      } catch (error) {
        console.error('❌ Failed to fetch image in editor:', absoluteUrl, error);
        toast.error('Failed to load image for editing. Please try again.');
      }
    };
    
    loadImage();
    
    // Reset preview when image changes
    setPreviewUrl(null);
    
    // Cleanup function to revoke blob URLs
    return () => {
      if (originalImageUrl && originalImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(originalImageUrl);
      }
    };
  }, [image, context]);

  // Note: We don't clear preview when entering crop mode anymore
  // The preview should persist so users can see their accumulated changes

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Create canvas to apply transformations and get edited image data
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      if (originalImage) {
        // Calculate crop dimensions
        const cropStartX = (originalImage.width * editing.cropX) / 100;
        const cropStartY = (originalImage.height * editing.cropY) / 100;
        const cropWidth = (originalImage.width * editing.cropWidth) / 100;
        const cropHeight = (originalImage.height * editing.cropHeight) / 100;
        
        // Determine if we should apply crop (if crop dimensions are different from 100%)
        const shouldCrop = editing.cropWidth < 100 || editing.cropHeight < 100 || editing.cropX > 0 || editing.cropY > 0;
        
        // Calculate canvas dimensions for rotation
        const rotationRadians = (editing.rotation * Math.PI) / 180;
        const cos = Math.abs(Math.cos(rotationRadians));
        const sin = Math.abs(Math.sin(rotationRadians));
        
        // For rotated images, calculate the bounding box that contains the entire rotated image
        const rotatedWidth = originalImage.width * cos + originalImage.height * sin;
        const rotatedHeight = originalImage.width * sin + originalImage.height * cos;
        
        // Set canvas size - use rotated dimensions if rotation is applied, otherwise use crop/original
        if (editing.rotation !== 0 && !shouldCrop) {
          // For rotation without cropping, use the full rotated bounding box
          canvas.width = Math.ceil(rotatedWidth);
          canvas.height = Math.ceil(rotatedHeight);
        } else if (shouldCrop) {
          // For cropping (with or without rotation), use crop dimensions
          canvas.width = cropWidth;
          canvas.height = cropHeight;
        } else {
          // No rotation, no cropping - use original dimensions
          canvas.width = originalImage.width;
          canvas.height = originalImage.height;
        }
        
        // Apply transformations
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        
        // Apply rotation around center of canvas
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((editing.rotation * Math.PI) / 180);
        
        // Apply filters
        ctx.filter = `brightness(${editing.brightness}%) contrast(${editing.contrast}%) saturate(${editing.saturation}%) sepia(${editing.sepia}%) blur(${editing.blur}px)`;
        
        if (shouldCrop) {
          // Draw cropped image (cropping takes priority over rotation preservation)
          ctx.drawImage(
            originalImage,
            cropStartX, cropStartY, cropWidth, cropHeight,
            -cropWidth / 2, -cropHeight / 2, cropWidth, cropHeight
          );
        } else {
          // Draw full image - this preserves the entire image even when rotated
          ctx.drawImage(originalImage, -originalImage.width / 2, -originalImage.height / 2);
        }
        ctx.restore();
        
        // Get the edited image data URL with high quality
        const editedImageUrl = canvas.toDataURL('image/jpeg', 0.95);
        console.log('Generated edited image URL:', editedImageUrl.substring(0, 50) + '...');
        
        console.log('Save called with context:', context, 'Image ID:', image.id);
        
        // Use the provided context directly - no prompting
        const actualContext = context;
        
        if (actualContext === 'pictures') {
          // For Pictures tab - try to save permanently to database
          try {
            console.log('Attempting to save image permanently:', image.id, 'Context:', context, 'User ID:', user?.id);
            if (!user?.id) {
              console.error('ERROR: user.id is undefined!', user);
              toast.error('Cannot save: User not authenticated');
              return;
            }
            
            // Get the authenticated user ID for admin impersonation support
            const effectiveUserId = user.id;
            const originalAdmin = localStorage.getItem('originalAdmin');
            const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : effectiveUserId;
            
            const formData = new FormData();
            formData.append('image_data', editedImageUrl);
            
            // Check if this is an album asset or unassigned image
            const isAlbumAsset = !!image.album_id; // Album assets always have album_id
            const endpoint = isAlbumAsset 
              ? `${getAPI()}/albums/${image.album_id}/assets/${image.id}/file?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`
              : `${getAPI()}/unassigned-images/${image.id}/file?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`;
            
            console.log('Making PUT request to:', endpoint, '(Album asset:', isAlbumAsset, ')');
            const response = await axios.put(endpoint, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            console.log('Save response:', response.data);
            
            // Call onSave to trigger parent refresh - pass response data with cache-busting timestamp
            await onSave({
              ...image,
              ...response.data.image,
              _forceRefresh: Date.now(), // Force refresh flag
              _cacheBuster: Date.now() // Add cache-busting parameter to force image reload
            });
          } catch (error) {
            console.error('Failed to save image to database, falling back to temporary save:', error.response?.data || error.message);
            toast.warning('Could not save permanently, applied as temporary changes');
            
            // Fall back to temporary save
            await onSave({
              ...editing,
              editedImageUrl: editedImageUrl,
              timestamp: Date.now(),
              permanent: false
            });
          }
        } else {
          // For Animation Generator - temporary changes only for Luma submission
          console.log('Saving as temporary changes for animation context');
          await onSave({
            ...editing,
            editedImageUrl: editedImageUrl,
            timestamp: Date.now(),
            permanent: false
          });
        }
      } else {
        await onSave(editing);
      }
      
      // If this is an album image, trigger the album refresh callback
      console.log('🔍 DEBUG: Checking album refresh callback:', {
        hasAlbumId: !!image.album_id,
        albumId: image.album_id,
        hasCallback: !!albumRefreshCallback,
        hasCurrent: !!albumRefreshCallback?.current,
        isFunction: typeof albumRefreshCallback?.current === 'function'
      });
      
      if (image.album_id && albumRefreshCallback?.current && typeof albumRefreshCallback.current === 'function') {
        console.log('🔄 Triggering album refresh from ImageEditorModal after save');
        // CRITICAL: Longer delay to ensure MongoDB write completes AND replication finishes
        // Even 2000ms wasn't enough - increasing to 3000ms to guarantee read-after-write consistency
        setTimeout(() => {
          try {
            albumRefreshCallback.current();
            console.log('✅ Album refresh triggered successfully after 3000ms');
          } catch (e) {
            console.error('❌ Failed to trigger album refresh:', e);
          }
        }, 3000);  // Increased from 2000ms to 3000ms for MongoDB replication
      }
      
      toast.success('Picture adjustments saved! ✨');
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save adjustments');
    } finally {
      setIsSaving(false);
    }
  };

  const resetEditing = async () => {
    if (context === 'pictures') {
      try {
        const effectiveUserId = user.id;
        const originalAdmin = localStorage.getItem('originalAdmin');
        const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : effectiveUserId;

        if (image.album_id) {
          // Album asset revert
          const response = await axios.put(`${getAPI()}/albums/${image.album_id}/assets/${image.id}/revert?user_id=${effectiveUserId}`);
          console.log('✅ Album revert successful:', response.data);
        } else {
          // Unassigned image revert
          const response = await axios.put(`${getAPI()}/unassigned-images/${image.id}/revert?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`);
          console.log('✅ Unassigned revert successful:', response.data);
        }

        // Reset editor UI
        setEditing({
          brightness: 100,
          contrast: 100,
          saturation: 100,
          rotation: 0,
          sepia: 0,
          blur: 0,
          cropX: 20,
          cropY: 20,
          cropWidth: 60,
          cropHeight: 60
        });
        setCropMode(false);
        setFullScreenCrop(false);
        setPreviewUrl(null);

        // Notify parent and close
        if (onSave) {
          await onSave({ reverted: true, imageId: image.id });
        }
        
        // If this is an album image, trigger the album refresh callback
        if (image.album_id && albumRefreshCallback?.current && typeof albumRefreshCallback.current === 'function') {
          console.log('🔄 Triggering album refresh from ImageEditorModal after revert');
          setTimeout(() => {
            try {
              albumRefreshCallback.current();
              console.log('✅ Album refresh triggered successfully after revert');
            } catch (e) {
              console.error('❌ Failed to trigger album refresh:', e);
            }
          }, 800);
        }
        
        onClose();
        toast.success('Reverted to original image');
      } catch (error) {
        console.error('Failed to revert image:', error);
        if (error.response?.status === 400) {
          toast.error('This image has no previous version to revert to. It may be the original upload.');
        } else {
          toast.error('Failed to revert to original. Please try again.');
        }
      }
    } else {
      // Animation context - just reset editing parameters (temporary)
      setEditing({
        brightness: 100,
        contrast: 100,
        saturation: 100,
        rotation: 0,
        sepia: 0,
        blur: 0,
        cropX: 0,
        cropY: 0,
        cropWidth: 100,
        cropHeight: 100
      });
      setCropMode(false);
      setFullScreenCrop(false);
      setPreviewUrl(null);
      toast.success('Reverted to original image');
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black backdrop-blur-sm"
      style={{ 
        zIndex: 2147483647,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      {/* Responsive Header */}
      <div className="bg-gradient-to-r from-purple-100 to-blue-100 border-b border-purple-200 p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-purple-800 mb-1">
              {context === 'pictures' ? '🎨 Edit Picture' : '🎨 Adjust Picture'}
            </h3>
            <p className="text-sm md:text-base text-purple-600">
              {context === 'pictures' 
                ? 'Make permanent changes to your image' 
                : 'Fine-tune your memory before bringing it to life'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 md:w-12 md:h-12 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6 text-purple-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Responsive Content */}
      <div className="flex flex-col md:flex-row h-[calc(100vh-100px)]">
        {/* Main Preview Area - Smaller on mobile to give more room to controls */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-2 sm:p-4 md:p-8 lg:p-12 flex items-center justify-center md:flex-1 max-h-[40vh] md:max-h-none overflow-hidden">
          <div className="relative w-full max-w-6xl lg:max-w-7xl">
            <div className="bg-white rounded-xl sm:rounded-2xl p-2 sm:p-3 md:p-6 lg:p-8 shadow-xl border border-amber-200 flex flex-col">
              <h4 className="font-medium text-amber-800 mb-1 sm:mb-2 md:mb-4 text-center text-sm md:text-base">Preview</h4>
              <div 
                className="bg-gray-800 rounded-lg sm:rounded-xl overflow-hidden border border-amber-200 flex items-center justify-center relative" 
                style={{ 
                  width: '100%',
                  height: 'clamp(250px, 35vh, 400px)',
                  minHeight: '250px'
                }}
              >
                <img loading="lazy"
                  key={previewUrl ? `preview-${previewUrl.split('#')[1] || Date.now()}` : `original-${originalImageUrl || image.id}-${Date.now()}`}
                  src={previewUrl || originalImageUrl || getImageUrl(image)}
                  alt={image.name}
                  className="object-contain"
                  style={{
                    filter: `brightness(${editing.brightness}%) contrast(${editing.contrast}%) saturate(${editing.saturation}%) sepia(${editing.sepia}%) blur(${editing.blur}px)`,
                    transform: `rotate(${editing.rotation}deg)`,
                    transformOrigin: 'center center',
                    // Ensure rotated image fits within container by scaling down if needed
                    maxWidth: editing.rotation % 90 !== 0 ? '70%' : '100%',
                    maxHeight: editing.rotation % 90 !== 0 ? '70%' : '100%',
                    width: 'auto',
                    height: 'auto'
                  }}
                />
                
                {/* Interactive crop overlay when in crop mode */}
                {cropMode && (
                  <div className="absolute inset-0 bg-black/40">
                    {/* Crop selection box */}
                    <div 
                      className="absolute border-4 border-white border-solid bg-transparent cursor-move shadow-lg"
                      style={{
                        width: `${editing.cropWidth}%`,
                        height: `${editing.cropHeight}%`,
                        left: `${editing.cropX}%`,
                        top: `${editing.cropY}%`,
                        minWidth: '50px',
                        minHeight: '50px'
                      }}
                      onMouseDown={(e) => {
                        const startX = e.clientX;
                        const startY = e.clientY;
                        const startCropX = editing.cropX;
                        const startCropY = editing.cropY;
                        
                        const handleMouseMove = (e) => {
                          const deltaX = ((e.clientX - startX) / e.target.parentElement.offsetWidth) * 100;
                          const deltaY = ((e.clientY - startY) / e.target.parentElement.offsetHeight) * 100;
                          
                          setEditing(prev => ({
                            ...prev,
                            cropX: Math.max(0, Math.min(100 - prev.cropWidth, startCropX + deltaX)),
                            cropY: Math.max(0, Math.min(100 - prev.cropHeight, startCropY + deltaY))
                          }));
                        };
                        
                        const handleMouseUp = () => {
                          document.removeEventListener('mousemove', handleMouseMove);
                          document.removeEventListener('mouseup', handleMouseUp);
                        };
                        
                        document.addEventListener('mousemove', handleMouseMove);
                        document.addEventListener('mouseup', handleMouseUp);
                      }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        const touch = e.touches[0];
                        const startX = touch.clientX;
                        const startY = touch.clientY;
                        const startCropX = editing.cropX;
                        const startCropY = editing.cropY;
                        
                        const handleTouchMove = (e) => {
                          e.preventDefault();
                          const currentTouch = e.touches[0];
                          if (!currentTouch) return;
                          
                          const deltaX = ((currentTouch.clientX - startX) / e.target.parentElement.offsetWidth) * 100;
                          const deltaY = ((currentTouch.clientY - startY) / e.target.parentElement.offsetHeight) * 100;
                          
                          setEditing(prev => ({
                            ...prev,
                            cropX: Math.max(0, Math.min(100 - prev.cropWidth, startCropX + deltaX)),
                            cropY: Math.max(0, Math.min(100 - prev.cropHeight, startCropY + deltaY))
                          }));
                        };
                        
                        const handleTouchEnd = () => {
                          document.removeEventListener('touchmove', handleTouchMove);
                          document.removeEventListener('touchend', handleTouchEnd);
                        };
                        
                        document.addEventListener('touchmove', handleTouchMove, { passive: false });
                        document.addEventListener('touchend', handleTouchEnd);
                      }}
                    >
                      {/* Corner resize handles */}
                      <div className="absolute -top-1 -left-1 w-4 h-4 sm:w-3 sm:h-3 bg-white border border-gray-400 cursor-nw-resize"
                           onMouseDown={(e) => {
                             e.stopPropagation();
                             const clientX = e.clientX;
                             const clientY = e.clientY;
                             const startCropX = editing.cropX;
                             const startCropY = editing.cropY;
                             const startCropWidth = editing.cropWidth;
                             const startCropHeight = editing.cropHeight;
                             
                             const handleMove = (e) => {
                               const currentX = e.clientX || (e.touches && e.touches[0]?.clientX) || clientX;
                               const currentY = e.clientY || (e.touches && e.touches[0]?.clientY) || clientY;
                               const deltaX = ((currentX - clientX) / e.target.parentElement.parentElement.offsetWidth) * 100;
                               const deltaY = ((currentY - clientY) / e.target.parentElement.parentElement.offsetHeight) * 100;
                               
                               setEditing(prev => ({
                                 ...prev,
                                 cropX: Math.max(0, startCropX + deltaX),
                                 cropY: Math.max(0, startCropY + deltaY),
                                 cropWidth: Math.max(10, startCropWidth - deltaX),
                                 cropHeight: Math.max(10, startCropHeight - deltaY)
                               }));
                             };
                             
                             const handleEnd = () => {
                               document.removeEventListener('mousemove', handleMove);
                               document.removeEventListener('mouseup', handleEnd);
                               document.removeEventListener('touchmove', handleMove);
                               document.removeEventListener('touchend', handleEnd);
                             };
                             
                             document.addEventListener('mousemove', handleMove);
                             document.addEventListener('mouseup', handleEnd);
                             document.addEventListener('touchmove', handleMove, { passive: false });
                             document.addEventListener('touchend', handleEnd);
                           }}
                           onTouchStart={(e) => {
                             e.stopPropagation();
                             const touch = e.touches[0];
                             const clientX = touch.clientX;
                             const clientY = touch.clientY;
                             const startCropX = editing.cropX;
                             const startCropY = editing.cropY;
                             const startCropWidth = editing.cropWidth;
                             const startCropHeight = editing.cropHeight;
                             
                             const handleMove = (e) => {
                               e.preventDefault();
                               const currentTouch = e.touches[0];
                               if (!currentTouch) return;
                               
                               const deltaX = ((currentTouch.clientX - clientX) / e.target.parentElement.parentElement.offsetWidth) * 100;
                               const deltaY = ((currentTouch.clientY - clientY) / e.target.parentElement.parentElement.offsetHeight) * 100;
                               
                               setEditing(prev => ({
                                 ...prev,
                                 cropX: Math.max(0, startCropX + deltaX),
                                 cropY: Math.max(0, startCropY + deltaY),
                                 cropWidth: Math.max(10, startCropWidth - deltaX),
                                 cropHeight: Math.max(10, startCropHeight - deltaY)
                               }));
                             };
                             
                             const handleEnd = () => {
                               document.removeEventListener('touchmove', handleMove);
                               document.removeEventListener('touchend', handleEnd);
                             };
                             
                             document.addEventListener('touchmove', handleMove, { passive: false });
                             document.addEventListener('touchend', handleEnd);
                           }}>
                      </div>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-white border border-gray-400 cursor-ne-resize"></div>
                      <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-white border border-gray-400 cursor-sw-resize"></div>
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-3 sm:h-3 bg-white border border-gray-400 cursor-se-resize"
                           onMouseDown={(e) => {
                             e.stopPropagation();
                             const clientX = e.clientX;
                             const clientY = e.clientY;
                             const startCropWidth = editing.cropWidth;
                             const startCropHeight = editing.cropHeight;
                             
                             const handleMove = (e) => {
                               const currentX = e.clientX || (e.touches && e.touches[0]?.clientX) || clientX;
                               const currentY = e.clientY || (e.touches && e.touches[0]?.clientY) || clientY;
                               const deltaX = ((currentX - clientX) / e.target.parentElement.parentElement.offsetWidth) * 100;
                               const deltaY = ((currentY - clientY) / e.target.parentElement.parentElement.offsetHeight) * 100;
                               
                               setEditing(prev => ({
                                 ...prev,
                                 cropWidth: Math.max(10, Math.min(100 - prev.cropX, startCropWidth + deltaX)),
                                 cropHeight: Math.max(10, Math.min(100 - prev.cropY, startCropHeight + deltaY))
                               }));
                             };
                             
                             const handleEnd = () => {
                               document.removeEventListener('mousemove', handleMove);
                               document.removeEventListener('mouseup', handleEnd);
                               document.removeEventListener('touchmove', handleMove);
                               document.removeEventListener('touchend', handleEnd);
                             };
                             
                             document.addEventListener('mousemove', handleMove);
                             document.addEventListener('mouseup', handleEnd);
                             document.addEventListener('touchmove', handleMove, { passive: false });
                             document.addEventListener('touchend', handleEnd);
                           }}
                           onTouchStart={(e) => {
                             e.stopPropagation();
                             const touch = e.touches[0];
                             const clientX = touch.clientX;
                             const clientY = touch.clientY;
                             const startCropWidth = editing.cropWidth;
                             const startCropHeight = editing.cropHeight;
                             
                             const handleMove = (e) => {
                               e.preventDefault();
                               const currentTouch = e.touches[0];
                               if (!currentTouch) return;
                               
                               const deltaX = ((currentTouch.clientX - clientX) / e.target.parentElement.parentElement.offsetWidth) * 100;
                               const deltaY = ((currentTouch.clientY - clientY) / e.target.parentElement.parentElement.offsetHeight) * 100;
                               
                               setEditing(prev => ({
                                 ...prev,
                                 cropWidth: Math.max(10, Math.min(100 - prev.cropX, startCropWidth + deltaX)),
                                 cropHeight: Math.max(10, Math.min(100 - prev.cropY, startCropHeight + deltaY))
                               }));
                             };
                             
                             const handleEnd = () => {
                               document.removeEventListener('touchmove', handleMove);
                               document.removeEventListener('touchend', handleEnd);
                             };
                             
                             document.addEventListener('touchmove', handleMove, { passive: false });
                             document.addEventListener('touchend', handleEnd);
                           }}>
                      </div>
                      
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white text-xs bg-black/60 px-2 py-1 rounded pointer-events-none">
                        ✂️ Crop Selection
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Controls Panel - Full width on mobile, sidebar on desktop */}
        <div className="flex-1 w-full md:w-96 lg:w-[450px] xl:w-[500px] md:flex-none bg-white border-t md:border-t-0 md:border-l border-purple-200 overflow-y-auto min-h-[60vh] md:min-h-auto">
          <div className="p-4 sm:p-4 md:p-6 space-y-4 sm:space-y-4 md:space-y-6" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
            
            {/* Crop Button - Top Priority */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border border-purple-200">
              <button
                onClick={() => {
                  if (!cropMode) {
                    setCropMode(true);
                    setFullScreenCrop(true);
                  } else {
                    setCropMode(false);
                    setFullScreenCrop(false);
                  }
                }}
                className={`w-full px-4 py-3 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
                  cropMode
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white shadow-lg'
                }`}
              >
                {cropMode ? '✓ Exit Crop' : '✂️ Crop'}
              </button>
            </div>
            
            {/* Basic Adjustments */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border border-purple-200 shadow-sm">
              <button 
                onClick={() => setShowBasicAdjustments(!showBasicAdjustments)}
                className="w-full text-left flex items-center justify-between mb-2 sm:mb-3 md:mb-4 hover:text-purple-900 transition-colors"
              >
                <h4 className="font-bold text-purple-800 flex items-center gap-2 text-sm sm:text-base">
                  <span>⚡</span> Basic Adjustments
                </h4>
                <span className={`text-purple-600 transition-transform ${showBasicAdjustments ? 'rotate-180' : ''}`}>▼</span>
              </button>
              {showBasicAdjustments && (
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <div>
                  <label className="text-sm font-medium text-purple-700 mb-2 block">Brightness ({editing.brightness}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={editing.brightness}
                    onChange={(e) => setEditing({ ...editing, brightness: parseInt(e.target.value) })}
                    className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider accent-purple-600"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-purple-700 mb-2 block">Contrast ({editing.contrast}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={editing.contrast}
                    onChange={(e) => setEditing({ ...editing, contrast: parseInt(e.target.value) })}
                    className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider accent-purple-600"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-purple-700 mb-2 block">Saturation ({editing.saturation}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="200"
                    value={editing.saturation}
                    onChange={(e) => setEditing({ ...editing, saturation: parseInt(e.target.value) })}
                    className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider accent-purple-600"
                  />
                </div>
              </div>
              )}
            </div>

            {/* Rotation & Transform */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border border-blue-200 shadow-sm">
              <button 
                onClick={() => setShowRotationEffects(!showRotationEffects)}
                className="w-full text-left flex items-center justify-between mb-2 sm:mb-3 md:mb-4 hover:text-blue-900 transition-colors"
              >
                <h4 className="font-bold text-blue-800 flex items-center gap-2 text-sm sm:text-base">
                  <span>🔄</span> Rotation & Transform
                </h4>
                <span className={`text-blue-600 transition-transform ${showRotationEffects ? 'rotate-180' : ''}`}>▼</span>
              </button>
              {showRotationEffects && (
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                <div>
                  <label className="text-sm font-medium text-blue-700 mb-2 block">Free Rotation ({editing.rotation}°)</label>
                  <input
                    type="range"
                    min="-180"
                    max="180"
                    value={editing.rotation}
                    onChange={(e) => setEditing({ ...editing, rotation: parseInt(e.target.value) })}
                    className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer slider accent-blue-600"
                  />
                  <input
                    type="number"
                    min="-180"
                    max="180"
                    value={editing.rotation}
                    onChange={(e) => setEditing({ ...editing, rotation: parseInt(e.target.value) || 0 })}
                    className="mt-2 w-full px-3 py-2 border border-blue-200 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter degrees"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setEditing({ ...editing, rotation: (editing.rotation - 90) % 360 })}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
                  >
                    ↺ 90° Left
                  </button>
                  <button
                    onClick={() => setEditing({ ...editing, rotation: (editing.rotation + 90) % 360 })}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
                  >
                    ↻ 90° Right
                  </button>
                  <button
                    onClick={() => setEditing({ ...editing, rotation: 0 })}
                    className="col-span-2 bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    Reset Rotation
                  </button>
                </div>
              </div>
              )}
            </div>

            {/* Effects */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-3 sm:p-5 border border-purple-200 shadow-sm">
              <button 
                onClick={() => setShowEffects(!showEffects)}
                className="w-full text-left flex items-center justify-between mb-4 hover:text-purple-900 transition-colors"
              >
                <h4 className="font-bold text-purple-800 flex items-center gap-2 text-sm sm:text-base">
                  <span>✨</span> Effects
                </h4>
                <span className={`text-purple-600 transition-transform ${showEffects ? 'rotate-180' : ''}`}>▼</span>
              </button>
              {showEffects && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-purple-700 mb-2 block">Vintage Feel ({editing.sepia}%)</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={editing.sepia}
                    onChange={(e) => setEditing({ ...editing, sepia: parseInt(e.target.value) })}
                    className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider accent-purple-600"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-purple-700 mb-2 block">Blur ({editing.blur}px)</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={editing.blur}
                    onChange={(e) => setEditing({ ...editing, blur: parseInt(e.target.value) })}
                    className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider accent-purple-600"
                  />
                </div>
              </div>
              )}
            </div>

            {/* Action Buttons - Enhanced for Mobile */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 sm:p-4 md:p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-t border-purple-200"
                 style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
              <button
                onClick={resetEditing}
                className="flex-1 bg-white border-2 border-purple-300 text-purple-700 px-4 py-4 sm:py-3 rounded-xl hover:bg-purple-50 transition-colors font-medium min-h-[48px] touch-manipulation shadow-sm"
              >
                🔄 Revert to Original
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-4 sm:py-3 rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px] touch-manipulation font-medium shadow-lg"
              >
                {isSaving ? 'Saving...' : (context === 'pictures' ? '💾 Save Changes' : '✨ Apply for Animation')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Full-Screen Crop Interface */}
      {fullScreenCrop && (
        <div className="fixed inset-0 bg-black z-[101] flex flex-col">
          {/* Top Bar */}
          <div className="flex items-center justify-between p-4 bg-black/80 backdrop-blur-sm text-white">
            <h3 className="text-lg font-semibold">✂️ Crop Image</h3>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  setCropMode(false);
                  setFullScreenCrop(false);
                }}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
          
          {/* Full-Screen Image Container */}
          <div className="flex-1 relative overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="relative max-w-full max-h-full">
                <img loading="lazy"
                  src={originalImageUrl || image.url || image.thumbnail_url}
                  alt={image.name}
                  className="max-w-full max-h-full object-contain"
                  style={{ 
                    maxHeight: 'calc(100vh - 200px)',
                    maxWidth: 'calc(100vw - 32px)',
                    imageRendering: 'high-quality'
                  }}
                />
                
                {/* Dark Overlay - Creates clear crop area with darkened outside using CSS box-shadow */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    zIndex: 5
                  }}
                >
                  {/* This creates a dark overlay everywhere EXCEPT the crop area */}
                  <div
                    className="absolute bg-transparent border-0"
                    style={{
                      left: `${editing.cropX}%`,
                      top: `${editing.cropY}%`,
                      width: `${editing.cropWidth}%`,
                      height: `${editing.cropHeight}%`,
                      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
                    }}
                  />
                </div>
                
                {/* Crop Selection Border - Interactive crop area */}
                <div
                  className="absolute border-2 border-white cursor-move"
                  style={{
                    left: `${editing.cropX}%`,
                    top: `${editing.cropY}%`,
                    width: `${editing.cropWidth}%`,
                    height: `${editing.cropHeight}%`,
                    minWidth: '50px',
                    minHeight: '50px',
                    backgroundColor: 'transparent',
                    zIndex: 10
                  }}
                  onMouseDown={(e) => {
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const startCropX = editing.cropX;
                    const startCropY = editing.cropY;
                    
                    const handleMouseMove = (e) => {
                      const deltaX = ((e.clientX - startX) / e.target.parentElement.offsetWidth) * 100;
                      const deltaY = ((e.clientY - startY) / e.target.parentElement.offsetHeight) * 100;
                      
                      setEditing(prev => ({
                        ...prev,
                        cropX: Math.max(0, Math.min(100 - prev.cropWidth, startCropX + deltaX)),
                        cropY: Math.max(0, Math.min(100 - prev.cropHeight, startCropY + deltaY))
                      }));
                    };
                    
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };
                    
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    const touch = e.touches[0];
                    const startX = touch.clientX;
                    const startY = touch.clientY;
                    const startCropX = editing.cropX;
                    const startCropY = editing.cropY;
                    
                    const handleTouchMove = (e) => {
                      e.preventDefault();
                      const currentTouch = e.touches[0];
                      if (!currentTouch) return;
                      
                      const deltaX = ((currentTouch.clientX - startX) / e.target.parentElement.offsetWidth) * 100;
                      const deltaY = ((currentTouch.clientY - startY) / e.target.parentElement.offsetHeight) * 100;
                      
                      setEditing(prev => ({
                        ...prev,
                        cropX: Math.max(0, Math.min(100 - prev.cropWidth, startCropX + deltaX)),
                        cropY: Math.max(0, Math.min(100 - prev.cropHeight, startCropY + deltaY))
                      }));
                    };
                    
                    const handleTouchEnd = () => {
                      document.removeEventListener('touchmove', handleTouchMove);
                      document.removeEventListener('touchend', handleTouchEnd);
                    };
                    
                    document.addEventListener('touchmove', handleTouchMove, { passive: false });
                    document.addEventListener('touchend', handleTouchEnd);
                  }}
                >
                  {/* Corner Handles */}
                  <div className="absolute -top-2 -left-2 w-6 h-6 bg-white border-2 border-gray-400 rounded-full cursor-nw-resize"
                       onMouseDown={(e) => {
                         e.stopPropagation();
                         const clientX = e.clientX;
                         const clientY = e.clientY;
                         const startCropX = editing.cropX;
                         const startCropY = editing.cropY;
                         const startCropWidth = editing.cropWidth;
                         const startCropHeight = editing.cropHeight;
                         
                         const handleMove = (e) => {
                           const currentX = e.clientX || (e.touches && e.touches[0]?.clientX) || clientX;
                           const currentY = e.clientY || (e.touches && e.touches[0]?.clientY) || clientY;
                           const deltaX = ((currentX - clientX) / e.target.parentElement.parentElement.offsetWidth) * 100;
                           const deltaY = ((currentY - clientY) / e.target.parentElement.parentElement.offsetHeight) * 100;
                           
                           setEditing(prev => ({
                             ...prev,
                             cropX: Math.max(0, startCropX + deltaX),
                             cropY: Math.max(0, startCropY + deltaY),
                             cropWidth: Math.max(10, startCropWidth - deltaX),
                             cropHeight: Math.max(10, startCropHeight - deltaY)
                           }));
                         };
                         
                         const handleEnd = () => {
                           document.removeEventListener('mousemove', handleMove);
                           document.removeEventListener('mouseup', handleEnd);
                           document.removeEventListener('touchmove', handleMove);
                           document.removeEventListener('touchend', handleEnd);
                         };
                         
                         document.addEventListener('mousemove', handleMove);
                         document.addEventListener('mouseup', handleEnd);
                         document.addEventListener('touchmove', handleMove, { passive: false });
                         document.addEventListener('touchend', handleEnd);
                       }}
                       onTouchStart={(e) => {
                         e.stopPropagation();
                         const touch = e.touches[0];
                         const clientX = touch.clientX;
                         const clientY = touch.clientY;
                         const startCropX = editing.cropX;
                         const startCropY = editing.cropY;
                         const startCropWidth = editing.cropWidth;
                         const startCropHeight = editing.cropHeight;
                         
                         const handleMove = (e) => {
                           e.preventDefault();
                           const currentTouch = e.touches[0];
                           if (!currentTouch) return;
                           
                           const deltaX = ((currentTouch.clientX - clientX) / e.target.parentElement.parentElement.offsetWidth) * 100;
                           const deltaY = ((currentTouch.clientY - clientY) / e.target.parentElement.parentElement.offsetHeight) * 100;
                           
                           setEditing(prev => ({
                             ...prev,
                             cropX: Math.max(0, startCropX + deltaX),
                             cropY: Math.max(0, startCropY + deltaY),
                             cropWidth: Math.max(10, startCropWidth - deltaX),
                             cropHeight: Math.max(10, startCropHeight - deltaY)
                           }));
                         };
                         
                         const handleEnd = () => {
                           document.removeEventListener('touchmove', handleMove);
                           document.removeEventListener('touchend', handleEnd);
                         };
                         
                         document.addEventListener('touchmove', handleMove, { passive: false });
                         document.addEventListener('touchend', handleEnd);
                       }}>
                  </div>
                  
                  <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-white border-2 border-gray-400 rounded-full cursor-se-resize"
                       onMouseDown={(e) => {
                         e.stopPropagation();
                         const clientX = e.clientX;
                         const clientY = e.clientY;
                         const startCropWidth = editing.cropWidth;
                         const startCropHeight = editing.cropHeight;
                         
                         const handleMove = (e) => {
                           const currentX = e.clientX || (e.touches && e.touches[0]?.clientX) || clientX;
                           const currentY = e.clientY || (e.touches && e.touches[0]?.clientY) || clientY;
                           const deltaX = ((currentX - clientX) / e.target.parentElement.parentElement.offsetWidth) * 100;
                           const deltaY = ((currentY - clientY) / e.target.parentElement.parentElement.offsetHeight) * 100;
                           
                           setEditing(prev => ({
                             ...prev,
                             cropWidth: Math.max(10, Math.min(100 - prev.cropX, startCropWidth + deltaX)),
                             cropHeight: Math.max(10, Math.min(100 - prev.cropY, startCropHeight + deltaY))
                           }));
                         };
                         
                         const handleEnd = () => {
                           document.removeEventListener('mousemove', handleMove);
                           document.removeEventListener('mouseup', handleEnd);
                           document.removeEventListener('touchmove', handleMove);
                           document.removeEventListener('touchend', handleEnd);
                         };
                         
                         document.addEventListener('mousemove', handleMove);
                         document.addEventListener('mouseup', handleEnd);
                         document.addEventListener('touchmove', handleMove, { passive: false });
                         document.addEventListener('touchend', handleEnd);
                       }}
                       onTouchStart={(e) => {
                         e.stopPropagation();
                         const touch = e.touches[0];
                         const clientX = touch.clientX;
                         const clientY = touch.clientY;
                         const startCropWidth = editing.cropWidth;
                         const startCropHeight = editing.cropHeight;
                         
                         const handleMove = (e) => {
                           e.preventDefault();
                           const currentTouch = e.touches[0];
                           if (!currentTouch) return;
                           
                           const deltaX = ((currentTouch.clientX - clientX) / e.target.parentElement.parentElement.offsetWidth) * 100;
                           const deltaY = ((currentTouch.clientY - clientY) / e.target.parentElement.parentElement.offsetHeight) * 100;
                           
                           setEditing(prev => ({
                             ...prev,
                             cropWidth: Math.max(10, Math.min(100 - prev.cropX, startCropWidth + deltaX)),
                             cropHeight: Math.max(10, Math.min(100 - prev.cropY, startCropHeight + deltaY))
                           }));
                         };
                         
                         const handleEnd = () => {
                           document.removeEventListener('touchmove', handleMove);
                           document.removeEventListener('touchend', handleEnd);
                         };
                         
                         document.addEventListener('touchmove', handleMove, { passive: false });
                         document.addEventListener('touchend', handleEnd);
                       }}>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
          
          {/* Bottom Controls */}
          <div className="p-4 bg-black/80 backdrop-blur-sm">
            <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
              {/* Preset Crop Ratios */}
              <button 
                onClick={() => setEditing({ ...editing, cropWidth: 75, cropHeight: 56.25, cropX: 12.5, cropY: 21.875 })} 
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors text-sm"
              >
                📱 4:3
              </button>
              <button 
                onClick={() => setEditing({ ...editing, cropWidth: 80, cropHeight: 45, cropX: 10, cropY: 27.5 })} 
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors text-sm"
              >
                📺 16:9
              </button>
              <button 
                onClick={() => setEditing({ ...editing, cropWidth: 70, cropHeight: 70, cropX: 15, cropY: 15 })} 
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors text-sm"
              >
                📷 1:1
              </button>
              <button 
                onClick={() => setEditing({ ...editing, cropWidth: 100, cropHeight: 100, cropX: 0, cropY: 0 })}
                className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors text-sm"
              >
                🖼️ Full
              </button>
            </div>
            
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={async () => {
                  try {
                    // Apply the crop immediately and update preview
                    const canvas = canvasRef.current;
                    const ctx = canvas.getContext('2d');
                    
                    if (originalImage) {
                      // Calculate crop dimensions
                      const cropStartX = (originalImage.width * editing.cropX) / 100;
                      const cropStartY = (originalImage.height * editing.cropY) / 100;
                      const cropWidth = (originalImage.width * editing.cropWidth) / 100;
                      const cropHeight = (originalImage.height * editing.cropHeight) / 100;
                      
                      // Set canvas size to cropped dimensions
                      canvas.width = cropWidth;
                      canvas.height = cropHeight;
                      
                      // Apply transformations
                      ctx.clearRect(0, 0, canvas.width, canvas.height);
                      ctx.save();
                      
                      // Apply rotation around center of canvas
                      ctx.translate(canvas.width / 2, canvas.height / 2);
                      ctx.rotate((editing.rotation * Math.PI) / 180);
                      
                      // Apply filters
                      ctx.filter = `brightness(${editing.brightness}%) contrast(${editing.contrast}%) saturate(${editing.saturation}%) sepia(${editing.sepia}%) blur(${editing.blur}px)`;
                      
                      // Draw cropped image
                      ctx.drawImage(
                        originalImage,
                        cropStartX, cropStartY, cropWidth, cropHeight,
                        -cropWidth / 2, -cropHeight / 2, cropWidth, cropHeight
                      );
                      ctx.restore();
                      
                      // Get the cropped image data URL with high quality
                      const croppedImageUrl = canvas.toDataURL('image/jpeg', 0.95);
                      
                      // Force preview update with timestamp to avoid caching issues
                      setPreviewUrl(croppedImageUrl + '#' + Date.now());
                      setCropMode(false);
                      setFullScreenCrop(false);
                      toast.success('Crop applied! Preview updated. Click "Save Changes" to finalize.');
                    }
                  } catch (error) {
                    console.error('Crop apply error:', error);
                    toast.error('Failed to apply crop');
                  }
                }}
                className="flex-1 max-w-xs bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg transition-colors font-medium"
              >
                ✓ Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for image processing */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

// Import from Albums Modal Component
const ImportFromAlbumsModal = ({ type, user, userVideos = [], userImages = [], onClose, onImport }) => {
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumDetails, setAlbumDetails] = useState(null); // Store full album details
  const [displayAlbumName, setDisplayAlbumName] = useState(''); // Separate state for display name
  const [albumAssets, setAlbumAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [fullscreenAsset, setFullscreenAsset] = useState(null); // For video preview
  // Set default grid size based on screen size: 'sm' (4 cols) on desktop, 'md' (3 cols) on mobile
  const [gridSize, setGridSize] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768 ? 'sm' : 'md'; // 'sm'=4 cols on desktop, 'md'=3 cols on mobile
    }
    return 'md';
  });
  const videoRefs = useRef({}); // Store video refs for hover play/pause
  
  // Helper function to check if a video is already owned by the user
  const isVideoAlreadyOwned = (asset) => {
    if (type !== 'moments') return false;
    
    // When videos are imported into albums, they get new IDs but store the original video ID in metadata
    // When videos are imported FROM albums to moments, they store the album asset ID in metadata.original_asset_id
    // We need to check:
    // 1. Direct ID match (asset.id === user video ID)
    // 2. Original video ID match (asset.metadata.original_video_id === user video ID) - video imported moments→album
    // 3. Original asset ID match (user video metadata.original_asset_id === asset.id) - video imported album→moments
    // 4. Image ID match (asset.image_id === user video's image_id)
    
    const owned = userVideos.some(video => {
      // Check if the album asset's ID matches a user video ID
      if (video.id === asset.id) return true;
      
      // Check if the album asset's original_video_id matches a user video ID
      // This handles the case where the user's video was imported into the album
      if (asset.metadata?.original_video_id && video.id === asset.metadata.original_video_id) {
        return true;
      }
      
      // NEW: Check if the user video was imported FROM this album asset
      // This handles the reverse case: album → moments
      if (video.metadata?.original_asset_id && video.metadata.original_asset_id === asset.id) {
        return true;
      }
      
      // Check image_id match (for videos generated from the same image)
      if (asset.image_id && video.image_id && video.image_id === asset.image_id) {
        return true;
      }
      
      return false;
    });
    
    // Debug logging for first few checks
    if (userVideos.length > 0 && albumAssets.indexOf(asset) < 3) {
      console.log('🔍 Checking if video is owned:', {
        assetId: asset.id,
        assetImageId: asset.image_id,
        assetOriginalVideoId: asset.metadata?.original_video_id,
        totalUserVideos: userVideos.length,
        owned,
        matchDetails: {
          directIdMatch: userVideos.some(v => v.id === asset.id),
          originalVideoIdMatch: userVideos.some(v => asset.metadata?.original_video_id && v.id === asset.metadata.original_video_id),
          originalAssetIdMatch: userVideos.some(v => v.metadata?.original_asset_id && v.metadata.original_asset_id === asset.id),
          imageIdMatch: userVideos.some(v => asset.image_id && v.image_id && v.image_id === asset.image_id)
        },
        // Show which user videos have original_asset_id pointing to this album asset
        userVideosWithMatchingOriginalAssetId: userVideos.filter(v => v.metadata?.original_asset_id === asset.id).map(v => ({
          id: v.id,
          original_asset_id: v.metadata?.original_asset_id
        }))
      });
    }
    
    return owned;
  };
  
  // Helper function to check if an image is already owned by the user
  const isImageAlreadyOwned = (asset) => {
    if (type !== 'pictures') return false;
    
    // When images are imported into albums, they get new IDs but store the original image ID in metadata
    // We need to check:
    // 1. Direct ID match (asset.id === user image ID)
    // 2. Original image ID match (asset.metadata.original_image_id === user image ID) - for images imported from Pictures to Album
    // 3. Original asset ID match (user image metadata.original_asset_id === asset.id) - for images imported FROM this album to Pictures
    
    const owned = userImages.some(image => {
      // Check if the album asset's ID matches a user image ID
      if (image.id === asset.id) return true;
      
      // Check if the album asset's metadata.original_image_id matches a user image ID
      // This handles images that were imported from user's Pictures into the album
      if (asset.metadata?.original_image_id && image.id === asset.metadata.original_image_id) {
        return true;
      }
      
      // Check if the user image was imported FROM this album asset
      // This handles reverse flow: album → Pictures → trying to re-import to album
      if (image.metadata?.original_asset_id && image.metadata.original_asset_id === asset.id) {
        return true;
      }
      
      return false;
    });
    
    // Debug logging for first few checks
    if (userImages.length > 0 && albumAssets.indexOf(asset) < 3) {
      console.log('🔍 Checking if image is owned:', {
        assetId: asset.id,
        assetMetadataOriginalImageId: asset.metadata?.original_image_id,
        totalUserImages: userImages.length,
        owned,
        matchDetails: {
          directIdMatch: userImages.some(img => img.id === asset.id),
          originalImageIdMatch: userImages.some(img => asset.metadata?.original_image_id && img.id === asset.metadata.original_image_id),
          originalAssetIdMatch: userImages.some(img => img.metadata?.original_asset_id && img.metadata.original_asset_id === asset.id)
        }
      });
    }
    
    return owned;
  };
  
  // Helper to get proper video URL for playback
  const getAssetVideoUrl = (asset) => {
    if (!asset) {
      console.log('⚠️ No asset provided for video URL');
      return '';
    }
    
    console.log('🎬 Getting video URL for asset:', {
      id: asset.id,
      r2_url: asset.r2_url,
      url: asset.url,
      animated_url: asset.animated_url,
      metadata: asset.metadata
    });
    
    // Priority 1: animated_url (for album video assets)
    if (asset.animated_url && typeof asset.animated_url === 'string' && asset.animated_url.trim()) {
      // If it's a full presigned URL, use it as-is
      if (asset.animated_url.includes('cloudflarestorage.com') || asset.animated_url.includes('X-Amz-')) {
        console.log('✅ Using presigned animated_url:', asset.animated_url.substring(0, 100));
        return asset.animated_url;
      }
      
      // If it's a relative path starting with /api/, use it as-is (domain-agnostic)
      if (asset.animated_url.startsWith('/api/')) {
        const relativeUrl = `${asset.animated_url}${asset.animated_url.includes('?') ? '&' : '?'}user_id=${user.id}`;
        console.log('✅ Using relative animated_url:', relativeUrl);
        return relativeUrl;
      }
      
      // If it's already a full URL
      if (asset.animated_url.startsWith('http')) {
        console.log('✅ Using full animated_url:', asset.animated_url.substring(0, 100));
        return asset.animated_url;
      }
      
      // Otherwise, treat it as an R2 key and construct relative URL
      const relativeUrl = `/api/r2/${asset.animated_url}?user_id=${user.id}`;
      console.log('✅ Constructed relative URL from animated_url key:', relativeUrl);
      return relativeUrl;
    }
    
    // Priority 2: r2_url
    if (asset.r2_url && typeof asset.r2_url === 'string' && asset.r2_url.trim()) {
      // If it's a full presigned URL, use it as-is
      if (asset.r2_url.includes('cloudflarestorage.com') || asset.r2_url.includes('X-Amz-')) {
        console.log('✅ Using presigned r2_url:', asset.r2_url.substring(0, 100));
        return asset.r2_url;
      }
      
      // If it's a relative path starting with /api/, use it as-is (domain-agnostic)
      if (asset.r2_url.startsWith('/api/')) {
        const relativeUrl = `${asset.r2_url}${asset.r2_url.includes('?') ? '&' : '?'}user_id=${user.id}`;
        console.log('✅ Using relative r2_url:', relativeUrl);
        return relativeUrl;
      }
      
      // If it's already a full URL
      if (asset.r2_url.startsWith('http')) {
        console.log('✅ Using full r2_url:', asset.r2_url.substring(0, 100));
        return asset.r2_url;
      }
      
      // Otherwise, treat it as an R2 key and construct relative URL
      const relativeUrl = `/api/r2/${asset.r2_url}?user_id=${user.id}`;
      console.log('✅ Constructed relative URL from r2_url key:', relativeUrl);
      return relativeUrl;
    }
    
    // Priority 3: generic url field
    if (asset.url && typeof asset.url === 'string' && asset.url.trim()) {
      if (asset.url.startsWith('http')) {
        console.log('✅ Using full url:', asset.url.substring(0, 100));
        return asset.url;
      }
      
      // If relative, use as-is (domain-agnostic)
      if (asset.url.startsWith('/api/')) {
        const relativeUrl = `${asset.url}${asset.url.includes('?') ? '&' : '?'}user_id=${user.id}`;
        console.log('✅ Using relative url:', relativeUrl);
        return relativeUrl;
      }
    }
    
    // Priority 4: Check if there's an r2_key in metadata that we can construct URL from
    if (asset.metadata?.r2_key && typeof asset.metadata.r2_key === 'string') {
      const relativeUrl = `/api/r2/${asset.metadata.r2_key}?user_id=${user.id}`;
      console.log('✅ Constructed relative URL from metadata.r2_key:', relativeUrl);
      return relativeUrl;
    }
    
    // Priority 5: DOMAIN-AGNOSTIC FALLBACK - Use /api/videos/{video_id} endpoint
    // This works across all domains (Emergent, production, localhost)
    if (asset.id) {
      const relativeUrl = `/api/videos/${asset.id}?user_id=${user.id}`;
      console.log('✅ Using domain-agnostic video endpoint:', relativeUrl);
      return relativeUrl;
    }
    
    console.log('❌ No valid video URL found for asset. Checked: animated_url, r2_url, url, metadata.r2_key, id');
    return '';
  };
  
  // Helper to get proper image URL for display
  const getAssetThumbnailUrl = (asset) => {
    if (!asset) {
      console.log('⚠️ No asset provided');
      return '';
    }
    
    console.log('🔍 Getting thumbnail for asset:', {
      id: asset.id,
      asset_type: asset.asset_type,
      thumbnail_url: asset.thumbnail_url,
      r2_url: asset.r2_url,
      r2_key: asset.r2_key,
      r2_thumbnail_key: asset.r2_thumbnail_key,
      image_id: asset.image_id,
      metadata: asset.metadata
    });
    
    // PRIORITY 0: For video assets, check r2_thumbnail_key FIRST
    // This is critical because thumbnail_url for videos points to the video file itself, not the thumbnail
    if (asset.asset_type === 'video' && asset.r2_thumbnail_key && typeof asset.r2_thumbnail_key === 'string') {
      const relativeUrl = `/api/r2/${asset.r2_thumbnail_key}?user_id=${user.id}`;
      console.log('✅ Constructed video thumbnail relative URL from r2_thumbnail_key:', relativeUrl);
      return relativeUrl;
    }
    
    // Priority 1: Check for R2 presigned thumbnail URL
    if (asset.r2_url && typeof asset.r2_url === 'string') {
      // If it's already a full presigned URL, use it
      if (asset.r2_url.includes('cloudflarestorage.com') || asset.r2_url.includes('X-Amz-')) {
        console.log('✅ Using presigned r2_url:', asset.r2_url.substring(0, 100));
        return asset.r2_url;
      }
    }
    
    // Priority 2: Use thumbnail_url if it's a relative API path
    if (asset.thumbnail_url && typeof asset.thumbnail_url === 'string') {
      // IMPORTANT: For videos, skip thumbnail_url if it points to the video's own ID
      // This happens when videos don't have separate thumbnails and thumbnail_url=/api/files/{video_id}
      // In this case, we should use r2_key fallback instead
      if (asset.asset_type === 'video' && asset.thumbnail_url.includes(asset.id)) {
        console.log('⚠️ Skipping thumbnail_url (points to video ID itself):', asset.thumbnail_url);
        // Don't return here - let it fall through to Priority 2.5
      } else {
        // If it's a relative path like /api/... use it as-is (domain-agnostic)
        if (asset.thumbnail_url.startsWith('/api/')) {
          const relativeUrl = `${asset.thumbnail_url}${asset.thumbnail_url.includes('?') ? '&' : '?'}user_id=${user.id}`;
          console.log('✅ Using relative thumbnail_url:', relativeUrl);
          return relativeUrl;
        }
        
        // If it's already a full URL
        if (asset.thumbnail_url.startsWith('http')) {
          console.log('✅ Using full thumbnail_url:', asset.thumbnail_url.substring(0, 100));
          return asset.thumbnail_url;
        }
      }
    }
    
    // Priority 2.5: For VIDEO assets WITHOUT valid thumbnail_url
    // NOTE: We DON'T use r2_key here because it contains video files (.mp4), not images
    // Browsers cannot display video files as <img> or <video poster>
    // Instead, we'll return empty string and let the frontend show a placeholder
    if (asset.asset_type === 'video') {
      console.log('⚠️ No valid thumbnail found for video, will use default placeholder');
      return ''; // Empty string will trigger default placeholder in UI
    }
    
    // Priority 3: Construct from r2_thumbnail_key
    if (asset.r2_thumbnail_key && typeof asset.r2_thumbnail_key === 'string') {
      const relativeUrl = `/api/r2/${asset.r2_thumbnail_key}?user_id=${user.id}`;
      console.log('✅ Constructed relative URL from r2_thumbnail_key:', relativeUrl);
      return relativeUrl;
    }
    
    // Priority 4: Construct from r2_key (main image)
    if (asset.r2_key && typeof asset.r2_key === 'string') {
      const relativeUrl = `/api/r2/${asset.r2_key}?user_id=${user.id}`;
      console.log('✅ Constructed relative URL from r2_key:', relativeUrl);
      return relativeUrl;
    }
    
    console.log('❌ No valid URL found for asset');
    return '';
  };
  
  // Fetch user's albums
  useEffect(() => {
    const fetchAlbums = async () => {
      setIsLoading(true);
      try {
        console.log('📥 Fetching albums for user:', user.id);
        console.log('📍 API endpoint:', `${getAPI()}/albums?user_id=${user.id}`);
        const response = await axios.get(`${getAPI()}/albums?user_id=${user.id}`);
        console.log('✅ Albums response:', response.data);
        
        // Backend returns {albums: [...]}
        const userAlbums = response.data.albums || [];
        console.log('📁 User albums count:', userAlbums.length);
        
        // Log each album to see their titles
        userAlbums.forEach((album, idx) => {
          console.log(`  Album ${idx + 1}: "${album.title}" (ID: ${album.id}) - ${album.photo_count || 0} photos, ${album.video_count || 0} videos`);
        });
        
        setAlbums(userAlbums);
      } catch (error) {
        console.error('❌ Failed to load albums:', error);
        console.error('Error details:', error.response?.data || error.message);
        toast.error('Failed to load albums');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user?.id) {
      fetchAlbums();
    }
  }, [user?.id]);
  
  // Fetch assets from selected album
  useEffect(() => {
    if (!selectedAlbum) {
      setAlbumAssets([]);
      setSelectedAssets([]);
      setAlbumDetails(null);
      setDisplayAlbumName('');
      return;
    }
    
    const fetchAlbumAssets = async () => {
      setIsLoading(true);
      try {
        console.log('📥 Fetching assets from album:', selectedAlbum.id);
        console.log('📍 Selected album from list:', selectedAlbum);
        const response = await axios.get(`${getAPI()}/albums/${selectedAlbum.id}?user_id=${user.id}`);
        console.log('✅ Full album details response:', response.data);
        
        // Store the full album details from response
        const albumData = response.data.album || {};
        console.log('📁 Album object from response:', albumData);
        console.log('📝 Album title from response:', albumData.title);
        console.log('📝 Album title type:', typeof albumData.title);
        setAlbumDetails(albumData);
        
        // Set display name immediately - use 'title' field from backend
        const albumTitle = albumData.title || selectedAlbum.title || '';
        const cleanName = (albumTitle && albumTitle !== 'undefined') ? albumTitle : 'Untitled Album';
        console.log('🏷️ Setting display name to:', cleanName);
        setDisplayAlbumName(cleanName);
        
        // Backend returns {album: {...}, assets: [...]}
        const assets = response.data.assets || [];
        console.log('📦 Total assets:', assets.length);
        
        // Log first asset for debugging
        if (assets.length > 0) {
          console.log('🔍 First asset sample:', assets[0]);
        }
        
        if (type === 'pictures') {
          // Filter for images only (using asset_type field)
          const pictures = assets.filter(asset => asset.asset_type === 'image');
          console.log('🖼️ Pictures found:', pictures.length);
          setAlbumAssets(pictures);
        } else {
          // Filter for videos only (using asset_type field)
          console.log('🎬 FILTERING FOR VIDEOS:');
          console.log('  Total assets from backend:', assets.length);
          
          if (assets.length > 0) {
            console.log('  ===== FIRST ASSET FULL STRUCTURE =====');
            console.log(JSON.stringify(assets[0], null, 2));
            console.log('  =====================================');
          }
          
          assets.forEach((asset, idx) => {
            console.log(`\n  📦 Asset ${idx + 1}:`, {
              id: asset.id,
              asset_type: asset.asset_type,
              animated: asset.animated,
              filename: asset.filename,
              // Check ALL possible URL fields
              r2_url: asset.r2_url ? `EXISTS (${asset.r2_url.substring(0, 50)}...)` : 'MISSING',
              url: asset.url ? `EXISTS (${asset.url.substring(0, 50)}...)` : 'MISSING',
              animated_url: asset.animated_url ? `EXISTS (${asset.animated_url.substring(0, 50)}...)` : 'MISSING',
              r2_key: asset.r2_key ? 'EXISTS' : 'MISSING',
              thumbnail_url: asset.thumbnail_url ? `EXISTS (${asset.thumbnail_url.substring(0, 50)}...)` : 'MISSING',
              r2_thumbnail_key: asset.r2_thumbnail_key ? 'EXISTS' : 'MISSING'
            });
          });
          
          const videos = assets.filter(asset => {
            const isVideo = asset.asset_type === 'video';
            console.log(`\n  🎯 Filtering asset ${asset.id}: asset_type="${asset.asset_type}" → ${isVideo ? 'KEEP ✅' : 'SKIP ❌'}`);
            return isVideo;
          });
          console.log('\n📹 Videos found after filtering:', videos.length);
          
          if (videos.length > 0) {
            console.log('  ===== FIRST VIDEO STRUCTURE =====');
            console.log(JSON.stringify(videos[0], null, 2));
            console.log('  ==================================');
          } else {
            console.log('  ⚠️ NO VIDEOS FOUND! Asset types seen:', assets.map(a => a.asset_type).join(', '));
          }
          
          setAlbumAssets(videos);
        }
      } catch (error) {
        console.error('❌ Failed to load album assets:', error);
        console.error('Error details:', error.response?.data || error.message);
        toast.error('Failed to load album content');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAlbumAssets();
  }, [selectedAlbum, user?.id, type, albumRefreshTrigger]); // Added albumRefreshTrigger for WebSocket updates
  
  const handleImport = async () => {
    if (selectedAssets.length === 0) {
      toast.error('Please select items to import');
      return;
    }
    
    setIsImporting(true);
    try {
      // Use displayAlbumName which has been properly set from the API response
      const albumName = displayAlbumName !== 'Untitled Album' ? displayAlbumName : 'Album';
      
      console.log('📤 Importing with album name:', albumName);
      console.log('📤 Selected asset IDs:', selectedAssets);
      console.log('📤 Asset type:', type === 'pictures' ? 'image' : 'video');
      
      const response = await axios.post(`${getAPI()}/import-from-album`, {
        user_id: user.id,
        album_id: selectedAlbum.id,
        album_name: albumName,
        asset_ids: selectedAssets,
        asset_type: type === 'pictures' ? 'image' : 'video'
      });
      
      console.log('✅ Import response:', response.data);
      toast.success(`${selectedAssets.length} ${type} imported successfully!`);
      
      // Call onImport callback to refresh parent data
      if (onImport) {
        await onImport();
      }
      
      // Close modal after successful import
      onClose();
    } catch (error) {
      console.error('❌ Import failed:', error);
      console.error('❌ Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to import items');
    } finally {
      setIsImporting(false);
    }
  };
  
  const toggleSelectAll = () => {
    // Filter out owned videos when selecting all
    const selectableAssets = albumAssets.filter(asset => !isVideoAlreadyOwned(asset));
    
    if (selectedAssets.length === selectableAssets.length && selectableAssets.length > 0) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(selectableAssets.map(asset => asset.id));
    }
  };
  
  // Get grid column classes based on size selection
  const getGridCols = () => {
    switch (gridSize) {
      case 'sm': return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'; // Small cards (4 columns on desktop)
      case 'md': return 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3'; // Medium cards (3 columns)
      case 'lg': return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2'; // Large cards (2 columns)
      default: return 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3';
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 pt-4 sm:pt-8 pb-4 overflow-y-auto" style={{ zIndex: 99999 }}>
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[calc(100vh-10rem)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0">
          <h2 className="text-base sm:text-xl md:text-2xl font-bold flex items-center gap-1.5 sm:gap-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            <span className="truncate">
              <span className="hidden sm:inline">Import {type === 'pictures' ? 'Pictures' : 'Moments'} from Albums</span>
              <span className="sm:hidden">Import from Albums</span>
            </span>
          </h2>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl sm:text-3xl flex-shrink-0 ml-2">×</button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
          {!selectedAlbum ? (
            // Album selection view
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Select an Album</h3>
              {isLoading ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-indigo-600"></div>
                  <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Loading albums...</p>
                </div>
              ) : albums.length === 0 ? (
                <div className="text-center py-8 sm:py-12 text-gray-500 text-sm sm:text-base">
                  <p>No albums found. Create an album first to import from it.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {albums.map((album, idx) => {
                    console.log(`📁 Rendering album ${idx + 1}:`, album.title);
                    return (
                      <button
                        key={album.id}
                        onClick={() => {
                          console.log('🖱️ User clicked album:', album.title, 'ID:', album.id);
                          setSelectedAlbum(album);
                        }}
                        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl hover:border-indigo-500 hover:bg-indigo-50 transition-all text-left"
                      >
                        <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                          📁
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                            {album.title && album.title !== 'undefined' 
                              ? album.title 
                              : 'Untitled Album'}
                          </h4>
                          <p className="text-xs sm:text-sm text-gray-500">
                            {type === 'pictures' 
                              ? `${album.photo_count || 0} picture${album.photo_count !== 1 ? 's' : ''}`
                              : `${album.video_count || 0} moment${album.video_count !== 1 ? 's' : ''}`
                            }
                          </p>
                        </div>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // Asset selection view
            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-0">
                <button
                  onClick={() => {
                    setSelectedAlbum(null);
                    setSelectedAssets([]);
                  }}
                  className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium text-sm sm:text-base"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Albums
                </button>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold truncate max-w-full">
                    {displayAlbumName || 'Untitled Album'}
                  </h3>
                  {type === 'moments' && (() => {
                    const ownedCount = albumAssets.filter(asset => isVideoAlreadyOwned(asset)).length;
                    return ownedCount > 0 ? (
                      <p className="text-xs text-orange-600 mt-1">
                        {ownedCount} video{ownedCount !== 1 ? 's' : ''} already owned
                      </p>
                    ) : null;
                  })()}
                </div>
              </div>
              
              {isLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                  <p className="mt-4 text-gray-600">Loading {type}...</p>
                </div>
              ) : albumAssets.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <p>No {type} found in this album.</p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={toggleSelectAll}
                      className="text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-2"
                    >
                      {selectedAssets.length === albumAssets.length ? '✅ Deselect All' : '☐ Select All'}
                    </button>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Grid Size:</label>
                        <div className="flex gap-1 border border-gray-300 rounded-lg p-1">
                          <button
                            onClick={() => setGridSize('sm')}
                            className={`px-3 py-1 text-xs rounded transition-colors ${
                              gridSize === 'sm'
                                ? 'bg-indigo-600 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            title="Small cards (4 columns)"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="2" y="2" width="8" height="8" />
                              <rect x="12" y="2" width="8" height="8" />
                              <rect x="2" y="12" width="8" height="8" />
                              <rect x="12" y="12" width="8" height="8" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setGridSize('md')}
                            className={`px-3 py-1 text-xs rounded transition-colors ${
                              gridSize === 'md'
                                ? 'bg-indigo-600 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            title="Medium cards (3 columns)"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="2" y="2" width="6" height="6" />
                              <rect x="10" y="2" width="6" height="6" />
                              <rect x="18" y="2" width="4" height="6" />
                              <rect x="2" y="10" width="6" height="6" />
                              <rect x="10" y="10" width="6" height="6" />
                              <rect x="18" y="10" width="4" height="6" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setGridSize('lg')}
                            className={`px-3 py-1 text-xs rounded transition-colors ${
                              gridSize === 'lg'
                                ? 'bg-indigo-600 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                            title="Large cards (2 columns)"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <rect x="2" y="2" width="9" height="9" />
                              <rect x="13" y="2" width="9" height="9" />
                              <rect x="2" y="13" width="9" height="9" />
                              <rect x="13" y="13" width="9" height="9" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <span className="text-sm text-gray-600">
                        {selectedAssets.length} of {albumAssets.length} selected
                      </span>
                    </div>
                  </div>
                  
                  <div className={`grid ${getGridCols()} gap-4`}>
                    {albumAssets.map(asset => {
                      const isSelected = selectedAssets.includes(asset.id);
                      const thumbnailUrl = getAssetThumbnailUrl(asset);  // Direct call, no wrapper needed
                      const videoUrl = getAssetVideoUrl(asset);
                      const isOwned = isVideoAlreadyOwned(asset); // Check if user already owns this video
                      
                      // For videos, render video element with hover-to-play
                      if (type === 'moments') {
                        if (!videoUrl) {
                          console.warn('⚠️ Video asset has no valid URL:', asset.id);
                          return null; // Skip rendering if no video URL
                        }
                        
                        return (
                          <div key={asset.id} className={`relative aspect-square rounded-lg overflow-hidden ${isOwned ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <video
                              ref={(el) => { 
                                if (el && !el._hlsInitialized) {
                                  el._hlsInitialized = true;
                                  videoRefs.current[asset.id] = el;
                                  
                                  // Initialize HLS for this video element
                                  if (videoUrl) {
                                    const isHLS = videoUrl.includes('.m3u8') || videoUrl.includes('/api/videos/');
                                    
                                    if (isHLS) {
                                      // Dynamically import HLS.js
                                      import('hls.js').then((Hls) => {
                                        const HlsClass = Hls.default;
                                        
                                        if (HlsClass.isSupported()) {
                                          const hls = new HlsClass({ 
                                            enableWorker: true, 
                                            lowLatencyMode: true,
                                            debug: false
                                          });
                                          
                                          hls.loadSource(videoUrl);
                                          hls.attachMedia(el);
                                          
                                          hls.on(HlsClass.Events.MANIFEST_PARSED, () => {
                                            console.log('✅ HLS loaded for hover video:', asset.id.substring(0, 8));
                                          });
                                          
                                          hls.on(HlsClass.Events.ERROR, (event, data) => {
                                            if (data.fatal) {
                                              console.error('❌ Fatal HLS error:', data.type, data.details);
                                            }
                                          });
                                          
                                          el._hls = hls;
                                        } else {
                                          console.warn('HLS not supported, trying direct playback');
                                          el.src = videoUrl;
                                        }
                                      }).catch(err => {
                                        console.error('Failed to load HLS.js:', err);
                                        el.src = videoUrl;
                                      });
                                    } else {
                                      // Direct playback for non-HLS
                                      el.src = videoUrl;
                                    }
                                  }
                                }
                              }}
                              poster={thumbnailUrl}
                              className={`w-full h-full object-cover ${isOwned ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                              preload="metadata"
                              loop
                              muted
                              playsInline
                              onMouseEnter={(e) => {
                                if (!isOwned) {
                                  e.currentTarget.play().catch(err => console.log('Play failed:', err));
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isOwned) {
                                  e.currentTarget.pause();
                                  e.currentTarget.currentTime = 0;
                                }
                              }}
                              onClick={(e) => {
                                if (!isOwned) {
                                  e.stopPropagation();
                                  setFullscreenAsset(asset);
                                }
                              }}
                              onError={(e) => {
                                console.error('❌ Video failed to load:', videoUrl);
                              }}
                            />
                            
                            {/* Selection checkbox overlay - only show if not owned */}
                            {!isOwned && (
                              <div 
                                className="absolute top-2 right-2 z-10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAssets(prev =>
                                    isSelected
                                      ? prev.filter(id => id !== asset.id)
                                      : [...prev, asset.id]
                                  );
                                }}
                              >
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center cursor-pointer ${
                                  isSelected
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white/80 text-gray-600 hover:bg-white'
                                } shadow-lg`}>
                                  {isSelected ? '✓' : '○'}
                                </div>
                              </div>
                            )}
                            
                            {/* Owned badge overlay */}
                            {isOwned && (
                              <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center pointer-events-none">
                                <div className="bg-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-semibold">
                                  Owned
                                </div>
                              </div>
                            )}
                            
                            {/* Play icon indicator */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="bg-black/40 rounded-full p-3">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      // For pictures, render with ownership checking
                      const isImageOwned = isImageAlreadyOwned(asset);
                      
                      return (
                        <div key={asset.id} className={`relative aspect-square rounded-lg overflow-hidden ${isImageOwned ? 'opacity-50' : ''}`}>
                          <button
                            onClick={() => {
                              if (!isImageOwned) {
                                setSelectedAssets(prev =>
                                  isSelected
                                    ? prev.filter(id => id !== asset.id)
                                    : [...prev, asset.id]
                                );
                              }
                            }}
                            className={`w-full h-full relative border-4 transition-all ${
                              isSelected
                                ? 'border-indigo-600 shadow-lg'
                                : 'border-transparent hover:border-gray-300'
                            } ${isImageOwned ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                            disabled={isImageOwned}
                          >
                            {thumbnailUrl ? (
                              <img
                                src={thumbnailUrl}
                                alt=""
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  console.error('Failed to load thumbnail:', thumbnailUrl);
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-400">No preview</span>
                              </div>
                            )}
                            
                            {/* Selection checkmark - only show if not owned */}
                            {!isImageOwned && isSelected && (
                              <div className="absolute inset-0 bg-indigo-600/20 flex items-center justify-center">
                                <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
                                  ✓
                                </div>
                              </div>
                            )}
                            
                            {/* Owned badge overlay */}
                            {isImageOwned && (
                              <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center pointer-events-none">
                                <div className="bg-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-semibold">
                                  Owned
                                </div>
                              </div>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        {selectedAlbum && albumAssets.length > 0 && (
          <div className="border-t bg-gray-50 px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0">
            <p className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              {type === 'pictures' 
                ? `Pictures will be copied to "Imported from ${displayAlbumName}" folder`
                : 'Moments will be added to your Moments tab'}
            </p>
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={selectedAssets.length === 0 || isImporting}
                className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm sm:text-base"
              >
                {isImporting ? 'Importing...' : `Import ${selectedAssets.length} ${type === 'pictures' ? 'Picture' : 'Moment'}${selectedAssets.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Fullscreen Video Preview Modal */}
      {fullscreenAsset && type === 'moments' && (
        <div 
          className="fixed inset-0 bg-black z-[110] flex items-center justify-center"
          onClick={() => setFullscreenAsset(null)}
        >
          <button
            onClick={() => setFullscreenAsset(null)}
            className="absolute top-4 right-4 z-10 text-white/80 hover:text-white text-4xl"
          >
            ×
          </button>
          
          <video
            src={getAssetVideoUrl(fullscreenAsset)}
            poster={getAssetThumbnailUrl(fullscreenAsset)}
            className="max-w-full max-h-full"
            preload="metadata"
            controls
            autoPlay
            loop
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              console.error('❌ Fullscreen video failed to load:', getAssetVideoUrl(fullscreenAsset));
            }}
          />
          
          {/* Selection controls */}
          <div className="absolute bottom-4 right-4 flex gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedAssets(prev =>
                  prev.includes(fullscreenAsset.id)
                    ? prev.filter(id => id !== fullscreenAsset.id)
                    : [...prev, fullscreenAsset.id]
                );
              }}
              className={`px-4 py-2 rounded-lg font-medium ${
                selectedAssets.includes(fullscreenAsset.id)
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-900 hover:bg-gray-100'
              }`}
            >
              {selectedAssets.includes(fullscreenAsset.id) ? '✓ Selected' : 'Select for Import'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Animated Moment Modal - Animation Generator
const CreateLivingMemoryModal = ({ projects, unassignedImages, folders = [], preSelectedImage, imagesWithVideos, setShowVideoGallery, fileInputRef, user, albumContext, checkSingleImageAnalysis, skipAnalysisRef, albumFilterSwitchCallback, albumRefreshCallback, setVideoJobs, onClose, onRefresh, currentView, setCurrentView, setActiveMainTab, setShowPendingVideos, setShowMagicProcessing, setProcessingCount }) => {
  console.log('🎭 CreateLivingMemoryModal RENDERING - albumContext:', albumContext);
  const { t } = useTranslation();
  
  // Helper to detect R2 presigned URLs
  const isPresignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  };
  
  // Helper function to add auth params to thumbnail URLs
  const getAuthenticatedThumbnailUrl = (thumbnailUrl) => {
    if (!thumbnailUrl || !user?.id) return thumbnailUrl;
    
    // NEVER modify presigned URLs - they have cryptographic signatures
    if (isPresignedUrl(thumbnailUrl)) {
      console.log('⚠️ Detected presigned thumbnail URL, returning as-is');
      return thumbnailUrl;
    }
    
    // If URL already has user_id, return as-is
    if (thumbnailUrl.includes('user_id=')) {
      return thumbnailUrl;
    }
    
    // Add user_id parameter
    const separator = thumbnailUrl.includes('?') ? '&' : '?';
    return `${thumbnailUrl}${separator}user_id=${user.id}`;
  };
  
  // Helper function to generate image URL with user_id for proper data isolation
  const getImageUrl = (image) => {
    if (!image) {
      console.warn('⚠️ getImageUrl called with no image');
      return '';
    }
    
    // If image has a data URL (edited), use that
    if (image.url && image.url.startsWith('data:')) return image.url;
    
    // Get authentication parameters with better fallback handling
    const effectiveUserId = user?.id || localStorage.getItem('effectiveUserId') || '';
    const authenticatedUserId = localStorage.getItem('authenticatedUserId') || user?.id || effectiveUserId;
    
    if (!effectiveUserId) {
      console.error('❌ No user ID available for image URL generation', image);
      return '';
    }
    
    // If image already has a URL with cache-busting parameter, preserve it
    if (image.url || image.thumbnail_url) {
      const existingUrl = image.thumbnail_url || image.url;
      
      // NEVER modify presigned URLs - they have cryptographic signatures
      if (isPresignedUrl(existingUrl)) {
        console.log('⚠️ Detected presigned URL, returning as-is:', existingUrl.substring(0, 100));
        return existingUrl;
      }
      
      // Check if URL already has cache-busting (_cb parameter)
      if (existingUrl.includes('_cb=')) {
        // URL has cache-busting, preserve the entire URL
        if (existingUrl.includes('user_id=')) {
          // Already has auth params, return as-is
          return existingUrl;
        } else {
          // Add auth params while preserving cache-busting
          const separator = existingUrl.includes('?') ? '&' : '?';
          return `${existingUrl}${separator}user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`;
        }
      }
      
      // Extract image ID from URL if it's an API URL
      const match = existingUrl.match(/\/api\/files\/([^?]+)/);
      if (match) {
        const imageId = match[1];
        return `${getAPI()}/files/${imageId}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}`;
      }
      // If it's already a full URL with parameters, return as-is
      if (existingUrl.startsWith('http')) return existingUrl;
      
      // Demo content or landing-assets with relative paths
      if (existingUrl.startsWith('/landing-assets/')) {
        return existingUrl;
      }
    }
    
    // Otherwise construct API URL with image ID and add cache-busting
    const imageId = image.id || image._id;
    if (!imageId) {
      console.warn('⚠️ Image has no ID', image);
      return '';
    }
    
    // Add cache-busting parameter for fresh images
    const cacheBust = image.updated_at ? new Date(image.updated_at).getTime() : Date.now();
    return `${getAPI()}/files/${imageId}?user_id=${effectiveUserId}&authenticated_user_id=${authenticatedUserId}&v=${cacheBust}`;
  };
  
  const [step, setStep] = useState(preSelectedImage ? 3 : 1);
  const [selectedImage, setSelectedImage] = useState(preSelectedImage || null);
  const [prompt, setPrompt] = useState(preSelectedImage?.enhancedPrompt || '');
  const [duration, setDuration] = useState(4); // Vidu 2.0 default (4 or 8 seconds supported)
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompts, setAiPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [aiPromptGenerated, setAiPromptGenerated] = useState(false);
  const [gallerySort, setGallerySort] = useState({ field: 'created_at', direction: 'desc' });
  const [memoryGridSize, setMemoryGridSize] = useState('medium'); // 'small', 'medium', 'large'
  const [selectedFolderFilter, setSelectedFolderFilter] = useState(null); // null = "All Pictures"
  const analysisApprovedRef = useRef(false); // Use ref to avoid state timing issues
  const [showingAnalysisWarning, setShowingAnalysisWarning] = useState(false); // Track if warning is currently shown
  const [showAnimationTip, setShowAnimationTip] = useState(false); // State for collapsible animation tip - collapsed by default
  const [promptsJustLoaded, setPromptsJustLoaded] = useState(false); // Track when prompts just finished loading for button animation
  const [imageBorderWarning, setImageBorderWarning] = useState(null); // State for border warning from AI analysis
  const [imageBrightBackground, setImageBrightBackground] = useState(false); // State for bright background flag from AI analysis
  const [imageFadedWarning, setImageFadedWarning] = useState(null); // State for faded image warning from AI analysis
  
  // Track if component is mounted to prevent operations after close
  const isMountedRef = useRef(true);
  
  // CRITICAL: Track polling interval to ensure cleanup
  const pollingIntervalRef = useRef(null);
  
  // CRITICAL: Cleanup polling interval on unmount + reset flags on mount
  useEffect(() => {
    // Reset flags when modal opens
    isMountedRef.current = true;
    analysisApprovedRef.current = false;
    setShowingAnalysisWarning(false);
    
    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        console.log('🧹 Cleaning up polling interval on unmount');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // AUTO-TRIGGER: Automatically load AI prompts when modal opens with preSelectedImage
  const hasAutoTriggeredRef = useRef(false);
  
  useEffect(() => {
    // Only auto-trigger once when component mounts with preSelectedImage
    if (preSelectedImage && selectedImage && !hasAutoTriggeredRef.current) {
      console.log('🤖 AUTO-TRIGGER: Modal opened with preSelectedImage, automatically loading AI prompts');
      console.log('🤖 AUTO-TRIGGER: selectedImage:', selectedImage.name || selectedImage.id);
      console.log('🤖 AUTO-TRIGGER: hasAutoTriggeredRef.current before:', hasAutoTriggeredRef.current);
      hasAutoTriggeredRef.current = true;
      
      // NEW: Don't auto-approve - let the credit check happen first
      // The check will approve if user has credits or is under the 5-free limit
      console.log('🚀 AUTO-TRIGGER: Calling loadAIPrompts() (will check credits first)...');
      console.log('🔍 AUTO-TRIGGER: isMountedRef.current:', isMountedRef.current);
      loadAIPrompts();
    }
  }, [preSelectedImage, selectedImage]);

  // Collect all images first
  const rawImages = React.useMemo(() => [
    // Images from projects
    ...projects.flatMap(project => 
      project.images?.filter(img => !img.is_deleted).map(img => ({
        ...img, 
        projectName: project.name, 
        projectId: project.id,
        name: img.name || img.original_filename || 'Untitled'
      })) || []
    ),
    // Unassigned images
    ...unassignedImages.filter(img => !img.is_deleted).map(img => ({
      ...img,
      projectName: 'Unassigned Pictures',
      projectId: 'unassigned',
      name: img.name || img.original_filename || 'Untitled'
    }))
  ], [projects, unassignedImages]);

  // Filter images by folder
  const filteredImages = React.useMemo(() => {
    if (!selectedFolderFilter) return rawImages;
    return rawImages.filter(img => img.folder_id === selectedFolderFilter.id);
  }, [rawImages, selectedFolderFilter]);
  
  // Sort images based on gallerySort state
  const allImages = React.useMemo(() => {
    console.log('🔄 Sorting images with config:', gallerySort);
    console.log('📊 Total images to sort:', filteredImages.length);
    
    const sorted = [...filteredImages].sort((a, b) => {
      // For created_at, try multiple possible field names and get timestamp in milliseconds
      const aTimestamp = a.created_at || a.createdAt || a.uploaded_at || a.timestamp || 0;
      const bTimestamp = b.created_at || b.createdAt || b.uploaded_at || b.timestamp || 0;
      
      // Convert to Date objects and then to time (milliseconds since epoch)
      const aTime = new Date(aTimestamp).getTime();
      const bTime = new Date(bTimestamp).getTime();
      
      // Handle invalid dates (NaN)
      const aValid = !isNaN(aTime) ? aTime : 0;
      const bValid = !isNaN(bTime) ? bTime : 0;
      
      // Debug first few items
      if (rawImages.indexOf(a) < 3) {
        console.log(`Image ${a.name}:`, {
          timestamp: aTimestamp,
          parsed: aTimestamp ? new Date(aTimestamp).toISOString() : 'NO TIMESTAMP',
          time: aValid
        });
      }
      
      if (gallerySort.direction === 'asc') {
        return aValid - bValid; // Oldest first
      } else {
        return bValid - aValid; // Newest first
      }
    });
    
    console.log('✅ Sorted result - First 3 images:', sorted.slice(0, 3).map(img => ({
      name: img.name,
      timestamp: img.created_at || img.createdAt || img.uploaded_at || img.timestamp
    })));
    
    return sorted;
  }, [filteredImages, gallerySort]);

  // Get grid classes based on size
  const getMemoryGridClasses = (size) => {
    switch(size) {
      case 'small':
        return 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6';
      case 'large':
        return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
      case 'medium':
      default:
        return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5';
    }
  };

  const estimatedCost = duration === 4 ? 5 : 10; // 5 credits for 4s, 10 credits for 8s

  // Handle selected image changes - DON'T auto-load prompts UNLESS in auto-trigger mode
  useEffect(() => {
    if (selectedImage) {
      console.log('🖼️ Selected Image:', selectedImage.name || selectedImage.id);
      console.log('🔍 Enhanced Prompt Check:', selectedImage.enhancedPrompt);
      console.log('🔍 Auto-trigger mode (preSelectedImage):', !!preSelectedImage);
      
      // Set prompt if there's an enhanced prompt - do this IMMEDIATELY
      if (selectedImage.enhancedPrompt) {
        console.log('✅ Setting enhanced prompt in text field:', selectedImage.enhancedPrompt);
        setPrompt(selectedImage.enhancedPrompt);
        // For enhanced prompts (Adjust & Reanimate), auto-load AI prompts since user expects them
        // This triggers the warning check before showing prompts
        setTimeout(() => {
          loadAIPrompts();
        }, 100);
      } else if (preSelectedImage) {
        // CRITICAL: If in auto-trigger mode (preSelectedImage exists), the auto-trigger effect will handle loading
        // DON'T reset approval or interfere with auto-trigger
        console.log('✅ Auto-trigger mode - will be handled by auto-trigger effect');
      } else {
        // For standard animations, DON'T auto-load prompts
        // User must click "Generate AI Prompts" button to trigger analysis
        console.log('⏭️ Standard animation - NOT auto-loading prompts. User must click button.');
      }
    } else {
      // Clear prompts when no image selected
      setAiPrompts([]);
      analysisApprovedRef.current = false; // Reset approval when image cleared
    }
    
    // CRITICAL: Only reset approval if NOT in auto-trigger mode
    // Auto-trigger effect sets approval, and we shouldn't override it
    if (!preSelectedImage) {
      console.log('🔄 Resetting analysis approval (not in auto-trigger mode)');
      analysisApprovedRef.current = false;
      setShowingAnalysisWarning(false);
    }
  }, [selectedImage]);

  // Load AI-generated prompts for the selected image
  // Now uses the shared AI analysis "brain" for consistency
  const loadAIPrompts = async () => {
    console.log('🔍 loadAIPrompts called - checking conditions...');
    console.log('  - selectedImage:', !!selectedImage);
    console.log('  - isMountedRef.current:', isMountedRef.current);
    
    if (!selectedImage || !isMountedRef.current) {
      console.log('❌ Early return: missing selectedImage or not mounted');
      return;
    }
    
    console.log('  - skipAnalysisRef:', !!skipAnalysisRef);
    console.log('  - skipAnalysisRef.current:', skipAnalysisRef?.current);
    
    // CHECK: If user chose to skip analysis (free option), skip the AI analysis
    // Use ref for immediate synchronous access without React state update delays
    if (skipAnalysisRef && skipAnalysisRef.current === true) {
      console.log('⏭️ Skipping AI analysis - user chose free option (skipAnalysisRef.current =', skipAnalysisRef.current, ')');
      // Set fallback prompts without calling AI
      const fallbackPrompts = [
        "Add only subtle breathing and gentle blinking while preserving the composed, posed nature of this family portrait",
        "Create minimal motion: soft breathing and natural eye movements while keeping subjects naturally still as they were when photographed",
        "Animate with gentle breathing and barely perceptible micro-expressions that respect the stillness of this captured moment"
      ];
      setAiPrompts(fallbackPrompts);
      setPrompt(fallbackPrompts[0]);
      console.log('✅ Set fallback prompts without analysis - NO API CALL MADE');
      return; // Exit early, no analysis needed
    }
    
    console.log('📊 skipAnalysisRef check: ref exists?', !!skipAnalysisRef, 'value:', skipAnalysisRef?.current);
    console.log('  - analysisApprovedRef.current:', analysisApprovedRef.current);
    console.log('  - showingAnalysisWarning:', showingAnalysisWarning);
    console.log('  - checkSingleImageAnalysis:', !!checkSingleImageAnalysis);
    
    // Check if analysis was already approved for this image
    if (!analysisApprovedRef.current && !showingAnalysisWarning && checkSingleImageAnalysis) {
      console.log('⚠️ Analysis not approved yet, checking with parent...');
      setShowingAnalysisWarning(true);
      
      // Call parent's check function which will show the warning modal
      await checkSingleImageAnalysis(selectedImage, () => {
        // This callback runs after user confirms
        console.log('✅ Analysis approved by user');
        analysisApprovedRef.current = true;
        // Reset warning flag BEFORE calling loadAIPrompts
        setShowingAnalysisWarning(false);
        // Trigger loadAIPrompts again now that it's approved
        // Use immediate invocation since flags are already set
        loadAIPrompts();
      });
      
      return; // Don't proceed until approved
    }

    console.log('🚀 loadAIPrompts starting...');
    console.log('📸 selectedImage:', selectedImage);
    console.log('🔄 isMountedRef:', isMountedRef.current);
    
    setLoadingPrompts(true);
    try {
      console.log('🧠 Animation Generator using shared AI analysis brain');
      const result = await analyzeImageWithAI(selectedImage, selectedImage.enhancedPrompt, user);
      console.log('📥 Received result:', result);
      
      // Handle result that may include border warning and faded image warning
      let suggestions;
      if (result && result.suggestions) {
        suggestions = result.suggestions;
        if (result.borderWarning) {
          console.warn('⚠️ Border warning from AI analysis:', result.borderWarning);
          setImageBorderWarning(result.borderWarning);
        }
        if (result.fadedWarning) {
          console.warn('⚠️ Faded image detected - may not animate well:', result.fadedWarning);
          setImageFadedWarning(result.fadedWarning);
        }
      } else if (Array.isArray(result)) {
        suggestions = result;
      }
      // CRITICAL: Always set AI prompts, even if component unmounted
      // React will safely ignore state updates on unmounted components
      console.log('✅ Setting aiPrompts state with:', suggestions);
      setAiPrompts(suggestions);
      // Automatically set the first AI suggestion as the prompt for better results
      if (suggestions && suggestions.length > 0) {
        setPrompt(suggestions[0]);
        console.log('✅ Auto-selected first AI suggestion as prompt:', suggestions[0]);
      }
    } catch (error) {
      console.error('❌ Error in loadAIPrompts:', error);
      // Silently ignore errors if component has unmounted (request cancelled)
      if (error.message !== 'Component unmounted' && isMountedRef.current) {
        console.error('Failed to load AI prompts:', error);
        console.log('🔍 Enhanced prompt in loadAIPrompts error:', selectedImage?.enhancedPrompt);
        
        // Use enhanced prompt fallback if available
        let fallbackPrompts;
        if (selectedImage?.enhancedPrompt) {
          console.log('✅ Using enhanced prompt in loadAIPrompts fallback');
          fallbackPrompts = [
            `${selectedImage.enhancedPrompt} with gentle natural movement and subtle animation`,
            `${selectedImage.enhancedPrompt} while maintaining realistic motion and proper framing`,
            `${selectedImage.enhancedPrompt} focusing on smooth transitions and natural dynamics`
          ];
        } else {
          fallbackPrompts = [
            "Add only subtle breathing and gentle blinking - keep posed subjects naturally still with minimal micro-expressions",
            "Create barely perceptible motion: soft lighting shifts and minimal environmental movement appropriate to indoor/outdoor setting", 
            "Animate with natural restraint: gentle eye movements and breath rhythm while maintaining the composed nature of posed photos"
          ];
        }
        
        console.log('📝 Setting fallback prompts:', fallbackPrompts);
        setAiPrompts(fallbackPrompts);
      }
    } finally {
      // CRITICAL: Always set loading to false, even if component unmounted
      // React will safely ignore state updates on unmounted components
      console.log('🔄 Setting loadingPrompts to false (isMountedRef.current:', isMountedRef.current, ')');
      setLoadingPrompts(false);
      
      // Trigger button flash animation when prompts finish loading
      setPromptsJustLoaded(true);
      setTimeout(() => setPromptsJustLoaded(false), 8000); // Flash for 8 seconds (matches shimmer duration)
    }
  };

  const promptSuggestions = [
    "Add only subtle breathing and gentle blinking while preserving the composed, posed nature of this family portrait",
    "Create minimal motion: soft breathing and natural eye movements while keeping subjects naturally still as they were when photographed",
    "Animate with gentle breathing and barely perceptible micro-expressions that respect the stillness of this captured moment",
    "Add subtle life through gentle breathing and soft lighting changes while maintaining the formal, posed composition",
    "Create natural stillness with minimal breathing and gentle eye movements appropriate for a carefully composed photograph"
  ];

  const handleGenerate = async () => {
    // Ensure API is defined in this function scope
    const API = `${getBACKEND_URL()}/api`;
    console.log('🔧 NEW CODE LOADED - API defined as:', API);
    console.log('🎬 handleGenerate called - albumContext:', albumContext);
    console.log('🎬 albumContext?.albumId:', albumContext?.albumId);
    
    if (!selectedImage || !prompt.trim()) {
      toast.error('Please select a memory and enter animation instructions');
      return;
    }

    setIsGenerating(true);
    try {
      const formData = new FormData();
      // Only add project_id if it's not unassigned
      if (selectedImage.projectId && selectedImage.projectId !== 'unassigned') {
        formData.append('project_id', selectedImage.projectId);
      }
      
      // Handle different image types: edited images, synthetic images, original images
      if (selectedImage.isEdited && selectedImage.url && selectedImage.url.startsWith('data:')) {
        // For edited images, upload the edited image data and use original image_id
        console.log('🎨 Uploading edited image for video generation');
        const response = await fetch(selectedImage.url);
        const blob = await response.blob();
        formData.append('image', blob, `edited_image_${selectedImage.editTimestamp || Date.now()}.jpg`);
        // Also send the edited image as thumbnail data for the video
        formData.append('thumbnail_data', selectedImage.url);
        // Use original image ID for database reference
        if (selectedImage.original_id) {
          formData.append('image_id', selectedImage.original_id);
        }
      } else if (selectedImage.projectName === 'Album Video' && selectedImage.url) {
        // For synthetic images from album videos, pass the image URL to backend
        // Backend will download it internally (it has access to internal endpoints)
        console.log('🔄 Passing synthetic image URL from album video to backend');
        console.log('📸 Synthetic image URL:', selectedImage.url);
        
        // Convert relative URL to absolute if needed
        let imageUrl = selectedImage.url;
        if (imageUrl.startsWith('/api/')) {
          imageUrl = `${getAPI()}${imageUrl}`;
        }
        
        // Ensure user_id parameter is present for internal URLs
        if (imageUrl.includes('/api/files/') && !imageUrl.includes('user_id=') && user?.id) {
          const separator = imageUrl.includes('?') ? '&' : '?';
          imageUrl = `${imageUrl}${separator}user_id=${user.id}`;
          console.log('🔧 Added user_id to video generation image URL');
        }
        
        console.log('📤 Final image URL for backend:', imageUrl);
        formData.append('image_url', imageUrl);
        
        // Use the video's original image_id if available for database reference
        if (selectedImage.id && !selectedImage.id.startsWith('synthetic_')) {
          formData.append('image_id', selectedImage.id);
        }
      } else {
        // For original images, use the image_id as usual
        console.log('🖼️ Using original image ID for video generation');
        console.log('📝 selectedImage.id:', selectedImage.id);
        formData.append('image_id', selectedImage.id);
        console.log('✅ Appended image_id to formData:', selectedImage.id);
      }
      
      formData.append('provider', 'luma');
      formData.append('prompt', prompt);
      formData.append('duration', duration);
      formData.append('overlay_text', '');
      formData.append('add_music', false);
      formData.append('music_style', 'none');
      formData.append('user_id', user.id); // CRITICAL: Pass user_id for data isolation
      formData.append('bright_background', imageBrightBackground ? 'true' : 'false'); // Pass bright background flag for prompt simplification

      // Add album context if animating from within an album
      if (albumContext) {
        console.log('📂 Album context detected in single image animation:', albumContext);
        formData.append('album_id', albumContext.albumId);
        formData.append('album_only', albumContext.albumOnly ? 'true' : 'false');
        
        // NEW: If this is a re-animation from Adjust & Reanimate flow, add source_video_id
        if (selectedImage.sourceVideoId) {
          console.log('🔗 [Single Image Animation] Tracking source video ID:', selectedImage.sourceVideoId);
          formData.append('source_video_id', selectedImage.sourceVideoId);
        } else {
          console.log('ℹ️ [Single Image Animation] No sourceVideoId in selectedImage');
        }
      }

      console.log('📦 About to send formData. Keys:', Array.from(formData.keys()));
      console.log('📦 FormData entries:');
      for (let pair of formData.entries()) {
        if (pair[1] instanceof File) {
          console.log(`  ${pair[0]}: [File: ${pair[1].name}]`);
        } else {
          console.log(`  ${pair[0]}: ${pair[1]}`);
        }
      }

      const response = await axios.post(`${getAPI()}/generate`, formData);
      console.log('✅ Generation API response:', response);
      // toast.success('Animated moment generation started! ✨✨'); // Removed per user request

      // IMMEDIATE UI UPDATE: Add job to local state so it appears instantly
      if (response.data?.job) {
        console.log(`📌 Adding job to local state immediately:`, response.data.job.id);
        setVideoJobs(prevJobs => [response.data.job, ...prevJobs]);
      }

      // Optimistically update cache with pending video
      // Note: This is non-critical since AlbumsTab fetches pending videos independently
      try {
        // Try to access global cache if available (cacheRef not in modal scope)
        const globalCache = (typeof window !== 'undefined' && window.__MM_MEDIA_CACHE__) ? window.__MM_MEDIA_CACHE__ : null;
        const srcId = selectedImage.original_id || selectedImage.id;
        if (srcId && globalCache) {
          // Also update global cache videos list with a lightweight pending record so Moments tab shows it quickly
          const pendingStub = {
            id: `pending_${Date.now()}`,
            image_id: srcId,
            status: 'submitted',
            created_at: new Date().toISOString(),
            thumbnail_url: selectedImage.thumbnail_url || selectedImage.url || '',
            album_id: albumContext?.albumId || null
          };
          try {
            const { setCachedVideos, getCachedVideos } = globalCache || {};
            const existing = (typeof getCachedVideos === 'function') ? (getCachedVideos() || []) : [];
            if (typeof setCachedVideos === 'function') {
              setCachedVideos([pendingStub, ...existing]);
              console.log('✅ Added pending video stub to cache');
            }
          } catch (e) {
            console.warn('Cache update for pending video skipped (non-blocking):', e);
          }
        }
      } catch (e) {
        console.warn('Optimistic UI update failed (non-blocking):', e);
      }
      
      console.log('🔒 Closing animation generator modal...');
      // Mark component as unmounted immediately to stop all operations
      isMountedRef.current = false;
      // Close modal
      onClose();
      
      console.log('✨ Showing magic processing overlay...');
      // Show magic processing overlay
      setShowMagicProcessing(true);
      setProcessingCount(1);
      
      // Refresh data immediately to load the pending video
      if (typeof onRefresh === 'function') {
        await onRefresh();
      }
      
      // Small delay to ensure data is loaded
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Only redirect if NOT animating from within an album
      // CRITICAL: Check BOTH albumContext AND currentView to avoid stale albumContext from previous album visit
      console.log('🔍 Single image animation - checking albumContext:', albumContext, 'currentView:', currentView);
      const isActuallyInAlbumSingle = albumContext?.albumId && currentView === 'projects';
      console.log('🔍 isActuallyInAlbumSingle:', isActuallyInAlbumSingle);
      
      if (!isActuallyInAlbumSingle) {
        console.log('➡️ Redirecting to Animated Moments tab (Pictures/non-album context)');
        // Redirect to Animated Moments with pending toggle
        setCurrentView('living_memories');
        setActiveMainTab('moments');
        setShowPendingVideos(true);
      } else {
        console.log('🎯 Staying in album (album context detected):', albumContext.albumId);
        // Stay in current view (album) - pending placeholder will appear in the album
      }
      
      // Show overlay for 3 seconds before redirecting
      // Videos are already in database, album/moments polling will handle updates
      const isAlbumContext = isActuallyInAlbumSingle;
      
      if (isAlbumContext) {
        // ALBUM CONTEXT: Show overlay for 4 seconds then redirect
        console.log('📁 Album context - showing overlay for 4 seconds');
        console.log('📁 Album ID:', albumContext.albumId);
        console.log('📁 User ID:', user.id);
        
        setTimeout(() => {
          console.log('⏰ 4 seconds elapsed, hiding overlay and switching to moments view');
          setShowMagicProcessing(false);
          
          // Switch to moments view
          if (albumFilterSwitchCallback.current && typeof albumFilterSwitchCallback.current === 'function') {
            console.log('🔄 Switching filter to moments to display pending video');
            albumFilterSwitchCallback.current('moments');
          }
          
          // Trigger album refresh in background
          if (albumRefreshCallback.current && typeof albumRefreshCallback.current === 'function') {
            console.log('🔄 Calling albumRefreshCallback in background to sync pending count');
            albumRefreshCallback.current();
          }
        }, 4000); // 4 second delay
        
        return; // Exit - setTimeout will handle everything
      }
      
      // NON-ALBUM CONTEXT (Pictures tab): Show overlay for 4 seconds then redirect
      console.log('📍 Non-album context - showing overlay for 4 seconds');
      
      // CRITICAL: Clear any existing polling interval before creating new one
      if (pollingIntervalRef.current) {
        console.log('⚠️ Clearing previous polling interval');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      setTimeout(() => {
        console.log('⏰ 4 seconds elapsed, hiding overlay');
        setShowMagicProcessing(false);
        
        // Scroll to Animated Moments section
        setTimeout(() => {
          const animatedMomentsSection = document.querySelector('[class*="living_memories"]') ||
                                        document.querySelector('h2[aria-label="Animated Moments"], h2.animated-moments, h2[data-section="animated-moments"]') ||
                                        document.querySelector('.text-amber-800');
          if (animatedMomentsSection) {
            animatedMomentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }, 4000); // 4 second delay
    } catch (error) {
      console.error('❌ Generation error:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to start time travel');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageUpload = async (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'image/hif', 'image/webp', 'image/gif'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'heic', 'heif', 'hif', 'webp', 'gif'];
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error(`Unsupported file type: ${file.name}. Please upload JPEG, PNG, HEIC, HEIF, HIF, WebP, or GIF images.`, {
        duration: 5000,
        position: 'top-center'
      });
      return;
    }
    
    try {
      // Fix orientation issues on mobile devices
      const correctedFile = await fixImageOrientation(file);
      
      const formData = new FormData();
      formData.append('file', correctedFile);
      formData.append('user_id', user.id); // CRITICAL: Pass user_id for data isolation
      // Don't create projects automatically - keep images unassigned
      
      formData.append('user_id', user.id);
      const response = await axios.post(`${getAPI()}/upload`, formData);
      const uploadedImg = {
        ...response.data.image,
        projectName: 'Unassigned Pictures',
        projectId: 'unassigned',
        name: response.data.image.original_filename || 'Uploaded Image'
      };
      setSelectedImage(uploadedImg);
      setStep(3);
      onRefresh(); // Refresh to show new image in library
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to upload image';
      toast.error(errorMessage, {
        duration: 6000,
        position: 'top-center'
      });
    }
  };

  // Hidden file input - always rendered regardless of step so it can be triggered from anywhere
  const globalFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,image/hif,image/webp,image/gif"
      onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
      className="hidden"
      id="direct-upload-input"
    />
  );

  if (step === 1) {
    return (
      <>
        {globalFileInput}
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 z-[100] overflow-y-auto" onClick={onClose}>
          <div className="bg-white rounded-2xl sm:rounded-3xl w-full max-w-[95vw] sm:max-w-[90vw] lg:max-w-2xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-gray-100" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="text-center mb-8">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t("animation.create_moment")}</h3>
              <p className="text-gray-600 text-base sm:text-lg">{t("animation.transform_photos")}</p>
            </div>
          
          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
            {/* Upload New Picture */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="group relative p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl hover:from-purple-100 hover:to-blue-100 transition-all duration-300 text-center border-2 border-transparent hover:border-purple-300 hover:shadow-xl transform hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md group-hover:shadow-lg transition-shadow">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <h5 className="font-bold text-gray-900 mb-2 text-lg">{t("animation.upload_new")}</h5>
                <p className="text-gray-600 text-sm">{t("animation.upload_desc")}</p>
              </div>
            </button>
            
            {/* Choose from Library */}
            <button
              onClick={() => setStep(3)}
              className="group relative p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl hover:from-amber-100 hover:to-orange-100 transition-all duration-300 text-center border-2 border-transparent hover:border-amber-300 hover:shadow-xl transform hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md group-hover:shadow-lg transition-shadow">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h5 className="font-bold text-gray-900 mb-2 text-lg">{t("animation.choose_library")}</h5>
                <p className="text-gray-600 text-sm">{allImages.length} {t("animation.photos_ready")}</p>
              </div>
            </button>
          </div>

          {/* Cancel Button */}
          <div className="text-center">
            <button 
              onClick={onClose} 
              className="px-8 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        {globalFileInput}
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 z-[100] overflow-y-auto" onClick={onClose}>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl sm:rounded-2xl lg:rounded-3xl w-full max-w-[95vw] sm:max-w-[90vw] lg:max-w-2xl overflow-y-auto p-4 sm:p-6 shadow-2xl border border-amber-200" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setStep(1)} className="text-amber-600 hover:text-amber-800 mb-3 sm:mb-4 text-sm sm:text-base">← Back</button>
          <h3 className="text-lg sm:text-xl font-bold text-amber-800 mb-3 sm:mb-4">Upload Your Picture</h3>
          
          <div className="border-2 border-dashed border-amber-300 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/heic,image/heif,image/hif,image/webp,image/gif"
              onChange={(e) => e.target.files[0] && handleImageUpload(e.target.files[0])}
              className="hidden"
              id="living-memory-upload"
            />
            <label htmlFor="living-memory-upload" className="cursor-pointer">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-amber-400 to-orange-400 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <p className="text-amber-800 font-medium text-sm sm:text-base">Upload Your Picture</p>
              <p className="text-amber-600 text-xs sm:text-sm mt-1">Supported: JPEG, PNG, HEIC, WebP, GIF, BMP, TIFF (up to 10MB)</p>
            </label>
          </div>
        </div>
      </div>
      </>
    );
  }

  // Step 3 - Animation Generator layout
  return (
    <>
      {globalFileInput}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[100] overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl sm:rounded-3xl w-full max-w-[98vw] sm:max-w-[95vw] lg:max-w-7xl max-h-[95vh] flex flex-col shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        {/* Professional Header */}
        <div className="p-6 sm:p-8 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Animation Generator</h3>
                  <p className="text-gray-600 text-sm sm:text-base">{t("animation.bring_memories")}</p>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="flex-1 p-3 sm:p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            {/* Left Column - Image Selection or Selected Memory */}
            <div className="space-y-4 sm:space-y-6">
              {selectedImage ? (
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-4 sm:p-6 border border-purple-200 shadow-sm">
                  <h4 className="font-semibold text-gray-900 text-base sm:text-lg mb-4">Selected Picture</h4>
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 flex justify-center bg-white shadow-inner">
                    {/* AI Analyzing Overlay */}
                    {loadingPrompts && (
                      <div className="absolute inset-0 bg-white/60 backdrop-blur-[4px] flex flex-col items-center justify-center z-10 rounded-xl">
                        {/* AI Brain Animation */}
                        <div className="relative w-16 h-16 mb-3">
                          {/* Flask outline with pulse */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-16 h-16 text-purple-500 animate-pulse" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M24 10 L24 24 L16 48 C14 52 16 58 24 58 L40 58 C48 58 50 52 48 48 L40 24 L40 10 Z" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="32" cy="45" r="3" fill="currentColor" opacity="0.3"/>
                              <circle cx="28" cy="38" r="2" fill="currentColor" opacity="0.2"/>
                              <circle cx="36" cy="40" r="2" fill="currentColor" opacity="0.2"/>
                            </svg>
                          </div>
                          
                          {/* Bubbling effect */}
                          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping"></div>
                          <div className="absolute bottom-6 left-8 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-ping" style={{animationDelay: '0.3s'}}></div>
                          <div className="absolute bottom-6 right-8 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                        </div>
                        
                        {/* Animated text */}
                        <div className="text-center px-4">
                          <div className="text-sm font-medium text-gray-800 mb-1 animate-pulse">
                            Our AI algorithms are analyzing your picture...
                          </div>
                          <div className="flex items-center justify-center gap-1">
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <img loading="lazy"
                      key={`animation-preview-${selectedImage.id}-${selectedImage.updated_at || selectedImage.created_at || 'stable'}`}
                      src={(() => {
                        // PRIORITY 1: Cloudflare delivery URL
                        if (selectedImage.cloudflare_delivery_url && 
                            (selectedImage.cloudflare_delivery_url.includes('imagedelivery.net') || 
                             selectedImage.cloudflare_delivery_url.includes('customer-'))) {
                          console.log('✅ Using cloudflare_delivery_url:', selectedImage.cloudflare_delivery_url.substring(0, 100));
                          return selectedImage.cloudflare_delivery_url;
                        }
                        
                        // PRIORITY 2: Check if existing URLs are Cloudflare URLs
                        const directUrl = selectedImage.thumbnail_url || selectedImage.url || selectedImage.r2_url;
                        if (directUrl && 
                            (directUrl.includes('imagedelivery.net') || 
                             directUrl.includes('customer-'))) {
                          console.log('✅ Using Cloudflare URL from fields:', directUrl.substring(0, 100));
                          return directUrl;
                        }
                        
                        // PRIORITY 3: Try other direct URLs
                        if (directUrl && (directUrl.startsWith('http') || directUrl.startsWith('data:'))) {
                          console.log('✅ Using direct URL for immediate display:', directUrl.substring(0, 100));
                          return directUrl;
                        }
                        
                        // Fallback to getImageUrl which requires user data
                        const constructedUrl = getImageUrl(selectedImage);
                        console.log('🔄 Using constructed URL:', constructedUrl?.substring(0, 100) || 'EMPTY');
                        return constructedUrl || '/placeholder.png'; // Fallback to placeholder if all else fails
                      })()}
                      alt={selectedImage.name}
                      className="max-w-full max-h-48 sm:max-h-96 object-contain"
                      onLoad={() => console.log('✅ CreateLiving Image loaded:', 
                        selectedImage.isEdited ? `EDITED (ID: ${selectedImage.id})` : 'Original', 
                        'URL:', selectedImage.url?.substring(0, 100))}
                      onError={(e) => {
                        console.error('❌ Image load error:', e.target.src);
                        if (localStorage.getItem('debugMM') === '1') {
                          console.log('🔧 [debugMM] SelectedImage.url fields:', {
                            url: selectedImage.url,
                            thumbnail_url: selectedImage.thumbnail_url,
                            r2_url: selectedImage.r2_url
                          });
                        }
                        // Try fallback URLs in order
                        if (selectedImage.r2_url && e.target.src !== selectedImage.r2_url) {
                          console.log('🔄 Falling back to r2_url');
                          e.target.src = selectedImage.r2_url;
                        } else if (selectedImage.thumbnail_url && e.target.src !== selectedImage.thumbnail_url) {
                          console.log('🔄 Falling back to thumbnail_url');
                          e.target.src = selectedImage.thumbnail_url;
                        } else if (selectedImage.original_url && e.target.src !== selectedImage.original_url) {
                          console.log('🔄 Falling back to original_url');
                          e.target.src = selectedImage.original_url;
                        } else {
                          // Last resort - try getImageUrl
                          const constructedUrl = getImageUrl(selectedImage);
                          if (constructedUrl && e.target.src !== constructedUrl) {
                            console.log('🔄 Last resort - using getImageUrl');
                            e.target.src = constructedUrl;
                          }
                        }
                      }}
                    />
                    
                    {/* View Videos Button - Top Left Corner - Only show if image has associated videos */}
                    {(imagesWithVideos.has(selectedImage.id) || imagesWithVideos.has(String(selectedImage.id))) && (
                      <button
                        onClick={() => setShowVideoGallery(selectedImage.id)}
                        className="absolute top-1 left-1 sm:top-2 sm:left-2 p-1.5 sm:p-2 text-white rounded-md sm:rounded-lg transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-xl overflow-hidden"
                        title="View animated moments"
                        style={{
                          background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #a855f7 75%, #ec4899 100%)',
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 8s linear infinite'
                        }}
                      >
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                    
                    {/* Adjust Picture Button - Top Right Corner */}
                    <button
                      onClick={() => setShowImageEditor(true)}
                      className="absolute top-1 right-1 sm:top-2 sm:right-2 p-1.5 sm:p-2 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white rounded-md sm:rounded-lg transition-colors backdrop-blur-sm shadow-lg"
                      title="Adjust picture before animation"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                      </svg>
                    </button>
                  </div>
                  {/* Generate Button - Moved below picture */}
                  <div className="mt-4 bg-gradient-to-br from-purple-500 via-purple-600 to-blue-600 p-[2px] rounded-2xl shadow-lg">
                    <div className="bg-white rounded-2xl p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-gray-900 text-sm">{t("animation.ai_powered")}</span>
                        </div>
                        <div className="flex items-center bg-gradient-to-r from-orange-50 to-orange-100 px-2 py-1 rounded-lg border border-orange-200">
                          <span className="text-xs font-bold text-orange-700">{estimatedCost} credits</span>
                        </div>
                      </div>
                      
                      {console.log('🔘 Rendering Generate button - handleGenerate exists?', typeof handleGenerate, 'disabled?', isGenerating || !prompt.trim() || !selectedImage)}
                      <button
                        onClick={() => {
                          console.log('🖱️ Generate button CLICKED!');
                          handleGenerate();
                        }}
                        disabled={isGenerating || !prompt.trim() || !selectedImage}
                        className="w-full bg-gradient-to-r from-purple-500 via-purple-600 to-blue-600 hover:from-purple-600 hover:via-purple-700 hover:to-blue-700 text-white px-4 py-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:transform-none"
                        style={promptsJustLoaded && !isGenerating && selectedImage ? {
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 8s linear infinite'
                        } : {}}
                        data-tutorial="generate-button"
                      >
                        {isGenerating ? (
                          <span className="flex items-center justify-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-sm">Creating Animated Moment...</span>
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-1.5">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            <span>Create Animated Moment ({estimatedCost} credits)</span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setSelectedImage(null)}
                    className="mt-4 w-full bg-gray-100 text-gray-700 px-4 py-2.5 rounded-xl hover:bg-gray-200 transition-all text-sm font-medium"
                  >
                    ← Choose Different Picture
                  </button>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4 sm:p-6 border border-gray-200 shadow-sm">
                  <h4 className="font-semibold text-gray-900 text-base mb-4 text-center">{t("animation.choose_memory")}</h4>
                  
                  {/* Controls - Centered below title */}
                  <div className="flex justify-center items-center gap-2 mb-3 flex-wrap">
                    {/* Folder Filter Dropdown */}
                    <select
                      value={selectedFolderFilter?.id || ''}
                      onChange={(e) => {
                        const folderId = e.target.value;
                        if (folderId === '') {
                          setSelectedFolderFilter(null);
                        } else {
                          const folder = folders.find(f => f.id === folderId);
                          setSelectedFolderFilter(folder);
                        }
                      }}
                      className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-700"
                    >
                      <option value="">📁 All Pictures</option>
                      {folders.map(folder => (
                        <option key={folder.id} value={folder.id}>
                          📂 {folder.name}
                        </option>
                      ))}
                    </select>
                    
                    {/* Grid Size Controls */}
                    <div className="flex bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                      {['small', 'medium', 'large'].map((size) => (
                        <button
                          key={size}
                          onClick={() => setMemoryGridSize(size)}
                          className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                            memoryGridSize === size
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                          title={`${size.charAt(0).toUpperCase() + size.slice(1)} grid`}
                        >
                          {size === 'small' ? '⊞' : size === 'medium' ? '⊡' : '⊟'}
                        </button>
                      ))}
                    </div>
                    
                    {/* Sort dropdown */}
                    <select
                      value={`${gallerySort.field}-${gallerySort.direction}`}
                      onChange={(e) => {
                        console.log('📅 Sort changed to:', e.target.value);
                        const [field, direction] = e.target.value.split('-');
                        console.log('Setting gallerySort:', { field, direction });
                        setGallerySort({ field, direction });
                      }}
                      className="text-xs bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-700"
                    >
                      <option value="created_at-desc">▼ Newest First</option>
                      <option value="created_at-asc">▲ Oldest First</option>
                    </select>
                  </div>
                  <div className={`grid ${getMemoryGridClasses(memoryGridSize)} gap-3 max-h-64 md:max-h-96 lg:max-h-[32rem] overflow-y-auto`}>
                    {allImages.map((image) => (
                      <div
                        key={`${image.projectId}-${image.id}`}
                        className="relative"
                      >
                        <div
                          className={`aspect-square rounded-lg overflow-hidden cursor-pointer transition-colors ${
                            imagesWithVideos.has(image.id) 
                              ? 'border-4 border-purple-500 shadow-lg hover:border-purple-600' 
                              : 'border-2 border-amber-200 hover:border-amber-400'
                          }`}
                          onClick={() => setSelectedImage(image)}
                        >
                          <img loading="lazy"
                            src={(() => {
                              // Priority 1: Cloudflare delivery URLs (most reliable)
                              if (image.cloudflare_delivery_url) {
                                console.log('Using cloudflare_delivery_url for', image.id);
                                return image.cloudflare_delivery_url;
                              }
                              
                              // Priority 2: Any URL with Cloudflare domain
                              const cfUrl = image.thumbnail_url || image.url || image.r2_url;
                              if (cfUrl && (cfUrl.includes('imagedelivery.net') || cfUrl.includes('customer-'))) {
                                console.log('Using Cloudflare URL for', image.id, cfUrl.substring(0, 50));
                                return cfUrl;
                              }
                              
                              // Priority 3: Direct http/https URLs (skip getImageUrl auth complexity)
                              if (image.url && (image.url.startsWith('http://') || image.url.startsWith('https://'))) {
                                console.log('Using direct URL for', image.id, image.url.substring(0, 50));
                                return image.url;
                              }
                              
                              if (image.thumbnail_url && (image.thumbnail_url.startsWith('http://') || image.thumbnail_url.startsWith('https://'))) {
                                console.log('Using direct thumbnail for', image.id, image.thumbnail_url.substring(0, 50));
                                return image.thumbnail_url;
                              }
                              
                              // Priority 4: Try getImageUrl (with auth)
                              const constructedUrl = getImageUrl(image);
                              if (constructedUrl) {
                                console.log('Using getImageUrl for', image.id, constructedUrl.substring(0, 50));
                                return constructedUrl;
                              }
                              
                              // Last resort: any URL available
                              const fallback = image.r2_url || image.url || image.thumbnail_url || '';
                              console.warn('Using fallback URL for', image.id, fallback.substring(0, 50));
                              return fallback;
                            })()}
                            alt={image.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('❌ Image grid load error for', image.id, 'URL:', e.target.src?.substring(0, 100));
                              console.log('Image object:', { 
                                url: image.url?.substring(0, 50), 
                                thumbnail_url: image.thumbnail_url?.substring(0, 50),
                                cloudflare_delivery_url: image.cloudflare_delivery_url?.substring(0, 50),
                                r2_url: image.r2_url?.substring(0, 50)
                              });
                              
                              // Try fallback URLs in priority order
                              if (image.cloudflare_delivery_url && e.target.src !== image.cloudflare_delivery_url) {
                                console.log('Fallback: Trying cloudflare_delivery_url');
                                e.target.src = image.cloudflare_delivery_url;
                              } else if (image.url && e.target.src !== image.url) {
                                console.log('Fallback: Trying url');
                                e.target.src = image.url;
                              } else if (image.thumbnail_url && e.target.src !== image.thumbnail_url) {
                                console.log('Fallback: Trying thumbnail_url');
                                e.target.src = image.thumbnail_url;
                              } else if (image.r2_url && e.target.src !== image.r2_url) {
                                console.log('Fallback: Trying r2_url');
                                e.target.src = image.r2_url;
                              } else {
                                console.error('All fallback URLs exhausted for', image.id);
                              }
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {allImages.length === 0 && (
                    <div className="text-center py-12">
                      <div className="mb-4">
                        <svg className="w-16 h-16 mx-auto text-amber-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-amber-800 font-medium mb-6">No images available yet</p>
                      </div>
                      <button
                        onClick={() => {
                          // Trigger file input directly without closing modal
                          if (fileInputRef && fileInputRef.current) {
                            fileInputRef.current.click();
                          }
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all transform hover:scale-105 shadow-lg"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Upload Your First Memory
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Right Column - Configuration */}
            <div className="space-y-6">
              {/* Animation Instructions */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-5 border-2 border-purple-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-gray-900">{t("animation.instructions")}</h4>
                  </div>
                  {prompt.trim() && (
                    <button
                      onClick={() => setPrompt('')}
                      className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-white/50 transition-colors flex items-center gap-1"
                      title="Clear text"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Clear
                    </button>
                  )}
                </div>
                
                {/* Luma Limitations Warning Banner - Collapsible - Subtle Design */}
                <div className="mb-3 bg-gray-50 border-l-2 border-gray-300 rounded-lg overflow-hidden">
                  <button 
                    onClick={() => setShowAnimationTip(!showAnimationTip)}
                    className="w-full flex items-start gap-2 p-2.5 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                    </svg>
                    <div className="flex-1 text-left">
                      <p className="text-xs font-medium text-gray-600">Animation Tip</p>
                    </div>
                    <svg 
                      className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showAnimationTip ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showAnimationTip && (
                    <div className="px-2.5 pb-2.5">
                      <p className="text-xs text-gray-600">
                        The AI works best with <span className="font-semibold">subtle movements</span> like breathing, blinking, gentle head turns, and facial expressions. 
                        Complex actions (rolling, jumping, walking) may not animate as expected.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Border Warning Banner */}
                {imageBorderWarning && imageBorderWarning.has_borders && (
                  <div className="mb-3 bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-800 mb-1">Image Quality Warning</p>
                        <p className="text-xs text-amber-700">
                          This image has white borders or uncropped sections ({imageBorderWarning.edges.join(', ')} edges). 
                          For best animation results, please crop the image to remove borders and ensure the photo fills the entire frame.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {imageFadedWarning && imageFadedWarning.is_faded && (
                  <div className="mb-3 bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-800 mb-1">Image Quality Warning</p>
                        <p className="text-xs text-amber-700">
                          This image has quality issues that may affect animation (faded, low contrast, blurry, or desaturated). 
                          Results may include limited movement or visual artifacts.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-800 placeholder-gray-400"
                  placeholder={t('animation.describe_prompt')}
                />
                
                {/* Complex Movement Warning (Dynamic) - Only for user-typed text */}
                {(() => {
                  // Check if current prompt is an AI-suggested prompt
                  const isAISuggestion = aiPrompts.some(suggestion => suggestion === prompt);
                  
                  // Only show warning for user-typed prompts, not AI suggestions
                  if (isAISuggestion) {
                    return null;
                  }
                  
                  const complexMovements = ['roll', 'jump', 'leap', 'flip', 'walk', 'run', 'sprint', 'dance', 'spin', 'twirl', 'cartwheel', 'somersault', 'fly', 'soar'];
                  const hasComplexMovement = complexMovements.some(word => prompt.toLowerCase().includes(word));
                  
                  if (hasComplexMovement && prompt.length > 10) {
                    return (
                      <div className="mt-2 bg-red-50 border-l-4 border-red-400 p-3 rounded-lg animate-pulse">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                          </svg>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-red-800 mb-1">⚠️ Complex Movement Detected</p>
                            <p className="text-xs text-red-700">
                              Your prompt includes complex movements that may not animate well. Try using an <span className="font-semibold">AI suggestion below</span> for better results, 
                              or describe subtle expressions and emotions instead.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div className="mt-3" data-tutorial="ai-prompts-section">
                  <p className="text-xs text-gray-600 mb-2 font-medium">
                    {selectedImage ? `🤖 ${t('animation.ai_generated_prompts')}` : `💡 ${t('animation.select_for_suggestions')}`}
                  </p>
                  {/* Scrollable container for AI prompts - max height to prevent overflow */}
                  <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                    {loadingPrompts ? (
                      <div className="flex flex-col items-center justify-center py-6 px-4">
                        {/* AI Brain Animation */}
                        <div className="relative w-16 h-16 mb-3">
                          {/* Brain outline with pulse */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-16 h-16 text-purple-500 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          
                          {/* Scanning lines */}
                          <div className="absolute inset-0 overflow-hidden">
                            <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-purple-400 to-transparent animate-scan"></div>
                          </div>
                          
                          {/* Neural dots */}
                          <div className="absolute top-2 left-2 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping"></div>
                          <div className="absolute top-4 right-3 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
                          <div className="absolute bottom-3 left-4 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping" style={{animationDelay: '0.4s'}}></div>
                          <div className="absolute bottom-2 right-2 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                        </div>
                        
                        {/* Animated text */}
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-700 mb-1 animate-pulse">
                            Your prompts will appear here after analysis
                          </div>
                          <div className="flex items-center justify-center gap-1">
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                          </div>
                        </div>
                      </div>
                    ) : aiPrompts.length > 0 ? (
                      <>
                        {aiPrompts.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => setPrompt(suggestion)}
                            className="text-xs bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 text-gray-700 px-3 py-2.5 rounded-lg transition-all text-left border border-purple-200 shadow-sm hover:shadow whitespace-pre-wrap"
                            data-tutorial="ai-prompt-suggestion"
                          >
                            {suggestion}
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            // Reset skip flag when user wants new AI prompts
                            console.log('🔄 Generate different prompts clicked - resetting skip flag');
                            if (skipAnalysisRef) {
                              skipAnalysisRef.current = false;
                            }
                            // DO NOT set analysisApprovedRef.current = true here!
                            // This would bypass the cost check - let loadAIPrompts handle it
                            loadAIPrompts();
                          }}
                          disabled={loadingPrompts}
                          className="text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white px-3 py-2 rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all mt-1 shadow-md flex-shrink-0"
                        >
                          ✨ Generate different prompts (Free)
                        </button>
                      </>
                    ) : selectedImage && !preSelectedImage ? (
                      // Only show manual button if NOT opened with preSelectedImage (auto-trigger mode)
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500 italic">No AI prompts available</p>
                        <button
                          onClick={async () => {
                            console.log('🎯 User clicked Generate AI Instructions button');
                            // DO NOT set analysisApprovedRef.current = true here!
                            // This would bypass the cost check and warning modal
                            // Let loadAIPrompts handle the cost check properly
                            
                            // CRITICAL FIX: isMountedRef might be false due to component lifecycle
                            // Temporarily set it to true since user is actively clicking
                            const wasMounted = isMountedRef.current;
                            isMountedRef.current = true;
                            console.log('🔧 Temporarily set isMountedRef to true (was:', wasMounted, ')');
                            
                            await loadAIPrompts();
                          }}
                          disabled={loadingPrompts}
                          className="w-full px-4 py-2 text-white text-sm rounded-lg transition-all font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                          style={{
                            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #a855f7 75%, #ec4899 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 8s linear infinite'
                          }}
                        >
                          {loadingPrompts ? (
                            <>
                              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                              Analyzing...
                            </>
                          ) : (
                            <>
                              🤖 Generate AI Animation Instructions (Free)
                            </>
                          )}
                        </button>
                      </div>
                    ) : selectedImage && preSelectedImage ? (
                      // When in auto-trigger mode (preSelectedImage), show a subtle message
                      <div className="flex flex-col items-center justify-center py-4">
                        <div className="text-xs text-gray-500 italic text-center">
                          AI prompts will appear here automatically
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              
              {/* Duration Setting - Moved below Animation Instructions */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-4 sm:p-5 border-2 border-purple-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">{t("animation.duration")}</h4>
                </div>
                <select
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  disabled={false}
                  className="w-full px-3 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all cursor-pointer hover:border-gray-400"
                  title="Vidu supports 4 or 8 seconds"
                >
                  <option value={4}>4 seconds (5 credits - Recommended)</option>
                  <option value={8}>8 seconds (10 credits)</option>
                </select>
              </div>
 
            </div>
          </div>
        </div>
        
        {/* Footer - Always visible with iOS safe area support */}
        <div className="flex-shrink-0 p-3 sm:p-6 pb-6 sm:pb-6 border-t border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 backdrop-blur-sm" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
            <button
              onClick={onClose}
              className="px-4 sm:px-6 py-2 sm:py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-lg sm:rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all text-sm sm:text-base order-2 sm:order-1 shadow-sm"
            >
              Cancel
            </button>
            
            <div className="text-xs sm:text-sm text-gray-700 font-medium order-1 sm:order-2 flex items-center gap-2 bg-white px-3 sm:px-4 py-2 rounded-lg border border-purple-200">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs sm:text-sm">💡</span>
              </div>
              <span className="text-xs sm:text-sm">{t('animation.pro_tip')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Image Editor Modal */}
    {showImageEditor && selectedImage && (
      <ImageEditorModalPortal
        image={selectedImage}
        context="animation"
        user={user}
        onClose={() => setShowImageEditor(false)}
        onSave={async (editingParams) => {
          try {
            console.log('Saving image edits:', editingParams);
            
            // If we have an edited image URL, update the selectedImage with the new edited version
            if (editingParams.editedImageUrl) {
              const timestamp = Date.now();
              
              // Create a completely new image object that forces React to re-render
              const newEditedImage = {
                id: `edited_${timestamp}`,
                name: `${selectedImage.name}_edited`,
                url: editingParams.editedImageUrl,
                thumbnail_url: editingParams.editedImageUrl,
                original_url: selectedImage.original_url || selectedImage.url,
                original_id: selectedImage.id,
                editedParams: editingParams,
                isEdited: true,
                editTimestamp: timestamp,
                // Force complete re-render by making this a completely new object
                _renderKey: `edited_image_${timestamp}_${Math.random()}`,
                // Preserve other properties
                projectName: selectedImage.projectName,
                description: selectedImage.description ? `${selectedImage.description} (edited)` : 'Edited image'
              };
              
              console.log('Creating completely new edited image object:', {
                id: newEditedImage.id,
                name: newEditedImage.name,
                isEdited: newEditedImage.isEdited,
                renderKey: newEditedImage._renderKey,
                urlType: newEditedImage.url.startsWith('data:') ? 'data URL' : 'regular URL'
              });
              
              // Force complete re-render by clearing state with a delay
              setSelectedImage(null);
              
              // Use a longer timeout to ensure complete state clearing
              setTimeout(() => {
                // Force a complete re-render by setting a new object with unique key
                const forceRerenderImage = {
                  ...newEditedImage,
                  _forceRerender: Date.now() + Math.random(),
                  key: `edited_${timestamp}_${Math.random()}`
                };
                setSelectedImage(forceRerenderImage);
                
                // Force immediate DOM update and re-analyze image
                requestAnimationFrame(() => {
                  // Trigger a custom event after DOM update
                  const event = new CustomEvent('imageEdited', { 
                    detail: { 
                      editedImageId: newEditedImage.id,
                      timestamp: timestamp,
                      isRotated: editingParams.rotation !== 0,
                      isCropped: editingParams.cropWidth < 100 || editingParams.cropHeight < 100
                    }
                  });
                  window.dispatchEvent(event);
                  console.log('🔄 Image edit completed - new image object created');
                  toast.success('Image adjustments applied to preview! ✨');
                  
                  // Re-analyze the edited image for new AI prompts
                  setTimeout(() => {
                    console.log('🤖 Re-analyzing edited image for new AI prompts...');
                    toast.info('Re-analyzing edited image for new suggestions...');
                    loadAIPrompts();
                  }, 500);
                });
              }, 100);
            }
            
            setShowImageEditor(false);
          } catch (error) {
            console.error('Error applying edits:', error);
            toast.error('Failed to apply image adjustments');
          }
        }}
      />
    )}
    </>
  );
};

// Collaboration Modal
const CollaborationModal = ({ onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('collaborate');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [collaborators, setCollaborators] = useState([
    // Mock data - in production, load from backend
    { id: 1, name: 'Sarah Johnson', email: 'sarah@example.com', role: 'editor', status: 'active' },
    { id: 2, name: 'Mike Chen', email: 'mike@example.com', role: 'viewer', status: 'pending' },
  ]);
  
  // Access Requests state
  const [accessRequests, setAccessRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  
  const getUserFromStorage = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  const user = getUserFromStorage();
  
  // Load access requests when tab is opened
  useEffect(() => {
    if (activeTab === 'requests') {
      loadAccessRequests();
    }
  }, [activeTab]);
  
  // Outsourcing state
  const [memoryTitle, setMemoryTitle] = useState('');
  const [memoryDescription, setMemoryDescription] = useState('');
  const [creditReward, setCreditReward] = useState(50);
  const [selectedImages, setSelectedImages] = useState([]);
  const [submittingOutsource, setSubmittingOutsource] = useState(false);

  const loadAccessRequests = async () => {
    if (!user) return;
    
    setLoadingRequests(true);
    try {
      // Get all albums where user is owner or editor
      const albumsResponse = await axios.get(`${getAPI()}/albums?user_id=${user.id}`);
      const albums = albumsResponse.data.albums || [];
      
      // Get requests for each album where user has editor access
      const allRequests = [];
      for (const album of albums) {
        // Check if user is owner or editor
        const isOwner = album.owner_id === user.id;
        const isEditor = album.collaborators?.some(c => c.user_id === user.id && c.role === 'editor');
        
        if (isOwner || isEditor) {
          try {
            const response = await axios.get(
              `${getAPI()}/albums/${album.id}/access-requests?user_id=${user.id}&status=pending`
            );
            if (response.data.success && response.data.requests) {
              response.data.requests.forEach(req => {
                allRequests.push({
                  ...req,
                  album_title: album.title,
                  album_id: album.id
                });
              });
            }
          } catch (err) {
            console.error(`Error loading requests for album ${album.id}:`, err);
          }
        }
      }
      
      setAccessRequests(allRequests);
    } catch (error) {
      console.error('Error loading access requests:', error);
      toast.error('Failed to load access requests');
    } finally {
      setLoadingRequests(false);
    }
  };
  
  const handleApproveRequest = async (requestId, albumId, grantedRole) => {
    if (!user) return;
    
    setProcessingRequest(requestId);
    try {
      await axios.post(
        `${getAPI()}/albums/access-requests/${requestId}/approve?user_id=${user.id}`,
        {
          action: 'approve',
          granted_role: grantedRole
        }
      );
      
      toast.success(`Access granted as ${grantedRole}!`);
      loadAccessRequests(); // Reload list
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessingRequest(null);
    }
  };
  
  const handleDenyRequest = async (requestId) => {
    if (!user) return;
    
    setProcessingRequest(requestId);
    try {
      await axios.post(
        `${getAPI()}/albums/access-requests/${requestId}/approve?user_id=${user.id}`,
        {
          action: 'deny',
          denied_reason: 'Access denied'
        }
      );
      
      toast.success('Request denied');
      loadAccessRequests(); // Reload list
    } catch (error) {
      console.error('Error denying request:', error);
      toast.error('Failed to deny request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) return;

    setInviting(true);
    try {
      // In production, call backend API
      // await axios.post(`${getAPI()}/collaborations/invite`, { email, role });
      
      // Mock success
      const newCollaborator = {
        id: Date.now(),
        name: email.split('@')[0],
        email: email,
        role: role,
        status: 'pending'
      };
      setCollaborators([...collaborators, newCollaborator]);
      setEmail('');
      toast.success('Invitation sent successfully!');
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-60" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-amber-200" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-amber-200 bg-gradient-to-r from-amber-100 to-orange-100">
          <h3 className="text-xl font-bold text-amber-800 mb-4">👥 Collaborate & Earn</h3>
          
          {/* Tabs */}
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('collaborate')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'collaborate'
                  ? 'bg-amber-200 text-amber-800'
                  : 'text-amber-600 hover:text-amber-800 hover:bg-amber-100'
              }`}
            >
              👥 Collaborate
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-4 py-2 rounded-lg font-medium transition-all relative ${
                activeTab === 'requests'
                  ? 'bg-amber-200 text-amber-800'
                  : 'text-amber-600 hover:text-amber-800 hover:bg-amber-100'
              }`}
            >
              🔔 Access Requests
              {accessRequests.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {accessRequests.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('outsource')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === 'outsource'
                  ? 'bg-amber-200 text-amber-800'
                  : 'text-amber-600 hover:text-amber-800 hover:bg-amber-100'
              }`}
            >
              💰 Outsource Memory Building
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === 'collaborate' ? (
            <>
              {/* Invite Section */}
          <div className="bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl p-4 border border-amber-200 mb-6">
            <h4 className="font-medium text-amber-800 mb-4">Send Invitation</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address..."
                  className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-amber-800"
                />
              </div>
              
              <div>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-amber-800"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            
            <button
              onClick={handleInvite}
              disabled={!email.trim() || inviting}
              className="mt-4 w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-3 rounded-xl hover:from-purple-600 hover:to-blue-600 transition-colors disabled:opacity-50 font-medium"
            >
              {inviting ? 'Sending...' : '📧 Send Invitation'}
            </button>
          </div>

          {/* Current Collaborators */}
          <div className="bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl p-4 border border-amber-200">
            <h4 className="font-medium text-amber-800 mb-4">Current Collaborators</h4>
            
            <div className="space-y-3">
              {collaborators.map((collaborator) => (
                <div key={collaborator.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-amber-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center text-white font-medium">
                      {collaborator.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-amber-800">{collaborator.name}</p>
                      <p className="text-sm text-amber-600">{collaborator.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      collaborator.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {collaborator.status}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                      {collaborator.role}
                    </span>
                    <button className="text-red-500 hover:text-red-700 p-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              
              {collaborators.length === 0 && (
                <p className="text-amber-600 text-center py-8">No collaborators yet. Invite friends and family to get started!</p>
              )}
            </div>
          </div>
          </>
          ) : activeTab === 'requests' ? (
            /* Access Requests Tab */
            <div className="space-y-4">
              {loadingRequests ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
                  <p className="text-amber-600">Loading requests...</p>
                </div>
              ) : accessRequests.length === 0 ? (
                <div className="bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl p-8 border border-amber-200 text-center">
                  <div className="text-6xl mb-4">🔔</div>
                  <h4 className="font-medium text-amber-800 mb-2">No Pending Requests</h4>
                  <p className="text-amber-600">When someone requests access to your shared albums, they'll appear here.</p>
                </div>
              ) : (
                <>
                  <div className="bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl p-4 border border-amber-200 mb-4">
                    <h4 className="font-medium text-amber-800 mb-2">📥 Pending Access Requests ({accessRequests.length})</h4>
                    <p className="text-sm text-amber-600">Review and approve requests from people who want to access your shared albums.</p>
                  </div>
                  
                  <div className="space-y-3">
                    {accessRequests.map((request) => (
                      <div key={request.id} className="bg-white rounded-xl border border-amber-200 p-4 shadow-sm">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-medium text-lg flex-shrink-0">
                              {request.user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-amber-800">{request.user.name}</p>
                              <p className="text-sm text-amber-600">{request.user.email}</p>
                              <p className="text-xs text-amber-500 mt-1">
                                Requested {new Date(request.requested_at).toLocaleDateString()} at {new Date(request.requested_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-amber-50 rounded-lg p-3 mb-3">
                          <p className="text-sm font-medium text-amber-800 mb-1">📁 Album: "{request.album_title}"</p>
                          {request.message && (
                            <p className="text-sm text-amber-600 italic">"{request.message}"</p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <select
                            defaultValue="viewer"
                            className="flex-1 px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                            id={`role-${request.id}`}
                            disabled={processingRequest === request.id}
                          >
                            <option value="viewer">Grant Viewer Access</option>
                            <option value="contributor">Grant Contributor Access</option>
                          </select>
                          
                          <button
                            onClick={() => {
                              const role = document.getElementById(`role-${request.id}`).value;
                              handleApproveRequest(request.id, request.album_id, role);
                            }}
                            disabled={processingRequest === request.id}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 font-medium text-sm flex items-center gap-1"
                          >
                            {processingRequest === request.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>...</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Approve
                              </>
                            )}
                          </button>
                          
                          <button
                            onClick={() => handleDenyRequest(request.id)}
                            disabled={processingRequest === request.id}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 font-medium text-sm flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Deny
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Outsource Memory Building */
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl p-4 border border-amber-200">
                <h4 className="font-medium text-amber-800 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Outsource Memory Creation & Earn Credits
                </h4>
                <p className="text-amber-600 mb-4">Let community members create animated moments for you and earn credits in return!</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-amber-700 mb-2">Memory Request Title</label>
                    <input
                      type="text"
                      value={memoryTitle}
                      onChange={(e) => setMemoryTitle(e.target.value)}
                      placeholder="e.g., Create a heartwarming family reunion video"
                      className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-amber-800"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-amber-700 mb-2">Description & Requirements</label>
                    <textarea
                      value={memoryDescription}
                      onChange={(e) => setMemoryDescription(e.target.value)}
                      placeholder="Describe what kind of animated moment you want created, any specific emotions, movements, or style preferences..."
                      rows="4"
                      className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-amber-800"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-amber-700 mb-2">Credit Reward</label>
                      <select
                        value={creditReward}
                        onChange={(e) => setCreditReward(Number(e.target.value))}
                        className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-amber-800"
                      >
                        <option value={25}>25 Credits - Simple animation</option>
                        <option value={50}>50 Credits - Standard quality</option>
                        <option value={75}>75 Credits - High quality</option>
                        <option value={100}>100 Credits - Premium creation</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-amber-700 mb-2">Deadline</label>
                      <select className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-amber-800">
                        <option value="24h">24 hours</option>
                        <option value="48h">48 hours</option>
                        <option value="1w">1 week</option>
                        <option value="2w">2 weeks</option>
                      </select>
                    </div>
                  </div>
                  
                  <button
                    onClick={async () => {
                      if (!memoryTitle.trim()) {
                        toast.error('Please enter a title for your memory request');
                        return;
                      }
                      
                      setSubmittingOutsource(true);
                      try {
                        // Mock API call - in production, would create outsource request
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        toast.success('Memory outsource request posted! Community members can now submit proposals.');
                        onClose();
                      } catch (error) {
                        toast.error('Failed to post outsource request');
                      } finally {
                        setSubmittingOutsource(false);
                      }
                    }}
                    disabled={submittingOutsource || !memoryTitle.trim()}
                    className="w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-3 rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50"
                  >
                    {submittingOutsource ? 'Posting Request...' : `🚀 Post Request (${creditReward} Credits)`}
                  </button>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50/80 to-purple-50/80 rounded-2xl p-4 border border-blue-200">
                <h5 className="font-medium text-blue-800 mb-3">💡 How It Works</h5>
                <div className="space-y-2 text-sm text-blue-700">
                  <p>• Post your memory creation request with images and requirements</p>
                  <p>• Community members submit proposals and sample previews</p>
                  <p>• Choose your favorite creator and approve their work</p>
                  <p>• Credits are transferred automatically when you approve</p>
                  <p>• You receive your beautiful animated moment!</p>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-amber-200 bg-amber-50/50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-amber-600">
              💡 Collaborators can view, comment, and help organize your memories
            </div>
            <button
              onClick={onClose}
              className="px-6 py-3 text-amber-700 bg-white border border-amber-200 rounded-xl hover:bg-amber-50 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Video Gallery Modal - Shows all videos created from a specific image
const VideoGalleryModal = ({ 
  imageId, 
  videos, 
  onClose, 
  onVideoSelect, 
  onCreateAnother, 
  user,
  showDownloadProgress,
  setShowDownloadProgress,
  downloadProgress,
  setDownloadProgress,
  downloadStatus,
  setDownloadStatus
}) => {
  // State for expandable prompts in video gallery
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState([]);
  
  // Helper to detect R2 presigned URLs
  const isPresignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  };
  
  // Helper function to add auth params to thumbnail URLs
  const getAuthenticatedThumbnailUrl = (thumbnailUrl) => {
    if (!thumbnailUrl || !user?.id) return thumbnailUrl;
    
    // NEVER modify presigned URLs - they have cryptographic signatures
    if (isPresignedUrl(thumbnailUrl)) {
      console.log('⚠️ Detected presigned thumbnail URL, returning as-is');
      return thumbnailUrl;
    }
    
    // If URL already has user_id, return as-is
    if (thumbnailUrl.includes('user_id=')) {
      return thumbnailUrl;
    }
    
    // Add user_id parameter
    const separator = thumbnailUrl.includes('?') ? '&' : '?';
    return `${thumbnailUrl}${separator}user_id=${user.id}`;
  };
  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [onClose]);
  
  if (!imageId || !videos || videos.length === 0) {
    return null;
  }
  
  const completedVideos = videos.filter(v => v.status === 'completed' && v.video_url);
  
  const handleDownloadSelected = async () => {
    console.log('🔽 handleDownloadSelected called with', selectedVideos.length, 'videos');
    
    if (selectedVideos.length === 0) {
      toast.error('No videos selected');
      return;
    }
    
    // Detect Safari browser (including iOS Safari)
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    // Detect if mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop', '| Safari:', isSafari);
    
    if (isMobile) {
      // Mobile approach - For single video, try fetch+blob on non-Safari, direct link on Safari
      if (selectedVideos.length === 1) {
        const video = videos.find(v => v.id === selectedVideos[0]);
        if (video && video.video_url) {
          const downloadUrl = video.video_url.includes('?') 
            ? `${video.video_url}&download=true`
            : `${video.video_url}?download=true`;
          
          // For Safari, use direct link approach
          if (isSafari) {
            console.log('🍎 Safari single video download - using direct link');
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;
            a.download = `magic-moment-${video.id}.mp4`;
            a.target = '_blank'; // Fallback for Safari
            document.body.appendChild(a);
            a.click();
            await new Promise(resolve => setTimeout(resolve, 500));
            document.body.removeChild(a);
          } else {
            // Non-Safari mobile: use blob approach
            try {
              const response = await fetch(downloadUrl);
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.style.display = 'none';
              a.href = url;
              a.download = `magic-moment-${video.id}.mp4`;
              document.body.appendChild(a);
              a.click();
              await new Promise(resolve => setTimeout(resolve, 500));
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error('Failed to download:', error);
              toast.error('Failed to download video');
            }
          }
        }
      } else {
        // Multiple videos - use direct links
        toast.info(`Opening ${selectedVideos.length} videos. Long-press each to save.`, { duration: 5000 });
        
        for (let i = 0; i < selectedVideos.length; i++) {
          const videoId = selectedVideos[i];
          const video = videos.find(v => v.id === videoId);
          if (!video || !video.video_url) continue;
          
          try {
            const downloadUrl = video.video_url.includes('?') 
              ? `${video.video_url}&download=true`
              : `${video.video_url}?download=true`;
            
            window.open(downloadUrl, '_blank');
            
            if (i < selectedVideos.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 1500));
            }
          } catch (error) {
            console.error('Failed to open video:', video.id, error);
          }
        }
      }
      
      setMultiSelectMode(false);
      setSelectedVideos([]);
    } else {
      // Desktop approach - use zip for 2+ videos, individual for single
      if (selectedVideos.length >= 2) {
        try {
          const BACKEND_URL = getBACKEND_URL();
          
          // Show progress modal
          setShowDownloadProgress(true);
          setDownloadProgress(10);
          setDownloadStatus('Preparing download...');
          
          // Prepare items for zip download
          const items = selectedVideos.map(videoId => {
            const video = videos.find(v => v.id === videoId);
            if (!video || !video.video_url) return null;
            
            const downloadUrl = video.video_url.includes('?') 
              ? `${video.video_url}&download=true`
              : `${video.video_url}?download=true`;
            
            return {
              url: downloadUrl,
              filename: `magic-moment-${video.id}.mp4`,
              type: 'video'
            };
          }).filter(Boolean);
          
          setDownloadProgress(20);
          setDownloadStatus('Creating zip archive...');
          
          // Simulate realistic progress during zip creation with file names
          let simulatedProgress = 20;
          const progressInterval = setInterval(() => {
            if (simulatedProgress < 55) {
              simulatedProgress += Math.random() * 3 + 1; // Increment 1-4%
              const fileIndex = Math.floor((simulatedProgress - 20) / 35 * items.length);
              const currentFile = items[fileIndex]?.filename || `magic-moment-${fileIndex + 1}.mp4`;
              setDownloadProgress(Math.min(simulatedProgress, 55));
              setDownloadStatus(`Adding to archive: ${currentFile}`);
            }
          }, 300); // Update every 300ms
          
          // Call zip endpoint
          const response = await fetch(`${BACKEND_URL}/api/media/download-zip?user_id=${user.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          });
          
          // Stop simulated progress
          clearInterval(progressInterval);
          
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          
          setDownloadProgress(60);
          setDownloadStatus('Processing files...');
          
          const blob = await response.blob();
          
          setDownloadProgress(95);
          setDownloadStatus('Starting download...');
          
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `magic-moments-${new Date().toISOString().slice(0,19).replace(/:/g, '-')}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
          
          setDownloadProgress(100);
          setDownloadStatus('Download complete!');
          
          // Close modal after 2 seconds
          setTimeout(() => {
            setShowDownloadProgress(false);
            setDownloadProgress(0);
          }, 2000);
          
          // Removed toast: videos downloaded as zip
          setMultiSelectMode(false);
          setSelectedVideos([]);
        } catch (error) {
          console.error('Failed to create zip:', error);
          setShowDownloadProgress(false);
          setDownloadProgress(0);
          toast.error('Failed to create zip. Trying individual downloads...');
          
          // Detect Safari for fallback downloads
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          
          // Fallback to individual downloads
          let downloadCount = 0;
          for (let i = 0; i < selectedVideos.length; i++) {
            const videoId = selectedVideos[i];
            const video = videos.find(v => v.id === videoId);
            if (!video || !video.video_url) continue;
            
            try {
              const downloadUrl = video.video_url.includes('?') 
                ? `${video.video_url}&download=true`
                : `${video.video_url}?download=true`;
              
              // For Safari, use direct link approach
              if (isSafari) {
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = downloadUrl;
                a.download = `magic-moment-${video.id}.mp4`;
                a.target = '_blank'; // Fallback for Safari
                document.body.appendChild(a);
                a.click();
                await new Promise(resolve => setTimeout(resolve, 1000));
                document.body.removeChild(a);
              } else {
                // Non-Safari: use blob approach
                const response = await fetch(downloadUrl);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `magic-moment-${video.id}.mp4`;
                document.body.appendChild(a);
                a.click();
                await new Promise(resolve => setTimeout(resolve, 1000));
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              }
              downloadCount++;
              if (i < selectedVideos.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 1500));
              }
            } catch (error) {
              console.error('Failed to download video:', video.id, error);
            }
          }
          
          // Removed toast: Downloaded X video(s)
          setMultiSelectMode(false);
          setSelectedVideos([]);
        }
      } else {
        // Detect Safari for single video download
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        
        // Single video - use original individual download
        const video = videos.find(v => v.id === selectedVideos[0]);
        if (video && video.video_url) {
          try {
            // Removed toast: Downloading video...
            const downloadUrl = video.video_url.includes('?') 
              ? `${video.video_url}&download=true`
              : `${video.video_url}?download=true`;
            
            // For Safari, use direct link approach
            if (isSafari) {
              const a = document.createElement('a');
              a.style.display = 'none';
              a.href = downloadUrl;
              a.download = `magic-moment-${video.id}.mp4`;
              a.target = '_blank'; // Fallback for Safari
              document.body.appendChild(a);
              a.click();
              await new Promise(resolve => setTimeout(resolve, 1000));
              document.body.removeChild(a);
            } else {
              // Non-Safari: use blob approach
              const response = await fetch(downloadUrl);
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.style.display = 'none';
              a.href = url;
              a.download = `magic-moment-${video.id}.mp4`;
              document.body.appendChild(a);
              a.click();
              await new Promise(resolve => setTimeout(resolve, 1000));
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }
            // Removed toast: Video downloaded
          } catch (error) {
            console.error('Failed to download video:', video.id, error);
            toast.error('Failed to download video');
          }
        }
        setMultiSelectMode(false);
        setSelectedVideos([]);
      }
    }
  };
  
  const handleSelectAll = () => {
    const allCompletedIds = completedVideos.map(v => v.id);
    setSelectedVideos(allCompletedIds);
  };
  
  const toggleVideoSelection = (videoId) => {
    setSelectedVideos(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 sm:p-8 z-[9999] pointer-events-none">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[70vh] overflow-hidden pointer-events-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-3 sm:p-4 text-white relative flex-shrink-0">
          {!multiSelectMode ? (
            <>
              <div className="text-center">
                <h3 className="text-lg sm:text-xl font-bold">Animated Moments</h3>
                <p className="text-purple-100 text-xs sm:text-sm mt-1">
                  {videos.length} magically animated moment{videos.length !== 1 ? 's' : ''} created from this picture
                </p>
              </div>
              
              {/* Select Button */}
              {completedVideos.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMultiSelectMode(true);
                    setSelectedVideos([]);
                  }}
                  className="absolute top-3 sm:top-4 left-3 sm:left-4 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all shadow-lg hover:shadow-xl overflow-hidden"
                  title="Select videos to download"
                  style={{
                    background: 'linear-gradient(135deg, #f97316 0%, #fb923c 25%, #fdba74 50%, #fbbf24 75%, #f59e0b 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 8s linear infinite'
                  }}
                >
                  Select
                </button>
              )}
              
              <button
                onClick={onClose}
                className="absolute top-3 sm:top-4 right-3 sm:right-4 w-7 h-7 sm:w-8 sm:h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => {
                    setMultiSelectMode(false);
                    setSelectedVideos([]);
                  }}
                  className="bg-white/20 hover:bg-white/30 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                
                <div className="text-center flex-1">
                  <p className="text-sm sm:text-base font-semibold">
                    {selectedVideos.length} selected
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleSelectAll();
                    }}
                    className="text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all shadow-lg hover:shadow-xl relative overflow-hidden whitespace-nowrap"
                    style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #fb923c 25%, #fdba74 50%, #fbbf24 75%, #f59e0b 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 8s linear infinite'
                    }}
                  >
                    Select All
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDownloadSelected();
                    }}
                    disabled={selectedVideos.length === 0}
                    className="bg-white hover:bg-white/90 text-purple-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span className="hidden sm:inline">Download</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Video Grid */}
        <div className="flex-1 overflow-y-auto pt-6 px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Build More Button - First Item */}
            <div
              onClick={onCreateAnother}
              className="relative aspect-video bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-100 transition-all cursor-pointer flex flex-col items-center justify-center group"
            >
              <div className="text-center px-4">
                <div className="flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                  <svg className="w-12 h-12 text-gray-400 group-hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div className="text-sm font-medium text-gray-600">Build More</div>
                <div className="text-xs text-gray-400 mt-1">Create another animation with this picture</div>
              </div>
            </div>
            
            {videos.map((video) => {
              const isSelected = selectedVideos.includes(video.id);
              const videoUrl = getVideoUrl(video, user?.id);
              const isCompleted = video.status === 'completed' && videoUrl;
              const canSelect = multiSelectMode && isCompleted;
              
              return (
              <div key={video.id} className="relative group">
                <div 
                  className={`aspect-video bg-gray-100 rounded-xl overflow-hidden border-2 transition-all ${
                    isSelected ? 'border-purple-500 ring-2 ring-purple-300' : 'border-gray-200'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    console.log('🎬 Video card clicked. multiSelectMode:', multiSelectMode, 'isCompleted:', isCompleted);
                    if (multiSelectMode && isCompleted) {
                      console.log('✅ Toggling video selection:', video.id);
                      toggleVideoSelection(video.id);
                    } else if (!multiSelectMode) {
                      console.log('📺 Opening video in full screen:', video.id);
                      onVideoSelect(video);
                    }
                  }}
                >
                  {video.status === 'completed' && videoUrl ? (
                    <video preload="auto"
                      src={videoUrl}
                      poster={getAuthenticatedThumbnailUrl(video.thumbnail_url)}
                      className={`w-full h-full object-cover transition-transform duration-300 ${
                        multiSelectMode ? 'pointer-events-none cursor-default' : 'cursor-pointer group-hover:scale-105'
                      }`}
                      muted
                      loop
                      playsInline
                      onLoadedData={(e) => {
                        // Auto-play on mobile
                        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                        if (isMobile && !multiSelectMode && e.target.src && isValidVideoUrl(e.target.src)) {
                          e.target.play().catch((err) => {
                            console.warn('Video gallery auto-play failed:', err);
                          });
                        }
                      }}
                      onMouseEnter={(e) => {
                        // Hover-to-play only on desktop
                        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                        if (!isMobile && !multiSelectMode && e.target.src && isValidVideoUrl(e.target.src)) {
                          e.target.play().catch((err) => {
                            console.warn('Video gallery hover play failed:', err);
                          });
                        }
                      }}
                      onMouseLeave={(e) => {
                        // Pause on hover leave only on desktop
                        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                        if (!isMobile) {
                          e.target.pause();
                        }
                      }}
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                    >
                      <div className="text-center p-4">
                        <div className={`text-3xl mb-2 ${
                          video.status === 'processing' ? 'text-blue-500' :
                          video.status === 'failed' ? 'text-red-500' : 
                          'text-gray-400'
                        }`}>
                          {video.status === 'processing' ? '⏳' :
                           video.status === 'failed' ? '❌' : '⏸️'}
                        </div>
                        <p className="text-sm text-gray-600 capitalize">{video.status}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Selection checkbox overlay */}
                  {canSelect && (
                    <div className="absolute top-2 right-2 z-10">
                      <div 
                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                          isSelected 
                            ? 'border-2 border-white relative overflow-hidden' 
                            : 'bg-white/80 border-2 border-gray-300'
                        }`}
                        style={isSelected ? {
                          background: 'linear-gradient(135deg, #f97316 0%, #fb923c 25%, #fdba74 50%, #fbbf24 75%, #f59e0b 100%)',
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 8s linear infinite'
                        } : {}}
                      >
                        {isSelected && (
                          <svg className="w-4 h-4 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Video info with expandable prompt */}
                <div className="mt-2 space-y-2">
                  {(() => {
                    const prompt = video.prompt || 'No prompt';
                    const isLongPrompt = prompt.length > 50;
                    const truncatedPrompt = isLongPrompt ? prompt.substring(0, 50) + '...' : prompt;
                    const showFullPrompt = expandedPrompts[video.id] || false;
                    
                    return (
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <div 
                            className={`text-sm font-medium text-gray-800 flex-1 ${
                              showFullPrompt ? 'max-h-32 overflow-y-auto pr-2 custom-scrollbar' : ''
                            }`}
                          >
                            <p className="whitespace-pre-wrap">
                              {showFullPrompt || !isLongPrompt ? prompt : truncatedPrompt}
                            </p>
                          </div>
                          {isLongPrompt && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedPrompts(prev => ({
                                  ...prev,
                                  [video.id]: !prev[video.id]
                                }));
                              }}
                              className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 px-2 py-1 rounded transition-colors flex-shrink-0"
                              title={showFullPrompt ? "Show less" : "Show full prompt"}
                            >
                              {showFullPrompt ? '−' : '+'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  <div className="flex items-center justify-end text-xs text-gray-500">
                    <span>{video.parameters?.duration || 5}s</span>
                  </div>
                  
                  <div className="text-xs text-gray-400">
                    Created: {new Date(video.created_at).toLocaleString()}
                  </div>
                </div>
                
                {/* Status badge */}
                {/* Status badge - only show for non-completed videos */}
                {video.status !== 'completed' && !multiSelectMode && (
                  <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
                    video.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                    video.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {video.status === 'processing' ? '⏳ Processing' :
                     video.status === 'failed' ? '❌ Failed' :
                     '⏸️ Pending'}
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>
        
        {/* Videos now open in the main app's FullScreenVideoModal with collapsible details and edit functionality */}
      </div>
    </div>
  );
};

// Full Screen Image Modal
const FullScreenImageModal = ({ image, imageList, currentIndex, onClose, onAnimate, onEdit, onDelete, onNavigate, onCopyToFolder, imagesWithVideos, user, selectedFolder }) => {
  // Helper to detect R2 presigned URLs
  const isPresignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  };
  
  // Helper function to generate image URL with user_id for proper data isolation
  const getImageUrl = (img) => {
    if (!img || !user?.id) return '';
    
    // Get backend URL
    const BACKEND_URL = getBACKEND_URL();
    
    // NEVER modify presigned URLs - they have cryptographic signatures
    if (img.url && isPresignedUrl(img.url)) {
      console.log('⚠️ Detected presigned URL, returning as-is:', img.url.substring(0, 100));
      return img.url;
    }
    
    // CRITICAL FIX: If image already has a full URL (demo content or landing-assets), return it directly
    if (img.url && (img.url.startsWith('http://') || img.url.startsWith('https://'))) {
      return img.url;
    }
    
    // Demo content or landing-assets with relative paths
    if (img.url && img.url.startsWith('/landing-assets/')) {
      return img.url;
    }
    
    const imageId = img.id || img._id;
    if (!imageId) return '';
    
    // CACHE BUSTING: Include updated_at timestamp to force reload after edits
    const timestamp = img.updated_at ? `&t=${new Date(img.updated_at).getTime()}` : '';
    const finalUrl = `${BACKEND_URL}/api/files/${imageId}?user_id=${user.id}${timestamp}`;
    
    if (timestamp) {
      console.log('🖼️ Fullscreen image URL with cache bust:', {
        imageId,
        updated_at: img.updated_at,
        timestamp,
        finalUrl: finalUrl.substring(0, 80)
      });
    }
    
    return finalUrl;
  };
  
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Handle navigation
  const goToPrevious = () => {
    if (imageList && currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };
  
  const goToNext = () => {
    if (imageList && currentIndex < imageList.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, imageList]);
  
  // Handle swipe gestures
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-[9999]" 
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        
        {/* Navigation Arrows */}
        {imageList && imageList.length > 1 && (
          <>
            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              disabled={currentIndex <= 0}
              className={`absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                currentIndex <= 0 
                  ? 'bg-white/20 text-white/40 cursor-not-allowed' 
                  : 'bg-white/30 text-white hover:bg-white/50 hover:scale-110'
              }`}
              title="Previous image (← or swipe right)"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={goToNext}
              disabled={currentIndex >= imageList.length - 1}
              className={`absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                currentIndex >= imageList.length - 1 
                  ? 'bg-white/20 text-white/40 cursor-not-allowed' 
                  : 'bg-white/30 text-white hover:bg-white/50 hover:scale-110'
              }`}
              title="Next image (→ or swipe left)"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Image Counter */}
            <div className="absolute top-4 center-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm z-10">
              {currentIndex + 1} of {imageList.length}
            </div>
          </>
        )}
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-white/20 text-white rounded-full flex items-center justify-center hover:bg-white/30 transition-colors z-10"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Image */}
        <img loading="lazy"
          key={`fullscreen-${image.id}-${image.updated_at || image._lastUpdated || Date.now()}`}
          src={getImageUrl(image)}
          alt={image.name}
          className="max-w-full max-h-full object-cover rounded-lg shadow-2xl"
        />
        
        {/* Image info - moved to top-left to match video layout */}
        <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm text-white p-3 rounded-xl max-w-sm z-20">
          <h4 className="font-semibold mb-1 truncate text-sm" title={image.name || 'Memory Photo'}>
            {image.name && image.name.length > 40 ? `${image.name.substring(0, 40)}...` : (image.name || 'Memory Photo')}
          </h4>
          <p className="text-xs text-gray-400 mt-1">
            Added: {new Date(image.created_at || image.uploaded_at || Date.now()).toLocaleString()}
          </p>
        </div>
        
        {/* Action buttons */}
        <div className="absolute bottom-4 right-4 flex space-x-3">
          {/* Edit button */}
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onEdit) {
                onEdit(image);
              }
              onClose();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onEdit) {
                onEdit(image);
              }
              onClose();
            }}
            className="bg-green-500 text-white p-3 rounded-full hover:bg-green-600 transition-colors shadow-lg"
            style={{
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
            title="Edit picture"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>

          {/* Delete button */}
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // No confirmation popup - direct delete for faster workflow
              if (onDelete) {
                onDelete(image);
              }
              
              // Navigate to next image instead of closing
              if (imageList && currentIndex < imageList.length - 1) {
                onNavigate(currentIndex + 1);
              } else if (imageList && currentIndex > 0) {
                onNavigate(currentIndex - 1);
              } else {
                // No more images, close fullscreen
                onClose();
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // No confirmation popup - direct delete for faster workflow
              if (onDelete) {
                onDelete(image);
              }
              
              // Navigate to next image instead of closing
              if (imageList && currentIndex < imageList.length - 1) {
                onNavigate(currentIndex + 1);
              } else if (imageList && currentIndex > 0) {
                onNavigate(currentIndex - 1);
              } else {
                // No more images, close fullscreen
                onClose();
              }
            }}
            className="bg-red-500 text-white p-3 rounded-full hover:bg-red-600 transition-colors shadow-lg"
            style={{
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
            title={selectedFolder ? "Remove from folder" : "Delete picture"}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>

          {/* Animate button */}
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAnimate(image);
              onClose();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAnimate(image);
              onClose();
            }}
            className="text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2 relative overflow-hidden font-medium"
            style={{
              background: 'linear-gradient(135deg, #a855f7 0%, #8b5cf6 25%, #7c3aed 50%, #6d28d9 75%, #5b21b6 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 8s linear infinite',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
            title={(imagesWithVideos?.has(image.id) || imagesWithVideos?.has(String(image.id))) ? 'Build another animation from this picture' : 'Animate this picture'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            <span className="text-sm font-medium">
              {(imagesWithVideos?.has(image.id) || imagesWithVideos?.has(String(image.id))) ? 'Build another' : 'Animate this picture'}
            </span>
          </button>
          
          {/* Download button */}
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchEnd={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              try {
                // Removed toast: Preparing download...
                // Fetch the image with CORS
                const response = await fetch(image.url, {
                  mode: 'cors',
                  credentials: 'omit'
                });
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `memory-${image.id || 'photo'}.jpg`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up blob URL
                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                // Removed toast: Download started
              } catch (error) {
                console.error('Download failed:', error);
                toast.error('Download failed. Please try again.');
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              try {
                // Removed toast: Preparing download...
                // Fetch the image with CORS
                const response = await fetch(image.url, {
                  mode: 'cors',
                  credentials: 'omit'
                });
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `memory-${image.id || 'photo'}.jpg`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up blob URL
                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
                // Removed toast: Download started
              } catch (error) {
                console.error('Download failed:', error);
                toast.error('Download failed. Please try again.');
              }
            }}
            className="bg-gray-500 text-white p-3 rounded-full hover:bg-gray-600 transition-colors shadow-lg"
            style={{
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
            title="Download image"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>

          {/* Copy to Folder button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onCopyToFolder) {
                onCopyToFolder(image);
              }
            }}
            className="bg-blue-500 text-white p-3 rounded-full hover:bg-blue-600 transition-colors shadow-lg"
            title="Copy to folder"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

// Adjust & Re-animate Modal
const AdjustReanimateModal = ({ video, user, onClose, onSubmit }) => {
  const [userFeedback, setUserFeedback] = useState('');
  const [enhancedPrompt, setEnhancedPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEnhanced, setShowEnhanced] = useState(false);
  
  const generateEnhancedPrompt = async () => {
    if (!userFeedback.trim()) {
      toast.error('Please describe what you would like to change or add');
      return;
    }
    
    setIsGenerating(true);
    try {
      const formData = new FormData();
      formData.append('prompt', `Original prompt: "${video.parameters?.prompt || video.metadata?.parameters?.prompt || video.prompt || ''}"

User feedback for improvements: "${userFeedback}"

Please enhance the original animation prompt by incorporating the user's feedback. Generate an improved prompt that:
1. Keeps the core elements from the original prompt
2. Adds or modifies based on the user's specific feedback  
3. Maintains the realistic, natural animation style
4. Is concise but detailed (max 50 words)
5. Preserves face detection logic (no random people if none in original)

Return only the enhanced prompt text, nothing else.`);
      
      formData.append('user_id', user.id);
      const response = await axios.post(`${getAPI()}/ai-analyze-image`, formData);
      
      if (response.data && response.data.suggestions && response.data.suggestions[0]) {
        setEnhancedPrompt(response.data.suggestions[0]);
        setShowEnhanced(true);
      } else {
        // Fallback: manually combine original + feedback
        const combined = `${video.parameters?.prompt || video.metadata?.parameters?.prompt || video.prompt || 'Gentle animation'} ${userFeedback}`;
        setEnhancedPrompt(combined);
        setShowEnhanced(true);
      }
    } catch (error) {
      console.error('Failed to generate enhanced prompt:', error);
      toast.error('Failed to enhance prompt. Please try again.');
      // Fallback: manually combine
      const combined = `${video.parameters?.prompt || video.metadata?.parameters?.prompt || video.prompt || 'Gentle animation'} ${userFeedback}`;
      setEnhancedPrompt(combined);
      setShowEnhanced(true);
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">🎬 Adjust & Re-animate</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
          
          {/* Original prompt display */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Original Animation Prompt:</label>
            <div className="bg-gray-50 p-4 rounded-lg border">
              <p className="text-gray-800 italic">"{video.parameters?.prompt || video.metadata?.parameters?.prompt || video.prompt || 'No original prompt available'}"</p>
            </div>
          </div>
          
          {/* User feedback input */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              What would you like to change or add about the animation?
            </label>
            <textarea
              value={userFeedback}
              onChange={(e) => setUserFeedback(e.target.value)}
              placeholder="e.g., 'Make the person smile more', 'Add gentle wind movement', 'Make it more dramatic', 'Slower movement', etc."
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              rows="4"
            />
          </div>
          
          {/* Enhanced prompt display */}
          {showEnhanced && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Enhanced Animation Prompt:</label>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <p className="text-gray-800 font-medium">"{enhancedPrompt}"</p>
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex gap-4 justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            
            {!showEnhanced ? (
              <button
                onClick={generateEnhancedPrompt}
                disabled={isGenerating || !userFeedback.trim()}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isGenerating ? 'Generating...' : '✨ Enhance Prompt'}
              </button>
            ) : (
              <button
                onClick={() => onSubmit(enhancedPrompt)}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                🎬 Re-animate with Enhanced Prompt
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Full Screen Video Modal
const FullScreenVideoModal = ({ video, videoList, currentIndex, onClose, onEdit, onNavigate, onAdjustReanimate, onDelete, user }) => {
  const API = `${getBACKEND_URL()}/api`;
  const [showAdjustPanel, setShowAdjustPanel] = useState(false);
  const [userFeedback, setUserFeedback] = useState('');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [momentsShareLinkCopied, setMomentsShareLinkCopied] = useState(false); // Track share link copied state
  // Track when user is selecting text in the info panel to avoid accidental close
  const [isSelectingText, setIsSelectingText] = useState(false);
  const infoRef = useRef(null);
  
  // Close share dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const shareMenu = document.getElementById('share-menu-fullscreen-' + video.id);
      const shareButton = event.target.closest('button[title="Share Video & Earn Credits"]');
      
      if (shareMenu && !shareMenu.classList.contains('hidden') && !shareMenu.contains(event.target) && !shareButton) {
        shareMenu.classList.add('hidden');
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [video.id]);
  
  // Helper to detect R2 presigned URLs
  const isPresignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  };
  
  // Helper function to add auth params to thumbnail URLs
  const getAuthenticatedThumbnailUrl = (thumbnailUrl) => {
    if (!thumbnailUrl || !user?.id) return thumbnailUrl;
    
    // NEVER modify presigned URLs - they have cryptographic signatures
    if (isPresignedUrl(thumbnailUrl)) {
      console.log('⚠️ Detected presigned thumbnail URL, returning as-is');
      return thumbnailUrl;
    }
    
    // If URL already has user_id, return as-is
    if (thumbnailUrl.includes('user_id=')) {
      return thumbnailUrl;
    }
    
    // Add user_id parameter
    const separator = thumbnailUrl.includes('?') ? '&' : '?';
    return `${thumbnailUrl}${separator}user_id=${user.id}`;
  };
  
  // Get proper video URL using helper
  const videoUrl = useMemo(() => {
    if (!video || !user) return null;
    
    // Check if this is a demo content video (has static file URL)
    const isDemoContent = video.video_url?.includes('/landing-assets/') || video.result_url?.includes('/landing-assets/');
    
    // Preserve static path for demo content, use API streaming for real videos
    if (isDemoContent) {
      return video.result_url || video.video_url;
    }
    
    return getVideoUrl(video, user.id);
  }, [video, user]);
  
  // Video ref for direct MP4 playback (no HLS.js needed)
  const videoRef = useRef(null);
  
  // Handle navigation
  const goToPrevious = () => {
    if (videoList && currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };
  
  const goToNext = () => {
    if (videoList && currentIndex < videoList.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, videoList]);
  
  // Handle swipe gestures
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };
  
  // Generate enhanced prompt and redirect to animation generator
  const generateEnhancedPromptAndRedirect = async () => {
    console.log('🎯 Adjust & Re-submit button clicked!');
    
    if (!userFeedback.trim()) {
      toast.error('Please describe what you would like to change');
      return;
    }
    
    setIsGenerating(true);
    try {
      // BYPASS AI ENHANCEMENT - Use user feedback directly
      // The AI enhancement is not properly incorporating user feedback, so we'll use it directly
      const enhancedPrompt = userFeedback;
      
      console.log('🎬 Original video prompt:', video.parameters?.prompt);
      console.log('📝 User feedback:', userFeedback);
      console.log('🔄 Using DIRECT user feedback as enhanced prompt:', enhancedPrompt);
      
      // Close adjustment panel
      setShowAdjustPanel(false);
      
      // Clear _returnToGallery flag to prevent video gallery from reopening
      if (video._returnToGallery) {
        console.log('🚫 Clearing _returnToGallery flag to prevent gallery from reopening');
        delete video._returnToGallery;
      }
      
      // Call the original onAdjustReanimate with enhanced prompt
      // This will open the animation generator
      if (onAdjustReanimate) {
        console.log('✅ Calling onAdjustReanimate...');
        onAdjustReanimate(video, enhancedPrompt);
      } else {
        console.error('❌ onAdjustReanimate is not defined!');
      }
      
      // Close the fullscreen modal (without reopening gallery due to cleared flag)
      console.log('🔒 Closing full-screen modal...');
      onClose();
  useEffect(() => {
    const el = infoRef.current;
    if (!el) return;
    const onMouseDown = (e) => {
      setIsSelectingText(true);
    };
    const onMouseUp = (e) => {
      // small delay to allow selection/copy interaction
      setTimeout(() => setIsSelectingText(false), 150);
    };
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('touchstart', onMouseDown, { passive: true });
    el.addEventListener('touchend', onMouseUp, { passive: true });
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('touchstart', onMouseDown);
      el.removeEventListener('touchend', onMouseUp);
    };
  }, []);

      
    } catch (error) {
      console.error('Error in adjustment workflow:', error);
      toast.error('Failed to process adjustment. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  const [showPromptDetails, setShowPromptDetails] = useState(false);
  
  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={(e) => {
      if (isSelectingText) return; // Do not close while selecting text
      // Close share menu if open when clicking outside
      const shareMenu = document.getElementById('share-menu-fullscreen-' + video.id);
      if (shareMenu && !shareMenu.classList.contains('hidden')) {
        // Check if click is outside the share menu
        if (!shareMenu.contains(e.target)) {
          shareMenu.classList.add('hidden');
        }
      } else {
        onClose();
      }
    }}>
      <div 
        className="relative w-full h-full flex items-center justify-center p-4" 
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close button - Enhanced visibility */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-14 h-14 bg-black/80 text-white rounded-full flex items-center justify-center hover:bg-black/90 transition-colors z-[60] border-3 border-white/40 shadow-lg backdrop-blur-sm"
          title="Close video"
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Video player */}
        {isValidVideoUrl(videoUrl) ? (
          <VideoErrorBoundary fallback={
            <div className="flex items-center justify-center bg-gray-100 rounded-lg p-8 text-center max-w-md">
              <div>
                <div className="text-4xl mb-4">🎥</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Video Unavailable</h3>
                <p className="text-sm text-gray-500">The video file could not be loaded.</p>
              </div>
            </div>
          }>
            <video
              ref={videoRef}
              src={videoUrl}
              key={video.id}
              preload="metadata"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              poster={getAuthenticatedThumbnailUrl(video.thumbnail_url || video.imageUrl || `/api/files/${video.image_id}`)}
              controls
              autoPlay
              muted
              playsInline
              loop
              onLoadedMetadata={(e) => {
                // Safari needs explicit play() call
                e.target.play().catch(err => console.log('Autoplay prevented:', err));
              }}
              onError={(e) => {
                console.warn('Fullscreen video load error:', videoUrl);
              }}
            />
          </VideoErrorBoundary>
        ) : (
          <div className="flex items-center justify-center bg-gray-100 rounded-lg p-8 text-center max-w-md">
            <div>
              <div className="text-4xl mb-4">🎥</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Video Unavailable</h3>
              <p className="text-sm text-gray-500">The video file could not be loaded.</p>
            </div>
          </div>
        )}
        
        {/* Video info - positioned to avoid overlap with adjustment panel */}
        <div ref={infoRef} className={`absolute top-4 left-4 ${showAdjustPanel ? 'right-4 md:right-96' : 'right-20 md:right-4'} md:max-w-sm bg-black/60 backdrop-blur-sm text-white p-2 md:p-3 rounded-lg transition-all duration-300`}>

          <div className="flex items-center justify-between mb-1 md:mb-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <h4 className="font-semibold text-xs md:text-sm truncate pr-2">{video.parameters?.prompt ? (video.parameters.prompt.length > 30 ? video.parameters.prompt.substring(0, 30) + '...' : video.parameters.prompt) : 'Animated Moment'}</h4>
              {/* Imported badge */}
              {video.imported_from_album && (
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-2 py-0.5 rounded-full text-[10px] md:text-xs font-semibold shadow-lg flex items-center gap-1 backdrop-blur-sm flex-shrink-0">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  <span className="hidden md:inline">Imported</span>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowPromptDetails(!showPromptDetails)}
              className="bg-white/20 hover:bg-white/40 text-white px-1.5 md:px-2 py-0.5 md:py-1 rounded text-xs transition-colors ml-1 md:ml-2 flex items-center gap-1 flex-shrink-0"
              title={showPromptDetails ? "Hide prompt details" : "Show full prompt"}
              data-tutorial="prompt-dropdown"
            >
              <span className="text-xs">
                {showPromptDetails ? 'Hide' : 'Details'}
              </span>
              <svg 
                className={`w-3 h-3 transform transition-transform ${showPromptDetails ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
          
          <p className="text-xs text-gray-400 mt-1 hidden md:block">
            Created: {new Date(video.created_at).toLocaleString()}
          </p>
          
          {/* Collapsible prompt details - more compact */}
          {showPromptDetails && (
            <div className="mt-3 pt-3 border-t border-white/40 bg-white/10 backdrop-blur-sm rounded-md p-2">
              <h5 className="text-xs font-semibold text-white mb-2 flex items-center gap-1">
                🎬 Animation Prompt:
              </h5>
              <div className="text-xs text-white leading-relaxed bg-black/30 p-2 rounded border-l-2 border-blue-400 max-h-32 overflow-y-auto custom-scrollbar">
                <p className="whitespace-pre-wrap">
                  "{video.parameters?.prompt || 'No prompt available'}"
                </p>
              </div>
              {video.parameters?.duration && (
                <div className="mt-2 text-xs">
                  <div className="bg-black/20 p-1.5 rounded inline-block">
                    <span className="text-gray-300">Duration:</span>
                    <span className="text-white font-medium ml-1">{video.parameters.duration}s</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="absolute bottom-4 right-4 flex gap-3">
          {/* Adjust Animation button */}
          {/* Adjust and Reanimate button - Hide for imported videos */}
          {!video.imported_from_album && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAdjustPanel(!showAdjustPanel);
              }}
              className="bg-purple-600/80 hover:bg-purple-700 text-white p-4 rounded-full transition-all shadow-lg backdrop-blur-sm border border-white/20"
              title="Enhance Prompt"
              data-tutorial="adjust-reanimate-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          
          {/* Share button with dropdown */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const shareMenu = document.getElementById('share-menu-fullscreen-' + video.id);
                if (shareMenu) {
                  shareMenu.classList.toggle('hidden');
                }
              }}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white p-4 rounded-full transition-all shadow-lg backdrop-blur-sm border border-white/20 relative animate-pulse-glow"
              title="Share Video & Earn Credits"
              style={{
                animation: 'pulse-glow 2s ease-in-out infinite'
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {/* Credits indicator badge */}
              <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full border-2 border-white shadow-sm">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                </svg>
              </span>
            </button>
            
            <style jsx>{`
              @keyframes pulse-glow {
                0%, 100% {
                  box-shadow: 0 0 20px rgba(147, 51, 234, 0.5), 0 0 40px rgba(59, 130, 246, 0.3);
                  transform: scale(1);
                }
                50% {
                  box-shadow: 0 0 30px rgba(147, 51, 234, 0.8), 0 0 60px rgba(59, 130, 246, 0.5);
                  transform: scale(1.05);
                }
              }
            `}</style>
            
            {/* Share dropdown menu - fixed for mobile visibility */}
            <div
              id={'share-menu-fullscreen-' + video.id}
              className="hidden fixed bottom-32 left-4 right-4 sm:absolute sm:bottom-full sm:mb-2 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-[380px] bg-white rounded-lg shadow-xl border border-gray-200 z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  
                  try {
                    // Create share token
                    console.log('🔍 DEBUG: Creating share with URL:', `${getAPI()}/videos/${video.id}/share`);
                    const response = await axios.post(
                      `${getAPI()}/videos/${video.id}/share?user_id=${user.id}&authenticated_user_id=${user.id}`
                    );
                    
                    // Construct share URL using current domain
                    const shareToken = response.data.share_token;
                    const shareUrl = `${window.location.origin}/watch/${shareToken}`;
                    
                    // Detect iOS Safari specifically
                    const isIOSSafari = () => {
                      const ua = navigator.userAgent;
                      const iOS = /iPad|iPhone|iPod/.test(ua);
                      const webkit = /WebKit/.test(ua);
                      const iOSSafari = iOS && webkit && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
                      return iOSSafari;
                    };
                    
                    // iOS-compatible copy function
                    const copyToClipboard = async (text) => {
                      // Try modern clipboard API first (works in newer iOS Safari and all Chrome)
                      if (navigator.clipboard && window.isSecureContext) {
                        try {
                          await navigator.clipboard.writeText(text);
                          return true;
                        } catch (err) {
                          console.log('Clipboard API failed, trying fallback:', err);
                        }
                      }
                      
                      // For iOS Safari specifically, show a user-friendly copy modal
                      if (isIOSSafari()) {
                        return 'safari-fallback';
                      }
                      
                      // Standard fallback for other browsers
                      const textArea = document.createElement('textarea');
                      textArea.value = text;
                      textArea.style.position = 'fixed';
                      textArea.style.top = '0';
                      textArea.style.left = '0';
                      textArea.style.width = '2em';
                      textArea.style.height = '2em';
                      textArea.style.padding = '0';
                      textArea.style.border = 'none';
                      textArea.style.outline = 'none';
                      textArea.style.boxShadow = 'none';
                      textArea.style.background = 'transparent';
                      textArea.style.color = 'transparent';
                      textArea.style.fontSize = '1px';
                      
                      document.body.appendChild(textArea);
                      textArea.focus();
                      textArea.select();
                      
                      if (typeof textArea.setSelectionRange === 'function') {
                        textArea.setSelectionRange(0, textArea.value.length);
                      }
                      
                      let success = false;
                      try {
                        success = document.execCommand('copy');
                      } catch (err) {
                        console.error('Copy fallback failed:', err);
                      }
                      
                      document.body.removeChild(textArea);
                      return success;
                    };
                    
                    const copied = await copyToClipboard(shareUrl);
                    
                    if (copied === true) {
                      // Set copied state for visual feedback
                      setMomentsShareLinkCopied(true);
                      
                      // Reset after 3 seconds
                      setTimeout(() => {
                        setMomentsShareLinkCopied(false);
                      }, 3000);
                      
                      // Track the share
                      try {
                        await axios.post(
                          `${getAPI()}/users/${user.id}/track-share?video_id=${video.id}&authenticated_user_id=${user.id}`
                        );
                      } catch (error) {
                        console.error('Error tracking share:', error);
                      }
                      
                      toast.success('Share link copied to clipboard! Anyone can view this video now.');
                    } else if (copied === 'safari-fallback') {
                      // Show iOS Safari friendly copy interface
                      const input = document.createElement('input');
                      input.value = shareUrl;
                      input.type = 'text';
                      input.readOnly = true;
                      input.style.position = 'fixed';
                      input.style.top = '50%';
                      input.style.left = '50%';
                      input.style.transform = 'translate(-50%, -50%)';
                      input.style.width = '80%';
                      input.style.maxWidth = '500px';
                      input.style.padding = '15px';
                      input.style.fontSize = '16px';
                      input.style.border = '2px solid #9333ea';
                      input.style.borderRadius = '8px';
                      input.style.backgroundColor = '#fff';
                      input.style.zIndex = '999999';
                      input.style.boxShadow = '0 10px 40px rgba(0,0,0,0.3)';
                      
                      const backdrop = document.createElement('div');
                      backdrop.style.position = 'fixed';
                      backdrop.style.top = '0';
                      backdrop.style.left = '0';
                      backdrop.style.width = '100%';
                      backdrop.style.height = '100%';
                      backdrop.style.backgroundColor = 'rgba(0,0,0,0.5)';
                      backdrop.style.zIndex = '999998';
                      
                      const label = document.createElement('div');
                      label.textContent = 'Tap and hold to copy link:';
                      label.style.position = 'fixed';
                      label.style.top = '50%';
                      label.style.left = '50%';
                      label.style.transform = 'translate(-50%, -70px)';
                      label.style.color = '#fff';
                      label.style.fontSize = '16px';
                      label.style.fontWeight = '600';
                      label.style.zIndex = '999999';
                      label.style.textAlign = 'center';
                      label.style.width = '80%';
                      
                      document.body.appendChild(backdrop);
                      document.body.appendChild(label);
                      document.body.appendChild(input);
                      
                      input.focus();
                      input.select();
                      input.setSelectionRange(0, input.value.length);
                      
                      const cleanup = () => {
                        document.body.removeChild(backdrop);
                        document.body.removeChild(label);
                        document.body.removeChild(input);
                        
                        axios.post(
                          `${getAPI()}/users/${user.id}/track-share?video_id=${video.id}&authenticated_user_id=${user.id}`
                        ).catch(err => console.error('Error tracking share:', err));
                      };
                      
                      backdrop.addEventListener('click', cleanup);
                      setTimeout(() => {
                        backdrop.addEventListener('touchend', cleanup);
                      }, 500);
                    } else {
                      alert('Copy this link to share:\n\n' + shareUrl);
                    }
                    
                    const shareMenu = document.getElementById('share-menu-fullscreen-' + video.id);
                    if (shareMenu) shareMenu.classList.add('hidden');
                  } catch (error) {
                    console.error('Error creating share link:', error);
                    toast.error('Failed to create share link');
                  }
                }}
                className="w-full text-left px-4 py-3 hover:bg-purple-50 flex items-center gap-3 rounded-lg transition-colors"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 flex items-center gap-2">
                    {momentsShareLinkCopied ? (
                      <>
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-green-600">Copied!</span>
                      </>
                    ) : (
                      'Share Video Link'
                    )}
                  </div>
                  {!momentsShareLinkCopied && (
                    <>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                        </svg>
                        <span className="text-sm font-medium text-yellow-700">Earn 0.001 credits per view</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">Your referral link is embedded! Get credit for signups too.</div>
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>
          
          {/* Delete button */}
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this video?')) {
                onDelete(video.id);
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this video?')) {
                onDelete(video.id);
              }
            }}
            className="bg-red-600/80 hover:bg-red-700 text-white p-4 rounded-full transition-all shadow-lg backdrop-blur-sm border border-white/20"
            style={{
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
            title="Delete video"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          
          {/* Download button */}
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onTouchEnd={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              try {
                // Removed toast: Preparing download...
                
                // Get the video's streaming URL
                const videoUrl = video.result_url;
                
                if (!videoUrl) {
                  toast.error('Video URL not available');
                  return;
                }
                
                // For Cloudflare Stream videos, we can't directly download
                // Instead, create a hidden link and open in new window with noopener
                if (videoUrl.includes('cloudflarestream.com') || videoUrl.includes('videodelivery.net')) {
                  // Create a temporary anchor element
                  const link = document.createElement('a');
                  link.href = videoUrl;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  link.style.display = 'none';
                  
                  // Add to document, click, and remove
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  
                  toast.success('Video opened in new tab. Right-click and select "Save video as..." to download', { duration: 5000 });
                } else {
                  // For direct URLs, try to download via fetch
                  const response = await fetch(videoUrl);
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `magic-moment-${video.id}.mp4`;
                  link.style.display = 'none';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  
                  // Removed toast: Download started
                }
              } catch (error) {
                console.error('Download failed:', error);
                toast.error('Unable to download. Try opening in new tab and saving manually.');
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              try {
                // Removed toast: Preparing download...
                
                // Use the backend download endpoint with proper streaming
                const BACKEND_URL = getBACKEND_URL();
                const downloadUrl = `${BACKEND_URL}/api/videos/${video.id}?user_id=${encodeURIComponent(user.id)}&authenticated_user_id=${encodeURIComponent(user.id)}&download=true`;
                
                console.log('📥 Starting download from:', downloadUrl);
                
                // Fetch the video through our backend proxy
                const response = await fetch(downloadUrl);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                
                console.log('✅ Blob received, size:', blob.size, 'type:', blob.type);
                
                // Create blob URL and trigger download
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `magic-moment-${video.id}.mp4`;
                link.style.display = 'none';
                link.target = '_self';
                document.body.appendChild(link);
                
                // Safari-specific: needs a small delay
                setTimeout(() => {
                  link.click();
                  console.log('✅ Download triggered for:', link.download);
                }, 10);
                
                // Cleanup
                setTimeout(() => {
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(blobUrl);
                }, 1000);
                
                // Removed toast: Download started
              } catch (error) {
                console.error('Download failed:', error);
                toast.error('Unable to download. Please try again.');
              }
            }}
            className="bg-green-600/80 hover:bg-green-700 text-white p-4 rounded-full transition-all shadow-lg backdrop-blur-sm border border-white/20"
            style={{
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation'
            }}
            title="Download video"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        </div>
        
        {/* Navigation arrows */}
        {videoList && videoList.length > 1 && (
          <>
            {/* Previous button */}
            {currentIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/30 hover:bg-white/50 text-white rounded-full flex items-center justify-center transition-all backdrop-blur-sm"
                title="Previous video"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {/* Next button */}
            {currentIndex < videoList.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/30 hover:bg-white/50 text-white rounded-full flex items-center justify-center transition-all backdrop-blur-sm"
                title="Next video"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            
            {/* Video counter - moved to top to avoid button overlap on mobile, moves down when prompt details shown */}
            <div className={`absolute left-4 bg-black/60 backdrop-blur-sm text-white px-3 py-2 rounded-full text-sm transition-all duration-300 ${showPromptDetails ? 'top-72' : 'top-20'}`}>
              {currentIndex + 1} / {videoList.length}
            </div>
          </>
        )}
        
        {/* Inline Adjustment Panel - Overlay Style matching Albums */}
        {showAdjustPanel && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/90 to-transparent p-6 max-h-[50vh] overflow-y-auto z-50">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">🎬 Adjust & Re-animate</h3>
                <button
                  onClick={() => {
                    setShowAdjustPanel(false);
                    setUserFeedback('');
                  }}
                  className="text-white/70 hover:text-white text-2xl"
                  title="Close adjustment panel"
                >
                  ×
                </button>
              </div>
              
              {/* User feedback input - Moved to top */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-white/90 mb-2">
                  What would you like to change or add?
                </label>
                <textarea
                  value={userFeedback}
                  onChange={(e) => setUserFeedback(e.target.value)}
                  placeholder="e.g., 'Make the person smile more', 'Add gentle wind movement', 'Make it more dramatic', 'Slower movement', etc."
                  className="w-full p-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows="3"
                />
              </div>
              
              {/* Original prompt display - Moved below input */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-white/90 mb-2">Original Animation Prompt:</label>
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-lg border border-white/20">
                  <p className="text-white/90 italic text-sm">
                    "{video.parameters?.prompt || 'No original prompt available'}"
                  </p>
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowAdjustPanel(false);
                    setUserFeedback('');
                  }}
                  className="px-4 py-2 text-white/90 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (!userFeedback.trim()) {
                      alert('Please describe what you would like to change or add');
                      return;
                    }
                    
                    // Pass raw user feedback directly to callback
                    console.log('🎯 Passing user feedback from Animated Moments:', userFeedback);
                    setShowAdjustPanel(false);
                    setUserFeedback('');
                    
                    // Close the fullscreen video modal
                    onClose();
                    
                    // Open animation generator with user feedback
                    onAdjustReanimate(video, userFeedback);
                  }}
                  disabled={!userFeedback.trim()}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  ✨ Apply Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Failed Video Details Modal
const FailedVideoDetailsModal = ({ job, onClose, onRetry, onDelete }) => {
  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" 
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-red-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-red-200 bg-gradient-to-r from-red-100 to-orange-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-red-800">❌ Generation Failed</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-red-200 text-red-800 rounded-full flex items-center justify-center hover:bg-red-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6 overflow-y-auto custom-scrollbar">
          {/* Credits Refunded Notice */}
          <div className="bg-green-100 border-2 border-green-300 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h5 className="font-semibold text-green-800 text-sm">✅ Credits Automatically Refunded</h5>
              <p className="text-xs text-green-700 mt-0.5">Your credits have been returned to your account since this generation failed</p>
            </div>
          </div>
          
          {/* Source Image */}
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-red-100 rounded-xl overflow-hidden border border-red-200">
              <img loading="lazy"
                src={job.imageUrl || job.thumbnail_url}
                alt="Source image"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h4 className="font-semibold text-red-800">Failed Generation</h4>
              <p className="text-sm text-red-600">Project: {job.projectName}</p>
              <p className="text-xs text-red-500">Failed: {new Date(job.completed_at || job.created_at).toLocaleString()}</p>
            </div>
          </div>
          
          {/* Animation Prompt Used */}
          {job.parameters?.prompt && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h5 className="font-medium text-blue-800 mb-2">🎬 Animation Prompt Used:</h5>
              <div className="text-sm text-blue-700 italic max-h-32 overflow-y-auto custom-scrollbar pr-1">
                <p className="whitespace-pre-wrap">"{job.parameters.prompt}"</p>
              </div>
              {job.parameters?.duration && (
                <p className="text-xs text-blue-600 mt-2">Duration: {job.parameters.duration} seconds</p>
              )}
            </div>
          )}
          
          {/* Error Details */}
          <div className="bg-red-100 border border-red-200 rounded-xl p-4">
            <h5 className="font-medium text-red-800 mb-2">Why This Failed:</h5>
            <p className="text-sm text-red-700 whitespace-pre-line">
              {job.error_message && job.error_message.startsWith('⚠️') || job.error_message?.startsWith('⏳') || job.error_message?.startsWith('🔑') || job.error_message?.startsWith('🖼️') || job.error_message?.startsWith('🔧') || job.error_message?.startsWith('❌')
                ? job.error_message  // Use the new user-friendly error messages directly
                : job.error_message?.includes('429') || job.error_message?.includes('quota') || job.error_message?.includes('rate limit') 
                  ? 'Google AI API rate limit exceeded. Please try again in a few minutes.' 
                  : job.error_message?.includes('contains IP') || job.error_message?.includes('intellectual property') || job.error_message?.includes('prompt not allowed')
                    ? '🚫 Copyrighted Content Blocked\n\nYour animation prompt includes references to copyrighted characters, brands, or trademarked content (like Disney characters, Marvel heroes, brand logos, etc.).\n\nThe AI video service cannot generate videos with copyrighted material for legal reasons.\n\n💡 What to do: Use generic descriptions instead of specific character names or brands. For example, instead of "Mickey Mouse dancing," try "a cheerful cartoon character dancing."'
                    : job.error_message || (job.status === 'timeout' ? 'Generation timed out after 30 minutes' : 'Video generation failed due to API error')}
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm('Are you sure you want to delete this failed generation? This action cannot be undone.')) {
                  onDelete(job.id);
                  onClose();
                }
              }}
              className="flex-1 bg-red-500 text-white rounded-xl px-4 py-3 font-medium hover:bg-red-600 transition-colors"
            >
              🗑️ Delete
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-red-200 text-red-800 rounded-xl px-4 py-3 font-medium hover:bg-red-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced project card with memory theme and inline editing
const MemoryProjectCard = ({ project, onSelect, onRefresh, showArchived, setCurrentView, setShowPendingVideos }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(project.name);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [editDesc, setEditDesc] = useState(project.description || '');
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  
  // Get all available media (images and videos) for preview
  const availableMedia = [
    ...(project.images?.filter(img => !img.is_deleted) || []),
    ...(project.video_generations?.filter(v => v.status === 'completed' && !v.is_deleted) || [])
  ];
  
  // Auto-cycle through media on hover
  React.useEffect(() => {
    let interval;
    if (availableMedia.length > 1) {
      interval = setInterval(() => {
        setCurrentPreviewIndex(prev => (prev + 1) % availableMedia.length);
      }, 2000); // Change every 2 seconds
    }
    return () => clearInterval(interval);
  }, [availableMedia.length]);
  
  const currentMedia = availableMedia[currentPreviewIndex];
  
  const handleArchive = async () => {
    try {
      const formData = new FormData();
      formData.append('is_archived', !project.is_archived);
      
      formData.append('user_id', user.id);
      await axios.put(`${getAPI()}/projects/${project.id}`, formData);
      toast.success(project.is_archived ? 'Memory restored to active!' : 'Memory safely archived!');
      onRefresh();
    } catch (error) {
      toast.error('Action failed');
    }
    setShowMenu(false);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this memory collection forever? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${getAPI()}/projects/${project.id}?user_id=${user.id}`);
      toast.success('Memory collection deleted');
      onRefresh();
    } catch (error) {
      toast.error('Delete failed');
    }
    setShowMenu(false);
  };

  const handleSaveName = async () => {
    try {
      const formData = new FormData();
      formData.append('name', editName);
      
      formData.append('user_id', user.id);
      await axios.put(`${getAPI()}/projects/${project.id}`, formData);
      toast.success('Memory name updated!');
      setIsEditingName(false);
      onRefresh();
    } catch (error) {
      toast.error('Update failed');
      setEditName(project.name);
    }
  };

  const handleSaveDesc = async () => {
    try {
      const formData = new FormData();
      formData.append('description', editDesc);
      
      formData.append('user_id', user.id);
      await axios.put(`${getAPI()}/projects/${project.id}`, formData);
      toast.success('Description updated!');
      setIsEditingDesc(false);
      onRefresh();
    } catch (error) {
      toast.error('Update failed');
      setEditDesc(project.description || '');
    }
  };

  const imageCount = project.images?.filter(img => !img.is_deleted).length || 0;
  const videoCount = project.video_generations?.filter(v => v.status === 'completed' && !v.is_deleted).length || 0;
  const collaboratorCount = project.collaborators?.length || 0;

  return (
    <div className="bg-gradient-to-br from-white/90 to-amber-50/90 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-amber-200/50 group">
      {/* Thumbnail */}
      <div className="relative">
        <div className="aspect-[4/3] bg-gradient-to-br from-amber-100 to-orange-100 relative overflow-hidden">
          {availableMedia.length > 0 && currentMedia ? (
            (currentMedia.result_url && isValidVideoUrl(currentMedia.result_url)) ? (
              // Show video preview for completed videos
              <VideoErrorBoundary fallback={
                <img loading="lazy"
                  src={currentMedia.thumbnail_url}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300 bg-black/5"
                  onClick={() => onSelect(project)}
                />
              }>
                <video preload="none"
                  src={currentMedia.result_url}
                  poster={currentMedia.thumbnail_url}
                  className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300 bg-black/5"
                  onClick={() => onSelect(project)}
                  muted
                  loop
                  preload="none"
                  onMouseEnter={(e) => {
                    if (e.target.src && isValidVideoUrl(e.target.src)) {
                      e.target.play().catch((err) => {
                        console.warn('Project video hover play failed:', err);
                      });
                    }
                  }}
                  onMouseLeave={(e) => e.target.pause()}
                  onError={(e) => {
                    console.warn('Project video preview error:', currentMedia.result_url);
                    e.target.style.display = 'none';
                  }}
                />
              </VideoErrorBoundary>
            ) : (
              // Show image preview
              <img loading="lazy"
                src={currentMedia.url || currentMedia.thumbnail_url}
                alt={project.name}
                className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300 bg-black/5"
                onClick={() => onSelect(project)}
              />
            )
          ) : (
            // Empty state when no media
            <div className="w-full h-full flex items-center justify-center cursor-pointer" onClick={() => onSelect(project)}>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-300 to-orange-300 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">✨</span>
                </div>
                <p className="text-amber-600 text-sm font-medium">No memories yet</p>
                <p className="text-amber-500 text-xs">Add photos to start</p>
              </div>
            </div>
          )}
          
          {/* Media count indicator */}
          {availableMedia.length > 1 && (
            <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              {currentPreviewIndex + 1} / {availableMedia.length}
            </div>
          )}
        </div>
        
        {/* Menu Button */}
        <div className="absolute top-3 right-3">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="w-8 h-8 bg-black/40 backdrop-blur-sm text-white rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" />
            </svg>
          </button>
          
          {showMenu && (
            <div className="absolute top-10 right-0 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl border border-amber-200 py-2 w-48 z-[100]">
              <button
                onClick={() => onSelect(project)}
                className="w-full text-left px-4 py-2 hover:bg-amber-50 transition-colors text-amber-800"
              >
                👁️ View Collection
              </button>
              <button
                onClick={() => {setIsEditingName(true); setShowMenu(false);}}
                className="w-full text-left px-4 py-2 hover:bg-amber-50 transition-colors text-amber-800"
              >
                ✏️ Edit Name
              </button>
              <button
                onClick={() => {setIsEditingDesc(true); setShowMenu(false);}}
                className="w-full text-left px-4 py-2 hover:bg-amber-50 transition-colors text-amber-800"
              >
                📝 Edit Description
              </button>
              <button
                onClick={() => {
                  setShowMenu(false);
                  const email = prompt('Enter collaborator email:');
                  if (email) {
                    toast.success(`Invite sent to ${email}! 📧`);
                    // TODO: Implement actual invite API call
                  }
                }}
                className="w-full text-left px-4 py-2 hover:bg-amber-50 transition-colors text-amber-800"
              >
                👥 Add Collaborators
              </button>
              <button
                onClick={handleArchive}
                className="w-full text-left px-4 py-2 hover:bg-amber-50 transition-colors text-amber-800"
              >
                {project.is_archived ? '📂 Restore' : '📦 Archive'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(e);
                }}
                className="w-full text-left px-4 py-2 hover:bg-red-50 text-red-600 transition-colors"
              >
                🗑️ Delete Forever
              </button>
            </div>
          )}
        </div>

        {/* Stats Badge */}
        <div className="absolute bottom-3 left-3">
          <div className="bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-2">
            <span>✨ {imageCount}</span>
            {videoCount > 0 && <span>✨ {videoCount}</span>}
            {collaboratorCount > 0 && <span>👥 {collaboratorCount}</span>}
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-5">
        {/* Editable Name */}
        {isEditingName ? (
          <div className="mb-3">
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full text-lg font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-amber-500"
              onBlur={handleSaveName}
              onKeyPress={(e) => e.key === 'Enter' && handleSaveName()}
              autoFocus
            />
          </div>
        ) : (
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-lg text-amber-800 truncate flex-1">
              {project.name}
            </h3>
            <button
              onClick={() => setIsEditingName(true)}
              className="p-1 text-amber-600 hover:text-amber-800 transition-colors opacity-0 group-hover:opacity-100"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Editable Description */}
        {isEditingDesc ? (
          <div className="mb-4">
            <textarea
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              className="w-full text-sm text-amber-700 bg-amber-50 border border-amber-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-amber-500 resize-none"
              rows="2"
              placeholder="Describe this memory collection..."
              onBlur={handleSaveDesc}
              autoFocus
            />
          </div>
        ) : (
          <div className="flex items-start justify-between mb-4">
            <p className="text-amber-700 text-sm line-clamp-2 flex-1">
              {project.description || 'Add a description to capture the essence of these memories'}
            </p>
            <button
              onClick={() => setIsEditingDesc(true)}
              className="p-1 text-amber-600 hover:text-amber-800 transition-colors opacity-0 group-hover:opacity-100 ml-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={() => onSelect(project)}
          className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white font-semibold py-3 px-4 rounded-2xl hover:from-amber-600 hover:via-orange-600 hover:to-red-600 transition-all transform hover:scale-[1.02] shadow-lg"
          data-testid="open-project-button"
        >
          {videoCount > 0 ? '✨ View Living Memories' : imageCount > 0 ? '✨ Create Time Travel' : '✨ Add First Memory'}
        </button>
      </div>
    </div>
  );
};

// Enhanced project modal with full video creation flow
const MemoryProjectModal = ({ project, aiProviders, autoOpenVideoGenerator, checkSingleImageAnalysis, onClose, onRefresh, setCurrentView, setActiveMainTab, setShowPendingVideos, setShowMagicProcessing, setProcessingCount }) => {
  const [activeTab, setActiveTab] = useState('photos');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showVideoGenerator, setShowVideoGenerator] = useState(autoOpenVideoGenerator || false);
  const [showCollaborators, setShowCollaborators] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  
  const activeImages = project.images?.filter(img => !img.is_deleted) || [];
  const activeVideos = project.video_generations?.filter(v => !v.is_deleted) || [];
  const pendingVideos = project.video_generations?.filter(v => ['queued', 'processing', 'submitted'].includes(v.status) && !v.is_deleted) || [];

  // Auto-select preselected or first image and open video generator if requested
  React.useEffect(() => {
    if (autoOpenVideoGenerator && activeImages.length > 0) {
      // Use preselected image if available, otherwise use first image
      const imageToSelect = project.preSelectedImage && activeImages.find(img => img.id === project.preSelectedImage.id) 
        ? project.preSelectedImage 
        : activeImages[0];
      setSelectedImage(imageToSelect);
    }
  }, [autoOpenVideoGenerator, activeImages, project.preSelectedImage]);

  const handleCreateVideo = (image) => {
    setSelectedImage(image);
    setShowVideoGenerator(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-amber-200" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-amber-200 bg-gradient-to-r from-amber-100 to-orange-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-amber-800">{project.name}</h2>
              <p className="text-amber-600 mt-1">{project.description}</p>
              <div className="flex items-center space-x-4 mt-3 text-sm text-amber-700">
                <span>✨ {activeImages.length} memories</span>
                <span>✨ {activeVideos.filter(v => v.status === 'completed').length} living moments</span>
                <span>👥 {(project.collaborators?.length || 0) + 1} time travelers</span>
                <span>👁️ {project.view_count || 0} views</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
              data-testid="close-project-modal"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={() => setShowVideoGenerator(true)}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:from-amber-600 hover:to-orange-600 transition-all"
              data-testid="create-video-button"
            >
              ✨ Create Time Travel
            </button>
            <button
              onClick={() => setShowCollaborators(true)}
              className="bg-white text-amber-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-amber-50 transition-colors border border-amber-200"
            >
              👥 Manage Collaborators
            </button>
            <button 
              onClick={() => setShowShareMenu(true)}
              className="bg-white text-amber-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-amber-50 transition-colors border border-amber-200"
            >
              📤 Share Memories
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-1 bg-amber-100/50 p-1 m-6 rounded-2xl">
          <button
            onClick={() => setActiveTab('photos')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all text-sm sm:text-base ${
              activeTab === 'photos'
                ? 'bg-white shadow-sm text-amber-800'
                : 'text-amber-700 hover:text-amber-900'
            }`}
          >
            ✨ Memory Album ({activeImages.length})
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all text-sm sm:text-base ${
              activeTab === 'videos'
                ? 'bg-white shadow-sm text-amber-800'
                : 'text-amber-700 hover:text-amber-900'
            }`}
          >
            ✨ Living Memories ({activeVideos.filter(v => v.status === 'completed').length})
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all text-sm sm:text-base ${
              activeTab === 'pending'
                ? 'bg-white shadow-sm text-amber-800'
                : 'text-amber-700 hover:text-amber-900'
            }`}
          >
            ⏳ Pending ({pendingVideos.length})
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh] sm:max-h-[60vh]">
          {activeTab === 'photos' && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {activeImages.map((image) => (
                <div key={image.id} className="group relative">
                  <div className={`aspect-square bg-amber-100 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
                    imagesWithVideos.has(image.id) 
                      ? 'border-4 border-purple-500 shadow-lg' 
                      : 'border border-amber-200'
                  }`}>
                    <img loading="lazy"
                      src={image.url}
                      alt={image.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onClick={() => handleCreateVideo(image)}
                    />
                  </div>
                  
                  {/* View Videos Button for Project Images - Only show if image has associated videos and not uploading */}
                  {(imagesWithVideos.has(image.id) || imagesWithVideos.has(String(image.id))) && !showCreateLivingMemory && !showMultiUpload && (
                    <div className="absolute top-3 left-3 z-30">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowVideoGallery(image.id);
                        }}
                        className="bg-gradient-to-r from-purple-500 to-blue-500 text-white p-2 rounded-full hover:from-purple-600 hover:to-blue-600 transition-all duration-300 shadow-lg"
                        title="View associated videos"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </div>
                  )}
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl flex items-end">
                    <div className="p-3 text-white">
                      <h4 className="font-medium text-sm truncate">{image.name}</h4>
                      {image.description && (
                        <p className="text-xs text-gray-200 mt-1 line-clamp-2">{image.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleCreateVideo(image)}
                      className="bg-amber-500 text-white p-2 rounded-xl hover:bg-amber-600 transition-colors shadow-lg"
                      title="Travel back to this moment"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              
              {activeImages.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <div className="w-20 h-20 bg-amber-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">✨</span>
                  </div>
                  <p className="text-amber-600 mb-4">No memories captured yet</p>
                  <button className="bg-amber-500 text-white px-6 py-3 rounded-2xl hover:bg-amber-600 transition-colors">
                    ✨ Add First Memory
                  </button>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'videos' && (
            <div className="space-y-6">
              {activeVideos.map((video) => (
                <div key={video.id} className="bg-gradient-to-r from-white/80 to-amber-50/80 rounded-2xl p-6 border border-amber-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-800 mb-1">
                        Time Travel with {video.provider?.toUpperCase() || 'AI'}
                      </h4>
                      <p className="text-amber-600 text-sm mb-2">{video.prompt}</p>
                      <div className="flex items-center space-x-4 text-xs text-amber-500">
                        <span>{(() => {
                          const actualDuration = getVideoDuration(video.video_url, video.id);
                          return actualDuration ? `${actualDuration}s` : `${video.parameters?.duration || 5}s`;
                        })()}</span>
                        <span>Style: {video.parameters?.style || 'realistic'}</span>
                        <span>Cost: {video.cost_credits} credits</span>
                        <span>{new Date(video.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      video.status === 'completed' ? 'bg-green-100 text-green-800' :
                      video.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                      video.status === 'queued' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {video.status === 'completed' ? '✨ Ready' : 
                       video.status === 'processing' ? '⏳ Traveling...' :
                       video.status === 'queued' ? '✨ Queued' : '❌ Failed'}
                    </span>
                  </div>
                  
                  {video.status === 'completed' && video.video_url && isValidVideoUrl(video.video_url) && (
                    <div className="aspect-video bg-black rounded-2xl overflow-hidden">
                      <VideoErrorBoundary fallback={
                        <img loading="lazy"
                          src={getAuthenticatedThumbnailUrl(video.thumbnail_url)}
                          alt="Video thumbnail"
                          className="w-full h-full object-cover"
                        />
                      }>
                        {video.video_url.includes('.mp4') ? (
                          <video
                            preload="none"
                            src={video.video_url}
                            controls
                            className="w-full h-full"
                            poster={getAuthenticatedThumbnailUrl(video.thumbnail_url)}
                            preload="none"
                            onError={(e) => {
                              console.warn('Pending video error:', video.video_url);
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <img loading="lazy"
                            src={video.video_url}
                            alt="Living moment"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </VideoErrorBoundary>
                    </div>
                  )}
                  
                  {video.status === 'processing' && (
                    <div className="aspect-video bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                        <p className="text-amber-600 font-medium">Traveling through time...</p>
                        <p className="text-amber-500 text-sm">This may take a few moments</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {activeVideos.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-amber-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">✨</span>
                  </div>
                  <p className="text-amber-600 mb-4">No time travels created yet</p>
                  <button
                    onClick={() => setShowVideoGenerator(true)}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-2xl hover:from-amber-600 hover:to-orange-600 transition-all"
                  >
                    ✨ Create First Time Travel
                  </button>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'pending' && (
            <div>
              {pendingVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pendingVideos.map((video) => (
                    <div key={video.id} className="bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl border border-amber-200 overflow-hidden shadow-lg">
                      {/* Thumbnail with processing overlay */}
                      <div className="aspect-video bg-amber-100 relative">
                        <img loading="lazy"
                          src={getAuthenticatedThumbnailUrl(video.thumbnail_url || activeImages.find(img => img.id === video.image_id)?.url)}
                          alt="Source image"
                          className="w-full h-full object-cover opacity-50"
                        />
                        
                        {/* Processing overlay */}
                        <div className="absolute inset-0 bg-amber-500/80 flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-white font-semibold">Processing...</p>
                            <p className="text-amber-100 text-sm mt-1 capitalize">{video.status}</p>
                            {video.step && (
                              <p className="text-amber-100 text-xs mt-2 max-w-48 mx-auto">{video.step}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Progress indicator */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-3">
                          <div className="flex items-center justify-between text-white text-xs mb-2">
                            <span>Progress</span>
                            <span>{video.progress || 0}%</span>
                          </div>
                          <div className="w-full bg-white/30 rounded-full h-1.5">
                            <div 
                              className="bg-white h-1.5 rounded-full transition-all duration-500"
                              style={{width: `${video.progress || 0}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Video details */}
                      <div className="p-4">
                        <h4 className="font-semibold text-amber-800 mb-2 line-clamp-2">
                          {video.parameters?.prompt || 'Creating living moment...'}
                        </h4>
                        
                        <div className="space-y-2 text-sm text-amber-600">
                          <div className="flex justify-between">
                            <span>Status:</span>
                            <span className="font-medium capitalize">{video.status}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Started:</span>
                            <span>{new Date(video.created_at).toLocaleTimeString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Duration:</span>
                            <span>{(() => {
                              const actualDuration = getVideoDuration(video.video_url, video.id);
                              return actualDuration ? `${actualDuration}s` : `${video.parameters?.duration || 4}s`;
                            })()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>AI Provider:</span>
                            <span className="capitalize">{video.provider || 'Vidu'}</span>
                          </div>
                          {video.step && (
                            <div className="flex justify-between">
                              <span>Current Step:</span>
                              <span className="text-xs">{video.step}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* ETA */}
                        <div className="mt-3 p-3 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-amber-800">Estimated completion:</span>
                            <span className="text-sm text-amber-700">
                              ~{Math.max(1, 5 - Math.floor((video.progress || 0) / 20))} min
                            </span>
                          </div>
                        </div>
                        
                        {/* Credits used */}
                        <div className="mt-2 text-xs text-amber-500 text-center">
                          {video.cost_credits || 12} credits used
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-gradient-to-br from-white/80 to-amber-50/80 backdrop-blur-sm rounded-3xl border border-amber-200">
                  <div className="w-24 h-24 bg-gradient-to-br from-amber-200 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    ⏳
                  </div>
                  <h3 className="text-xl font-bold text-amber-800 mb-2">No pending videos</h3>
                  <p className="text-amber-600 mb-6 max-w-md mx-auto">
                    When you create new living memories from this collection, they'll appear here while processing
                  </p>
                  <button
                    onClick={() => {setActiveTab('photos'); setShowVideoGenerator(true);}}
                    className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white px-8 py-4 rounded-2xl hover:from-amber-600 hover:via-orange-600 hover:to-red-600 transition-all transform hover:scale-105 font-semibold text-lg shadow-lg"
                  >
                    ✨ Create First Living Moment
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Sub Modals */}
      {showVideoGenerator && (
        <MemoryVideoGenerator
          project={project}
          selectedImage={selectedImage}
          aiProviders={aiProviders}
          user={user}
          albumContext={albumContext}
          checkSingleImageAnalysis={checkSingleImageAnalysis}
          onClose={() => {
            setShowVideoGenerator(false);
            setSelectedImage(null);
          }}
          onRefresh={onRefresh}
          setCurrentView={setCurrentView}
          setActiveMainTab={setActiveMainTab}
          setShowPendingVideos={setShowPendingVideos}
          setShowMagicProcessing={setShowMagicProcessing}
          setProcessingCount={setProcessingCount}
        />
      )}
      
      {showCollaborators && (
        <CollaboratorsModal
          project={project}
          onClose={() => setShowCollaborators(false)}
          onRefresh={onRefresh}
        />
      )}
      
      {showShareMenu && (
        <ShareMemoryModal
          project={project}
          onClose={() => setShowShareMenu(false)}
        />
      )}
    </div>
  );
};

// Enhanced video generator with full AI provider selection
const MemoryVideoGenerator = ({ project, selectedImage, aiProviders, user, albumContext, checkSingleImageAnalysis, onClose, onRefresh, setCurrentView, setActiveMainTab, setShowPendingVideos, setShowMagicProcessing, setProcessingCount }) => {
  // Simplified - only Google AI, no provider selection
  const selectedProvider = 'luma';
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(4); // Vidu 2.0 default (4 or 8 seconds supported)
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompts, setAiPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const analysisApprovedRef = useRef(false); // Use ref to avoid state timing issues
  const [showingAnalysisWarning, setShowingAnalysisWarning] = useState(false);
  const [showAnimationTip, setShowAnimationTip] = useState(false); // State for collapsible animation tip - collapsed by default
  const [promptsJustLoaded, setPromptsJustLoaded] = useState(false); // Track when prompts just finished loading for button animation
  const [imageBorderWarning, setImageBorderWarning] = useState(null); // State for border warning from AI analysis
  const [imageBrightBackground, setImageBrightBackground] = useState(false); // State for bright background flag from AI analysis
  const [imageFadedWarning, setImageFadedWarning] = useState(null); // State for faded image warning from AI analysis

  // Track if component is mounted to prevent operations after close
  const isMountedRef = useRef(true);

  // Fixed cost estimation for video generation
  const estimatedCost = duration === 4 ? 5 : 10; // 5 credits for 4s, 10 credits for 8s

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Animation generator component unmounting - cleaning up');
      isMountedRef.current = false;
    };
  }, []);

  // Load AI prompts when image is selected (trigger for ANY image selection)
  useEffect(() => {
    let aiPromptsTimeout;
    
    if (selectedImage && isMountedRef.current) {
      console.log('🖼️ Selected Image:', selectedImage.name || selectedImage.id);
      console.log('🔍 Enhanced Prompt Check:', selectedImage.enhancedPrompt);
      
      // Set prompt if there's an enhanced prompt
      if (selectedImage.enhancedPrompt && !prompt) {
        console.log('✅ Setting enhanced prompt in text field:', selectedImage.enhancedPrompt);
        setPrompt(selectedImage.enhancedPrompt);
      }
      
      // Small delay to ensure UI has updated
      aiPromptsTimeout = setTimeout(() => {
        if (isMountedRef.current) {
          loadAIPrompts();
        }
      }, 100);
    } else {
      // Clear prompts when no image selected
      if (isMountedRef.current) {
        setAiPrompts([]);
        analysisApprovedRef.current = false;
      }
    }
    
    // Reset analysis approval when image changes
    analysisApprovedRef.current = false;
    setShowingAnalysisWarning(false);
    
    // Cleanup: cancel pending AI prompts load when component unmounts or selectedImage changes
    return () => {
      if (aiPromptsTimeout) {
        clearTimeout(aiPromptsTimeout);
      }
    };
  }, [selectedImage]);

  // Load AI-generated prompts for the selected image
  // Now uses the shared AI analysis "brain" for consistency
  const loadAIPrompts = async () => {
    console.log('🔍 [BATCH] loadAIPrompts called - checking conditions...');
    console.log('  - selectedImage:', !!selectedImage);
    console.log('  - isMountedRef.current:', isMountedRef.current);
    
    if (!selectedImage || !isMountedRef.current) {
      console.log('❌ [BATCH] Early return: missing selectedImage or not mounted');
      return;
    }
    
    console.log('  - analysisApprovedRef.current:', analysisApprovedRef.current);
    console.log('  - showingAnalysisWarning:', showingAnalysisWarning);
    console.log('  - checkSingleImageAnalysis:', !!checkSingleImageAnalysis);
    
    // Check if analysis was already approved for this image
    if (!analysisApprovedRef.current && !showingAnalysisWarning && checkSingleImageAnalysis) {
      console.log('⚠️ Analysis not approved yet, checking with parent...');
      setShowingAnalysisWarning(true);
      
      // Call parent's check function which will show the warning modal
      await checkSingleImageAnalysis(selectedImage, () => {
        // This callback runs after user confirms
        console.log('✅ Analysis approved by user');
        analysisApprovedRef.current = true;
        setShowingAnalysisWarning(false);
        // Trigger loadAIPrompts again now that it's approved
        setTimeout(() => loadAIPrompts(), 50);
      });
      
      return; // Don't proceed until approved
    }

    console.log('🚀 loadAIPrompts starting...');
    console.log('📸 selectedImage:', selectedImage);
    console.log('🔄 isMountedRef:', isMountedRef.current);
    
    setLoadingPrompts(true);
    try {
      console.log('🧠 Animation Generator using shared AI analysis brain');
      const result = await analyzeImageWithAI(selectedImage, selectedImage.enhancedPrompt, user);
      console.log('📥 Received result:', result);
      
      // Handle result that may include border warning and faded image warning
      let suggestions;
      if (result && result.suggestions) {
        suggestions = result.suggestions;
        if (result.borderWarning) {
          console.warn('⚠️ Border warning from AI analysis:', result.borderWarning);
          setImageBorderWarning(result.borderWarning);
        }
        if (result.fadedWarning) {
          console.warn('⚠️ Faded image detected - may not animate well:', result.fadedWarning);
          setImageFadedWarning(result.fadedWarning);
        }
      } else if (Array.isArray(result)) {
        suggestions = result;
      }
      // CRITICAL: Always set AI prompts, even if component unmounted
      // React will safely ignore state updates on unmounted components
      console.log('✅ Setting aiPrompts state with:', suggestions);
      setAiPrompts(suggestions);
      // Automatically set the first AI suggestion as the prompt for better results
      if (suggestions && suggestions.length > 0) {
        setPrompt(suggestions[0]);
        console.log('✅ Auto-selected first AI suggestion as prompt:', suggestions[0]);
      }
    } catch (error) {
      console.error('❌ Error in loadAIPrompts:', error);
      // Silently ignore errors if component has unmounted (request cancelled)
      if (error.message !== 'Component unmounted' && isMountedRef.current) {
        console.error('Failed to load AI prompts:', error);
        console.log('🔍 Enhanced prompt in loadAIPrompts error:', selectedImage?.enhancedPrompt);
        
        // Use enhanced prompt fallback if available
        let fallbackPrompts;
        if (selectedImage?.enhancedPrompt) {
          console.log('✅ Using enhanced prompt in loadAIPrompts fallback');
          fallbackPrompts = [
            `${selectedImage.enhancedPrompt} with gentle natural movement and subtle animation`,
            `${selectedImage.enhancedPrompt} while maintaining realistic motion and proper framing`,
            `${selectedImage.enhancedPrompt} focusing on smooth transitions and natural dynamics`
          ];
        } else {
          fallbackPrompts = [
            "Add only subtle breathing and gentle blinking - keep posed subjects naturally still with minimal micro-expressions",
            "Create barely perceptible motion: soft lighting shifts and minimal environmental movement appropriate to indoor/outdoor setting", 
            "Animate with natural restraint: gentle eye movements and breath rhythm while maintaining the composed nature of posed photos"
          ];
        }
        
        console.log('📝 Setting fallback prompts:', fallbackPrompts);
        setAiPrompts(fallbackPrompts);
      }
    } finally {
      // CRITICAL: Always set loading to false, even if component unmounted
      // React will safely ignore state updates on unmounted components
      console.log('🔄 Setting loadingPrompts to false (isMountedRef.current:', isMountedRef.current, ')');
      setLoadingPrompts(false);
      
      // Trigger button flash animation when prompts finish loading
      setPromptsJustLoaded(true);
      setTimeout(() => setPromptsJustLoaded(false), 8000); // Flash for 8 seconds (matches shimmer duration)
    }
  };


  const promptSuggestions = [
    "Add only subtle breathing and gentle blinking while preserving the composed, posed nature of this family portrait",
    "Create minimal motion: soft breathing and natural eye movements while keeping subjects naturally still as they were when photographed",
    "Animate with gentle breathing and barely perceptible micro-expressions that respect the stillness of this captured moment",
    "Add subtle life through gentle breathing and soft lighting changes while maintaining the formal, posed composition",
    "Create natural stillness with minimal breathing and gentle eye movements appropriate for a carefully composed photograph"
  ];

  return (
    <>
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-60" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl sm:rounded-3xl max-w-[98vw] sm:max-w-[95vw] lg:max-w-7xl w-full max-h-[95vh] overflow-hidden shadow-2xl border border-amber-200" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-amber-200 bg-gradient-to-r from-amber-100 to-orange-100">
          <div className="flex items-start space-x-3">
            <Logo className="w-10 h-10 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-bold text-amber-800 mb-2">Animation Generator</h3>
              <p className="text-amber-600">Transform your memory into a living moment with AI magic</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Selected Memory */}
            <div className="space-y-6">
              {/* Selected Memory Display */}
              {selectedImage && selectedImage.url && (
                <div className="bg-gradient-to-br from-white/80 to-amber-50/80 rounded-2xl p-4 border border-amber-200">
                  <h4 className="font-medium text-amber-800 mb-3">Selected Picture</h4>
                  <div className="relative rounded-xl overflow-hidden border border-amber-200 flex justify-center bg-gray-50">
                    <img loading="lazy"
                      key={selectedImage._renderKey || `memory-image-${selectedImage.id}-${selectedImage.editTimestamp || 'original'}`}
                      src={selectedImage.url}
                      alt={selectedImage.name}
                      className="max-w-full max-h-96 object-contain"
                      onLoad={() => console.log('✅ MemoryVideo Image loaded:', 
                        selectedImage.isEdited ? `EDITED (ID: ${selectedImage.id})` : 'Original', 
                        selectedImage.url?.substring(0, 30))}
                      onError={(e) => {
                        console.error('❌ MemoryVideo Image load error:', e.target.src);
                        // Fallback to original URL if available
                        if (selectedImage.original_url && e.target.src !== selectedImage.original_url) {
                          console.log('🔄 MemoryVideo falling back to original URL');
                          e.target.src = selectedImage.original_url;
                        }
                      }}
                    />
                    {/* Adjust Picture Button - Top Right Corner */}
                    <button
                      onClick={() => setShowImageEditor(true)}
                      className="absolute top-1 right-1 sm:top-2 sm:right-2 p-1.5 sm:p-2 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white rounded-md sm:rounded-lg transition-colors backdrop-blur-sm shadow-lg"
                      title="Adjust picture before animation"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                      </svg>
                    </button>
                  </div>
                  <div className="mt-3">
                    <p className="font-medium text-amber-800">{selectedImage.name}</p>
                    {selectedImage.description && (
                      <p className="text-sm text-amber-600">{selectedImage.description}</p>
                    )}
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={() => setShowImageEditor(true)}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all font-medium text-sm flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Adjust Picture
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Right Column - Simplified Configuration */}
            <div className="space-y-6">
              {/* Animation Instructions */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-5 border-2 border-purple-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h4 className="font-semibold text-gray-900">{t("animation.instructions")}</h4>
                  </div>
                  {prompt.trim() && (
                    <button
                      onClick={() => setPrompt('')}
                      className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-white/50 transition-colors flex items-center gap-1"
                      title="Clear text"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Clear
                    </button>
                  )}
                </div>
                
                {/* Luma Limitations Warning Banner - Collapsible - Subtle Design */}
                <div className="mb-3 bg-gray-50 border-l-2 border-gray-300 rounded-lg overflow-hidden">
                  <button 
                    onClick={() => setShowAnimationTip(!showAnimationTip)}
                    className="w-full flex items-start gap-2 p-2.5 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                    </svg>
                    <div className="flex-1 text-left">
                      <p className="text-xs font-medium text-gray-600">Animation Tip</p>
                    </div>
                    <svg 
                      className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showAnimationTip ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showAnimationTip && (
                    <div className="px-2.5 pb-2.5">
                      <p className="text-xs text-gray-600">
                        The AI works best with <span className="font-semibold">subtle movements</span> like breathing, blinking, gentle head turns, and facial expressions. 
                        Complex actions (rolling, jumping, walking) may not animate as expected.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Border Warning Banner */}
                {imageBorderWarning && imageBorderWarning.has_borders && (
                  <div className="mb-3 bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-800 mb-1">Image Quality Warning</p>
                        <p className="text-xs text-amber-700">
                          This image has white borders or uncropped sections ({imageBorderWarning.edges.join(', ')} edges). 
                          For best animation results, please crop the image to remove borders and ensure the photo fills the entire frame.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {imageFadedWarning && imageFadedWarning.is_faded && (
                  <div className="mb-3 bg-amber-50 border-l-4 border-amber-400 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-amber-800 mb-1">Image Quality Warning</p>
                        <p className="text-xs text-amber-700">
                          This image has quality issues that may affect animation (faded, low contrast, blurry, or desaturated). 
                          Results may include limited movement or visual artifacts.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-800 placeholder-gray-400"
                  placeholder={t('animation.describe_prompt')}
                />
                
                {/* Complex Movement Warning (Dynamic) - Only for user-typed text */}
                {(() => {
                  // Check if current prompt is an AI-suggested prompt
                  const isAISuggestion = aiPrompts.some(suggestion => suggestion === prompt);
                  
                  // Only show warning for user-typed prompts, not AI suggestions
                  if (isAISuggestion) {
                    return null;
                  }
                  
                  const complexMovements = ['roll', 'jump', 'leap', 'flip', 'walk', 'run', 'sprint', 'dance', 'spin', 'twirl', 'cartwheel', 'somersault', 'fly', 'soar'];
                  const hasComplexMovement = complexMovements.some(word => prompt.toLowerCase().includes(word));
                  
                  if (hasComplexMovement && prompt.length > 10) {
                    return (
                      <div className="mt-2 bg-red-50 border-l-4 border-red-400 p-3 rounded-lg animate-pulse">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                          </svg>
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-red-800 mb-1">⚠️ Complex Movement Detected</p>
                            <p className="text-xs text-red-700">
                              Your prompt includes complex movements that may not animate well. Try using an <span className="font-semibold">AI suggestion below</span> for better results, 
                              or describe subtle expressions and emotions instead.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div className="mt-3" data-tutorial="ai-prompts-section">
                  <p className="text-xs text-gray-600 mb-2 font-medium">
                    {selectedImage ? `🤖 ${t('animation.ai_generated_prompts')}` : `💡 ${t('animation.select_for_suggestions')}`}
                  </p>
                  {/* Scrollable container for AI prompts - max height to prevent overflow */}
                  <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                    {loadingPrompts ? (
                      <div className="flex flex-col items-center justify-center py-6 px-4">
                        {/* AI Brain Animation */}
                        <div className="relative w-16 h-16 mb-3">
                          {/* Brain outline with pulse */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-16 h-16 text-purple-500 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          
                          {/* Scanning lines */}
                          <div className="absolute inset-0 overflow-hidden">
                            <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-purple-400 to-transparent animate-scan"></div>
                          </div>
                          
                          {/* Neural dots */}
                          <div className="absolute top-2 left-2 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping"></div>
                          <div className="absolute top-4 right-3 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
                          <div className="absolute bottom-3 left-4 w-1.5 h-1.5 bg-purple-400 rounded-full animate-ping" style={{animationDelay: '0.4s'}}></div>
                          <div className="absolute bottom-2 right-2 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                        </div>
                        
                        {/* Animated text */}
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-700 mb-1 animate-pulse">
                            Your prompts will appear here after analysis
                          </div>
                          <div className="flex items-center justify-center gap-1">
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                          </div>
                        </div>
                      </div>
                    ) : aiPrompts.length > 0 ? (
                      <>
                        {aiPrompts.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => setPrompt(suggestion)}
                            className="text-xs bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 text-gray-700 px-3 py-2.5 rounded-lg transition-all text-left border border-purple-200 shadow-sm hover:shadow whitespace-pre-wrap"
                            data-tutorial="ai-prompt-suggestion"
                          >
                            {suggestion}
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            // Reset skip flag when user wants new AI prompts
                            console.log('🔄 Generate different prompts clicked - resetting skip flag');
                            if (skipAnalysisRef) {
                              skipAnalysisRef.current = false;
                            }
                            // DO NOT set analysisApprovedRef.current = true here!
                            // This would bypass the cost check - let loadAIPrompts handle it
                            loadAIPrompts();
                          }}
                          disabled={loadingPrompts}
                          className="text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white px-3 py-2 rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all mt-1 shadow-md flex-shrink-0"
                        >
                          ✨ Generate different prompts (Free)
                        </button>
                      </>
                    ) : selectedImage && !preSelectedImage ? (
                      // Only show manual button if NOT opened with preSelectedImage (auto-trigger mode)
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500 italic">No AI prompts available</p>
                        <button
                          onClick={async () => {
                            console.log('🎯 User clicked Generate AI Instructions button');
                            // DO NOT set analysisApprovedRef.current = true here!
                            // This would bypass the cost check and warning modal
                            // Let loadAIPrompts handle the cost check properly
                            
                            // CRITICAL FIX: isMountedRef might be false due to component lifecycle
                            // Temporarily set it to true since user is actively clicking
                            const wasMounted = isMountedRef.current;
                            isMountedRef.current = true;
                            console.log('🔧 Temporarily set isMountedRef to true (was:', wasMounted, ')');
                            
                            await loadAIPrompts();
                          }}
                          disabled={loadingPrompts}
                          className="w-full px-4 py-2 text-white text-sm rounded-lg transition-all font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
                          style={{
                            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #a855f7 75%, #ec4899 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 8s linear infinite'
                          }}
                        >
                          {loadingPrompts ? (
                            <>
                              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                              Analyzing...
                            </>
                          ) : (
                            <>
                              🤖 Generate AI Animation Instructions (Free)
                            </>
                          )}
                        </button>
                      </div>
                    ) : selectedImage && preSelectedImage ? (
                      // When in auto-trigger mode (preSelectedImage), show a subtle message
                      <div className="flex flex-col items-center justify-center py-4">
                        <div className="text-xs text-gray-500 italic text-center">
                          AI prompts will appear here automatically
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              
              {/* Duration Setting - Moved below Animation Instructions */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-4 sm:p-5 border-2 border-purple-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-500 rounded-lg flex items-center justify-center shadow-md">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm">{t("animation.duration")}</h4>
                </div>
                <select
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value))}
                  disabled={false}
                  className="w-full px-3 py-2.5 bg-white border-2 border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all cursor-pointer hover:border-gray-400"
                  title="Vidu supports 4 or 8 seconds"
                >
                  <option value={4}>4 seconds (5 credits - Recommended)</option>
                  <option value={8}>8 seconds (10 credits)</option>
                </select>
              </div>
 
            </div>
          </div>
        </div>
        
        {/* Footer - Always visible with iOS safe area support */}
        <div className="flex-shrink-0 p-3 sm:p-6 pb-6 sm:pb-6 border-t border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 backdrop-blur-sm" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
            <button
              onClick={onClose}
              className="px-4 sm:px-6 py-2 sm:py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-lg sm:rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all text-sm sm:text-base order-2 sm:order-1 shadow-sm"
            >
              Cancel
            </button>
            
            <div className="text-xs sm:text-sm text-gray-700 font-medium order-1 sm:order-2 flex items-center gap-2 bg-white px-3 sm:px-4 py-2 rounded-lg border border-purple-200">
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs sm:text-sm">💡</span>
              </div>
              <span className="text-xs sm:text-sm">{t('animation.pro_tip')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    {/* Image Editor Modal */}
    {showImageEditor && selectedImage && (
      <ImageEditorModalPortal
        image={selectedImage}
        context="animation"
        user={user}
        onClose={() => setShowImageEditor(false)}
        onSave={async (editingParams) => {
          try {
            console.log('Saving image edits:', editingParams);
            
            // If we have an edited image URL, update the selectedImage with the new edited version
            if (editingParams.editedImageUrl) {
              const timestamp = Date.now();
              
              // Create a completely new image object that forces React to re-render
              const newEditedImage = {
                id: `edited_${timestamp}`,
                name: `${selectedImage.name}_edited`,
                url: editingParams.editedImageUrl,
                thumbnail_url: editingParams.editedImageUrl,
                original_url: selectedImage.original_url || selectedImage.url,
                original_id: selectedImage.id,
                editedParams: editingParams,
                isEdited: true,
                editTimestamp: timestamp,
                // Force complete re-render by making this a completely new object
                _renderKey: `edited_image_${timestamp}_${Math.random()}`,
                // Preserve other properties
                projectName: selectedImage.projectName,
                description: selectedImage.description ? `${selectedImage.description} (edited)` : 'Edited image'
              };
              
              console.log('Creating completely new edited image object:', {
                id: newEditedImage.id,
                name: newEditedImage.name,
                isEdited: newEditedImage.isEdited,
                renderKey: newEditedImage._renderKey,
                urlType: newEditedImage.url.startsWith('data:') ? 'data URL' : 'regular URL'
              });
              
              // Force complete re-render by clearing state with a delay
              setSelectedImage(null);
              
              // Use a longer timeout to ensure complete state clearing
              setTimeout(() => {
                // Force a complete re-render by setting a new object with unique key
                const forceRerenderImage = {
                  ...newEditedImage,
                  _forceRerender: Date.now() + Math.random(),
                  key: `edited_${timestamp}_${Math.random()}`
                };
                setSelectedImage(forceRerenderImage);
                
                // Force immediate DOM update and re-analyze image
                requestAnimationFrame(() => {
                  // Trigger a custom event after DOM update
                  const event = new CustomEvent('imageEdited', { 
                    detail: { 
                      editedImageId: newEditedImage.id,
                      timestamp: timestamp,
                      isRotated: editingParams.rotation !== 0,
                      isCropped: editingParams.cropWidth < 100 || editingParams.cropHeight < 100
                    }
                  });
                  window.dispatchEvent(event);
                  console.log('🔄 Image edit completed - new image object created');
                  toast.success('Image adjustments applied to preview! ✨');
                  
                  // Re-analyze the edited image for new AI prompts
                  setTimeout(() => {
                    console.log('🤖 Re-analyzing edited image for new AI prompts...');
                    toast.info('Re-analyzing edited image for new suggestions...');
                    loadAIPrompts();
                  }, 500);
                });
              }, 100);
            }
            
            setShowImageEditor(false);
          } catch (error) {
            console.error('Error applying edits:', error);
            toast.error('Failed to apply image adjustments');
          }
        }}
      />
    )}
    </>
  );
};

// New project modal with memory theme
const MemoryNewProjectModal = ({ onClose, onRefresh }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('user_id', user.id);
      
      formData.append('user_id', user.id);
      await axios.post(`${getAPI()}/projects`, formData);
      toast.success('Memory collection created! Time to preserve precious moments ✨✨');
      onClose();
      onRefresh();
    } catch (error) {
      toast.error('Failed to create memory collection');
    } finally {
      setIsLoading(false);
    }
  };

  const projectSuggestions = [
    "Family Reunion 2024",
    "Childhood Adventures",
    "Wedding Memories",
    "Vacation Highlights",
    "Grandparents' Legacy",
    "School Years Collection"
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl max-w-md w-full p-6 border border-amber-200 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-start space-x-3">
            <Logo className="w-8 h-8 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-amber-800">Create Memory Collection</h2>
              <p className="text-amber-600 text-sm">Start preserving precious moments</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-amber-800 mb-2">Memory Collection Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-amber-800 placeholder-amber-500"
              placeholder="e.g., Family Summer 2024"
              data-testid="project-name-input"
              required
            />
          </div>
          
          <div>
            <p className="text-sm text-amber-700 mb-2">Quick suggestions:</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {projectSuggestions.slice(0, 4).map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setName(suggestion)}
                  className="text-xs bg-amber-200 hover:bg-amber-300 text-amber-700 px-3 py-1 rounded-full transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-amber-800 mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all resize-none text-amber-800 placeholder-amber-500"
              placeholder="What makes this collection special? Describe the memories you'll preserve..."
              data-testid="project-description-input"
            />
          </div>
          
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 p-4 rounded-2xl border border-amber-200">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-amber-300 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg className="w-3 h-3 text-amber-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-medium text-amber-800 mb-1">Memory Collection Tips</h4>
                <p className="text-sm text-amber-700">
                  Create separate collections for different events or time periods. 
                  You can invite friends and family members to collaborate and add their own memories!
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-4 text-amber-700 bg-white border border-amber-200 rounded-2xl hover:bg-amber-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white rounded-2xl hover:from-amber-600 hover:via-orange-600 hover:to-red-600 transition-all disabled:opacity-50 font-medium shadow-lg"
              data-testid="create-project-button"
            >
              {isLoading ? 'Creating...' : '✨ Create Collection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Album Management Modal
const AlbumModal = ({ item, projects, user, onClose, onRefresh }) => {
  const [selectedAlbum, setSelectedAlbum] = useState(item.album || '');
  const [newAlbumName, setNewAlbumName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [collaborativeAlbums, setCollaborativeAlbums] = useState([]);
  const [loadingAlbums, setLoadingAlbums] = useState(true);
  
  // Fetch collaborative albums on mount
  useEffect(() => {
    const fetchCollaborativeAlbums = async () => {
      try {
        setLoadingAlbums(true);
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.get(`${getAPI()}/albums?user_id=${user.id}`);
        const albums = response.data.albums || [];  // Changed from response.data to response.data.albums
        
        // Show ALL albums - both owned and collaborated
        // User can add their moments to any album they have access to
        const accessibleAlbums = albums.filter(album => {
          const isOwner = album.owner_id === user.id;
          const isCollaborator = album.collaborators && album.collaborators.some(c => 
            c.user_id === user.id || c.userId === user.id
          );
          return isOwner || isCollaborator; // Show if owner OR collaborator
        });
        
        setCollaborativeAlbums(accessibleAlbums);
        console.log('Fetched albums:', albums);
        console.log('Accessible albums:', accessibleAlbums);
      } catch (error) {
        console.error('Error fetching collaborative albums:', error);
        toast.error('Failed to load collaborative albums');
      } finally {
        setLoadingAlbums(false);
      }
    };
    
    fetchCollaborativeAlbums();
  }, []);
  
  const handleSave = async () => {
    const albumName = selectedAlbum === 'new' ? newAlbumName.trim() : selectedAlbum;
    
    if (!albumName) {
      toast.error('Please select or create an album');
      return;
    }
    
    setIsLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('album', albumName);
      
      if (item.type === 'photo') {
        formData.append('user_id', user.id);
        await axios.put(`${getAPI()}/projects/${item.projectId}/images/${item.id}`, formData);
      } else {
        formData.append('user_id', user.id);
        await axios.put(`${getAPI()}/video-generations/${item.id}`, formData);
      }
      
      toast.success(`Added to "${albumName}" album! 📁✨`);
      onRefresh();
      onClose();
    } catch (error) {
      toast.error('Failed to add to album');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[70]" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                Add to Collaborative Album
              </h3>
              <p className="text-gray-600 mt-1 text-sm">Share your {(item.type === 'photo' || item.type === 'image') ? 'picture' : 'living moment'} with others</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors shadow-md"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Item preview */}
          <div className="mb-6">
            <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200 flex items-center justify-center shadow-md">
              {(item.type === 'photo' || item.type === 'image') ? (
                <img loading="lazy"
                  src={item.url || item.thumbnail_url || item.imageUrl || `/api/files/${item.image_id}`}
                  alt={item.name}
                  className="max-w-full max-h-full object-cover"
                />
              ) : (
                (item.result_url && isValidVideoUrl(item.result_url)) ? (
                  <VideoErrorBoundary fallback={
                    <img loading="lazy"
                      src={item.thumbnail_url}
                      alt="Video thumbnail"
                      className="max-w-full max-h-full object-cover"
                    />
                  }>
                    <video
                      preload="none"
                      src={item.result_url}
                      className="max-w-full max-h-full object-cover"
                      poster={item.thumbnail_url}
                      preload="none"
                      onError={(e) => {
                        console.warn('Modal video error:', item.result_url);
                        e.target.style.display = 'none';
                      }}
                    />
                  </VideoErrorBoundary>
                ) : (
                  (item.imageUrl || item.thumbnail_url || item.url) ? (
                    <img loading="lazy"
                      src={item.imageUrl || item.thumbnail_url || item.url || `/api/files/${item.image_id}`}
                      alt="Video thumbnail"
                      className="max-w-full max-h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="flex flex-col items-center justify-center text-gray-600 py-8"><div class="text-4xl mb-2">🎥</div><div class="text-sm">Processing Video</div></div>';
                      }}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-600 py-8">
                      <div className="text-4xl mb-2">🎥</div>
                      <div className="text-sm">Processing Video</div>
                    </div>
                  )
                )
              )}
            </div>
            <p className="text-sm text-gray-700 mt-3 font-medium">
              {item.name || item.parameters?.prompt || 'Untitled'}
            </p>
          </div>
          
          {/* Album selection */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Choose Collaborative Album</label>
              
              {loadingAlbums ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-gray-600 mt-3 text-sm">Loading albums...</p>
                </div>
              ) : collaborativeAlbums.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="text-gray-600 font-medium">No collaborative albums yet</p>
                  <p className="text-gray-500 text-sm mt-1">You need to be invited to an album first</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                  {collaborativeAlbums.map((album) => (
                    <label key={album.id} className="flex items-center p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg hover:from-purple-100 hover:to-blue-100 transition-colors cursor-pointer border-2 border-transparent hover:border-purple-300">
                      <input
                        type="radio"
                        name="album"
                        value={album.title}
                        checked={selectedAlbum === album.title}
                        onChange={(e) => setSelectedAlbum(e.target.value)}
                        className="mr-3 w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex-1">
                        <span className="text-gray-800 font-medium">{album.title}</span>
                        {album.description && (
                          <p className="text-xs text-gray-600 mt-0.5">{album.description}</p>
                        )}
                      </div>
                      <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        👥 {album.collaborators?.length || 0}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-2 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !selectedAlbum || loadingAlbums || collaborativeAlbums.length === 0}
              className="flex-1 px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg"
            >
              {isLoading ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Adding...</span>
                </span>
              ) : (
                'Add to Album'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Memory Edit Modal - Edit photos and living memories
const MemoryEditModal = ({ memory, onClose, onRefresh }) => {
  const [editData, setEditData] = useState({
    name: memory.name || memory.parameters?.prompt || '',
    description: memory.description || '',
    tags: memory.tags || [],
    album: memory.album || ''
  });
  const [videoDurations, setVideoDurations] = useState({});
  
  // Helper function to get actual video duration (local copy)
  const getVideoDuration = (videoUrl, jobId) => {
    if (videoDurations[jobId]) {
      return videoDurations[jobId];
    }
    
    if (videoUrl && isValidVideoUrl(videoUrl)) {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.onloadedmetadata = () => {
        const duration = Math.round(video.duration);
        setVideoDurations(prev => ({ ...prev, [jobId]: duration }));
      };
    }
    
    return null; // Return null while loading
  };
  const [newTag, setNewTag] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSave = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      Object.keys(editData).forEach(key => {
        if (key === 'tags') {
          formData.append(key, JSON.stringify(editData[key]));
        } else {
          formData.append(key, editData[key]);
        }
      });
      
      // Update based on memory type
      if (memory.type === 'photo') {
        formData.append('user_id', user.id);
        await axios.put(`${getAPI()}/projects/${memory.projectId}/images/${memory.id}`, formData);
      } else {
        formData.append('user_id', user.id);
        await axios.put(`${getAPI()}/video-generations/${memory.id}`, formData);
      }
      
      toast.success('Memory updated successfully! ✨');
      onRefresh();
      onClose();
    } catch (error) {
      toast.error('Failed to update memory');
    } finally {
      setIsLoading(false);
    }
  };
  
  const addTag = () => {
    if (newTag.trim() && !editData.tags.includes(newTag.trim())) {
      setEditData({
        ...editData,
        tags: [...editData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };
  
  const removeTag = (tagToRemove) => {
    setEditData({
      ...editData,
      tags: editData.tags.filter(tag => tag !== tagToRemove)
    });
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-amber-200 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-amber-200 bg-gradient-to-r from-amber-100 to-orange-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-amber-800">
                {memory.type === 'photo' ? '✨ Edit Memory' : '✨ Edit Living Moment'}
              </h3>
              <p className="text-amber-600 mt-1">Update details and organize your memory</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Preview */}
          <div className="mb-6">
            <div className="aspect-video bg-amber-100 rounded-xl overflow-hidden border border-amber-200">
              {memory.type === 'photo' ? (
                <img loading="lazy"
                  src={memory.url || memory.thumbnail_url}
                  alt={memory.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                (memory.result_url && isValidVideoUrl(memory.result_url)) ? (
                  <VideoErrorBoundary fallback={
                    <img loading="lazy"
                      src={memory.thumbnail_url || memory.imageUrl}
                      alt="Video thumbnail"
                      className="w-full h-full object-cover"
                    />
                  }>
                    <video
                      preload="none"
                      src={memory.result_url}
                      className="w-full h-full object-cover"
                      controls
                      poster={memory.thumbnail_url}
                      preload="none"
                      onError={(e) => {
                        console.warn('Memory modal video error:', memory.result_url);
                        e.target.style.display = 'none';
                      }}
                    />
                  </VideoErrorBoundary>
                ) : (
                  <img loading="lazy"
                    src={memory.imageUrl || memory.thumbnail_url}
                    alt="Video thumbnail"
                    className="w-full h-full object-cover"
                  />
                )
              )}
            </div>
          </div>
          
          {/* Edit Form */}
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-2">
                {memory.type === 'photo' ? 'Photo Name' : 'Video Title'}
              </label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                placeholder="Enter a memorable name..."
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-2">Description</label>
              <textarea
                value={editData.description}
                onChange={(e) => setEditData({...editData, description: e.target.value})}
                rows={3}
                className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all resize-none"
                placeholder="Share the story behind this memory..."
              />
            </div>
            
            {/* Album */}
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-2">Album</label>
              <input
                type="text"
                value={editData.album}
                onChange={(e) => setEditData({...editData, album: e.target.value})}
                className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                placeholder="Add to album (e.g., Family Vacation 2024)"
              />
            </div>
            
            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-2">Tags</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {editData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-amber-200 text-amber-800 px-3 py-1 rounded-full text-sm flex items-center space-x-2"
                  >
                    <span>{tag}</span>
                    <button
                      onClick={() => removeTag(tag)}
                      className="text-amber-600 hover:text-amber-800"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  className="flex-1 px-4 py-2 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                  placeholder="Add a tag..."
                />
                <button
                  onClick={addTag}
                  className="bg-amber-500 text-white px-4 py-2 rounded-xl hover:bg-amber-600 transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
          
          {/* Memory Info */}
          <div className="mt-6 p-4 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl border border-amber-200">
            <h4 className="text-sm font-semibold text-amber-800 mb-2">Memory Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-amber-700">
              <div>
                <span className="font-medium">From Project:</span>
                <p>{memory.projectName}</p>
              </div>
              <div>
                <span className="font-medium">Created:</span>
                <p>{new Date(memory.created_at || memory.upload_timestamp).toLocaleString()}</p>
              </div>
              {memory.type !== 'photo' && (
                <>
                  <div>
                    <span className="font-medium">Duration:</span>
                    <p>{(() => {
                      const actualDuration = getVideoDuration(memory.result_url, memory.id);
                      return actualDuration ? `${actualDuration}s` : `${memory.parameters?.duration || 4}s`;
                    })()}</p>
                  </div>
                  <div>
                    <span className="font-medium">AI Provider:</span>
                    <p className="capitalize">{memory.provider || 'Vidu'}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="p-6 border-t border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 text-amber-700 bg-white border border-amber-200 rounded-xl hover:bg-amber-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !editData.name.trim()}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white rounded-xl hover:from-amber-600 hover:via-orange-600 hover:to-red-600 transition-all disabled:opacity-50 font-medium shadow-lg"
            >
              {isLoading ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </span>
              ) : (
                '✨ Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Collaborators modal 
const CollaboratorsModal = ({ project, onClose, onRefresh }) => {
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('viewer');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddCollaborator = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setIsAdding(true);
    try {
      const formData = new FormData();
      formData.append('email', newEmail);
      formData.append('role', newRole);

      formData.append('user_id', user.id);
      await axios.post(`${getAPI()}/projects/${project.id}/collaborators`, formData);
      toast.success('Family member invited to share memories! 👨‍👩‍👧‍👦');
      setNewEmail('');
      onRefresh();
    } catch (error) {
      toast.error('Failed to add collaborator');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-70" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl max-w-lg w-full p-6 border border-amber-200 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-amber-800">👨‍👩‍👧‍👦 Friends and Family Collaboration</h3>
            <p className="text-amber-600 text-sm">Invite friends and family to share in these precious memories</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Add New Collaborator */}
        <form onSubmit={handleAddCollaborator} className="mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Family member's email"
              className="sm:col-span-2 px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 text-amber-800"
              required
            />
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="px-4 py-3 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500 text-amber-800"
            >
              <option value="viewer">👁️ Viewer</option>
              <option value="editor">✏️ Editor</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={isAdding || !newEmail.trim()}
            className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all disabled:opacity-50"
          >
            {isAdding ? 'Inviting...' : '👨‍👩‍👧‍👦 Invite Family Member'}
          </button>
        </form>

        {/* Current Collaborators */}
        <div>
          <h4 className="font-medium text-amber-800 mb-3">Current Family Members</h4>
          <div className="space-y-3">
            {project.collaborators?.map((collab, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-white/60 rounded-xl border border-amber-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-amber-300 rounded-full flex items-center justify-center">
                    <span className="text-amber-800 font-medium text-sm">{collab.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-medium text-amber-800">{collab.name}</p>
                    <p className="text-xs text-amber-600">{collab.email}</p>
                  </div>
                </div>
                <span className="text-xs bg-amber-200 text-amber-700 px-2 py-1 rounded-full">
                  {collab.role === 'editor' ? '✏️ Editor' : '👁️ Viewer'}
                </span>
              </div>
            ))}
            
            {(!project.collaborators || project.collaborators.length === 0) && (
              <div className="text-center py-6 text-amber-600">
                <p>No family members added yet</p>
                <p className="text-sm">Invite your loved ones to share in these memories!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Share memory modal
const ShareMemoryModal = ({ project, onClose }) => {
  const socialPlatforms = [
    { id: 'facebook', name: 'Facebook', icon: '📘', credits: 5 },
    { id: 'instagram', name: 'Instagram', icon: '📷', credits: 5 },
    { id: 'tiktok', name: 'TikTok', icon: '🎵', credits: 10 },
    { id: 'twitter', name: 'Twitter', icon: '🐦', credits: 3 },
  ];

  const handleShare = async (platform) => {
    try {
      const formData = new FormData();
      formData.append('platform', platform.id);
      formData.append('project_id', project.id);

      formData.append('user_id', user.id);
      const response = await axios.post(`${getAPI()}/social-rewards/share`, formData);
      toast.success(`Share to ${platform.name} to earn ${platform.credits} free credits! 🎉`);
      
      // Simulate opening share dialog
      window.open(response.data.share_url, '_blank');
    } catch (error) {
      toast.error('Share failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-70" onClick={onClose}>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl max-w-md w-full p-6 border border-amber-200 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-amber-800">📤 Share Memories</h3>
            <p className="text-amber-600 text-sm">Spread the joy and earn free credits!</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-3">
          {socialPlatforms.map((platform) => (
            <button
              key={platform.id}
              onClick={() => handleShare(platform)}
              className="w-full flex items-center justify-between p-4 bg-white/60 hover:bg-white/80 rounded-xl border border-amber-200 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{platform.icon}</span>
                <span className="font-medium text-amber-800">Share to {platform.name}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-green-600">+{platform.credits} credits</span>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl border border-amber-200">
          <p className="text-sm text-amber-700">
            💡 <strong>Earn more credits:</strong> Share your living memories with friends and family to earn free credits for creating more time travels!
          </p>
        </div>
      </div>
    </div>
  );
};

// Frame Preview Demo Videos - Specific Videos from Landing Page
const FRAME_PREVIEW_VIDEOS = [
  { 
    id: 'frame-demo-1', 
    video_url: '/landing-assets/demo1-left-video.mp4',
    thumbnail_url: '/landing-assets/demo1-left-image.jpg'
  },
  { 
    id: 'frame-demo-2', 
    video_url: '/landing-assets/demo2-middle-video.mp4',
    thumbnail_url: '/landing-assets/demo2-middle-image.jpg'
  },
  { 
    id: 'frame-demo-3', 
    video_url: '/landing-assets/demo3-right-video.mp4',
    thumbnail_url: '/landing-assets/demo3-right-image.jpg'
  },
  { 
    id: 'frame-demo-4', 
    video_url: '/landing-assets/demo4-family-video.mp4',
    thumbnail_url: '/landing-assets/demo4-family-image.jpg'
  }
];

// Mini Frame Preview Component for Dashboard Tile
const MiniFramePreview = ({ videos, isDemo = false, totalUserVideos = 0 }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = React.useState(0);
  const videoRef = React.useRef(null);

  // Helper to detect R2 presigned URLs
  const isPresignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  };

  // Helper function to add auth params to thumbnail URLs
  const getAuthenticatedThumbnailUrl = (thumbnailUrl) => {
    if (!thumbnailUrl) return thumbnailUrl;
    
    // NEVER modify presigned URLs - they have cryptographic signatures
    if (isPresignedUrl(thumbnailUrl)) {
      console.log('⚠️ Detected presigned thumbnail URL, returning as-is');
      return thumbnailUrl;
    }
    
    // If URL already has user_id, return as-is
    if (thumbnailUrl.includes('user_id=')) {
      return thumbnailUrl;
    }
    
    // Get user from localStorage for demo component
    const storedUser = localStorage.getItem('effectiveUserId');
    if (!storedUser) return thumbnailUrl;
    
    // Add user_id parameter
    const separator = thumbnailUrl.includes('?') ? '&' : '?';
    return `${thumbnailUrl}${separator}user_id=${storedUser}`;
  };

  React.useEffect(() => {
    if (videos.length === 0) return;
    
    // Cycle through videos every 3 seconds
    const interval = setInterval(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % videos.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [videos.length]);

  // Ensure video plays when it changes (Safari fix)
  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn('MiniFramePreview autoplay prevented:', err);
        });
      }
    }
  }, [currentVideoIndex]);

  if (videos.length === 0) return null;

  const currentVideo = videos[currentVideoIndex];
  // Normalize field names (demo videos use video_url, real videos use result_url)
  const videoUrl = currentVideo?.result_url || currentVideo?.video_url;

  return (
    <div className="relative w-32 h-48 group-hover:scale-110 transition-transform duration-300" style={{ perspective: '1000px' }}>
      {/* Frame Border (White Outline) - Portrait orientation with rotating animation */}
      <div className="absolute inset-0 rounded-lg border-4 border-white/90 shadow-2xl bg-gradient-to-br from-gray-800 to-black" style={{ animation: 'rotateFrame 6s ease-in-out infinite' }}>
        {/* Inner Border */}
        <div className="absolute inset-1 border-2 border-white/30 rounded-sm"></div>
        
        {/* Video Display Area */}
        <div className="absolute inset-3 bg-black rounded-sm overflow-hidden">
          {videoUrl ? (
            <video
              ref={videoRef}
              key={videoUrl}
              src={videoUrl}
              className="w-full h-full object-cover"
              preload="auto"
              autoPlay
              loop
              muted
              playsInline
              onCanPlay={(e) => {
                e.target.play().catch(err => console.warn('Mini frame video play failed:', err));
              }}
            />
          ) : currentVideo?.thumbnail_url ? (
            <img
              src={getAuthenticatedThumbnailUrl(currentVideo.thumbnail_url)}
              alt="Preview"
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/50 text-xs">
              Video Preview
            </div>
          )}
        </div>
        
        {/* Video Counter Badge */}
        <div className="absolute -bottom-2 -right-2 bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full border-2 border-white shadow-lg">
          {isDemo ? (
            'demo'
          ) : (
            `${currentVideoIndex + 1}/${totalUserVideos}`
          )}
        </div>
      </div>
      
      {/* Shimmer Effect */}
      <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
          style={{
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s infinite'
          }}
        ></div>
      </div>
    </div>
  );
};

// Main App Component with memory theme
function App() {
  // Verbose log removed for production
  const { t } = useTranslation();
  
  // PHASE 3: Media Cache hook - Store in ref so closures can access it
  const cacheRef = useRef(null);
  
  try {
    // Verbose log removed for production
  // Expose cache globally as a safe fallback for closures defined before hook init
  React.useEffect(() => {
    try {
      if (cacheRef && cacheRef.current) {
        window.__MM_MEDIA_CACHE__ = cacheRef.current;
        // Verbose log removed for production
      }
    } catch (e) {
      console.warn('Unable to register global cache fallback');
    }
  }, [cacheRef?.current]);

    const mediaCache = useMediaCache();
    // Verbose log removed for production
    
    if (mediaCache) {
      cacheRef.current = mediaCache;
      // Verbose log removed for production
    } else {
      console.error('❌❌❌ mediaCache is null/undefined - cache disabled');
    }
  } catch (error) {
    console.error('❌❌❌ EXCEPTION initializing cache hook:', error);
  }
  
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLandingPage, setShowLandingPage] = useState(true); // Show landing page by default
  // const [showComingSoonGate, setShowComingSoonGate] = useState(false); // REMOVED - Direct access enabled
  const [activeMainTab, setActiveMainTab] = useState('moments');
  const [showProfile, setShowProfile] = useState(false);
  const [showCreditsDropdown, setShowCreditsDropdown] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showEarnCredits, setShowEarnCredits] = useState(false);
  const [showRedeemCredits, setShowRedeemCredits] = useState(false);
  const [showGiftCheckout, setShowGiftCheckout] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showAccessRequestSentModal, setShowAccessRequestSentModal] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showOrdersDashboard, setShowOrdersDashboard] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showMagicProcessing, setShowMagicProcessing] = useState(false);
  const [currentView, setCurrentView] = useState('shop'); // 'shop', 'pictures', 'moments', 'albums', 'my-orders', 'mail-in-service'
  const [myOrders, setMyOrders] = useState({ shopify_orders: [], mail_in_orders: [], credit_purchases: [], video_history: [], total_orders: 0 });
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showReferralDashboard, setShowReferralDashboard] = useState(false);
  
  // Gift preview modals
  const [showMailInGiftPreview, setShowMailInGiftPreview] = useState(false);
  const [showMailInGiftOccasion, setShowMailInGiftOccasion] = useState(false);
  const [mailInGiftOccasion, setMailInGiftOccasion] = useState('');
  const [showCreditsGiftPreview, setShowCreditsGiftPreview] = useState(false);
  const [showGiftCardPurchaseModal, setShowGiftCardPurchaseModal] = useState(false);
  
  // Mail-in service checkout state
  const [showMailInCheckout, setShowMailInCheckout] = useState(false);
  const [selectedMailInPackage, setSelectedMailInPackage] = useState(null);
  const [showWhatsNextModal, setShowWhatsNextModal] = useState(false);
  const processedMailInSessionRef = useRef(null); // Track processed Stripe sessions
  const [mailInOrders, setMailInOrders] = useState([]);
  const [loadingMailInOrders, setLoadingMailInOrders] = useState(false);
  
  // Track Your Order section state (for mail-in service landing page)
  const [trackingOrders, setTrackingOrders] = useState([]);
  const [loadingTrackingOrders, setLoadingTrackingOrders] = useState(false);
  const hasFetchedTrackingOrders = useRef(false);
  const loadingTimerRef = useRef(null);
  
  // Fetch active tracking orders for Track Your Order section
  const fetchTrackingOrders = async () => {
    if (!user) {
      console.log('❌ Cannot fetch tracking orders: no user');
      hasFetchedTrackingOrders.current = false;
      return;
    }
    
    try {
      console.log('🔄 Fetching tracking orders for user:', user.email);
      
      // Only show loading spinner if fetch takes longer than 300ms
      loadingTimerRef.current = setTimeout(() => {
        setLoadingTrackingOrders(true);
      }, 300);
      const url = `${getAPI()}/mailin-service/orders?user_id=${user.id}&t=${Date.now()}`;
      console.log('📡 Fetching from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        cache: 'no-store'
      });
      
      console.log('📬 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Tracking orders received:', data.orders?.length || 0, 'orders');
        
        // Filter to show only active/recent orders (not completed or cancelled)
        const activeOrders = (data.orders || []).filter(order => {
          const isCancelled = order.status === 'cancelled' || order.fulfillment_status === 'cancelled';
          const isCompleted = order.status === 'completed' || order.status === 'photos_returned' || order.fulfillment_status === 'completed';
          
          console.log(`Order ${order.order_number}: status=${order.status}, fulfillment=${order.fulfillment_status}, cancelled=${isCancelled}, completed=${isCompleted}`);
          
          return !isCancelled && !isCompleted;
        });
        
        console.log('📦 Active tracking orders:', activeOrders.length);
        setTrackingOrders(activeOrders);
      } else {
        console.error('❌ Failed to fetch tracking orders:', response.status, response.statusText);
        setTrackingOrders([]);
      }
    } catch (error) {
      console.error('❌ Error fetching tracking orders:', error);
      setTrackingOrders([]);
    } finally {
      // Clear the loading timer and set loading to false
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setLoadingTrackingOrders(false);
    }
  };
  
  // IMMEDIATELY call useEffect after function definition - Rules of Hooks
  useEffect(() => {
    console.log('🔍 Track Your Order useEffect triggered:', { user: !!user, currentView, hasFetched: hasFetchedTrackingOrders.current });
    if (user && currentView === 'mail-in-service' && !hasFetchedTrackingOrders.current) {
      console.log('✅ Fetching tracking orders for Track Your Order section...');
      hasFetchedTrackingOrders.current = true;
      fetchTrackingOrders();
    } else {
      console.log('❌ Not fetching tracking orders from Track section:', { hasUser: !!user, currentView, hasFetched: hasFetchedTrackingOrders.current });
    }
  }, [user, currentView]);
  
  // Credit purchase animation state
  const [showCreditAnimation, setShowCreditAnimation] = useState(false);
  const [purchasedCredits, setPurchasedCredits] = useState(0);
  const [isWelcomeAnimation, setIsWelcomeAnimation] = useState(false);
  const processedCreditPurchaseRef = useRef(null); // Track processed sessions
  
  // Album refresh callback - defined here in App so it's accessible to ImageEditorModal
  const albumRefreshCallback = React.useRef(null);
  const albumFilterSwitchCallback = React.useRef(null);
  
  // Check for pending credit animation from gift redemption or welcome credits
  useEffect(() => {
    const pendingAnimation = localStorage.getItem('pendingCreditAnimation');
    if (pendingAnimation) {
      try {
        const data = JSON.parse(pendingAnimation);
        // Only trigger if less than 30 seconds old (prevent stale animations)
        if (Date.now() - data.timestamp < 30000) {
          console.log('🎁 Triggering credit animation:', data.credits, 'isWelcome:', data.isWelcome);
          localStorage.removeItem('pendingCreditAnimation');
          setPurchasedCredits(data.credits);
          setIsWelcomeAnimation(data.isWelcome || false);
          setShowCreditAnimation(true);
        } else {
          localStorage.removeItem('pendingCreditAnimation');
        }
      } catch (err) {
        console.error('Error parsing pending credit animation:', err);
        localStorage.removeItem('pendingCreditAnimation');
      }
    }
  }, []);

  // Listen for immediate credit animation trigger from gift redemption or welcome credits
  useEffect(() => {
    const handleCreditAnimation = (event) => {
      console.log('🎁 Received triggerCreditAnimation event:', event.detail);
      if (event.detail && event.detail.credits > 0) {
        setPurchasedCredits(event.detail.credits);
        setIsWelcomeAnimation(event.detail.isWelcome || false);
        
        // Add URL param to indicate this is a gift redemption for the animation component
        if (event.detail.isGift) {
          const url = new URL(window.location);
          url.searchParams.set('gift_redeem', 'true');
          window.history.replaceState({}, '', url);
        }
        
        setShowCreditAnimation(true);
      }
    };

    window.addEventListener('triggerCreditAnimation', handleCreditAnimation);
    
    return () => {
      window.removeEventListener('triggerCreditAnimation', handleCreditAnimation);
    };
  }, []);
  
  // Capture referral code and OAuth response from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    const error = urlParams.get('error');
    const authSuccess = urlParams.get('auth_success');
    const userParam = urlParams.get('user');
    const tokenParam = urlParams.get('token');
    
    console.log('🔍 OAuth Check:', { authSuccess, hasUser: !!userParam, hasToken: !!tokenParam });
    
    // Handle OAuth success with session token (from Facebook login)
    const sessionTokenParam = urlParams.get('session_token');
    const isNewSignup = urlParams.get('is_new_signup') === 'true';
    const welcomeCredits = parseInt(urlParams.get('welcome_credits')) || 0;
    
    if (authSuccess === 'true' && sessionTokenParam) {
      console.log('🎯 Processing Facebook OAuth login with session token...');
      
      // If new signup, set up welcome credits animation
      if (isNewSignup && welcomeCredits > 0) {
        localStorage.setItem('pendingCreditAnimation', JSON.stringify({
          credits: welcomeCredits,
          isGift: false,
          isWelcome: true,
          timestamp: Date.now()
        }));
        console.log('🎉 Set pending welcome credit animation for OAuth:', welcomeCredits, 'credits');
      }
      
      // Fetch user data from backend using session token
      const fetchUserData = async () => {
        try {
          const BACKEND_URL = getBACKEND_URL();
          const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${sessionTokenParam}`
            }
          });
          
          if (response.data.success && response.data.user) {
            const userData = response.data.user;
            console.log('📦 Fetched user data:', userData.email, userData.id);
            
            // Store user data and session token in localStorage
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('effectiveUserId', userData.id);
            localStorage.setItem('authenticatedUserId', userData.id);
            localStorage.setItem('session_token', sessionTokenParam);
            console.log('✅ Facebook OAuth login successful');
            
            // Clean URL to remove auth params
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Reload to trigger user state update
            console.log('🔄 Reloading page to apply login...');
            window.location.reload();
          } else {
            throw new Error('Invalid user data received');
          }
        } catch (e) {
          console.error('❌ Failed to fetch user data:', e);
          toast.error('Login failed. Please try again.');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      };
      
      fetchUserData();
      return; // Exit early to prevent other logic from running
    }
    
    // Handle OAuth success (from Google/Facebook login - old format)
    if (authSuccess === 'true' && userParam && tokenParam) {
      console.log('🎯 Processing OAuth login...');
      
      // If new signup, set up welcome credits animation
      if (isNewSignup && welcomeCredits > 0) {
        localStorage.setItem('pendingCreditAnimation', JSON.stringify({
          credits: welcomeCredits,
          isGift: false,
          isWelcome: true,
          timestamp: Date.now()
        }));
        console.log('🎉 Set pending welcome credit animation for OAuth:', welcomeCredits, 'credits');
      }
      
      try {
        const userData = JSON.parse(decodeURIComponent(userParam));
        console.log('📦 Parsed user data:', userData.email, userData.id);
        
        // Store user data in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('effectiveUserId', userData.id);
        localStorage.setItem('authenticatedUserId', userData.id);
        console.log('✅ OAuth login successful, user data stored in localStorage');
        
        // Clean URL to remove auth params
        window.history.replaceState({}, document.title, window.location.pathname);
        
        console.log('🔄 Reloading page to apply login...');
        // Reload to trigger user state update
        window.location.reload();
      } catch (e) {
        console.error('❌ Failed to process OAuth response:', e);
        toast.error('Login failed. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      return; // Exit early to prevent other logic from running
    }
    
    // Handle OAuth errors
    if (error === 'google_auth_failed') {
      toast.error('Google authentication failed. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Handle referral code
    if (refCode) {
      // Store in localStorage for signup
      localStorage.setItem('referral_code', refCode);
      console.log('📣 Referral code captured:', refCode);
      
      // Clean URL (optional - removes ?ref= from address bar)
      if (!error) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, []);
  
  // Expose tab switching to InviteAcceptHandler via window
  useEffect(() => {
    window.switchToAlbumsTab = () => {
      console.log('🔗 Switching to albums tab via window.switchToAlbumsTab');
      setCurrentView('albums');
      setActiveMainTab('collaborate');
    };
    
    return () => {
      delete window.switchToAlbumsTab;
    };
  }, []);
  const [ordersTab, setOrdersTab] = useState('frames'); // 'frames' or 'mailin'
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingCount, setProcessingCount] = useState(false);
  const [showFrameDemo, setShowFrameDemo] = useState(false);
  
  // DEBUG: Track showFrameDemo state changes
  useEffect(() => {
    console.log('🔄 showFrameDemo changed:', showFrameDemo);
    if (showFrameDemo) {
      console.log('✅ Modal should be VISIBLE now');
    } else {
      console.log('❌ Modal should be HIDDEN now');
    }
  }, [showFrameDemo]);
  
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [preSelectedFrameColor, setPreSelectedFrameColor] = useState(null);
  const [preSelectedFrameProduct, setPreSelectedFrameProduct] = useState(null);
  const [isGiftMode, setIsGiftMode] = useState(false);
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [giftCardProduct, setGiftCardProduct] = useState(null);
  const [loadCartFrameItem, setLoadCartFrameItem] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [showToastFlag, setShowToastFlag] = useState(false);
  
  // Ref for file input element
  const fileInputRef = useRef();
  
  // Image editor state
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  
  // Image editor handler
  const handleEditImage = (image) => {
    console.log('✏️ handleEditImage called with image:', {
      id: image?.id,
      url: image?.url,
      thumbnail_url: image?.thumbnail_url,
      album_id: image?.album_id,
      r2_key: image?.r2_key,
      fullImage: image
    });
    
    // For album assets, construct proper Cloudflare URL if available
    const BACKEND_URL = getBACKEND_URL();
    let finalUrl = image.url;
    let finalThumbnail = image.thumbnail_url;
    
    // Album assets use Cloudflare Images - use /api/files/ endpoint for editor access
    if (image.album_id && image.id) {
      const userId = user?.id || '';
      const originalAdmin = localStorage.getItem('originalAdmin');
      const authenticatedUserId = originalAdmin ? JSON.parse(originalAdmin).id : userId;
      // Use /api/files/ endpoint which supports both unassigned images and album assets
      finalUrl = `${BACKEND_URL}/api/files/${image.id}?user_id=${userId}&authenticated_user_id=${authenticatedUserId}&proxy=true`;
      console.log('📸 Constructed /api/files/ URL for album asset (proxy mode):', finalUrl);
    }
    
    const cleanImage = {
      ...image,
      url: finalUrl,
      thumbnail_url: finalThumbnail
    };
    
    setSelectedImage(cleanImage);
    setShowImageEditor(true);
  };
  
  // Restore location after reload (from image editor save)
  React.useEffect(() => {
    const returnPath = sessionStorage.getItem('returnToAfterReload');
    if (returnPath) {
      sessionStorage.removeItem('returnToAfterReload');
      // Use navigate from react-router if available, otherwise just update window location
      window.history.replaceState(null, '', returnPath);
    }
  }, []);
  
  // Toast notification function
  const showToast = (message) => {
    setToastMessage(message);
    setShowToastFlag(true);
    setTimeout(() => {
      setShowToastFlag(false);
      setTimeout(() => setToastMessage(null), 300); // Clear after fade out
    }, 2000);
  };
  
  // Helper to detect R2 presigned URLs
  const isPresignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Algorithm') || 
           url.includes('X-Amz-Signature') || 
           url.includes('X-Amz-Credential');
  };
  
  // Helper function to add auth params to thumbnail URLs
  const getAuthenticatedThumbnailUrl = (thumbnailUrl) => {
    if (!thumbnailUrl || !user?.id) return thumbnailUrl;
    
    // NEVER modify presigned URLs - they have cryptographic signatures
    if (isPresignedUrl(thumbnailUrl)) {
      console.log('⚠️ Detected presigned thumbnail URL, returning as-is');
      return thumbnailUrl;
    }
    
    // If URL already has user_id, return as-is
    if (thumbnailUrl.includes('user_id=')) {
      return thumbnailUrl;
    }
    
    // Add user_id parameter
    const separator = thumbnailUrl.includes('?') ? '&' : '?';
    return `${thumbnailUrl}${separator}user_id=${user.id}`;
  };
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showSupport) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showSupport]);

  // Check for pending album redirect (for logged-in users clicking invitation links)
  useEffect(() => {
    if (user) {
      const redirectAlbumId = localStorage.getItem('redirectToAlbum');
      if (redirectAlbumId && currentView !== 'albums') {
        console.log('🔗 Detected pending album redirect:', redirectAlbumId);
        // Switch to Albums tab
        setCurrentView('albums');
        setActiveMainTab('collaborate');
        // Don't remove the redirectAlbumId here - let AlbumsTab handle it
      }
    }
  }, [user, currentView]);

  // Global video error interceptor to prevent uncaught video loading errors
  useEffect(() => {
    const handleVideoErrors = (event) => {
      if (event.target && event.target.tagName === 'VIDEO') {
        const video = event.target;
        const error = video.error;
        
        // Only hide video if it's a permanent failure (media decode error, network error after retries)
        // Don't hide for transient errors that might recover
        if (error && (error.code === MediaError.MEDIA_ERR_DECODE || error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED)) {
          console.warn('Global video error intercepted (permanent failure):', video.src, error);
          video.style.display = 'none';
        } else {
          // Log transient errors but don't hide the video
          console.log('Video error (may recover):', video.src, error);
        }
        
        // Don't prevent default or stop propagation - let video handle its own errors
        // event.preventDefault();
        // event.stopPropagation();
      }
    };
    
    // Add global error listener for video elements
    document.addEventListener('error', handleVideoErrors, true);
    
    return () => {
      document.removeEventListener('error', handleVideoErrors, true);
    };
  }, []);

  // Load orders when navigating to my-orders view
  useEffect(() => {
    if (currentView === 'my-orders' && user?.id) {
      loadMyOrders();
    }
  }, [currentView, user?.id]);

  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔍 checkAuth starting...');
      try {
        // STEP 1: Check for OAuth callback (session_id in URL fragment)
        const urlFragment = window.location.hash;
        if (urlFragment.includes('session_id=')) {
          console.log('🔐 OAuth callback detected');
          const sessionId = urlFragment.split('session_id=')[1].split('&')[0];
          
          if (sessionId) {
            // Show loading state
            setIsLoading(true);
            
            try {
              // Process session_id with backend
              const response = await axios.post(
                `${getAPI()}/auth/google/session-data`,
                {},
                {
                  headers: {
                    'X-Session-ID': sessionId
                  }
                }
              );
              
              // Store user and session token
              const userData = response.data.user;
              const sessionToken = response.data.session_token;
              
              localStorage.setItem('user', JSON.stringify(userData));
              localStorage.setItem('session_token', sessionToken);
              
              setUser(userData);
              setShowLandingPage(false);
              
              // Clean URL fragment
              window.history.replaceState(null, '', window.location.pathname);
              
              toast.success('Welcome! Signed in with Google ✨');
              setIsLoading(false);
              return;
            } catch (error) {
              console.error('OAuth session error:', error);
              const errorMessage = error.response?.data?.detail || error.message || 'Google sign-in failed';
              toast.error(`Google sign-in failed: ${errorMessage}`);
              window.history.replaceState(null, '', window.location.pathname);
              setIsLoading(false);
              setShowLandingPage(true);
              return;
            }
          }
        }
        
        // STEP 2: Check if session_token exists (existing session)
        const sessionToken = localStorage.getItem('session_token');
        console.log('🎫 Session token:', sessionToken ? 'exists' : 'none');
        if (sessionToken) {
          try {
            const response = await axios.get(`${getAPI()}/auth/session/validate`, {
              headers: {
                'Authorization': `Bearer ${sessionToken}`
              }
            });
            
            console.log('✅ Session validated');
            setUser(response.data.user);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            setShowLandingPage(false);
            setIsLoading(false);
            return;
          } catch (error) {
            console.log('❌ Session invalid/expired');
            // Session invalid/expired, clear it
            localStorage.removeItem('session_token');
            localStorage.removeItem('user');
          }
        }
        
        // STEP 3: Skip admin switching check (causes scope issues)
        // This feature is handled elsewhere in the app
        
        // STEP 4: Normal auth flow - restore from localStorage
        const storedUser = localStorage.getItem('user');
        console.log('💾 Stored user:', storedUser ? 'exists' : 'none');
        if (storedUser) {
            const userData = JSON.parse(storedUser);
            console.log('👤 User data:', userData.email, userData.id);
            // Verify the stored user is still valid
            try {
              console.log('🔄 Validating user with /auth/me...');
              const response = await axios.get(`${getAPI()}/auth/me?user_id=${userData.id}`);
              console.log('✅ User validated successfully');
              setUser(response.data);
              // Update localStorage with fresh data
              localStorage.setItem('user', JSON.stringify(response.data));
              setShowLandingPage(false); // Hide landing page for authenticated users
              setIsLoading(false);
            } catch (error) {
              console.error('❌ User validation failed:', error.response?.status, error.message);
              // Token/user is invalid, clear storage
              localStorage.removeItem('user');
              setUser(null);
              setShowLandingPage(true); // Show landing page for unauthenticated users
              setIsLoading(false);
            }
        } else {
          console.log('🏠 No stored user - showing landing page');
          // No stored user - show landing page first
          setUser(null);
          setShowLandingPage(true);
          setIsLoading(false);
        }
      } catch (error) {
        // User not authenticated
        console.error('❌ Auth check error:', error);
        setUser(null);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Check for pending redemption token when user is already logged in
  const pendingRedemptionChecked = React.useRef(false);
  // Check for ?redeem=CODE parameter in URL and store for redemption
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redeemCode = urlParams.get('redeem');
    const giftCheckout = urlParams.get('gift-checkout');
    
    if (redeemCode) {
      console.log('🎁 Found redeem code in URL:', redeemCode);
      // Store the code in localStorage so it persists through login if needed
      localStorage.setItem('pendingRedemptionToken', redeemCode);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // If user is already logged in, open the modal immediately
      if (user) {
        setTimeout(() => {
          setShowRedeemCredits(true);
        }, 500);
      }
      // If not logged in, the modal will open after login (handled by the effect below)
    }
    
    if (giftCheckout === 'true') {
      console.log('🎁 Opening gift checkout modal');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Open gift checkout if user is logged in
      if (user) {
        setShowGiftCheckout(true);
      }
    }
  }, [user]); // Run when user changes

  useEffect(() => {
    if (!user || pendingRedemptionChecked.current) return; // Only run once when user is logged in
    
    const pendingToken = localStorage.getItem('pendingRedemptionToken');
    if (pendingToken) {
      console.log('🎁 Found pending redemption token for logged-in user, opening redeem modal');
      pendingRedemptionChecked.current = true; // Mark as checked to prevent duplicate runs
      // Small delay to ensure UI is ready
      setTimeout(() => {
        setShowRedeemCredits(true);
        // NOTE: Do NOT remove pendingRedemptionToken here - let RedeemCreditsModal handle it
        // This prevents double redemption by ensuring only RedeemCreditsModal's useEffect processes the token
      }, 500);
    }
  }, [user]); // Runs when user state changes (after login or on mount if already logged in)
  
  // Listen for access request sent modal trigger
  useEffect(() => {
    const handleShowModal = () => {
      const shouldShow = localStorage.getItem('showAccessRequestSentModal');
      
      if (shouldShow === 'true') {
        console.log('🎨 ✅ Showing access request sent modal from App.js');
        localStorage.removeItem('showAccessRequestSentModal');
        setShowAccessRequestSentModal(true);
        
        // Auto-close after 4 seconds
        setTimeout(() => {
          console.log('🎨 Auto-closing modal');
          setShowAccessRequestSentModal(false);
        }, 4000);
      } else {
        // Modal not shown (no pending request)
      }
    };
    
    console.log('🎨 Setting up access request modal listeners');
    
    // Listen for custom event (storage event only fires across different windows/tabs)
    window.addEventListener('showAccessRequestModal', (e) => {
      console.log('🎨 🔔 Received showAccessRequestModal event');
      handleShowModal();
    });
    // Also listen for storage events for cross-tab support
    window.addEventListener('storage', (e) => {
      // Silently handle storage events
      handleShowModal();
    });
    // Also check on mount
    console.log('🎨 Checking on mount');
    handleShowModal();
    
    return () => {
      window.removeEventListener('showAccessRequestModal', handleShowModal);
      window.removeEventListener('storage', handleShowModal);
    };
  }, []);

  // Check for purchase success and refresh credits
  useEffect(() => {
    const checkPurchaseSuccess = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const creditsParam = urlParams.get('credits');
      const purchaseSuccess = urlParams.get('purchase_success');
      
      console.log('🔍 Checking purchase success:', { purchaseSuccess, creditsParam, hasUser: !!user });
      
      if (purchaseSuccess === 'true' && user) {
        const purchaseKey = `purchase_${creditsParam}`;
        const giftOrder = urlParams.get('gift_order');
        
        // Check if we've already processed this purchase
        if (processedCreditPurchaseRef.current === purchaseKey) {
          console.log('⚠️ Credit purchase already processed, skipping');
          return;
        }
        
        // Mark as processed IMMEDIATELY
        processedCreditPurchaseRef.current = purchaseKey;
        
        console.log('🎉 Purchase success detected! Credits:', creditsParam, 'Gift Order:', giftOrder);
        
        // DON'T clean URL yet if this is a gift order - need to preserve gift_order param
        // Credit animation will check for it
        if (giftOrder !== 'true') {
          // Clean URL immediately only for non-gift purchases
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        
        const creditsAdded = parseInt(creditsParam) || 0;
        
        console.log('🎬 Showing credit animation for', creditsAdded, 'credits');
        
        // Show credit animation immediately
        if (creditsAdded > 0) {
          setShowCreditAnimation(true);
          setPurchasedCredits(creditsAdded);
        } else {
          toast.success('✨ Purchase complete! Your credits have been added.', {
            duration: 5000
          });
        }
        
        // Refresh user data in background
        setTimeout(async () => {
          try {
            const response = await axios.get(`${getAPI()}/auth/me?user_id=${user.id}`);
            const freshUserData = response.data;
            
            setUser(freshUserData);
            localStorage.setItem('user', JSON.stringify(freshUserData));
            
            console.log('✅ User data refreshed');
          } catch (err) {
            console.error('Error refreshing user data:', err);
          }
        }, 1000);
      }
    };
    
    checkPurchaseSuccess();
  }, [user, window.location.search]); // Added location.search to trigger on URL change


  // Check for gift order completion (no credits case)
  useEffect(() => {
    const checkGiftOrder = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const giftOrder = urlParams.get('gift_order');
      const purchaseSuccess = urlParams.get('purchase_success');
      
      console.log('🔍 Checking gift order params:', { giftOrder, purchaseSuccess, hasUser: !!user });
      
      // If gift_order=true but NO purchase_success (meaning no credits)
      if (giftOrder === 'true' && purchaseSuccess !== 'true' && user) {
        console.log('🎁 Gift order completed without credits, showing WhatsNext modal');
        
        // Get order data from localStorage
        const giftOrderData = localStorage.getItem('gift_order_complete');
        console.log('📦 Gift order data from localStorage:', giftOrderData);
        
        if (giftOrderData) {
          const { orderNumber, addonCredits } = JSON.parse(giftOrderData);
          console.log('✅ Parsed order data:', { orderNumber, addonCredits });
          
          // Set order info with a delay to ensure state updates
          setTimeout(() => {
            setSelectedMailInPackage({
              order_number: orderNumber,
              addon_credits: addonCredits || 0
            });
            
            // Show WhatsNext modal
            setShowWhatsNextModal(true);
            console.log('✅ WhatsNext modal should now be visible');
            
            // Clean URL and localStorage
            window.history.replaceState({}, document.title, window.location.pathname);
            localStorage.removeItem('gift_order_complete');
          }, 300);
        } else {
          console.warn('⚠️ No gift order data found in localStorage');
        }
      }
    };
    
    checkGiftOrder();
  }, [user]);



  // Polling mechanism to check for credit updates (when admin adds/removes credits)
  useEffect(() => {
    if (!user) return;

    const checkCreditUpdates = async () => {
      try {
        const response = await axios.get(`${getAPI()}/auth/me?user_id=${user.id}`);
        const freshUserData = response.data;
        
        // Check if credits have changed
        if (freshUserData.credits !== user.credits) {
          const creditDiff = freshUserData.credits - user.credits;
          const absChange = Math.abs(creditDiff);
          
          // Only show notification if change is significant (> 0.01 credits)
          // This prevents false positives from floating-point rounding differences
          const NOTIFICATION_THRESHOLD = 0.01;
          
          if (absChange > NOTIFICATION_THRESHOLD) {
            const changeType = creditDiff > 0 ? 'added' : 'removed';
            
            // Show notification only for credits ADDED (not removed)
            if (creditDiff > 0) {
              // Suppress toasts within 10 seconds of batch cancellation to avoid false positives
              const timeSinceCancel = Date.now() - lastBatchCancelTime.current;
              const isRecentCancel = timeSinceCancel < 10000; // 10 seconds
              
              if (!isRecentCancel) {
                // Check if this is a small amount (likely from video shares)
                if (absChange <= 0.2) {
                  toast.success(`🎉 Your video is popular! Thanks for sharing!`, {
                    duration: 5000,
                    description: `+${absChange.toFixed(2)} credits earned • Balance: ${freshUserData.credits.toFixed(2)} credits`
                  });
                } else {
                  // Removed toast: credits added to your account (silent for non-gift additions)
                }
              } else {
                console.log('🔕 Suppressing credit toast (recent batch cancel)');
              }
            }
            // Credit deductions are always silent (no toast)
          } else {
            // Silent update for minor differences (rounding errors)
            console.log(`🔄 Minor credit sync: ${creditDiff.toFixed(4)} (no notification)`);
          }
          
          // Update user state regardless of threshold
          setUser(freshUserData);
          localStorage.setItem('user', JSON.stringify(freshUserData));
        }
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.error('Error checking credit updates:', error);
      }
    };

    // Check for credit updates every 10 seconds
    const intervalId = setInterval(checkCreditUpdates, 10000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [user]);

  // Load user's orders from Shopify and frame_orders
  const loadMyOrders = async () => {
    if (!user?.id) return;
    
    setLoadingOrders(true);
    try {
      const response = await axios.get(`${getAPI()}/my-orders?user_id=${user.id}`);
      setMyOrders(response.data);
      console.log(`📦 Loaded ${response.data.total_orders} orders`);
      console.log(`   Shopify orders: ${response.data.shopify_orders.length}`);
      console.log(`   Mail-in orders: ${response.data.mail_in_orders.length}`);
      console.log('   Mail-in orders data:', response.data.mail_in_orders);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleLogin = (userData) => {
    console.log('🔐 handleLogin called with user:', userData?.email);
    
    // DEBUG: Show ALL localStorage values related to access request
    console.log('🔍 localStorage after login:', {
      redirectAfterLogin: localStorage.getItem('redirectAfterLogin'),
      requestAccessAfterLogin: localStorage.getItem('requestAccessAfterLogin'),
      albumShareToken: localStorage.getItem('albumShareToken'),
      redirectToAlbum: localStorage.getItem('redirectToAlbum')
    });
    
    setUser(userData);
    // Store user data in localStorage for persistence across page refreshes
    localStorage.setItem('user', JSON.stringify(userData));
    setShowLandingPage(false); // Hide landing page after successful login
    
    // Check for pending redemption
    const pendingToken = localStorage.getItem('pendingRedemptionToken');
    if (pendingToken) {
      console.log('🎁 Found pending redemption token, opening redeem modal');
      localStorage.removeItem('pendingRedemptionToken');
      // Open redeem modal after a short delay
      setTimeout(() => {
        setShowRedeemCredits(true);
      }, 500);
    }
    
    // Check for album URL parameter (e.g., from public share page "View Album" button)
    const urlParams = new URLSearchParams(window.location.search);
    const albumIdFromUrl = urlParams.get('album');
    const tabFromUrl = urlParams.get('tab');
    
    if (albumIdFromUrl) {
      console.log('🔗 Album ID from URL:', albumIdFromUrl);
      // Store in localStorage so AlbumsTab can open it
      localStorage.setItem('redirectToAlbum', albumIdFromUrl);
      // Switch to Collaborate tab
      setActiveMainTab('collaborate');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (tabFromUrl === 'collaboration') {
      // Just open collaboration tab without specific album
      console.log('🔗 Opening collaboration tab from URL');
      setActiveMainTab('collaborate');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check for pending album redirect from invitation
    const redirectAlbumId = localStorage.getItem('redirectToAlbum');
    if (redirectAlbumId) {
      // Switch to Collaborate tab (Albums)
      setActiveMainTab('collaborate');
      // DON'T remove redirectToAlbum here - let AlbumsTab handle it after opening the album
      console.log('🔗 Redirecting to album after login:', redirectAlbumId);
    }
    
    // Check for pending redirect after login
    const redirectAfterLogin = localStorage.getItem('redirectAfterLogin');
    console.log('🔍 Checking redirectAfterLogin:', redirectAfterLogin);
    
    if (redirectAfterLogin === 'mail-in-service') {
      console.log('🔗 Redirecting to mail-in service after login');
      setCurrentView('mail-in-service');
      localStorage.removeItem('redirectAfterLogin');
    } else if (redirectAfterLogin && redirectAfterLogin.startsWith('/album/share/')) {
      // Redirect to album share page for access request
      console.log('🔗 Redirecting to album share page after login:', redirectAfterLogin);
      console.log('🔗 About to do window.location.href redirect...');
      // Navigate will happen from PublicAlbumShare component
      window.location.href = redirectAfterLogin;
    } else {
      console.log('❌ No redirectAfterLogin or doesn\'t match pattern. Value:', redirectAfterLogin);
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend logout if session token exists
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        try {
          await axios.post(`${getAPI()}/auth/logout`, {}, {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          });
        } catch (error) {
          console.error('Logout API error:', error);
          // Continue with local logout even if API fails
        }
      }
      
      setUser(null);
      // Clear all auth data from localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('session_token');
      localStorage.removeItem('token');
      setShowLandingPage(true); // Show landing page after logout
      toast.success('Until we meet again in the memories! 👋✨');
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if there's an error
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('session_token');
      localStorage.removeItem('token');
      setShowLandingPage(true);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-amber-800 font-medium">Loading your precious moments...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App">
        <AuthContext.Provider value={{ user, login: handleLogin, logout: handleLogout }}>
          <Suspense fallback={
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-50">
              <div className="text-center">
                <div className="mx-auto mb-4 animate-pulse">
                  <Logo width={64} height={64} />
                </div>
                <p className="text-amber-700 font-medium">Loading Magic Moments...</p>
              </div>
            </div>
          }>
            {user ? (
              <>
                <Routes>
                  <Route path="/" element={<MemoryDashboard 
                    user={user}
                    setUser={setUser}
                    showSupport={showSupport} 
                    setShowSupport={setShowSupport}
                    showOrdersDashboard={showOrdersDashboard}
                    setShowOrdersDashboard={setShowOrdersDashboard}
                    showAdminPanel={showAdminPanel}
                    setShowAdminPanel={setShowAdminPanel}
                    showReferralDashboard={showReferralDashboard}
                    setShowReferralDashboard={setShowReferralDashboard}
                    onLogout={handleLogout}
                    onProfileClick={() => setShowProfile(true)}
                    onCreditsClick={(type) => {
                      if (type === 'buy') {
                        setShowCredits(true);
                      } else if (type === 'earn') {
                        startTransition(() => setShowEarnCredits(true));
                      } else if (type === 'redeem') {
                        setShowRedeemCredits(true);
                      } else if (type === 'pricing') {
                        setShowPricingModal(true);
                      }
                    }}
                    showCreditsDropdown={showCreditsDropdown}
                    setShowCreditsDropdown={setShowCreditsDropdown}
                    showMagicProcessing={showMagicProcessing}
                    setShowMagicProcessing={setShowMagicProcessing}
                    setProcessingMessage={setProcessingMessage}
                    setProcessingCount={setProcessingCount}
                    showFrameDemo={showFrameDemo}
                    setShowFrameDemo={setShowFrameDemo}
                    preSelectedFrameColor={preSelectedFrameColor}
                    setPreSelectedFrameColor={setPreSelectedFrameColor}
                    preSelectedFrameProduct={preSelectedFrameProduct}
                    setPreSelectedFrameProduct={setPreSelectedFrameProduct}
                    isGiftMode={isGiftMode}
                    setIsGiftMode={setIsGiftMode}
                    showGiftCardModal={showGiftCardModal}
                    setShowGiftCardModal={setShowGiftCardModal}
                    giftCardProduct={giftCardProduct}
                    setGiftCardProduct={setGiftCardProduct}
                    loadCartFrameItem={loadCartFrameItem}
                    setLoadCartFrameItem={setLoadCartFrameItem}
                    showToast={showToast}
                    showTerms={showTerms}
                    setShowTerms={setShowTerms}
                    showPrivacy={showPrivacy}
                    setShowPrivacy={setShowPrivacy}
                    showContact={showContact}
                    setShowContact={setShowContact}
                    fileInputRef={fileInputRef}
                    onEditImage={handleEditImage}
                    albumRefreshCallback={albumRefreshCallback}
                    albumFilterSwitchCallback={albumFilterSwitchCallback}
                    showMailInCheckout={showMailInCheckout}
                    setShowMailInCheckout={setShowMailInCheckout}
                    selectedMailInPackage={selectedMailInPackage}
                    setSelectedMailInPackage={setSelectedMailInPackage}
                    showWhatsNextModal={showWhatsNextModal}
                    setShowWhatsNextModal={setShowWhatsNextModal}
                    mailInOrders={mailInOrders}
                    setMailInOrders={setMailInOrders}
                    loadingMailInOrders={loadingMailInOrders}
                    trackingOrders={trackingOrders}
                    loadingTrackingOrders={loadingTrackingOrders}
                    fetchTrackingOrders={fetchTrackingOrders}
                    hasFetchedTrackingOrders={hasFetchedTrackingOrders}
                    showMailInGiftOccasion={showMailInGiftOccasion}
                    setShowMailInGiftOccasion={setShowMailInGiftOccasion}
                    mailInGiftOccasion={mailInGiftOccasion}
                    setMailInGiftOccasion={setMailInGiftOccasion}
                    setLoadingMailInOrders={setLoadingMailInOrders}
                    showGiftCheckout={showGiftCheckout}
                    setShowGiftCheckout={setShowGiftCheckout}
                  />} />
                  {/* Custom checkout page */}
                  <Route path="/checkout" element={<CheckoutPage user={user} />} />
                  {/* Shop order success page */}
                  <Route path="/order-success" element={<OrderSuccess user={user} />} />
                  {/* Stripe purchase success page */}
                  <Route path="/purchase-success" element={<PurchaseSuccess user={user} setUser={setUser} />} />
                  {/* Gift success page */}
                  <Route path="/gift/success" element={<GiftSuccessPage />} />
                  {/* Order confirmation page */}
                  <Route path="/order-confirmation" element={<OrderConfirmation user={user} />} />
                  {/* Standalone Admin Panel - Full Screen */}
                  <Route path="/admin" element={
                    user ? (
                      <div className="fixed inset-0 bg-white overflow-hidden">
                        <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <AdminPanel currentUser={user} onClose={() => window.close()} isFullScreen={true} />
                        </Suspense>
                      </div>
                    ) : <Navigate to="/" />
                  } />
                  {/* Admin Metrics Dashboard */}
                  <Route path="/admin/metrics" element={
                    user && user.role === 'admin' ? (
                      <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>}>
                        <AdminMetricsDashboard user={user} />
                      </Suspense>
                    ) : <Navigate to="/" />
                  } />
                  {/* Standalone Orders Dashboard - Full Screen */}
                  <Route path="/my-orders" element={
                    user ? (
                      <div className="fixed inset-0 bg-white overflow-hidden">
                        <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
                          <OrdersDashboard userEmail={user?.email} onClose={() => window.close()} />
                        </Suspense>
                      </div>
                    ) : <Navigate to="/" />
                  } />
                  {/* Handle invitation acceptance for logged-in users */}
                  <Route path="/accept-invite/:token" element={<InviteAcceptHandler user={user} />} />
                  {/* Public video watch - accessible even when logged in */}
                  <Route path="/watch/:shareToken" element={<PublicVideoWatch />} />
                  {/* Public album share - accessible even when logged in */}
                  <Route path="/album/share/:shareToken" element={<PublicAlbumShare />} />
                  <Route path="/redeem/:token" element={<GiftRedeemPage user={user} />} />
                  <Route path="/redeem" element={<GiftRedeemPage user={user} />} />
                  {/* Legal Pages */}
                  <Route path="/privacy-policy" element={<PrivacyPolicy onClose={() => window.location.href = 'https://magic-moments.ai'} />} />
                  <Route path="/return-policy" element={<ReturnPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService onClose={() => window.location.href = 'https://magic-moments.ai'} />} />
                  <Route path="/data-deletion" element={<DataDeletion />} />
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
                {/* Global Modals */}
                {showProfile && <MemoryProfileModalPortal 
                  user={user} 
                  onClose={() => setShowProfile(false)} 
                  onNavigateToProjects={() => {
                    setCurrentView('projects');
                    setProjectSubtab('my');
                    setShowArchived(false);
                  }}
                  onProfileUpdate={(updatedUser) => {
                    // Update user context with new data
                    setUser(updatedUser);
                    
                    // If we're impersonating, also update localStorage
                    const impersonatedUser = localStorage.getItem('impersonatedUser');
                    if (impersonatedUser) {
                      const parsedImpersonated = JSON.parse(impersonatedUser);
                      if (parsedImpersonated.id === updatedUser.id) {
                        localStorage.setItem('impersonatedUser', JSON.stringify(updatedUser));
                      }
                    }
                  }}
                  onUserSwitch={(newUser) => {
                    // Store impersonation in localStorage
                    localStorage.setItem('impersonatedUser', JSON.stringify(newUser));
                    localStorage.setItem('originalAdmin', JSON.stringify(user));
                    
                    setUser(newUser);
                    toast.success(`Switched to ${newUser.email}`);
                    // Reload page to refresh all data for new user
                    setTimeout(() => window.location.reload(), 500);
                  }}
                />}
              </>
            ) : showLandingPage ? (
              <Routes>
                {/* Public routes MUST come first to prevent landing page from catching them */}
                <Route path="/watch/:shareToken" element={<PublicVideoWatch />} />
                <Route path="/album/share/:shareToken" element={<PublicAlbumShare />} />
                <Route path="/" element={<LandingPage onShowAuth={(redirectTo) => {
                  if (redirectTo === 'signup') {
                    localStorage.setItem('auth_initial_mode', 'signup');
                  } else if (redirectTo) {
                    localStorage.setItem('redirectAfterLogin', redirectTo);
                  }
                  setShowLandingPage(false);
                }} onShowFrameDemo={() => setShowFrameDemo(true)} setShowTerms={setShowTerms} setShowPrivacy={setShowPrivacy} setShowContact={setShowContact} />} />
                <Route path="/redeem/:token" element={<GiftRedeemPage user={user} />} />
                <Route path="/redeem" element={<GiftRedeemPage user={user} />} />
                <Route path="/accept-invite/:token" element={<LandingPage onShowAuth={(redirectTo) => {
                  if (redirectTo === 'signup') {
                    localStorage.setItem('auth_initial_mode', 'signup');
                  } else if (redirectTo) {
                    localStorage.setItem('redirectAfterLogin', redirectTo);
                  }
                  setShowLandingPage(false);
                }} onShowFrameDemo={() => setShowFrameDemo(true)} setShowTerms={setShowTerms} setShowPrivacy={setShowPrivacy} setShowContact={setShowContact} />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy onClose={() => window.location.href = 'https://magic-moments.ai'} />} />
                <Route path="/terms-of-service" element={<TermsOfService onClose={() => window.location.href = 'https://magic-moments.ai'} />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            ) : (
              <Routes>
                {/* Public routes MUST come first to prevent login form from catching them */}
                <Route path="/watch/:shareToken" element={<PublicVideoWatch />} />
                <Route path="/album/share/:shareToken" element={<PublicAlbumShare />} />
                <Route path="/" element={<MemoryLoginForm onLogin={handleLogin} />} />
                <Route path="/accept-invite/:token" element={<MemoryLoginForm onLogin={handleLogin} isInviteAccept={true} />} />
                <Route path="/redeem/:token" element={<GiftRedeemPage user={user} />} />
                <Route path="/redeem" element={<GiftRedeemPage user={user} />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy onClose={() => window.location.href = 'https://magic-moments.ai'} />} />
                <Route path="/terms-of-service" element={<TermsOfService onClose={() => window.location.href = 'https://magic-moments.ai'} />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            )}
          </Suspense>
          
          {/* Render toasts in a portal with high z-index above modals */}
          {ReactDOM.createPortal(
            <Toaster 
              position="top-right"
              toastOptions={{ 
                style: { zIndex: 10000 },
                className: 'toast-notification'
              }} 
            />,
            document.body
          )}
          {ReactDOM.createPortal(
            <ToastContainer 
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              style={{ zIndex: 10000 }}
              newestOnTop={true}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />,
            document.body
          )}
          {/* Credits Modals */}
          {showCredits && <BuyCreditsModalPortal 
            isOpen={showCredits} 
            onClose={() => setShowCredits(false)} 
            user={user} 
            onPurchaseComplete={async () => {
              // Refresh user data
              try {
                const response = await axios.get(`${getAPI()}/auth/me?user_id=${user.id}`);
                setUser(response.data);
                localStorage.setItem('user', JSON.stringify(response.data));
              } catch (error) {
                console.error('Failed to refresh user:', error);
              }
            }}
            onOpenGiftPreview={(pkg) => {
              setSelectedMailInPackage(pkg);
              setShowCreditsGiftPreview(true);
            }}
          />}
          {showEarnCredits && <EarnCreditsModal isOpen={showEarnCredits} onClose={() => setShowEarnCredits(false)} user={user} onCreditsUpdated={async () => {
            // Refresh user data
            try {
              const response = await axios.get(`${getAPI()}/auth/me?user_id=${user.id}`);
              setUser(response.data);
              localStorage.setItem('user', JSON.stringify(response.data));
            } catch (error) {
              console.error('Failed to refresh user:', error);
            }
          }} />}
          
          {/* Access Request Sent Modal */}
          {showAccessRequestSentModal && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl transform animate-scale-in">
                <div className="mb-6 inline-block">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center animate-bounce-once mx-auto">
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Request Sent!</h3>
                <p className="text-gray-600 mb-6">
                  Your access request has been sent to the album owner. You'll be notified when they respond.
                </p>
              </div>
            </div>
          )}
          
          {/* Redeem Credits Modal */}
          {showRedeemCredits && (
            <Suspense fallback={<div>Loading...</div>}>
              <RedeemCreditsModal 
                onClose={() => setShowRedeemCredits(false)} 
                user={user} 
                onSuccess={async (creditsAdded) => {
                  // Refresh user data
                  try {
                    const response = await axios.get(`${getAPI()}/auth/me?user_id=${user.id}`);
                    setUser(response.data);
                    localStorage.setItem('user', JSON.stringify(response.data));
                  } catch (error) {
                    console.error('Failed to refresh user:', error);
                  }
                }}
              />
            </Suspense>
          )}
          
          {/* Mail-In Gift Checkout Modal */}
          {showGiftCheckout && (
            <Suspense fallback={<div>Loading...</div>}>
              <MailInGiftCheckout 
                user={user}
                onClose={() => {
                  setShowGiftCheckout(false);
                  // Optionally clear gift data if user cancels
                  // localStorage.removeItem('mailin_gift_redemption');
                }}
              />
            </Suspense>
          )}
          
          {/* Pricing Modal */}
          {showPricingModal && <PricingModalPortal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} />}
          
          {/* Magical Processing Overlay */}
          {showMagicProcessing && (
            <MagicProcessingOverlay 
              message={processingMessage}
              count={processingCount}
            />
          )}

          {/* Digital Frame Demo - Will be passed from MemoryDashboard */}
          
          {/* Toast Notification */}
          {toastMessage && (
            <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-[9999] transition-all duration-300 ${
              showToastFlag ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}>
              <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-2xl flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-semibold">{toastMessage}</span>
              </div>
            </div>
          )}
        </AuthContext.Provider>
      </div>

      {/* Coming Soon Gate - REMOVED - Direct access enabled */}

      {/* Legal Modals */}
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showContact && <ContactUs onClose={() => setShowContact(false)} />}
      
      {/* Image Editor Modal */}
      {showImageEditor && selectedImage && (
        <ImageEditorModalPortal
          image={selectedImage}
          context="pictures"
          user={user}
          albumRefreshCallback={albumRefreshCallback}
          onClose={() => {
            setShowImageEditor(false);
            setSelectedImage(null);
          }}
          onSave={async (result) => {
            try {
              const albumId = selectedImage?.album_id;
              setShowImageEditor(false);
              setSelectedImage(null);

              if (albumId) {
                // Album context: Refresh is now handled inside ImageEditorModal itself
                // This avoids the closure/scoping issue with portals
                console.log('📝 Album image saved, refresh will be triggered by ImageEditorModal');
              } else {
                // Pictures tab context: Update the specific image in state with fresh data from backend
                showToast('Image saved! Refreshing…');
                
                // Update the image in unassignedImages state with the fresh data from backend
                if (result && selectedImage?.id) {
                  console.log('📝 Updating image in state after save:', {
                    imageId: selectedImage.id,
                    hasUpdatedAt: !!result.updated_at,
                    hasUrl: !!result.url,
                    resultData: result
                  });
                  
                  const updatedImage = { ...selectedImage, ...result, _lastUpdated: Date.now() };
                  
                  setUnassignedImages(prev => prev.map(img => 
                    img.id === selectedImage.id 
                      ? updatedImage
                      : img
                  ));
                  
                  // CRITICAL: Also update fullScreenImage if it's currently showing this image
                  setFullScreenImage(prev => {
                    if (prev && prev.id === selectedImage.id) {
                      console.log('📝 Also updating fullScreenImage with fresh data');
                      return updatedImage;
                    }
                    return prev;
                  });
                }
                
                // Also refresh full data to ensure consistency
                await loadDashboardData?.();
                await loadVideoJobs?.();
              }
            } catch (err) {
              console.error('Post-save handler error:', err);
            }
          }}
        />
      )}
      
      {/* Mail-In Gift Occasion Selection Modal */}
      <MailInGiftOccasionModal
        isOpen={showMailInGiftOccasion}
        onClose={() => {
          setShowMailInGiftOccasion(false);
          setSelectedMailInPackage(null);
        }}
        onSelectOccasion={(occasion) => {
          setMailInGiftOccasion(occasion);
          setShowMailInGiftOccasion(false);
          setShowMailInGiftPreview(true);
        }}
      />

      {/* Mail-In Service eGift Preview Modal */}
      {showMailInGiftPreview && selectedMailInPackage && (
        <MailInServiceEGiftPreview
          isOpen={showMailInGiftPreview}
          onClose={() => {
            setShowMailInGiftPreview(false);
            setSelectedMailInPackage(null);
            setMailInGiftOccasion('');
          }}
          packageDetails={selectedMailInPackage}
          occasion={mailInGiftOccasion}
          user={user}
          userEmail={user?.email}
          userName={user?.name || user?.email?.split('@')[0]}
        />
      )}
      
      {/* Credits eGift Preview Modal */}
      {showCreditsGiftPreview && selectedMailInPackage && (
        <CreditsEGiftPreview
          isOpen={showCreditsGiftPreview}
          onClose={() => {
            setShowCreditsGiftPreview(false);
            setSelectedMailInPackage(null);
          }}
          creditPackage={selectedMailInPackage}
          onAddToCart={async () => {
            try {
              console.log('Adding gift to cart, package:', selectedMailInPackage);
              
              // Add gift to cart with placeholder data - user will configure at checkout
              const payload = {
                product_id: `gift-credits-${selectedMailInPackage.id}`,
                variant_id: 0, // Use 0 for gift cards (not a Shopify variant)
                title: `Gift Card - ${selectedMailInPackage.credits} Credits`,
                price: parseFloat(selectedMailInPackage.price),
                quantity: 1,
                image_url: null,
                recipient_name: "To be configured",
                recipient_email: "configure@checkout.com",
                gift_message: null,
                delivery_date: null
              };
              
              console.log('Cart payload:', payload);
              
              const response = await axios.post(
                `${getAPI()}/shop/cart/add-gift?user_id=${user.id}`,
                payload
              );
              
              console.log('Cart response:', response.data);
              
              // Update cart count
              if (response.data.cart_count !== undefined) {
                window.cartCount = response.data.cart_count;
              }
              
              console.log('Closing modals...');
              
              // Close gift preview
              setShowCreditsGiftPreview(false);
              setSelectedMailInPackage(null);
              
              // Close Buy Credits modal
              console.log('Attempting to close Buy Credits modal, current state:', showCredits);
              setShowCredits(false);
              console.log('Buy Credits modal should be closed');
              
              // Show success toast
              toast.success(`🎁 Gift added to cart!`);
              
              // Open cart modal after a brief delay
              setTimeout(() => {
                console.log('Opening cart modal...');
                if (window.openCartModal) {
                  window.openCartModal();
                } else {
                  console.error('window.openCartModal is not defined');
                }
              }, 300);
            } catch (error) {
              console.error('Error adding gift to cart:', error);
              console.error('Error response:', error.response?.data);
              toast.error(error.response?.data?.detail || 'Failed to add gift to cart. Please try again.');
            }
          }}
        />
      )}
      
      {/* Gift Card Purchase Configuration Modal */}
      {showGiftCardPurchaseModal && selectedMailInPackage && (
        <GiftCardPurchaseModal
          isOpen={showGiftCardPurchaseModal}
          onClose={() => {
            setShowGiftCardPurchaseModal(false);
            setSelectedMailInPackage(null);
          }}
          user={user}
          selectedPackage={selectedMailInPackage}
          productType="credits"
        />
      )}
      
      {/* Mail-In Service Checkout Modal */}
      {showMailInCheckout && selectedMailInPackage && (
        <MailInCheckoutModal
          isOpen={showMailInCheckout}
          onClose={() => {
            setShowMailInCheckout(false);
            setSelectedMailInPackage(null);
          }}
          packageDetails={selectedMailInPackage}
          onSuccess={() => {
            setShowWhatsNextModal(true);
          }}
        />
      )}
      
      {/* Credit Purchase Animation */}
      {showCreditAnimation && (
        <CreditPurchaseAnimation 
          credits={purchasedCredits} 
          isGiftOrder={new URLSearchParams(window.location.search).get('gift_order') === 'true' || new URLSearchParams(window.location.search).get('gift_redeem') === 'true'}
          isWelcome={isWelcomeAnimation}
          onComplete={(isGiftOrder) => {
            console.log('🎬 Credit animation complete, isGiftOrder:', isGiftOrder);
            setShowCreditAnimation(false);
            setIsWelcomeAnimation(false); // Reset welcome flag
            
            // Clean up URL parameter if it was a gift redemption
            if (new URLSearchParams(window.location.search).get('gift_redeem') === 'true') {
              window.history.replaceState({}, '', window.location.pathname);
            }
            
            // If this is a gift order, show WhatsNext modal
            if (isGiftOrder) {
              const giftOrderData = localStorage.getItem('gift_order_complete');
              console.log('📦 Gift order data after credit animation:', giftOrderData);
              
              if (giftOrderData) {
                const { orderNumber, addonCredits } = JSON.parse(giftOrderData);
                console.log('✅ Showing WhatsNext modal with:', { orderNumber, addonCredits });
                
                // Set order info with delay to ensure animation completes
                setTimeout(() => {
                  setSelectedMailInPackage({
                    order_number: orderNumber,
                    addon_credits: addonCredits || 0
                  });
                  setShowWhatsNextModal(true);
                  localStorage.removeItem('gift_order_complete');
                  
                  // NOW clean the URL after showing the modal
                  window.history.replaceState({}, document.title, window.location.pathname);
                  console.log('✅ Cleaned URL after showing WhatsNext modal');
                }, 500);
              } else {
                console.warn('⚠️ No gift order data found after credit animation');
                // Clean URL even if no data found
                window.history.replaceState({}, document.title, window.location.pathname);
              }
            }
          }} 
        />
      )}
      
      {/* Mail-In Service "What's Next" Modal */}
      <WhatsNextModal
        isOpen={showWhatsNextModal}
        onClose={() => setShowWhatsNextModal(false)}
        orderNumber={selectedMailInPackage?.order_number}
        addonCredits={selectedMailInPackage?.addon_credits || 0}
        onCreditsUpdated={async () => {
          // Refresh user credits from server
          try {
            const response = await axios.get(`${getAPI()}/auth/me?user_id=${user.id}`);
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            console.log('✅ User credits refreshed:', response.data.credits);
          } catch (error) {
            console.error('Failed to refresh user credits:', error);
          }
        }}
      />
    </BrowserRouter>
  );
}

export default App;